/* eslint-disable unicode-bom */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { nb } from "date-fns/locale";
import { db, auth, app } from "./firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged, signInAnonymously,
} from "firebase/auth";
import {
  collection, addDoc, onSnapshot, doc, updateDoc,
  getDocs, deleteDoc, serverTimestamp, setDoc, getDoc,
  runTransaction, deleteField,
} from "firebase/firestore";

registerLocale("nb", nb);

// Google Fonts - enkle, lesbare fonter uten fancy underheng
(function() {
  if (typeof document === "undefined" || document.getElementById("vl-fonts")) return;
  const l = document.createElement("link");
  l.id = "vl-fonts"; l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Playfair+Display:wght@700;900&family=Libre+Baskerville:wght@700&family=Oswald:wght@600;700&family=Bebas+Neue&family=Black+Ops+One&family=Alfa+Slab+One&family=Russo+One&family=Anton&family=Teko:wght@600;700&family=Roboto+Condensed:wght@700;900&family=Montserrat:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,700;1,900&family=Cormorant+Garamond:wght@500;700&family=EB+Garamond:wght@500;700&family=Lora:wght@500;700&family=Cinzel:wght@500;700;900&family=Marcellus&family=Poppins:wght@500;700;900&family=Raleway:wght@500;700;900&family=Abril+Fatface&family=Righteous&family=Bungee&family=Pacifico&family=Dancing+Script:wght@500;700&family=Great+Vibes&family=Lobster&family=Caveat:wght@500;700&display=swap";
  document.head.appendChild(l);
})();

// Tilgjengelige fonter for tittel. v40: utvidet til 25 fonter i 4 kategorier.
// `category`-feltet brukes av FontPicker for å gruppere alternativene med
// header-linjer i popupen.
const TITLE_FONTS = [
  // Klassisk / elegant
  { category:"Klassisk", label:"Playfair Display",   value:"'Playfair Display',Georgia,serif" },
  { category:"Klassisk", label:"Libre Baskerville",  value:"'Libre Baskerville',Georgia,serif" },
  { category:"Klassisk", label:"Cormorant Garamond", value:"'Cormorant Garamond',Georgia,serif" },
  { category:"Klassisk", label:"EB Garamond",        value:"'EB Garamond',Georgia,serif" },
  { category:"Klassisk", label:"Lora",               value:"'Lora',Georgia,serif" },
  { category:"Klassisk", label:"Cinzel",             value:"'Cinzel',Georgia,serif" },
  { category:"Klassisk", label:"Marcellus",          value:"'Marcellus',Georgia,serif" },
  { category:"Klassisk", label:"Georgia",            value:"Georgia,serif" },
  // Moderne / sans-serif
  { category:"Moderne",  label:"Montserrat",         value:"'Montserrat',sans-serif" },
  { category:"Moderne",  label:"Poppins",            value:"'Poppins',sans-serif" },
  { category:"Moderne",  label:"Raleway",            value:"'Raleway',sans-serif" },
  { category:"Moderne",  label:"Oswald",             value:"'Oswald',sans-serif" },
  { category:"Moderne",  label:"Bebas Neue",         value:"'Bebas Neue',Impact,sans-serif" },
  { category:"Moderne",  label:"Roboto Condensed",   value:"'Roboto Condensed',sans-serif" },
  { category:"Moderne",  label:"Teko",               value:"'Teko',sans-serif" },
  // Display / dekorativ
  { category:"Display",  label:"Anton",              value:"'Anton',Impact,sans-serif" },
  { category:"Display",  label:"Alfa Slab One",      value:"'Alfa Slab One',Georgia,serif" },
  { category:"Display",  label:"Russo One",          value:"'Russo One',Impact,sans-serif" },
  { category:"Display",  label:"Black Ops One",      value:"'Black Ops One',Impact,sans-serif" },
  { category:"Display",  label:"Abril Fatface",      value:"'Abril Fatface',Georgia,serif" },
  { category:"Display",  label:"Righteous",          value:"'Righteous',sans-serif" },
  { category:"Display",  label:"Bungee",             value:"'Bungee',sans-serif" },
  // Hånd-skrevet / script
  { category:"Hånd",     label:"Pacifico",           value:"'Pacifico',cursive" },
  { category:"Hånd",     label:"Dancing Script",     value:"'Dancing Script',cursive" },
  { category:"Hånd",     label:"Great Vibes",        value:"'Great Vibes',cursive" },
  { category:"Hånd",     label:"Lobster",            value:"'Lobster',cursive" },
  { category:"Hånd",     label:"Caveat",             value:"'Caveat',cursive" },
];

const ALLOWED_EMAILS = ["baard.westgaard@gmail.com","mondoranse@gmail.com"];

// v135: delte avlys-banner-strenger så mobil og PC ikke driver fra hverandre.
// Brukes i begge spillside-grenene (mobil + PC). Marup-stilen håndteres lokalt
// i hver gren, men teksten kommer herfra.
const AVLYS_TITTEL = "Forrige runde ble avlyst";
const AVLYS_FORKLARING = "De som hadde betalt for lodd har fått tilgodehavende. Beløpet trekkes automatisk fra ved neste loddkjøp.";
const AVLYS_VENTER = "⏳ Venter på at arrangør starter ny runde";
// v325 (minLodd steg c): spesifikk forklaring når serveren avlyste pga. for få
// solgte lodd (lottery/current.avlysReason skrives av utforAvlysning i functions).
const AVLYS_FORKLARING_FOR_FAA_LODD = "Lotteriet ble avlyst på grunn av for få solgte lodd. Beløpet er lagt til som tilgode til neste trekning.";
const avlysForklaringFor = (reason) => reason === "for-faa-lodd" ? AVLYS_FORKLARING_FOR_FAA_LODD : AVLYS_FORKLARING;

// v144: stor forbokstav på hvert ord. Rører kun små forbokstaver (etter
// streng-start eller mellomrom), så "McAllister"/"von Trapp" og allerede
// kapitaliserte navn er trygge. Brukes ved input + ved e-post-rendering.
const capName = (str) => (str || "").replace(/(^|\s)([a-zæøå])/g, (m, sep, letter) => sep + letter.toUpperCase());

// v153: synkron clipboard-kopiering. MÅ kalles innenfor den synkrone user-
// gesture-konteksten (før noen await), ellers avviser iOS Safari skrivingen
// stille. Forsøker clipboard-API + execCommand-fallback for eldre iOS.
function copyToClipboardSync(text) {
  let ok = false;
  try {
    if (navigator.clipboard?.writeText) { navigator.clipboard.writeText(text); ok = true; }
  } catch (_) {}
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    ta.setAttribute("readonly", "");
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    const result = document.execCommand("copy");
    document.body.removeChild(ta);
    if (result) ok = true;
  } catch (_) {}
  return ok;
}

// Appens versjonsnummer — oppdateres ved hver nye releasese.
// Format: MAJOR.MINOR.PATCH
// MAJOR: Store arkitektur-endringer eller nye trekningsmetoder
// MINOR: Nye funksjoner som ikke bryter eksisterende oppsett
// PATCH: Bugfiks, UI-justeringer, finpuss
// ─── APP-VERSJON ──────────────────────────────────────────────────────
// Format: YYYY.MM.DD-N (dato + iterasjon-nummer den dagen)
// Vises på arrangør-siden under uke-label, og i footers.
// ØK DETTE for hver ny iterasjon!
const APP_VERSION = "2026.07.14-325";

// v167: trekningsmetoder — delt data-kilde for hurtig-velgeren i Trekning-
// dropdownen (PC + mobil). "mix" er en egen indikator-verdi (ikke i denne
// arrayen). v171: miks veksler nå mellom ALLE fire spillene i shuffled
// rekkefølge (ny stokking hver 4. premie) — ingen utvalg-checkboxes lenger.
const TREKNING_METODER = [
  { id:"hjul",    icon:"🎡", navn:"Lykkehjul" },
  { id:"banditt", icon:"🎰", navn:"Slot machine" },
  { id:"lodd",    icon:"🎫", navn:"Loddtrekning" },
  { id:"lotto",   icon:"🎱", navn:"Lotto-kule" },
];
// v204 (Vei C fase 2): klient-side getMixOrder + TREKNING_METODE_IDS fjernet —
// miks-rekkefølgen bestemmes nå utelukkende av serveren (functions getMixOrder).

// Unik enhets-ID for enkel-enhet-sjekk. v121: lagres i localStorage så flere
// tabs/windows i SAMME nettleser deler identitet. Tidligere var dette en
// modul-konstant generert per page-load, noe som kickte to vinduer på samme
// PC mot hverandre. localStorage deles mellom tabs+windows for samme origin,
// så ekte annen-enhet (annen browser, annen PC, mobil) fortsatt får distinkt
// ID. Navnet SESSION_ID beholdes for å unngå downstream-rename på linje
// ~13462 (nextPrize) og ~14121 (checkForKick).
const SESSION_ID = (() => {
  try {
    let id = localStorage.getItem("vl_deviceId");
    if (!id) {
      id = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("vl_deviceId", id);
    }
    return id;
  } catch (e) {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
})();

// v106: Modul-nivå claim — kalles fra App-rootens kick-listener-effekt så
// snart en arrangør-e-post er innlogget. Skriver BÅDE settings/adminSession
// (drevet av kick-listeneren) og lottery/current.activeSession (lest av
// nextPrize på linje ~13315 for å blokkere videreklikk fra en kicket enhet).
async function claimAdminSessionForEmail(email) {
  if (!email) return;
  await setDoc(
    doc(db, "settings", "adminSession"),
    { sessionId: SESSION_ID, claimedAt: serverTimestamp(), email },
    { merge: false }
  );
  try {
    await setDoc(
      doc(db, "lottery", "current"),
      { activeSession: SESSION_ID },
      { merge: true }
    );
  } catch (e) {
    console.error("[adminSession] Kunne ikke skrive activeSession til lottery/current:", e);
  }
}

// v108: Atomisk allokering av loddnumre. Telleren ligger i counters/tickets.next.
// Tidligere v107 brukte lottery/current.nextTicketNumber, men firestore.rules
// tillater ikke anonyme deltakere å skrive til lottery/current — den er
// arrangør-only. Egen counters-path har egne regler som tillater monotonisk
// inkrement fra alle, og full reset kun fra Google-innloggede arrangører.
//
// Migrasjon (variant a): vi gjør outside-reads for å bygge en fallback-verdi
// hvis counters/tickets ikke eksisterer ennå. Prioritert:
//   1) lottery/current.nextTicketNumber (skrevet av v107, hvis runden var i
//      gang da v107 var deployet)
//   2) totalSold + 1 fra getDocs(participants)
// Når counters/tickets er skrevet er den autoritativ.
async function allocateTicketNumbers(qty) {
  let fallbackStart = 1;
  try {
    const lotterySnap = await getDoc(doc(db, "lottery", "current"));
    const v107Field = lotterySnap.exists() ? lotterySnap.data().nextTicketNumber : undefined;
    if (typeof v107Field === "number" && v107Field > 0) {
      fallbackStart = v107Field;
    } else {
      const partSnap = await getDocs(collection(db, "participants"));
      const sold = partSnap.docs.reduce((s, d) => s + (d.data().tickets || 0), 0);
      fallbackStart = sold + 1;
    }
  } catch (e) {
    console.warn("[ticket-alloc] fallback-init feilet, bruker 1:", e);
  }
  return await runTransaction(db, async (tx) => {
    const ref = doc(db, "counters", "tickets");
    const snap = await tx.get(ref);
    const existing = snap.exists() ? snap.data().next : undefined;
    const fromField = typeof existing === "number" && existing > 0;
    const start = fromField ? existing : fallbackStart;
    const nums = Array.from({ length: qty }, (_, i) => start + i);
    const next = start + qty;
    // tx.set fungerer både for create (første kjøp) og update (vanlig flow).
    // Kun feltet `next` skrives — matcher firestore.rules-valideringen.
    tx.set(ref, { next });
    return nums;
  });
}

// v55 FIX 1: profesjonaliserte tittel-effekter, delt mellom SettingsPanel-
// forhåndsvisningen og spiller-vyens tittel-rendering. Metallene har en
// skarp høylys-stripe midt i gradienten; fargene er mettede dyp-til-lys-
// overganger; ild er en rik gul→oransje→rød. EFFEKT_FILTER legger
// drop-shadow/glød på gradient-effekter (text-shadow funker ikke med
// background-clip:text). EFFEKT_RELIEFF er den 3D-pregede emboss-skyggen.
const EFFEKT_GRADIENTS = {
  "gull":"linear-gradient(160deg,#fff6da 0%,#e9cd7e 16%,#C9A84C 32%,#fffaf0 49%,#d8b85e 60%,#9a7820 80%,#6e5212 100%)",
  "sølv":"linear-gradient(160deg,#ffffff 0%,#dcdcdc 16%,#9a9a9a 32%,#ffffff 49%,#c4c4c4 60%,#7a7a7a 80%,#4a4a4a 100%)",
  "rød":"linear-gradient(160deg,#ffb3a7 0%,#ff5a52 34%,#c0182b 66%,#7a0a14 100%)",
  "blå":"linear-gradient(160deg,#bfe0ff 0%,#4dabf7 34%,#1971c2 66%,#0b3a6b 100%)",
  "grønn":"linear-gradient(160deg,#a7ffd9 0%,#1ed99a 34%,#0a9d6f 66%,#055239 100%)",
  "lilla":"linear-gradient(160deg,#ecccff 0%,#b06aff 34%,#8b2fd6 66%,#4a1480 100%)",
  "ild":"linear-gradient(160deg,#fff27a 0%,#ffd000 24%,#ff8c00 52%,#ff4500 76%,#c81e00 100%)",
};
const EFFEKT_SHADOW = {
  "glød-gull":"0 0 5px rgba(201,168,76,0.95), 0 0 14px rgba(201,168,76,0.75), 0 0 30px rgba(201,168,76,0.4)",
  "glød-hvit":"0 0 5px rgba(255,255,255,0.95), 0 0 14px rgba(255,255,255,0.65), 0 0 30px rgba(255,255,255,0.35)",
  "glød-rød":"0 0 5px rgba(255,80,80,0.95), 0 0 14px rgba(255,55,55,0.75), 0 0 30px rgba(255,40,40,0.4)",
};
const EFFEKT_FILTER = {
  "gull":"drop-shadow(0 2px 3px rgba(0,0,0,0.55))",
  "sølv":"drop-shadow(0 2px 3px rgba(0,0,0,0.55))",
  "rød":"drop-shadow(0 1px 2px rgba(0,0,0,0.5)) drop-shadow(0 0 8px rgba(255,90,82,0.45))",
  "blå":"drop-shadow(0 1px 2px rgba(0,0,0,0.5)) drop-shadow(0 0 8px rgba(77,171,247,0.45))",
  "grønn":"drop-shadow(0 1px 2px rgba(0,0,0,0.5)) drop-shadow(0 0 8px rgba(30,217,154,0.45))",
  "lilla":"drop-shadow(0 1px 2px rgba(0,0,0,0.5)) drop-shadow(0 0 8px rgba(176,106,255,0.45))",
  "ild":"drop-shadow(0 1px 2px rgba(0,0,0,0.5)) drop-shadow(0 0 11px rgba(255,140,0,0.55))",
};
// 3D-emboss: lyst høylys oppe-venstre + mørk skygge nede-høyre + mykt dyp-fall.
const EFFEKT_RELIEFF = "-1px -1px 1px rgba(255,255,255,0.4), 1px 1px 1px rgba(0,0,0,0.95), 2px 2px 4px rgba(0,0,0,0.65)";

// v56 FIX 3: regnbue-kantet fargevelger. Samme gradient-ramme som
// "Egendefinert"-boksen i Farger-seksjonen (v55), gjenbrukt for alle
// egendefinerte color-pickere i Utseende-tabben slik at de er konsistente.
// Den indre ruta viser gjeldende farge; <input type="color"> ligger
// usynlig oppå og åpner systemets fargevelger ved klikk.
function RegnbueFargevelger({ value, onChange, size = 30, title = "Egendefinert farge — klikk for fargevelger" }) {
  return (
    <label title={title}
      style={{ position:"relative", width:size, height:size, borderRadius:6, cursor:"pointer",
        padding:3, boxSizing:"border-box", flexShrink:0,
        background:"linear-gradient(135deg,#ff0040,#ff8c00,#ffd000,#33d17a,#3584e4,#9141ac)" }}>
      <span style={{ display:"block", width:"100%", height:"100%", borderRadius:3, background:value }}/>
      <input type="color" value={value} onChange={onChange}
        style={{ position:"absolute", inset:0, width:"100%", height:"100%", opacity:0, cursor:"pointer" }}/>
    </label>
  );
}

// v50 FIX 3: Innstillinger-undertabber. Løftet til modul-scope slik at
// både SettingsPanel (mobil-rad) og PC-dashboardets hovedtab-rad kan
// rendre dem fra samme kilde.
const SETTINGS_SUBTABS = [
  {id:"lotteri",label:"🎲 Lotteri"},
  {id:"utseende",label:"🎨 Utseende"},
  {id:"betaling",label:"💜 Vipps"},
  {id:"epost",label:"📧 E-post"},
  {id:"system",label:"⚙️ System"},
];
// v147: settings-undermenyene er flate hoved-tabs på PC. Disse id-ene gjenkjennes
// som "vis SettingsPanel med denne sub-fanen" i både PC- og mobil-grenen.
const SETTINGS_TAB_IDS = SETTINGS_SUBTABS.map(s => s.id);

// v57: PC-arrangør-dashboard er nå PERMANENT default. PC (innerWidth >= 900)
// får dashboard-layouten; mobil (< 900) får den klassiske kolonne-baserte
// arrangør-vyen. ?admin=pcprototype-flagget og localStorage-persistens fra
// v27 er fjernet — beslutningen er nå rent bredde-basert. ?admin=classic
// beholdes som en URL-only backup-bryter (ingen localStorage) for å teste
// den gamle smale layouten på PC ved behov.
function isClassicOverride() {
  try { return new URLSearchParams(window.location.search).get('admin') === 'classic'; }
  catch (e) { return false; }
}
// v58: primitiv hook — true når window.innerWidth >= 900, reaktiv på resize.
// Brukes både av arrangør-dashboardet og PC-spillsiden (Fase 1, Alt A).
function useIsPcWidth() {
  const [isWide, setIsWide] = useState(() => {
    try { return window.innerWidth >= 900; }
    catch (e) { return true; }
  });
  useEffect(() => {
    const handler = () => setIsWide(window.innerWidth >= 900);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isWide;
}
// Reaktiv hook for arrangør-dashboard: PC-bredde + ikke classic-override.
function usePcDashboard() {
  return useIsPcWidth() && !isClassicOverride();
}

// v85: haptic feedback for mobile actions. navigator.vibrate finnes ikke
// på iOS Safari (no-op der) og er udefinert på desktop — alle hjelperne
// er trygge å kalle uansett plattform.
function hapticTick()    { try { navigator.vibrate && navigator.vibrate(8); } catch (e) {} }
function hapticTap()     { try { navigator.vibrate && navigator.vibrate(20); } catch (e) {} }
function hapticSuccess() { try { navigator.vibrate && navigator.vibrate([50, 30, 50]); } catch (e) {} }

// v85: Vipps-betaling fra mobil — forsøk app-deep-link først, fall tilbake
// til web/universal link etter 1.5 sek hvis siden fortsatt er synlig (dvs.
// Vipps-appen åpnet ikke). Brukes kun fra mobil-grenen i ParticipantView.
function openVippsPaymentMobile(settings) {
  const vippsLink = settings?.vippsLink || null;
  const vippsNumber = settings?.vippsNumber || null;
  // Hvis arrangør har konfigurert en https://qr.vipps.no/-lenke, er det en
  // Universal Link som åpner appen automatisk på iOS/Android — bruk direkte.
  if (vippsLink && /^https?:\/\//i.test(vippsLink)) {
    window.location.href = vippsLink;
    return;
  }
  // Ellers: forsøk vipps://-skjema, fall tilbake til vipps.no hvis appen
  // ikke fanger opp (dvs. visibilityState forblir "visible" etter timer).
  if (vippsNumber) {
    // v126: ?acct= tolkes som QR/merchant-konto og gir "QR-kode feilet" for
    // personlige nummer. Rent vipps:// åpner appen uten QR-tolkning; nummeret
    // ligger på utklippstavlen (kopiert i handleBuy) klart til innliming.
    const deepLink = `vipps://`;
    window.location.href = deepLink;
    setTimeout(() => {
      if (document.visibilityState === "visible") {
        window.location.href = "https://vipps.no";
      }
    }, 1500);
    return;
  }
  // Siste utvei: hvis vi har en vippsLink uten http-prefix, prøv den.
  if (vippsLink) window.location.href = vippsLink;
}

// Custom confirm som ikke viser nettstedsnavn
// Bruker en Promise-basert tilnærming via en global callback
// Custom modal confirm — viser ingen "På X.no"-tekst
let _confirmResolve = null; // eslint-disable-line no-unused-vars
function showConfirm(msg) {
  return new Promise(res => {
    _confirmResolve = res;
    // Bruk setTimeout for å sikre at React er ferdig med rendering
    // før vi legger til overlay i DOM
    setTimeout(() => {
      // Fjern eventuell gammel overlay
      const old = document.getElementById("vl-confirm-overlay");
      if (old) old.remove();
      // Vis custom modal
      const overlay = document.createElement("div");
      overlay.id = "vl-confirm-overlay";
    overlay.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,0.75);z-index:99999;display:flex;align-items:center;justify-content:center;padding:20px;box-sizing:border-box;font-family:Inter,system-ui,sans-serif";
    const box = document.createElement("div");
    box.style.cssText = "background:#1a1a1a;border:1px solid #444;border-radius:8px;padding:24px;max-width:360px;width:100%;box-shadow:0 8px 40px rgba(0,0,0,0.8)";
    const lines = msg.split("\n").filter(Boolean);
    const title = lines[0];
    const body = lines.slice(1).join(" ").trim();
    box.innerHTML = `
      <p style="margin:0 0 ${body?'10px':'20px'};font-size:15px;font-weight:600;color:#f5efe0">${title}</p>
      ${body?`<p style="margin:0 0 20px;font-size:13px;color:#aaa;line-height:1.5">${body}</p>`:""}
      <div style="display:flex;gap:10px;justify-content:flex-end">
        <button id="vl-confirm-cancel" style="padding:8px 18px;border-radius:5px;border:1px solid #444;background:#2a2a2a;color:#ccc;cursor:pointer;font-size:13px">Avbryt</button>
        <button id="vl-confirm-ok" style="padding:8px 18px;border-radius:5px;border:none;background:#C9A84C;color:#000;cursor:pointer;font-size:13px;font-weight:bold">OK</button>
      </div>`;
    overlay.appendChild(box);
    document.body.appendChild(overlay);
    const cleanup = (result) => {
      document.body.removeChild(overlay);
      _confirmResolve = null;
      res(result);
    };
    box.querySelector("#vl-confirm-ok").onclick = () => cleanup(true);
    box.querySelector("#vl-confirm-cancel").onclick = () => cleanup(false);
    overlay.onclick = (e) => { if(e.target===overlay) cleanup(false); };
    }, 0); // end setTimeout
  });
}

// Custom alert — viser ingen "På X.no"-tekst
function showAlert(msg) {
  return new Promise(res => {
    const overlay = document.createElement("div");
    overlay.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,0.75);z-index:99999;display:flex;align-items:center;justify-content:center;padding:20px;box-sizing:border-box;font-family:Inter,system-ui,sans-serif";
    const box = document.createElement("div");
    box.style.cssText = "background:#1a1a1a;border:1px solid #444;border-radius:8px;padding:24px;max-width:360px;width:100%;box-shadow:0 8px 40px rgba(0,0,0,0.8)";
    const lines = msg.split("\n").filter(Boolean);
    const title = lines[0];
    const body = lines.slice(1).join(" ").trim();
    box.innerHTML = `
      <p style="margin:0 0 ${body?'10px':'20px'};font-size:15px;font-weight:600;color:#f5efe0">${title}</p>
      ${body?`<p style="margin:0 0 20px;font-size:13px;color:#aaa;line-height:1.5">${body}</p>`:""}
      <div style="display:flex;justify-content:flex-end">
        <button id="vl-alert-ok" style="padding:8px 18px;border-radius:5px;border:none;background:#C9A84C;color:#000;cursor:pointer;font-size:13px;font-weight:bold">OK</button>
      </div>`;
    overlay.appendChild(box);
    document.body.appendChild(overlay);
    const cleanup = () => { document.body.removeChild(overlay); res(); };
    box.querySelector("#vl-alert-ok").onclick = cleanup;
    overlay.onclick = (e) => { if(e.target===overlay) cleanup(); };
  });
}

// ─── HJELPEFUNKSJONER ─────────────────────────────────────────────────────────
function getWeekNumber(d) {
  d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function formatNorwegianDate(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString("nb-NO", { weekday:"short", day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" });
}

function isMobile() {
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

function useCountUp(target, duration = 800) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setVal(Math.floor(p * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration]);
  return val;
}

// ─── LYDER ────────────────────────────────────────────────────────────────────
// ── Global feil-toast ────────────────────────────────────────────────────────
function showErrorToast(msg) {
  const existing = document.getElementById("vl-error-toast");
  if (existing) existing.remove();
  const toast = document.createElement("div");
  toast.id = "vl-error-toast";
  toast.style.cssText = "position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#b71c1c;color:#fff;padding:12px 20px;border-radius:8px;font-size:14px;font-family:Inter,system-ui,sans-serif;z-index:99999;max-width:90vw;text-align:center;box-shadow:0 4px 20px rgba(0,0,0,0.5);";
  toast.textContent = "⚠️ " + msg;
  document.body.appendChild(toast);
  setTimeout(() => { if (toast.parentNode) toast.remove(); }, 5000);
}

let globalAudioCtx = null;
// Lazy-create AudioContext. v28's eager-create er reverted — Chrome's
// autoplay policy kan blokkere AudioContexts opprettet før user-gesture
// i en state hvor resume() ikke virker.
function getAudioCtx() {
  if (!globalAudioCtx || globalAudioCtx.state === "closed") {
    try { globalAudioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch { return null; }
  }
  if (globalAudioCtx.state === "suspended") globalAudioCtx.resume();
  return globalAudioCtx;
}
if (typeof window !== "undefined") {
  let _audioWarmedUp = false;
  const unlock = async () => {
    if (_audioWarmedUp) return;
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      try { await ctx.resume(); } catch (e) {}
    }
    // Spill av nær-stille buffer (gain 0.0001, 50 ms) for å fullt vekke
    // audio-engine. Browsers holder AudioContext suspended inntil reell
    // lyd-output har skjedd — uten dette kan FØRSTE reelle beep komme
    // for sent (50-200ms forsinkelse mens engine wakes up).
    try {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      g.gain.value = 0.0001;
      o.connect(g); g.connect(ctx.destination);
      o.start();
      o.stop(ctx.currentTime + 0.05);
    } catch (e) {}
    _audioWarmedUp = true;
    document.removeEventListener("click", unlock);
    document.removeEventListener("touchstart", unlock);
  };
  document.addEventListener("click", unlock, { passive: true });
  document.addEventListener("touchstart", unlock, { passive: true });
}
// ─── LOTTO RUMBLE ──────────────────────────────────────────────────────
// Lyd av kuler som klikker mot hverandre + en mild rullelyd under spinning.
// Klikk = kuler som treffer hverandre. Rullelyd = kuler som triller mot glass.
let lottoRumbleNodes = null;
function startLottoRumble() {
  const ac = getAudioCtx(); if (!ac) return;
  if (lottoRumbleNodes) return;

  // ─── KLIKK-LYD ──────────────────────────────────────────
  function playClick() {
    const acT = getAudioCtx(); if (!acT) return;
    const tNow = acT.currentTime;
    const len = Math.floor(acT.sampleRate * 0.012);
    const buf = acT.createBuffer(1, len, acT.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (len * 0.15));
    }
    const src = acT.createBufferSource();
    src.buffer = buf;
    const filter = acT.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 2500 + Math.random() * 1500;
    filter.Q.value = 4.0;
    const gain = acT.createGain();
    const vol = 0.05 + Math.random() * 0.06;
    gain.gain.setValueAtTime(vol, tNow);
    gain.gain.exponentialRampToValueAtTime(0.0001, tNow + 0.015);
    src.connect(filter).connect(gain).connect(acT.destination);
    src.start(tNow);
    src.stop(tNow + 0.02);
  }

  function scheduleNextClick() {
    if (!lottoRumbleNodes) return;
    playClick();
    if (Math.random() < 0.3) {
      setTimeout(playClick, 15 + Math.random() * 25);
    }
    const nextDelay = 60 + Math.random() * 180;
    lottoRumbleNodes.tickTimeoutId = setTimeout(scheduleNextClick, nextDelay);
  }

  // ─── RULLELYD ──────────────────────────────────────────
  // Kontinuerlig filtrert støy med modulert filter-frekvens (gir trillende karakter)
  const bufferSize = ac.sampleRate * 2;
  const buffer = ac.createBuffer(1, bufferSize, ac.sampleRate);
  const data = buffer.getChannelData(0);
  // Hvit støy
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * 0.8;
  }
  const rollSrc = ac.createBufferSource();
  rollSrc.buffer = buffer;
  rollSrc.loop = true;

  // Båndpass-filter — mid-frekvens for å gi "rullende" karakter
  const rollFilter = ac.createBiquadFilter();
  rollFilter.type = 'bandpass';
  rollFilter.frequency.value = 600;  // mid-range — verken bass eller treble
  rollFilter.Q.value = 1.2;

  // LFO modulerer filter-frekvensen så lyden "ruller" (varierer i tonefarge)
  const lfo = ac.createOscillator();
  lfo.type = 'sine';
  lfo.frequency.value = 4;  // 4 Hz = sakte wobble
  const lfoGain = ac.createGain();
  lfoGain.gain.value = 200;  // ±200 Hz modulasjon rundt 600 Hz
  lfo.connect(lfoGain).connect(rollFilter.frequency);
  lfo.start();

  const rollGain = ac.createGain();
  rollGain.gain.setValueAtTime(0, ac.currentTime);
  rollGain.gain.linearRampToValueAtTime(0.06, ac.currentTime + 0.2);  // lavt volum

  rollSrc.connect(rollFilter).connect(rollGain).connect(ac.destination);
  rollSrc.start();

  lottoRumbleNodes = {
    tickTimeoutId: null,
    rollSrc, rollGain, lfo,
  };
  scheduleNextClick();
}
function stopLottoRumble() {
  const ac = getAudioCtx();
  if (!ac || !lottoRumbleNodes) return;
  const { tickTimeoutId, rollSrc, rollGain, lfo } = lottoRumbleNodes;
  if (tickTimeoutId) clearTimeout(tickTimeoutId);
  // Fade ut rullelyd
  if (rollGain) {
    rollGain.gain.cancelScheduledValues(ac.currentTime);
    rollGain.gain.setValueAtTime(rollGain.gain.value, ac.currentTime);
    rollGain.gain.linearRampToValueAtTime(0, ac.currentTime + 0.2);
  }
  setTimeout(() => {
    try { rollSrc && rollSrc.stop(); } catch {}
    try { lfo && lfo.stop(); } catch {}
  }, 250);
  lottoRumbleNodes = null;
}

// Rich 3-layer knock sound for wheel pin hits (v26)
function playWheelTick(intensity = 1) {
  const ac = getAudioCtx(); if (!ac) return;
  const now = ac.currentTime;
  const vol = Math.min(1, intensity);
  // 1) NOISE BURST — crack of the hit
  const bufferSize = Math.floor(ac.sampleRate * 0.05);
  const noiseBuffer = ac.createBuffer(1, bufferSize, ac.sampleRate);
  const noiseData = noiseBuffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    noiseData[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.25));
  }
  const noiseSrc = ac.createBufferSource();
  noiseSrc.buffer = noiseBuffer;
  const noiseFilter = ac.createBiquadFilter();
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.value = 3200;
  noiseFilter.Q.value = 1.2;
  const noiseGain = ac.createGain();
  noiseGain.gain.setValueAtTime(0.22 * vol, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
  noiseSrc.connect(noiseFilter).connect(noiseGain).connect(ac.destination);
  noiseSrc.start(now); noiseSrc.stop(now + 0.05);
  // 2) LOW BODY — weight
  const body = ac.createOscillator();
  const bodyGain = ac.createGain();
  body.type = 'triangle';
  body.frequency.setValueAtTime(180 + Math.random() * 60, now);
  body.frequency.exponentialRampToValueAtTime(70, now + 0.08);
  bodyGain.gain.setValueAtTime(0, now);
  bodyGain.gain.linearRampToValueAtTime(0.18 * vol, now + 0.003);
  bodyGain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
  body.connect(bodyGain).connect(ac.destination);
  body.start(now); body.stop(now + 0.12);
  // 3) HIGH PING — metallic ring
  const ping = ac.createOscillator();
  const pingGain = ac.createGain();
  ping.type = 'sine';
  ping.frequency.setValueAtTime(1400 + Math.random() * 300, now);
  pingGain.gain.setValueAtTime(0, now);
  pingGain.gain.linearRampToValueAtTime(0.1 * vol, now + 0.002);
  pingGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
  ping.connect(pingGain).connect(ac.destination);
  ping.start(now); ping.stop(now + 0.07);
}
function playWinFanfare() {
  const ctx = getAudioCtx(); if (!ctx) return;
  [523, 659, 784, 1047, 1319].forEach((freq, i) => {
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = freq;
    const t = ctx.currentTime + i * 0.13;
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.5, t + 0.05);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    o.start(t); o.stop(t + 0.5);
  });
}
// v240: playCountdownBeep fjernet — spilte under premie-presentasjonen og
// rotet med lyden der. v245: premie-presentasjonen bruker playPrizeFanfare.
// ─── INTRO-TONER (v241, omlagt v291) ───────────────────────────────────
// Fem stigende dur-treklanger, én per tekstlinje (C→D→E→G→C oktav opp).
// v291: tidligere planla playIntroSequence ALLE akkordene som én kjede på
// AudioContext-klokka fra intro-mount, med faste offsets (0.8/2.0/2.9/3.8/
// 5.0s). Linjene styres derimot av CSS-delays som både SKALERES (v288-
// komprimering ved sen inntreden) og FORSKYVES (v290-kontinuitet fra holde-
// skjermen) — to klokker som bare traff hverandre når alt gikk fort. Nå er
// LINJE-VISNINGEN eneste tidskilde: hver akkord utløses av animationstart-
// hendelsen på sin linje (se onIntroLineStart i DrawIntroBlikkfang), så lyd
// følger bilde uansett lead, skala og forskyvning. Linjer som hopper inn
// allerede synlige (negativ delay) får ingen tone.
// v293: JUSTERBAR struktur — still stemningen her, ikke i logikken.
//   gain   = topp-volum for akkorden (multipliseres med INTRO_MASTER_GAIN)
//   attack = sekunder opp til toppen (kortere = skarpere anslag)
//   dur    = total utklang i sekunder
// Akkord 0 var 0.11 og samtidig LAVEST i register — lave sinustoner oppfattes
// svakere ved samme gain (øret er mindre følsomt der), så den hørtes spinkel
// ut. Nå register-kompensert (0.14) + skarpere attack; deretter svak stigning
// (crescendo) mot siste akkord, som får en ekstra grunntone (262 Hz, oktaven
// under roten) for fylde — opptakt til fanfaren UTEN volumhopp (maks-gain 0.16
// er uendret fra før).
const INTRO_MASTER_GAIN = 1.0;
const INTRO_CHORDS = [
  { freqs: [262, 330, 392],      gain: 0.140, attack: 0.030, dur: 1.2 }, // C-dur          — "Spenningen stiger" (register-kompensert)
  { freqs: [294, 370, 440],      gain: 0.135, attack: 0.040, dur: 1.2 }, // D-dur          — deltakere
  { freqs: [330, 415, 494],      gain: 0.145, attack: 0.040, dur: 1.2 }, // E-dur          — lodd
  { freqs: [392, 494, 587],      gain: 0.155, attack: 0.040, dur: 1.3 }, // G-dur          — premier
  { freqs: [262, 523, 659, 784], gain: 0.160, attack: 0.050, dur: 2.4 }, // C-dur oktav opp + grunntone — "Trekningen starter…", kulminasjon
];
let introChordVoices = []; // aktive stemmer — fades rent ut ved intro→next/unmount
// v292: holdeskjermen (imminent) spiller nå også linje-toner. Vakta hindrer
// dobbel tone for samme linje ved imminent→intro-overgangen: en linje som
// rakk å tone inn på holdeskjermen like før byttet (delvis vist, liten
// negativ/positiv delay i introen) ville ellers sluppet gjennom elapsedTime-
// filteret og spilt på nytt. 15s-vinduet dekker hold+intro for samme
// trekning, men blokkerer aldri en senere trekning.
let introChordLastMs = [0, 0, 0, 0, 0];
function playIntroChord(i) {
  const ctx = getAudioCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") { try { ctx.resume(); } catch (e) {} }
  // Fortsatt suspendert (ingen brukerinteraksjon ennå): IKKE skedulér — en
  // frosset audio-klokke ville klumpet tonene i en haug når konteksten senere
  // våkner. Da heller stille; neste linje prøver på nytt.
  if (ctx.state !== "running") return;
  if (Date.now() - introChordLastMs[i] < 15000) return; // dobbel-tone-vakt
  introChordLastMs[i] = Date.now();
  const { freqs, gain, attack, dur } = INTRO_CHORDS[i] || INTRO_CHORDS[0];
  const peak = gain * INTRO_MASTER_GAIN;
  // v293: 50 ms lead (var 20). Tone 0 spilles nå ofte tett på at konteksten
  // NETTOPP gikk suspended→running (brukergest millisekunder før, jf. holde-
  // skjermen) — output-motoren kan fortsatt være i oppstart, og et 20 ms-
  // skedulert anslag kunne klippes. 50 ms er trygt og uhørbart mot linje-
  // faden (1.0 s). Ingen master-gain-rampe finnes (akkordene går rett til
  // destination), så ingenting annet demper første avspilling.
  const t = ctx.currentTime + 0.05;
  freqs.forEach((freq) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + attack);  // myk attack (justerbar)
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);   // klokke/pad-utklang
    o.connect(g).connect(ctx.destination);
    o.start(t);
    o.stop(t + dur + 0.05);
    introChordVoices.push({ o, g, ctx });
  });
}
function stopIntroChords() {
  introChordVoices.forEach(({ o, g, ctx }) => {
    try {
      const tn = ctx.currentTime;
      g.gain.cancelScheduledValues(tn);
      g.gain.setValueAtTime(Math.max(0.0001, g.gain.value || 0.0001), tn);
      g.gain.exponentialRampToValueAtTime(0.0001, tn + 0.15);
    } catch (e) {}
    try { o.stop(ctx.currentTime + 0.2); } catch (e) {}
  });
  introChordVoices = [];
}
// ─── PREMIE-TROMPETFANFARE (v246) ──────────────────────────────────────
// Ekte messing-/trompetfanfare-mp3 (public/sounds/fanfare.mp3) under premie-
// presentasjonen (next-variant). Spilles via AudioContext (decodeAudioData +
// BufferSource gjennom gain) slik at den gjenbruker unlock-mekanismen og kan
// fades rent ut idet spinning starter / unmount. Spiller fra start; INGEN loop
// (fanfare = engangs-flourish). Returnerer { stop } for opprydding.
//
// v246: byttet fra et 40s korpsstykke (som ble kuttet midt i en frase) til en
// kort, SELVSTENDIG "TaDa!"-fanfare på ~6s som runder av naturlig innenfor
// premie-presentasjonen (4-6s) — ingen hørbart kutt.
//
// Kilde: "TaDa!" (freesound.org/sounds/256128), lisens CC0 (public domain /
// fri kommersiell bruk, ingen attribusjon påkrevd).
let fanfareBuffer = null;      // dekodet AudioBuffer (cache etter første gang)
let fanfareArrayBuf = null;    // rå ArrayBuffer (prefetch før AudioContext finnes)
let fanfareFetchStarted = false;
function ensureFanfareLoaded() {
  if (fanfareFetchStarted) return;
  fanfareFetchStarted = true;
  fetch(process.env.PUBLIC_URL + "/sounds/fanfare.mp3")
    .then(r => r.arrayBuffer())
    .then(buf => { fanfareArrayBuf = buf; })
    .catch(() => {});
}
if (typeof window !== "undefined") ensureFanfareLoaded(); // prefetch ved oppstart

function playPrizeFanfare() {
  const ctx = getAudioCtx();
  if (!ctx) return { stop() {} };
  if (ctx.state === "suspended") { try { ctx.resume(); } catch (e) {} }

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.21, ctx.currentTime + 0.08); // rask inn-fade
  gain.connect(ctx.destination);

  let src = null;
  let stopped = false;

  function startWith(buffer) {
    if (stopped) return;
    src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(gain);
    src.start();                     // spiller fra start, ingen loop
  }

  if (fanfareBuffer) {
    startWith(fanfareBuffer);
  } else if (fanfareArrayBuf) {
    ctx.decodeAudioData(fanfareArrayBuf.slice(0))
      .then(b => { fanfareBuffer = b; startWith(b); })
      .catch(() => {});
  } else {
    ensureFanfareLoaded();
    fetch(process.env.PUBLIC_URL + "/sounds/fanfare.mp3")
      .then(r => r.arrayBuffer())
      .then(ab => ctx.decodeAudioData(ab))
      .then(b => { fanfareBuffer = b; startWith(b); })
      .catch(() => {});
  }

  return {
    stop() {
      stopped = true;
      const tn = ctx.currentTime;
      try {
        gain.gain.cancelScheduledValues(tn);
        gain.gain.setValueAtTime(Math.max(0.0001, gain.gain.value || 0.0001), tn);
        gain.gain.exponentialRampToValueAtTime(0.0001, tn + 0.35); // myk slutt når presentasjonen er over
      } catch (e) {}
      try { src && src.stop(tn + 0.4); } catch (e) {}
    }
  };
}

// ─── KONFETTI ─────────────────────────────────────────────────────────────────
function Confetti({ active }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current; if (!canvas) return;
    canvas.width = window.innerWidth; canvas.height = window.innerHeight;
    const ctx = canvas.getContext("2d");
    const colors = ["#C9A84C","#e53935","#4caf50","#2196f3","#ff9800","#9c27b0","#fff"];
    let particles = Array.from({ length: 180 }, () => ({
      x: Math.random() * canvas.width, y: -20,
      w: Math.random() * 12 + 4, h: Math.random() * 7 + 3,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * Math.PI * 2, rotSpeed: (Math.random() - 0.5) * 0.2,
      vx: (Math.random() - 0.5) * 5, vy: Math.random() * 4 + 1,
    }));
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles = particles.filter(p => p.y < canvas.height + 20);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.rotation += p.rotSpeed;
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rotation);
        ctx.fillStyle = p.color; ctx.fillRect(-p.w/2, -p.h/2, p.w, p.h); ctx.restore();
      });
      if (particles.length > 0) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { cancelAnimationFrame(rafRef.current); ctx.clearRect(0,0,canvas.width,canvas.height); };
  }, [active]);
  return <canvas ref={canvasRef} style={{ position:"fixed",top:0,left:0,pointerEvents:"none",zIndex:999 }}/>;
}

// ─── QR-KODE (for PC-visning av Vipps-lenke) ──────────────────────────────────
function QRCode({ url, size = 160 }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!url || !canvasRef.current) return;
    // Enkel QR via Google Charts API-lignende URL (ingen ekstern lib nødvendig)
    // Tegn placeholder med URL som tekst – bruker faktisk Google Charts
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = "#333";
    ctx.font = "11px monospace";
    ctx.textAlign = "center";
    ctx.fillText("Laster QR...", size/2, size/2);
  }, [url, size]);

  if (!url) return null;
  // Bruker Google Charts QR API
  const qrUrl = `https://chart.googleapis.com/chart?chs=${size}x${size}&cht=qr&chl=${encodeURIComponent(url)}&choe=UTF-8`;
  return (
    <div style={{ background:"#fff", padding:8, borderRadius:4, display:"inline-block" }}>
      <img src={qrUrl} alt="QR-kode" width={size} height={size}
        style={{ display:"block" }}
        onError={(e)=>{ e.target.style.display="none"; }}
      />
    </div>
  );
}



// ─── LYKKEHJUL — ZOOM (PC-default fra v31) ───────────────────────────────────
// Brukes på PC for hjul-trekninger (LiveDrawView + DrawScreen). Hjulet tegnes
// alltid i native canvas-pixels: zoom gjøres med ctx.translate + ctx.scale per
// frame (ikke CSS), slik at det
// forblir skarpt uansett zoom-nivå. Pin-tippen er fast ankerpunkt øverst
// på skjermen — hjulet "vokser" ut av pinen ettersom zoom øker.
// Støtter opp til 500+ lodd via vinkel-culling og LOD (sub-pixel skip,
// tall vises kun når sektor er bred nok).

// v22-fix: DELT pool-bygging for hjul-rendering. Tidligere bygde DrawScreen
// (arrangør) sin remainingPool via en lokal `buildPool` som bruker
// `count = p.tickets || 1` og padder manglende ticketNum med null, mens
// LiveDrawView (spiller) bygde sin wheelTickets via `flatMap` over
// `p.ticketNumbers` — som gir én entry per faktisk tildelt ticketNum og
// hopper over phantom-tickets. Ved data-inkonsistens (deltaker med
// `tickets > ticketNumbers.length`) divergerte n mellom arrangør og spiller.
// Pluss DrawScreen filtrerte tidligere vinnere fra winnerHistory, mens
// LiveDrawView aldri filtrerte. Begge avvikene er løst ved å bruke denne
// felles funksjonen i begge views med samme parametere.
// v24: DELT beregning av "høyeste lodd-nummer solgt" — brukt av Lotto- og
// Lodd-spillet for å bestemme antall kuler/lodd som vises i maskinen/hatten.
// Tidligere bygde DrawScreen (PC arrangør) sin egen verdi fra rå
// participants.ticketNumbers, mens LiveDrawView (spiller) brukte filtrert
// wheelTickets — som divergerte etter første premie. Nå bruker begge denne
// felles funksjonen med rå deltaker-data slik at totalTickets-propen som
// sendes til LottoDraw3D/LotteryDraw alltid er identisk.
function computeMaxTicketNum(participants) {
  const all = (participants || []).flatMap(p => p.ticketNumbers || []);
  return all.length > 0 ? Math.max(...all) : 0;
}

function buildWheelPool(participants, usedTicketNums = []) {
  const pool = [];
  (participants || []).forEach(p => {
    const nums = p.ticketNumbers || [];
    const count = p.tickets || nums.length || 1;
    for (let i = 0; i < count; i++) {
      const tNum = nums[i] ?? null;
      pool.push({ ticketNum: tNum, name: p.name });
    }
  });
  let remaining = pool;
  if (usedTicketNums && usedTicketNums.length > 0) {
    remaining = [...pool];
    for (const used of usedTicketNums) {
      const idx = used != null
        ? remaining.findIndex(p => p.ticketNum === used)
        : remaining.findIndex(p => p.ticketNum == null);
      if (idx >= 0) remaining.splice(idx, 1);
    }
  }
  return remaining.sort((a, b) => (a.ticketNum || 0) - (b.ticketNum || 0));
}

// DELT slutt-rotasjons-beregning for LuckyWheelZoom (PC) og
// LuckyWheelZoomMobile. Garanterer at begge komponenter alltid lander pilen
// på samme sektor for samme inngangs-data — ingen drift mellom plattformer.
function computeSpinTargets(winnerIdx, n, rand01, startRotation) {
  const slice = (Math.PI * 2) / n;
  const intraSectorOffset = 0;
  const targetBase = -(winnerIdx + 0.5) * slice + intraSectorOffset;
  const fullSpins = 3 + Math.floor(rand01 * 2);
  const targetRotation = targetBase - fullSpins * Math.PI * 2;
  const totalTravel = targetRotation - startRotation;
  const snapAngleAbs = Math.min(0.04, slice * 0.4);
  const travelSign = totalTravel >= 0 ? 1 : -1;
  const targetRotationMain = targetRotation - snapAngleAbs * travelSign;
  const totalTravelMain = targetRotationMain - startRotation;
  return { slice, targetBase, fullSpins, targetRotation, totalTravel,
    snapAngleAbs, travelSign, targetRotationMain, totalTravelMain };
}

function LuckyWheelZoom({
  tickets,
  winnerName,
  winnerTicketNum,
  prizeName,
  prizeCountry,
  liveCount,
  showWinner,
  onSpinDone,
  onCanvasReady,
  onZoomStart,
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const hasSpunRef = useRef(false);
  const currentRotationRef = useRef(0);
  const currentZoomRef = useRef(0);
  const lastBorderRef = useRef(-1);
  const lastPointerSectorRef = useRef(-1);
  const winnerIndexRef = useRef(-1);
  const winnerPulseRef = useRef(0);
  const pulseRafRef = useRef(null);
  const pinRef = useRef(null);
  const lastTickTimeRef = useRef(0);
  const [pointerName, setPointerName] = useState('');
  const [spinDoneState, setSpinDoneState] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);

  const segments = [...(tickets || [])].sort((a, b) => (a.ticketNum || 0) - (b.ticketNum || 0));
  const n = segments.length;

  const [VP, setVP] = useState({ W: window.innerWidth, H: window.innerHeight });
  useEffect(() => {
    const onResize = () => setVP({ W: window.innerWidth, H: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const VP_W = VP.W, VP_H = VP.H;

  const NUMBER_FONT_FAMILY = '"Impact", "Arial Black", "Helvetica Neue", sans-serif';
  const WHEEL_COLORS = ['#722F37', '#8B4513', '#4A1E24', '#A0522D', '#5C1F2B', '#6B3410'];

  // R_world er konstant — alt skaleres med zoom. startZoom velges slik at
  // hjuldiameteren får plass i viewport. endZoom velges slik at vinnersektor
  // blir lesbar bred. For små n trengs liten/ingen zoom.
  const R_WORLD = 4000;
  // Mobil/PC-bryter — påvirker hjul-bredde og countdown-plassering.
  const isMobileLayout = VP_W < 768;

  // PIN_Y — pin-tippens y-posisjon i viewport. Ankerpunkt for zoom.
  // PC bruker mindre topp-margin enn mobil for å gi plass til større
  // hjul. n>80 går enda høyere opp (mer plass under = enda større hjul).
  // Mobil uendret (140px / 22% av VP_H).
  let PIN_Y;
  if (isMobileLayout) {
    PIN_Y = Math.max(140, Math.round(VP_H * 0.22));
  } else if (n <= 80) {
    PIN_Y = Math.max(110, Math.round(VP_H * 0.16));
  } else {
    PIN_Y = Math.max(95, Math.round(VP_H * 0.11));
  }
  // To-radius-design:
  //   IDLE-radius (start): hele hjulet synlig (passer i viewport vertikalt
  //   og bredt nok til å se sektorer). Brukes som startZoom.
  //   PEAK-radius (slutt): for n>80 — geometrisk formel som sikrer at
  //   hjul-kanten dekker viewport-bredden ved viewport-bunnen y=VP_H, slik
  //   at det aldri er sortfelt på sidene under zoom. Pythagoras med pin
  //   på toppen: ved y=VP_H krever half-width ≥ VP_W/2 at r ≥ (w² + h²)
  //   / (2h), w=VP_W/2, h=VP_H-PIN_Y. Margin × 1.05 mot avrunding.
  const widthFactor = isMobileLayout ? 0.55 : 0.46;
  const reservedBottom = isMobileLayout ? 230 : 30;
  const idleRadiusPx = Math.min(
    VP_W * widthFactor,
    (VP_H - PIN_Y - reservedBottom) * 0.50
  );
  let peakRadiusPx;
  if (n <= 80) {
    peakRadiusPx = idleRadiusPx;
  } else if (isMobileLayout) {
    // Mobil uendret fra v12: r = avstand pin-til-bunn.
    peakRadiusPx = VP_H - PIN_Y;
  } else {
    // PC n>80: piecewise zoom-skalering. Steepere ramp for tydeligere tall.
    //   80 ≤ n < 250:  linear (80,1.3) → (250,4.0)  — slope 0.01588
    //   n ≥ 250:       linear (250,4.0) → (500,5.0) — slope 0.004, cap 5.5 ved n=625
    // Kontinuerlig ved n=250. Gir 4.0 ved n=250, 5.0 ved n=500, 5.4 ved n=600.
    // peakR_ratio = (VP_W/2) × 1.10 × userRatio.
    let userRatio;
    if (n < 250) {
      userRatio = 1.3 + (n - 80) * 0.01588;
    } else {
      userRatio = Math.min(5.5, 4.0 + (n - 250) * 0.004);
    }
    const peakRFromRatio = (VP_W / 2) * 1.10 * userRatio;
    const w = VP_W / 2;
    const h = VP_H - PIN_Y;
    const disc = 4.862 * h * h - 0.452 * (w * w + h * h);
    const peakRGeomFloor = disc > 0
      ? (2.205 * h - Math.sqrt(disc)) / 0.205
      : ((w * w + h * h) / (2 * h)) * 1.05;
    peakRadiusPx = Math.max(peakRFromRatio, peakRGeomFloor);
  }
  const startZoom = idleRadiusPx / R_WORLD;
  let endZoom;
  if (n <= 80) {
    // Små n: ingen zoom — hjulet er allerede lesbart fra start.
    endZoom = startZoom;
  } else {
    endZoom = peakRadiusPx / R_WORLD;
    if (endZoom < startZoom * 1.5) endZoom = startZoom * 1.5;
  }

  function shadeColor(hex, percent) {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, Math.max(0, (num >> 16) + amt));
    const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amt));
    const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
    return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
  }

  function getPulsingWhite() {
    const p = (Math.sin(winnerPulseRef.current * Math.PI * 2) + 1) / 2;
    const r = Math.round(58 + (255 - 58) * p);
    const g = Math.round(15 + (245 - 15) * p);
    const b = Math.round(21 + (220 - 21) * p);
    return `rgb(${r},${g},${b})`;
  }

  function getSectorAtPointer(rotation) {
    if (n === 0) return 0;
    const slice = (Math.PI * 2) / n;
    let normRot = -rotation;
    normRot = ((normRot % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    let i = Math.floor(normRot / slice);
    return ((i % n) + n) % n;
  }

  function getBorderIndex(rotation, slice) {
    let norm = -rotation;
    norm = ((norm % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    return Math.floor(norm / slice) % n;
  }

  const drawWheel = useCallback((rotation, zoom, highlightIdx = -1) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // DPR cap=2 holder backing-store under ~8 MB selv på 2K-skjerm.
    // Skarpheten kommer fra at zoom skjer i ctx-rommet, ikke fra DPR.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const targetW = Math.round(VP_W * dpr);
    const targetH = Math.round(VP_H * dpr);
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
      canvas.style.width = VP_W + 'px';
      canvas.style.height = VP_H + 'px';
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, VP_W, VP_H);
    if (n === 0) return;

    // Pin-tippen er ankerpunktet for zoom. Sett origo der, skaler, og legg
    // hjulsenter på lokal (0, R_WORLD). Da forblir pin-tippen fast på
    // skjermen mens hjulet vokser/krymper.
    ctx.save();
    ctx.translate(VP_W / 2, PIN_Y);
    ctx.scale(zoom, zoom);

    const cx = 0, cy = R_WORLD;
    const slice = (Math.PI * 2) / n;
    const arcPxOuter = slice * R_WORLD * zoom;

    // Synlig vinkel-sektor — terskel: cy_screen vs VP_H.
    //   Mens hjul-senter (nav) er synlig (cy ≤ VP_H): tegn HELE hjulet
    //   (halfVis = π). Hjulet skal alltid se ut som en sirkel så lenge
    //   brukeren ser naven — ingen wedges klippes vekk.
    //   Først når cy > VP_H (naven har falt under skjermbunnen):
    //   klipp wedges som ikke kan paint'e i viewport. Klippings-vinkelen
    //   er atan(w/dyBottom) — vinkelen fra pin-retning til viewport-bunn-
    //   corner. Pluss slice·4 margin for safety.
    const cy_screen = PIN_Y + R_WORLD * zoom;
    const w_half = VP_W / 2;
    let halfVis;
    if (cy_screen <= VP_H) {
      halfVis = Math.PI;
    } else {
      const dyBottom = cy_screen - VP_H;
      halfVis = Math.min(Math.atan(w_half / dyBottom) + slice * 4, Math.PI);
    }

    // Sektorer
    for (let i = 0; i < n; i++) {
      const midAngle = rotation + (i + 0.5) * slice - Math.PI / 2;
      let delta = midAngle + Math.PI / 2;
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      if (Math.abs(delta) > halfVis) continue;
      if (arcPxOuter < 0.4) continue;
      const a0 = rotation + i * slice - Math.PI / 2;
      const a1 = a0 + slice;
      const isHighlight = (i === highlightIdx);
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, R_WORLD, a0, a1);
      ctx.closePath();
      const baseColor = WHEEL_COLORS[i % WHEEL_COLORS.length];
      if (isHighlight) {
        const grad = ctx.createRadialGradient(cx, cy, R_WORLD * 0.2, cx, cy, R_WORLD);
        grad.addColorStop(0, '#F4D06F');
        grad.addColorStop(0.7, '#C9A84C');
        grad.addColorStop(1, '#8b6f2a');
        ctx.fillStyle = grad;
      } else if (arcPxOuter < 8) {
        ctx.fillStyle = baseColor;
      } else {
        const grad = ctx.createRadialGradient(cx, cy, R_WORLD * 0.3, cx, cy, R_WORLD);
        grad.addColorStop(0, shadeColor(baseColor, 22));
        grad.addColorStop(1, baseColor);
        ctx.fillStyle = grad;
      }
      ctx.fill();
      if (arcPxOuter >= 4) {
        ctx.strokeStyle = 'rgba(0,0,0,0.45)';
        ctx.lineWidth = 1 / zoom;
        ctx.stroke();
      }
    }

    // Tall — vises kun etter at zoom-progresjon har nådd 8% av veien fra
    // startZoom til endZoom. Lineær fade-inn fra 0.08 til 0.22. For n<=80
    // (ingen zoom-range) settes progress=1 og tall er alltid synlige.
    // Min font-størrelse 10 px sikrer leselighet.
    const zoomProgress = (endZoom - startZoom) > 0.001
      ? Math.max(0, Math.min(1, (zoom - startZoom) / (endZoom - startZoom)))
      : 1;
    const labelAlpha = zoomProgress < 0.08 ? 0
      : zoomProgress < 0.22 ? (zoomProgress - 0.08) / 0.14
      : 1;
    if (arcPxOuter >= 4 && labelAlpha > 0) {
      ctx.save();
      ctx.globalAlpha = labelAlpha;
      const fontPxScreen = Math.min(Math.max(arcPxOuter * 0.55, 10), 90);
      const fontLocal = fontPxScreen / zoom;
      ctx.font = `900 ${fontLocal}px ${NUMBER_FONT_FAMILY}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineWidth = Math.max(1, fontLocal * 0.05);
      ctx.lineJoin = 'round';
      const textRadius = R_WORLD - fontLocal * 0.65;
      for (let i = 0; i < n; i++) {
        const midAngle = rotation + (i + 0.5) * slice - Math.PI / 2;
        let delta = midAngle + Math.PI / 2;
        while (delta > Math.PI) delta -= Math.PI * 2;
        while (delta < -Math.PI) delta += Math.PI * 2;
        if (Math.abs(delta) > halfVis) continue;
        const isHighlight = (i === highlightIdx);
        const x = cx + Math.cos(midAngle) * textRadius;
        const y = cy + Math.sin(midAngle) * textRadius;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(midAngle + Math.PI / 2);
        // Etikett = sektor-indeks + 1 (sekvensielle 1..n) i stedet for
        // den underliggende ticketNum. Vinner-trefning bruker fortsatt
        // ticketNum (winnerIdx-finding), så geometri er uendret — kun
        // visning er omnummerert til 1..n.
        const label = String(i + 1);
        ctx.strokeStyle = 'rgba(0,0,0,0.75)';
        ctx.strokeText(label, 0, 0);
        ctx.fillStyle = isHighlight ? getPulsingWhite() : '#F5E6D3';
        ctx.fillText(label, 0, 0);
        ctx.restore();
      }
      ctx.restore();
    }

    // Gull-ring — LÅST på konstant 10 px screen-bredde. Vokser ikke med
    // zoom slik at ringens øvre kant ALDRI beveger seg høyere oppover på
    // skjermen enn den er ved spinn-start. Hjulet vokser kun nedover.
    // ringWLocal = 10/zoom (verdens-bredde varierer omvendt med zoom så
    // ringens screen-bredde forblir 10).
    const ringScreenPx = 10;
    const ringWLocal = ringScreenPx / zoom;
    ctx.strokeStyle = '#C9A84C';
    ctx.lineWidth = ringWLocal;
    ctx.beginPath();
    if (halfVis >= Math.PI - 0.01) {
      ctx.arc(cx, cy, R_WORLD + ringWLocal * 0.5, 0, Math.PI * 2);
    } else {
      ctx.arc(cx, cy, R_WORLD + ringWLocal * 0.5, -Math.PI / 2 - halfVis, -Math.PI / 2 + halfVis);
    }
    ctx.stroke();

    // Nagler/prikker — størrelse skalerer med zoom (R_WORLD × zoom),
    // wedge-bredde × 40%, og maks halv ring-bredde. Cap 7 px absolutt
    // (~30% reduksjon fra forrige 10 — mindre dominante ved peak).
    const nailScreenPx = Math.max(
      1.3,
      Math.min(7, ringScreenPx * 0.5, R_WORLD * zoom * 0.0035, arcPxOuter * 0.4)
    );
    const nailRadiusLocal = nailScreenPx / zoom;
    const nailR = R_WORLD + ringWLocal * 0.5;
    for (let i = 0; i < n; i++) {
      const borderAngle = rotation + i * slice - Math.PI / 2;
      let delta = borderAngle + Math.PI / 2;
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      if (Math.abs(delta) > halfVis) continue;
      const nx = cx + Math.cos(borderAngle) * nailR;
      const ny = cy + Math.sin(borderAngle) * nailR;
      ctx.beginPath();
      ctx.arc(nx, ny, nailRadiusLocal, 0, Math.PI * 2);
      ctx.fillStyle = '#0a0806';
      ctx.fill();
      if (nailScreenPx >= 2.5) {
        ctx.beginPath();
        ctx.arc(nx - nailRadiusLocal * 0.3, ny - nailRadiusLocal * 0.3, nailRadiusLocal * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(180,140,60,0.75)';
        ctx.fill();
      }
    }

    // Hub i sentrum (15% av hjul-diameter) — gull/messing-stil. Skjuler
    // sektor-konvergensen og gir hjulet et naturlig "lykkehjul"-uttrykk.
    // Tegnes kun når hjul-senteret er innenfor (eller nær) viewport — ved
    // høy zoom havner senteret langt under skjermen og hub er ikke synlig.
    const hubScreenY = PIN_Y + R_WORLD * zoom;
    if (hubScreenY < VP_H + R_WORLD * 0.15 * zoom) {
      const hubR = R_WORLD * 0.15;
      const hubGrad = ctx.createRadialGradient(
        cx - hubR * 0.3, cy - hubR * 0.3, hubR * 0.1,
        cx, cy, hubR
      );
      hubGrad.addColorStop(0, '#fff5d0');
      hubGrad.addColorStop(0.6, '#C9A84C');
      hubGrad.addColorStop(1, '#8b6f2a');
      ctx.beginPath();
      ctx.arc(cx, cy, hubR, 0, Math.PI * 2);
      ctx.fillStyle = hubGrad;
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.5)';
      ctx.lineWidth = 2 / zoom;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, hubR * 0.55, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(74, 30, 36, 0.5)';
      ctx.lineWidth = 1.5 / zoom;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, hubR * 0.08, 0, Math.PI * 2);
      ctx.fillStyle = '#4a1e24';
      ctx.fill();
    }

    ctx.restore();
  }, [n, VP_W, VP_H]); // eslint-disable-line

  // Idle-render — bruker startZoom når ingen trekning er aktiv.
  useEffect(() => {
    if (!hasSpunRef.current && !spinDoneState) {
      currentZoomRef.current = startZoom;
      currentRotationRef.current = 0;
      drawWheel(0, startZoom, -1);
      lastPointerSectorRef.current = getSectorAtPointer(0);
    }
  }, [drawWheel, startZoom, spinDoneState]); // eslint-disable-line

  useEffect(() => {
    if (onCanvasReady && canvasRef.current) onCanvasReady(canvasRef.current);
  }, [onCanvasReady]); // eslint-disable-line

  function startWinnerPulse(winnerIdx) {
    if (pulseRafRef.current) cancelAnimationFrame(pulseRafRef.current);
    const startTime = performance.now();
    const frame = (now) => {
      winnerPulseRef.current = ((now - startTime) / 1000) * 2;
      drawWheel(currentRotationRef.current, currentZoomRef.current, winnerIdx);
      pulseRafRef.current = requestAnimationFrame(frame);
    };
    pulseRafRef.current = requestAnimationFrame(frame);
  }
  function stopWinnerPulse() {
    if (pulseRafRef.current) { cancelAnimationFrame(pulseRafRef.current); pulseRafRef.current = null; }
    winnerPulseRef.current = 0;
  }

  // Reset mellom runder
  useEffect(() => {
    if (!winnerName) {
      hasSpunRef.current = false;
      setSpinDoneState(false);
      setIsSpinning(false);
      setPointerName('');
      stopWinnerPulse();
      currentRotationRef.current = 0;
      currentZoomRef.current = startZoom;
      drawWheel(0, startZoom, -1);
      // Reset pin-skalering til 1.0 mellom runder (CSS-variabel)
      if (pinRef.current) {
        pinRef.current.style.setProperty('--lwz-pin-scale', '1');
      }
    }
  }, [winnerName]); // eslint-disable-line

  // Spinn med synkronisert zoom-progresjon.
  useEffect(() => {
    if (!winnerName || hasSpunRef.current || n === 0) return;
    hasSpunRef.current = true;
    setIsSpinning(true);
    getAudioCtx();

    const winnerIdx = winnerTicketNum != null
      ? segments.findIndex(s => s.ticketNum === winnerTicketNum)
      : segments.findIndex(s => s.name === winnerName);
    if (winnerIdx < 0) {
      hasSpunRef.current = false;
      setIsSpinning(false);
      return;
    }
    winnerIndexRef.current = winnerIdx;
    const seed = (winnerTicketNum || 1) * 9301 + 49297;
    const rand01 = (seed % 1000) / 1000;
    const startRotation = currentRotationRef.current;
    // Bruk DELT helper for slutt-rotasjons-beregning. Garanterer numerisk
    // identisk resultat med LuckyWheelZoomMobile.
    const { slice, targetRotation, snapAngleAbs, travelSign,
      targetRotationMain, totalTravelMain } =
      computeSpinTargets(winnerIdx, n, rand01, startRotation);

    let duration = 14000;
    if (n > 60) duration = 16000;
    if (n > 100) duration = 18000;
    if (n > 300) duration = 20000;
    if (n > 800) duration = 22000;

    // Rotasjon: power 5 gir markant kortere høyfart-fase + lang slow-
    // glide. ~76% rotasjon ferdig ved t=0.25, ~83% ved t=0.30, ~99.7% ved
    // t=0.70 — siste 70% av tida dekker ~24% av rotasjonen. Brukeren får
    // god tid til å lese sektorer som passerer mot slutten.
    const easeRot = (t) => 1 - Math.pow(1 - t, 5);
    // Zoom-kurve: 1 sek absolutt idle-hold, så smootherstep fra 0 til 1
    // over resterende tid. Smootherstep (6t⁵-15t⁴+10t³) er kontinuerlig
    // differensierbar i 2. orden — null derivat i begge endepunkter,
    // ingen kantete overganger eller "byks" mot slutten.
    const idleHoldT = Math.min(0.5, 1000 / duration);
    // v269: «Lykke til»-tekst skjules idet hjulet zoomer inn. Fyres én gang
    // fra frame-loopen når idle-holdet er over OG zoom virkelig skjer
    // (n>80 → endZoom>startZoom). Små hjul zoomer ikke → teksten blir til
    // vinner avsløres. Auto-kansellert med rAF-cleanup (ingen egen timer).
    const zoomHappens = endZoom > startZoom + 0.001;
    let zoomFired = false;
    const easeZoom = (t) => {
      if (t < idleHoldT) return 0;
      const zt = (t - idleHoldT) / (1 - idleHoldT);
      return zt * zt * zt * (zt * (zt * 6 - 15) + 10);
    };

    lastBorderRef.current = getBorderIndex(startRotation, slice);
    lastTickTimeRef.current = 0;
    const startTime = performance.now();

    const frame = (now) => {
      const t = Math.min((now - startTime) / duration, 1);
      const rotEased = easeRot(t);
      currentRotationRef.current = startRotation + totalTravelMain * rotEased;
      currentZoomRef.current = startZoom + (endZoom - startZoom) * easeZoom(t);
      if (!zoomFired && zoomHappens && t >= idleHoldT && onZoomStart) {
        zoomFired = true;
        onZoomStart();
      }
      // Pil skalerer fra 1.0 (idle) til 1.3x (peak) basert på zoom-progress
      // — vekst likt fordelt om sentrum (transform-origin 50% 50%). 30%
      // total vekst: tydelig, men ikke dominerende.
      if (pinRef.current) {
        const zp = (endZoom - startZoom) > 0.001
          ? Math.max(0, Math.min(1, (currentZoomRef.current - startZoom) / (endZoom - startZoom)))
          : 0;
        const pinScale = 1 + zp * 0.3;
        pinRef.current.style.setProperty('--lwz-pin-scale', pinScale.toFixed(3));
      }
      const border = getBorderIndex(currentRotationRef.current, slice);
      if (border !== lastBorderRef.current) {
        // Tick når pilen passerer prikk (sektor-grense). Tids-throttle på
        // 80 ms hindrer at høy spin-fart gir kontinuerlig støy — ved slow-
        // glide (få crossings/sek) hører hver tick tydelig som et "klikk".
        const tickNow = Date.now();
        if (tickNow - lastTickTimeRef.current >= 80) {
          const speed = 1 - rotEased;
          playWheelTick(0.3 + speed * 0.7);
          lastTickTimeRef.current = tickNow;
        }
        lastBorderRef.current = border;
      }
      drawWheel(currentRotationRef.current, currentZoomRef.current, -1);
      const ptr = getSectorAtPointer(currentRotationRef.current);
      if (ptr !== lastPointerSectorRef.current) {
        lastPointerSectorRef.current = ptr;
        if (segments[ptr]) setPointerName(segments[ptr].name);
      }
      if (t < 1) {
        rafRef.current = requestAnimationFrame(frame);
      } else {
        // Hovedspinn ferdig på targetRotationMain. Lås zoom til endZoom og
        // start snap-fasen: rask, mekanisk lukking av siste snapAngleAbs
        // mot targetRotation, med et lite overshoot (105% ved 80% snap-tid)
        // før den låser eksakt på winner-sektor-senter.
        currentRotationRef.current = targetRotationMain;
        currentZoomRef.current = endZoom;
        if (pinRef.current) {
          pinRef.current.style.setProperty('--lwz-pin-scale', '1.300');
        }
        // Klikk-tick som markerer at snap starter (lås-klikk).
        playWheelTick(1.0);
        const snapDur = 130;
        const snapStart = now;
        const overshootRot = targetRotation + snapAngleAbs * travelSign * 0.05;
        const snapFrame = (sNow) => {
          const sT = Math.min((sNow - snapStart) / snapDur, 1);
          let snapRot;
          if (sT < 0.8) {
            // Cubic ease-out fra targetRotationMain til overshootRot
            // i første 80% av snap-tida — skarp, mekanisk akselerasjon
            // ut mot litt forbi mål.
            const u = sT / 0.8;
            const eased = 1 - Math.pow(1 - u, 3);
            snapRot = targetRotationMain + (overshootRot - targetRotationMain) * eased;
          } else {
            // Siste 20%: lineær settle fra overshoot tilbake til eksakt
            // targetRotation. Gir den lille "klikk-tilbake" effekten.
            const u = (sT - 0.8) / 0.2;
            snapRot = overshootRot + (targetRotation - overshootRot) * u;
          }
          currentRotationRef.current = snapRot;
          drawWheel(snapRot, endZoom, -1);
          if (sT < 1) {
            rafRef.current = requestAnimationFrame(snapFrame);
          } else {
            currentRotationRef.current = targetRotation;
            drawWheel(targetRotation, endZoom, winnerIdx);
            if (segments[winnerIdx]) setPointerName(segments[winnerIdx].name);
            startWinnerPulse(winnerIdx);
            setSpinDoneState(true);
            // Fanfare ETTER snap — confetti og lyd kommer når låsen
            // klikker på plass, ikke før.
            onSpinDone && onSpinDone();
          }
        };
        rafRef.current = requestAnimationFrame(snapFrame);
      }
    };
    rafRef.current = requestAnimationFrame(frame);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      stopWinnerPulse();
    };
  }, [winnerName]); // eslint-disable-line

  // Pin-størrelse og live-navn-plassering: PC har mindre pin og navn høyere
  // opp for å frigjøre plass til større hjul. n>80 (PC) går enda lenger.
  let pinW, pinH, liveNameTopY;
  if (isMobileLayout) {
    pinW = 16; pinH = 44;
    liveNameTopY = Math.max(20, Math.round(PIN_Y * 0.22));
  } else if (n <= 80) {
    pinW = 14; pinH = 36;
    liveNameTopY = 18;
  } else {
    pinW = 12; pinH = 28;
    liveNameTopY = 12;
  }
  const pinTopY = PIN_Y - pinH + 2;
  const showLiveName = (isSpinning || spinDoneState) && !(showWinner && spinDoneState);
  // Countdown — PC: top-anchored øverst. Mobil: bottom-anchored slik at
  // den alltid er over iOS-toolbar / Android nav-bar (env(safe-area-inset)
  // sørger for at den ikke havner bak notch/hjemme-stripe).

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10,
      background: 'radial-gradient(ellipse at center,#1a1410 0%,#0a0806 70%,#000 100%)',
      overflow: 'hidden', touchAction: 'none', overscrollBehavior: 'none',
      userSelect: 'none',
    }} onClick={() => getAudioCtx()}>
      <Confetti active={showWinner && spinDoneState}/>

      <canvas ref={canvasRef} style={{
        position: 'absolute', inset: 0, display: 'block', zIndex: 1
      }}/>

      {/* Live-navn over hjulet — ren gyllen tekst (ingen text-shadow).
          Glow-aura ligger bak teksten som en bred radial-pille (~22 ch),
          sentrert horisontalt. Tekst er på toppen, glow under. */}
      {showLiveName && (
        <div style={{
          position: 'absolute', top: liveNameTopY, left: 0, right: 0,
          zIndex: 22, pointerEvents: 'none', textAlign: 'center', padding: '0 24px',
        }}>
          {/* Glow-bakgrunn — bred radial pille bak teksten */}
          <div style={{
            position: 'absolute',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '22ch',
            height: '2.4em',
            fontSize: 'clamp(28px, 5.2vw, 44px)',
            background: 'radial-gradient(ellipse at center, rgba(232, 184, 90, 0.55) 0%, rgba(232, 184, 90, 0.28) 35%, rgba(232, 184, 90, 0.10) 65%, rgba(232, 184, 90, 0) 100%)',
            filter: 'blur(4px)',
            pointerEvents: 'none',
          }}/>
          {/* Tekst — ren, uten text-shadow */}
          <div style={{
            position: 'relative',
            fontFamily: "'Montserrat', 'Playfair Display', Georgia, serif",
            fontWeight: 900,
            fontSize: 'clamp(28px, 5.2vw, 44px)',
            color: '#e8b85a',
            letterSpacing: '1px',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            zIndex: 1,
          }}>{pointerName || ' '}</div>
        </div>
      )}

      <div ref={pinRef} style={{
        position: 'absolute', top: pinTopY, left: '50%',
        // CSS-variabel for skala. JSX-style bruker var() — endres
        // imperativt via setProperty under spinn (CSS-vars overstyres
        // ikke av React's style-prop-oppdateringer ved re-render).
        transform: 'translateX(-50%) scale(var(--lwz-pin-scale, 1))',
        transformOrigin: '50% 50%',
        zIndex: 20, pointerEvents: 'none',
        filter: 'drop-shadow(0 3px 8px rgba(0,0,0,0.85))',
        willChange: 'transform'
      }}>
        <svg width={pinW} height={pinH} viewBox="0 0 22 58">
          <defs>
            <linearGradient id="lwzPinGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#444"/>
              <stop offset="40%" stopColor="#c8c8c8"/>
              <stop offset="50%" stopColor="#ffffff"/>
              <stop offset="60%" stopColor="#c8c8c8"/>
              <stop offset="100%" stopColor="#444"/>
            </linearGradient>
            <linearGradient id="lwzPinTipGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#888"/>
              <stop offset="100%" stopColor="#222"/>
            </linearGradient>
          </defs>
          <circle cx="11" cy="6" r="6" fill="url(#lwzPinGrad)" stroke="#222" strokeWidth="0.6"/>
          <path d="M8 6 Q11 6 14 6 L13 48 L11 56 L9 48 Z"
            fill="url(#lwzPinTipGrad)" stroke="#111" strokeWidth="0.5"/>
        </svg>
      </div>

      {/* v211: nedtellingstall fjernet — blikkfanget eier nedtellingen (lyd beholdt). */}

      {showWinner && spinDoneState && winnerName && (
        // v216: inline gratulerer-JSX konsolidert til ElegantWinnerCard. Wrapperen
        // beholdes (absolute bottom + lwzSlideUp). noAnim=true så kun wrapperens
        // slideUp spiller (ikke dobbel animasjon).
        // v279: vinrød/sort linear-gradient-wrapper fjernet og softBackdrop=true
        // slik at hjul-kortet har EKSAKT samme bakgrunn som slot machine/lotto/
        // lodd. CARD_LOWER_PX (justerbar) senker kortet ved å spise av bunn-
        // paddingen — gjør at det sitter litt nærmere skjermkanten.
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: `24px 14px ${Math.max(4, 28 - 18)}px`, // 18 = CARD_LOWER_PX
          zIndex: 45, pointerEvents: 'none',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          animation: 'lwzSlideUp 0.5s cubic-bezier(0.34,1.56,0.64,1) both'
        }}>
          <ElegantWinnerCard
            winnerName={winnerName}
            prizeName={prizeName}
            prizeCountry={prizeCountry}
            noAnim={true}
            softBackdrop={true}
          />
        </div>
      )}

      <style>{`
        @keyframes lwzCountPulse {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.15); opacity: 1; }
          75% { transform: scale(0.95); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes lwzSlideUp {
          0% { transform: translateY(20px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}


// ─── LYKKEHJUL — ZOOM MOBIL (mobil-default fra v23) ──────────────────────────
// Mobil-variant av LuckyWheelZoom. Tilpasset portrait-layout: smalere widthFactor
// og redusert toppmargin slik at hjulet får rom under gullkanten. Viser samme
// gull-stil som lodd/lotto/banditt på mobil (CSS border-image) — gull-rammen
// ligger som DOM-overlay over canvas + alle UI-elementer slik at den aldri kuttes.
function LuckyWheelZoomMobile({
  tickets,
  winnerName,
  winnerTicketNum,
  prizeName,
  prizeCountry,
  liveCount,
  showWinner,
  onSpinDone,
  onCanvasReady,
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const hasSpunRef = useRef(false);
  const currentRotationRef = useRef(0);
  const currentZoomRef = useRef(0);
  const lastBorderRef = useRef(-1);
  const lastPointerSectorRef = useRef(-1);
  const winnerIndexRef = useRef(-1);
  const winnerPulseRef = useRef(0);
  const pulseRafRef = useRef(null);
  const pinRef = useRef(null);
  const lastTickTimeRef = useRef(0);
  const [pointerName, setPointerName] = useState('');
  const [spinDoneState, setSpinDoneState] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);

  const segments = [...(tickets || [])].sort((a, b) => (a.ticketNum || 0) - (b.ticketNum || 0));
  const n = segments.length;

  const [VP, setVP] = useState({ W: window.innerWidth, H: window.innerHeight });
  useEffect(() => {
    const onResize = () => setVP({ W: window.innerWidth, H: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const VP_W = VP.W, VP_H = VP.H;

  const NUMBER_FONT_FAMILY = '"Impact", "Arial Black", "Helvetica Neue", sans-serif';
  const WHEEL_COLORS = ['#722F37', '#8B4513', '#4A1E24', '#A0522D', '#5C1F2B', '#6B3410'];

  // Gull-kant: 6 px (samme som lotto/lodd/banditt på mobil). Trekkes
  // fra geometriske nyttige områder slik at hjul/pil ikke krysser kanten.
  const GOLD_BORDER = 6;
  const R_WORLD = 4000;
  // Ring rundt hjulet i screen-px (matcher ringScreenPx i drawWheel under).
  // Beholdes synkronisert: brukes i idleRadiusPx-formelen for å garantere
  // 2 px klaring fra ringens YTTERSTE kant til gullkantens innside.
  const RING_SCREEN_PX = 8;

  // PIN_Y — skalerer mer aggressivt fra topp på mobil (mindre vertikal plass).
  // Klemmes til minst GOLD_BORDER + 90 px slik at gullkanten + live-navn får
  // plass over pin-tippen. Maks ~22% av VP_H slik at hjulet kan fylle resten.
  const PIN_Y = Math.max(GOLD_BORDER + 90, Math.round(VP_H * 0.20));

  // Idle-radius: ringens YTTERSTE kant skal være EKSAKT 7 px innenfor
  // gullkantens innside (var 2 px i v8), på ALLE viewport-størrelser. Ringen
  // er RING_SCREEN_PX bred og tegnes UTENFOR sektor-radien (R_WORLD). Sektor-
  // radius på skjerm = idleRadiusPx, så ring-ytter på skjerm =
  // idleRadiusPx + RING_SCREEN_PX. Setter dette til VP_W/2 - GOLD_BORDER - 7:
  //   idleRadiusPx + RING_SCREEN_PX = VP_W/2 - GOLD_BORDER - 7
  //   idleRadiusPx = VP_W/2 - GOLD_BORDER - 7 - RING_SCREEN_PX
  //                = VP_W/2 - 6 - 7 - 8 = VP_W/2 - 21
  // INGEN vertikal-clamp — den dominerte på korte iPhone SE-skjermer i v7.
  // Hjulet kan trygt vokse forbi viewport-bunnen siden cull-regelen
  // (cy > VP_H) håndterer overflyt og bunn-elementene (nedtelling,
  // gratulerer) er absolutt-posisjonert.
  // Math.max(50, ...) sikrer at radius aldri blir negativ/NaN ved ekstrem
  // viewport (rotert mobil, mini-vindu, osv.).
  const SIDE_GAP = 7;
  const idleRadiusHoriz = VP_W / 2 - GOLD_BORDER - SIDE_GAP - RING_SCREEN_PX;
  const idleRadiusPx = Math.max(50, idleRadiusHoriz);

  // Peak-radius: piecewise N_visible-interpolering (v13-spec) — antall sektor-
  // tall synlige i full skjermbredde ved peak zoom:
  //    n ≤  65:                   ingen zoom (peak = idle)
  //    66 ≤ n <= 100:             N_visible 12 → 10
  //    100 < n <= 150:            N_visible 10 → 8
  //    150 < n <= 250:            N_visible  8 → 6
  //    n > 250:                   N_visible 6 (uendret)
  // Geometri: 2R·sin(α/2) = VP_W der α = N_visible × (2π/n) ⇒
  //   R_peak = (VP_W/2) / sin(α/2)
  // Sterkere zoom-skalering enn v12 — tallene blir tydelig leselige også for
  // 66-249-området. v12 piecewise i zoom-rom (1.2× → 3.5× over n=65-200) ble
  // for mildt — denne N_visible-baserte spec'en gir naturlig progresjon mot
  // 6-tall-spec'en ved n=250.
  // Geometrisk floor er fjernet (var redundant — cull-regelen cy > VP_H +
  // Math.max(idleR, …) håndterer black-bar-prevention automatisk).
  let peakRadiusPx;
  if (n <= 65) {
    peakRadiusPx = idleRadiusPx;
  } else {
    let nVisible;
    if (n <= 100) {
      // 66 → 12, 100 → 10
      nVisible = 12 + (n - 66) * (10 - 12) / (100 - 66);
    } else if (n <= 150) {
      // 100 → 10, 150 → 8
      nVisible = 10 + (n - 100) * (8 - 10) / (150 - 100);
    } else if (n <= 250) {
      // 150 → 8, 250 → 6
      nVisible = 8 + (n - 150) * (6 - 8) / (250 - 150);
    } else {
      // n > 250: konstant 6
      nVisible = 6;
    }
    const visibleAngle = nVisible * (2 * Math.PI / n);
    const sinHalf = Math.sin(visibleAngle / 2);
    const peakRFromAngle = sinHalf > 0.001
      ? (VP_W / 2) / sinHalf
      : idleRadiusPx * 25;
    const maxPeakR = idleRadiusPx * 25;
    peakRadiusPx = Math.max(idleRadiusPx, Math.min(maxPeakR, peakRFromAngle));
  }
  const startZoom = idleRadiusPx / R_WORLD;
  // n ≤ 65 = ingen zoom (peak = idle ⇒ endZoom = startZoom). For n > 65
  // bruker peakRadiusPx fra N_visible-formelen ovenfor. peakRadiusPx ≥
  // idleRadiusPx er garantert via Math.max, så endZoom ≥ startZoom alltid.
  const endZoom = n <= 65 ? startZoom : peakRadiusPx / R_WORLD;

  function shadeColor(hex, percent) {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, Math.max(0, (num >> 16) + amt));
    const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amt));
    const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
    return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
  }

  function getPulsingWhite() {
    const p = (Math.sin(winnerPulseRef.current * Math.PI * 2) + 1) / 2;
    const r = Math.round(58 + (255 - 58) * p);
    const g = Math.round(15 + (245 - 15) * p);
    const b = Math.round(21 + (220 - 21) * p);
    return `rgb(${r},${g},${b})`;
  }

  function getSectorAtPointer(rotation) {
    if (n === 0) return 0;
    const slice = (Math.PI * 2) / n;
    let normRot = -rotation;
    normRot = ((normRot % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    let i = Math.floor(normRot / slice);
    return ((i % n) + n) % n;
  }

  function getBorderIndex(rotation, slice) {
    let norm = -rotation;
    norm = ((norm % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    return Math.floor(norm / slice) % n;
  }

  const drawWheel = useCallback((rotation, zoom, highlightIdx = -1) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const targetW = Math.round(VP_W * dpr);
    const targetH = Math.round(VP_H * dpr);
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
      canvas.style.width = VP_W + 'px';
      canvas.style.height = VP_H + 'px';
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, VP_W, VP_H);
    if (n === 0) return;

    ctx.save();
    ctx.translate(VP_W / 2, PIN_Y);
    ctx.scale(zoom, zoom);

    const cx = 0, cy = R_WORLD;
    const slice = (Math.PI * 2) / n;
    const arcPxOuter = slice * R_WORLD * zoom;

    const cy_screen = PIN_Y + R_WORLD * zoom;
    const w_half = VP_W / 2;
    let halfVis;
    if (cy_screen <= VP_H) {
      halfVis = Math.PI;
    } else {
      const dyBottom = cy_screen - VP_H;
      halfVis = Math.min(Math.atan(w_half / dyBottom) + slice * 4, Math.PI);
    }

    for (let i = 0; i < n; i++) {
      const midAngle = rotation + (i + 0.5) * slice - Math.PI / 2;
      let delta = midAngle + Math.PI / 2;
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      if (Math.abs(delta) > halfVis) continue;
      if (arcPxOuter < 0.4) continue;
      const a0 = rotation + i * slice - Math.PI / 2;
      const a1 = a0 + slice;
      const isHighlight = (i === highlightIdx);
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, R_WORLD, a0, a1);
      ctx.closePath();
      const baseColor = WHEEL_COLORS[i % WHEEL_COLORS.length];
      if (isHighlight) {
        const grad = ctx.createRadialGradient(cx, cy, R_WORLD * 0.2, cx, cy, R_WORLD);
        grad.addColorStop(0, '#F4D06F');
        grad.addColorStop(0.7, '#C9A84C');
        grad.addColorStop(1, '#8b6f2a');
        ctx.fillStyle = grad;
      } else if (arcPxOuter < 8) {
        ctx.fillStyle = baseColor;
      } else {
        const grad = ctx.createRadialGradient(cx, cy, R_WORLD * 0.3, cx, cy, R_WORLD);
        grad.addColorStop(0, shadeColor(baseColor, 22));
        grad.addColorStop(1, baseColor);
        ctx.fillStyle = grad;
      }
      ctx.fill();
      if (arcPxOuter >= 4) {
        ctx.strokeStyle = 'rgba(0,0,0,0.45)';
        ctx.lineWidth = 1 / zoom;
        ctx.stroke();
      }
    }

    const zoomProgress = (endZoom - startZoom) > 0.001
      ? Math.max(0, Math.min(1, (zoom - startZoom) / (endZoom - startZoom)))
      : 1;
    const labelAlpha = zoomProgress < 0.08 ? 0
      : zoomProgress < 0.22 ? (zoomProgress - 0.08) / 0.14
      : 1;
    if (arcPxOuter >= 4 && labelAlpha > 0) {
      ctx.save();
      ctx.globalAlpha = labelAlpha;
      // Font-clamp + n<60-boost.
      // Diagnose v15→v16: v15 forsøkte å heve fontMin/fontMax fra
      // [12, 112] til [13, 113] for n<60 — men det er clamp-grenser.
      // For n<60 er sektorene brede nok til at arcPxOuter*0.55 alltid
      // havner langt inne i clamp-intervallet, så grensene aktiveres
      // aldri og endringen ble synlig null.
      // Fix: legg på et konstant n<60-tillegg ETTER clamp-en, slik at
      // tallene faktisk vokser uavhengig av clamp-oppførsel. Totalt +6
      // px ift. opprinnelig basis før v13. Cap heves tilsvarende slik
      // at boosten ikke klippes av ved svært små n.
      //   n < 60:       basis-clamp [12, 112] + 6 px = effektivt [18, 118]
      //   60 ≤ n ≤ 250:  [12, 112] (uendret)
      //   n > 250:      [10, 110] (uendret)
      const fontMin = n <= 250 ? 12 : 10;
      const fontMax = n <= 250 ? 112 : 110;
      const fontBoost = n < 60 ? 6 : 0;
      const fontPxScreen = Math.min(Math.max(arcPxOuter * 0.55, fontMin), fontMax) + fontBoost;
      const fontLocal = fontPxScreen / zoom;
      ctx.font = `900 ${fontLocal}px ${NUMBER_FONT_FAMILY}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineWidth = Math.max(1, fontLocal * 0.05);
      ctx.lineJoin = 'round';
      const textRadius = R_WORLD - fontLocal * 0.65;
      for (let i = 0; i < n; i++) {
        const midAngle = rotation + (i + 0.5) * slice - Math.PI / 2;
        let delta = midAngle + Math.PI / 2;
        while (delta > Math.PI) delta -= Math.PI * 2;
        while (delta < -Math.PI) delta += Math.PI * 2;
        if (Math.abs(delta) > halfVis) continue;
        const isHighlight = (i === highlightIdx);
        const x = cx + Math.cos(midAngle) * textRadius;
        const y = cy + Math.sin(midAngle) * textRadius;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(midAngle + Math.PI / 2);
        const label = String(i + 1);
        ctx.strokeStyle = 'rgba(0,0,0,0.75)';
        ctx.strokeText(label, 0, 0);
        ctx.fillStyle = isHighlight ? getPulsingWhite() : '#F5E6D3';
        ctx.fillText(label, 0, 0);
        ctx.restore();
      }
      ctx.restore();
    }

    const ringScreenPx = RING_SCREEN_PX;
    const ringWLocal = ringScreenPx / zoom;
    ctx.strokeStyle = '#C9A84C';
    ctx.lineWidth = ringWLocal;
    ctx.beginPath();
    if (halfVis >= Math.PI - 0.01) {
      ctx.arc(cx, cy, R_WORLD + ringWLocal * 0.5, 0, Math.PI * 2);
    } else {
      ctx.arc(cx, cy, R_WORLD + ringWLocal * 0.5, -Math.PI / 2 - halfVis, -Math.PI / 2 + halfVis);
    }
    ctx.stroke();

    const nailScreenPx = Math.max(
      1.2,
      Math.min(6, ringScreenPx * 0.5, R_WORLD * zoom * 0.0035, arcPxOuter * 0.4)
    );
    const nailRadiusLocal = nailScreenPx / zoom;
    const nailR = R_WORLD + ringWLocal * 0.5;
    for (let i = 0; i < n; i++) {
      const borderAngle = rotation + i * slice - Math.PI / 2;
      let delta = borderAngle + Math.PI / 2;
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      if (Math.abs(delta) > halfVis) continue;
      const nx = cx + Math.cos(borderAngle) * nailR;
      const ny = cy + Math.sin(borderAngle) * nailR;
      ctx.beginPath();
      ctx.arc(nx, ny, nailRadiusLocal, 0, Math.PI * 2);
      ctx.fillStyle = '#0a0806';
      ctx.fill();
      if (nailScreenPx >= 2.5) {
        ctx.beginPath();
        ctx.arc(nx - nailRadiusLocal * 0.3, ny - nailRadiusLocal * 0.3, nailRadiusLocal * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(180,140,60,0.75)';
        ctx.fill();
      }
    }

    const hubScreenY = PIN_Y + R_WORLD * zoom;
    if (hubScreenY < VP_H + R_WORLD * 0.15 * zoom) {
      const hubR = R_WORLD * 0.15;
      const hubGrad = ctx.createRadialGradient(
        cx - hubR * 0.3, cy - hubR * 0.3, hubR * 0.1,
        cx, cy, hubR
      );
      hubGrad.addColorStop(0, '#fff5d0');
      hubGrad.addColorStop(0.6, '#C9A84C');
      hubGrad.addColorStop(1, '#8b6f2a');
      ctx.beginPath();
      ctx.arc(cx, cy, hubR, 0, Math.PI * 2);
      ctx.fillStyle = hubGrad;
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.5)';
      ctx.lineWidth = 2 / zoom;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, hubR * 0.55, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(74, 30, 36, 0.5)';
      ctx.lineWidth = 1.5 / zoom;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, hubR * 0.08, 0, Math.PI * 2);
      ctx.fillStyle = '#4a1e24';
      ctx.fill();
    }

    ctx.restore();
  }, [n, VP_W, VP_H]); // eslint-disable-line

  useEffect(() => {
    if (!hasSpunRef.current && !spinDoneState) {
      currentZoomRef.current = startZoom;
      currentRotationRef.current = 0;
      drawWheel(0, startZoom, -1);
      lastPointerSectorRef.current = getSectorAtPointer(0);
    }
  }, [drawWheel, startZoom, spinDoneState]); // eslint-disable-line

  useEffect(() => {
    if (onCanvasReady && canvasRef.current) onCanvasReady(canvasRef.current);
  }, [onCanvasReady]); // eslint-disable-line

  function startWinnerPulse(winnerIdx) {
    if (pulseRafRef.current) cancelAnimationFrame(pulseRafRef.current);
    const startTime = performance.now();
    const frame = (now) => {
      winnerPulseRef.current = ((now - startTime) / 1000) * 2;
      drawWheel(currentRotationRef.current, currentZoomRef.current, winnerIdx);
      pulseRafRef.current = requestAnimationFrame(frame);
    };
    pulseRafRef.current = requestAnimationFrame(frame);
  }
  function stopWinnerPulse() {
    if (pulseRafRef.current) { cancelAnimationFrame(pulseRafRef.current); pulseRafRef.current = null; }
    winnerPulseRef.current = 0;
  }

  useEffect(() => {
    if (!winnerName) {
      hasSpunRef.current = false;
      setSpinDoneState(false);
      setIsSpinning(false);
      setPointerName('');
      stopWinnerPulse();
      currentRotationRef.current = 0;
      currentZoomRef.current = startZoom;
      drawWheel(0, startZoom, -1);
      if (pinRef.current) {
        pinRef.current.style.setProperty('--lwzm-pin-scale', '1');
      }
    }
  }, [winnerName]); // eslint-disable-line

  useEffect(() => {
    if (!winnerName || hasSpunRef.current || n === 0) return;
    hasSpunRef.current = true;
    setIsSpinning(true);
    getAudioCtx();

    const winnerIdx = winnerTicketNum != null
      ? segments.findIndex(s => s.ticketNum === winnerTicketNum)
      : segments.findIndex(s => s.name === winnerName);
    if (winnerIdx < 0) {
      hasSpunRef.current = false;
      setIsSpinning(false);
      return;
    }
    winnerIndexRef.current = winnerIdx;
    const seed = (winnerTicketNum || 1) * 9301 + 49297;
    const rand01 = (seed % 1000) / 1000;
    const startRotation = currentRotationRef.current;
    // Bruk DELT helper for slutt-rotasjons-beregning. Garanterer numerisk
    // identisk resultat med LuckyWheelZoom (PC).
    const { slice, targetRotation, snapAngleAbs, travelSign,
      targetRotationMain, totalTravelMain } =
      computeSpinTargets(winnerIdx, n, rand01, startRotation);

    // Spinn-timing IDENTISK med PC (LuckyWheelZoom): n-basert duration-stige,
    // power-5 ease-out for rotasjon, 1-sek absolutt idle-hold før smootherstep
    // zoom over resterende tid. Synkroniseringen sikrer felles vedlikehold og
    // konsistent opplevelse på tvers av plattform. Endringer her skal også
    // gjøres i LuckyWheelZoom.
    let duration = 14000;
    if (n > 60) duration = 16000;
    if (n > 100) duration = 18000;
    if (n > 300) duration = 20000;
    if (n > 800) duration = 22000;

    const easeRot = (t) => 1 - Math.pow(1 - t, 5);
    const idleHoldT = Math.min(0.5, 1000 / duration);
    const easeZoom = (t) => {
      if (t < idleHoldT) return 0;
      const zt = (t - idleHoldT) / (1 - idleHoldT);
      return zt * zt * zt * (zt * (zt * 6 - 15) + 10);
    };

    lastBorderRef.current = getBorderIndex(startRotation, slice);
    lastTickTimeRef.current = 0;
    const startTime = performance.now();

    const frame = (now) => {
      const t = Math.min((now - startTime) / duration, 1);
      const rotEased = easeRot(t);
      currentRotationRef.current = startRotation + totalTravelMain * rotEased;
      currentZoomRef.current = startZoom + (endZoom - startZoom) * easeZoom(t);
      if (pinRef.current) {
        const zp = (endZoom - startZoom) > 0.001
          ? Math.max(0, Math.min(1, (currentZoomRef.current - startZoom) / (endZoom - startZoom)))
          : 0;
        const pinScale = 1 + zp * 0.3;
        pinRef.current.style.setProperty('--lwzm-pin-scale', pinScale.toFixed(3));
      }
      const border = getBorderIndex(currentRotationRef.current, slice);
      if (border !== lastBorderRef.current) {
        const tickNow = Date.now();
        if (tickNow - lastTickTimeRef.current >= 80) {
          const speed = 1 - rotEased;
          playWheelTick(0.3 + speed * 0.7);
          lastTickTimeRef.current = tickNow;
        }
        lastBorderRef.current = border;
      }
      drawWheel(currentRotationRef.current, currentZoomRef.current, -1);
      const ptr = getSectorAtPointer(currentRotationRef.current);
      if (ptr !== lastPointerSectorRef.current) {
        lastPointerSectorRef.current = ptr;
        if (segments[ptr]) setPointerName(segments[ptr].name);
      }
      if (t < 1) {
        rafRef.current = requestAnimationFrame(frame);
      } else {
        // Snap-fasen IDENTISK med PC: 130 ms, cubic ease-out til 5% overshoot
        // forbi targetRotation i første 80%, lineær settle tilbake til eksakt
        // targetRotation i siste 20%. Gir den mekaniske "klikk-tilbake"
        // følelsen. Slutt-posisjon = targetRotation = sektor-senter (siden
        // intraSectorOffset = 0 på mobil).
        currentRotationRef.current = targetRotationMain;
        currentZoomRef.current = endZoom;
        if (pinRef.current) {
          pinRef.current.style.setProperty('--lwzm-pin-scale', '1.300');
        }
        playWheelTick(1.0);
        const snapDur = 130;
        const snapStart = now;
        const overshootRot = targetRotation + snapAngleAbs * travelSign * 0.05;
        const snapFrame = (sNow) => {
          const sT = Math.min((sNow - snapStart) / snapDur, 1);
          let snapRot;
          if (sT < 0.8) {
            const u = sT / 0.8;
            const eased = 1 - Math.pow(1 - u, 3);
            snapRot = targetRotationMain + (overshootRot - targetRotationMain) * eased;
          } else {
            const u = (sT - 0.8) / 0.2;
            snapRot = overshootRot + (targetRotation - overshootRot) * u;
          }
          currentRotationRef.current = snapRot;
          drawWheel(snapRot, endZoom, -1);
          if (sT < 1) {
            rafRef.current = requestAnimationFrame(snapFrame);
          } else {
            currentRotationRef.current = targetRotation;
            drawWheel(targetRotation, endZoom, winnerIdx);
            if (segments[winnerIdx]) setPointerName(segments[winnerIdx].name);
            startWinnerPulse(winnerIdx);
            setSpinDoneState(true);
            onSpinDone && onSpinDone();
          }
        };
        rafRef.current = requestAnimationFrame(snapFrame);
      }
    };
    rafRef.current = requestAnimationFrame(frame);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      stopWinnerPulse();
    };
  }, [winnerName]); // eslint-disable-line

  // Pin og live-navn — mobil-tunet. Pin halvert mot PC for å føles diskret på
  // smal skjerm. Skalering (1.0 → 1.3x ved peak-zoom) er bevart via
  // --lwzm-pin-scale CSS-variabelen, slik at +30%-veksten gjelder uansett
  // base-størrelse.
  const pinW = 7, pinH = 19;
  const pinTopY = PIN_Y - pinH + 2;
  const liveNameTopY = Math.max(GOLD_BORDER + 14, Math.round(PIN_Y * 0.20));
  const showLiveName = (isSpinning || spinDoneState) && !(showWinner && spinDoneState);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10,
      background: 'radial-gradient(ellipse at center,#1a1410 0%,#0a0806 70%,#000 100%)',
      overflow: 'hidden', touchAction: 'none', overscrollBehavior: 'none',
      userSelect: 'none',
    }} onClick={() => getAudioCtx()}>
      <Confetti active={showWinner && spinDoneState}/>

      <canvas ref={canvasRef} style={{
        position: 'absolute', inset: 0, display: 'block', zIndex: 1
      }}/>

      {showLiveName && (
        <div style={{
          position: 'absolute', top: liveNameTopY, left: 0, right: 0,
          zIndex: 22, pointerEvents: 'none', textAlign: 'center',
          padding: `0 ${GOLD_BORDER + 18}px`,
        }}>
          {/* Glow-bakgrunn — radial pille bak teksten (smalere enn PC). */}
          <div style={{
            position: 'absolute',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '18ch',
            height: '2.4em',
            fontSize: 'clamp(22px, 5.4vw, 32px)',
            background: 'radial-gradient(ellipse at center, rgba(232, 184, 90, 0.55) 0%, rgba(232, 184, 90, 0.28) 35%, rgba(232, 184, 90, 0.10) 65%, rgba(232, 184, 90, 0) 100%)',
            filter: 'blur(4px)',
            pointerEvents: 'none',
          }}/>
          <div style={{
            position: 'relative',
            fontFamily: "'Montserrat', 'Playfair Display', Georgia, serif",
            fontWeight: 900,
            fontSize: 'clamp(22px, 5.4vw, 32px)',
            color: '#e8b85a',
            letterSpacing: '1px',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            zIndex: 1,
          }}>{pointerName || ' '}</div>
        </div>
      )}

      <div ref={pinRef} style={{
        position: 'absolute', top: pinTopY, left: '50%',
        transform: 'translateX(-50%) scale(var(--lwzm-pin-scale, 1))',
        transformOrigin: '50% 50%',
        zIndex: 20, pointerEvents: 'none',
        filter: 'drop-shadow(0 3px 8px rgba(0,0,0,0.85))',
        willChange: 'transform'
      }}>
        <svg width={pinW} height={pinH} viewBox="0 0 22 58">
          <defs>
            <linearGradient id="lwzmPinGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#444"/>
              <stop offset="40%" stopColor="#c8c8c8"/>
              <stop offset="50%" stopColor="#ffffff"/>
              <stop offset="60%" stopColor="#c8c8c8"/>
              <stop offset="100%" stopColor="#444"/>
            </linearGradient>
            <linearGradient id="lwzmPinTipGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#888"/>
              <stop offset="100%" stopColor="#222"/>
            </linearGradient>
          </defs>
          <circle cx="11" cy="6" r="6" fill="url(#lwzmPinGrad)" stroke="#222" strokeWidth="0.6"/>
          <path d="M8 6 Q11 6 14 6 L13 48 L11 56 L9 48 Z"
            fill="url(#lwzmPinTipGrad)" stroke="#111" strokeWidth="0.5"/>
        </svg>
      </div>

      {/* v211: nedtellingstall fjernet — blikkfanget eier nedtellingen (lyd beholdt). */}

      {showWinner && spinDoneState && winnerName && (
        // v216: inline gratulerer-JSX konsolidert til ElegantWinnerCard. Wrapperen
        // beholdes (absolute bottom + lwzmSlideUp + GOLD_BORDER-offset). noAnim=true
        // så kun wrapperens slideUp spiller.
        // v279: vinrød/sort linear-gradient-wrapper fjernet og softBackdrop=true
        // slik at hjul-kortet har EKSAKT samme bakgrunn som slot machine/lotto/
        // lodd. CARD_LOWER_PX (justerbar) senker kortet litt.
        <div style={{
          position: 'absolute', bottom: GOLD_BORDER, left: GOLD_BORDER, right: GOLD_BORDER,
          padding: `20px 14px ${Math.max(4, 24 - 14)}px`, // 14 = CARD_LOWER_PX (mobil)
          zIndex: 45, pointerEvents: 'none',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          animation: 'lwzmSlideUp 0.5s cubic-bezier(0.34,1.56,0.64,1) both'
        }}>
          <ElegantWinnerCard
            winnerName={winnerName}
            prizeName={prizeName}
            prizeCountry={prizeCountry}
            noAnim={true}
            softBackdrop={true}
          />
        </div>
      )}

      {/* Gullkant — DOM-overlay med samme stil som lodd/lotto/banditt mobil
          (kopiert fra LottoDraw3D linje ~7150). zIndex 70 sikrer at den
          ligger over canvas, pin, navn, nedtelling og gratulerer-blokken. */}
      <div style={{
        position: 'absolute', inset: 0,
        border: `${GOLD_BORDER}px solid`,
        borderImage: 'linear-gradient(145deg,#fff5a0 0%,#C9A84C 20%,#8B6B1A 50%,#C9A84C 80%,#fff5a0 100%) 1',
        boxShadow: 'inset 0 0 0 2px rgba(0,0,0,0.4), 0 0 20px rgba(201,168,76,0.3)',
        pointerEvents: 'none',
        zIndex: 70,
      }}/>

      <style>{`
        @keyframes lwzmCountPulse {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.15); opacity: 1; }
          75% { transform: scale(0.95); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes lwzmSlideUp {
          0% { transform: translateY(20px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}


// ─── NEDTELLING ───────────────────────────────────────────────────────────────
function Countdown({ targetTime, compact = false }) {
  const [timeLeft, setTimeLeft] = useState(null);
  useEffect(() => {
    if (!targetTime) return;
    const update = () => { const diff = new Date(targetTime)-new Date(); setTimeLeft(diff>0?diff:0); };
    update(); const i = setInterval(update,1000); return () => clearInterval(i);
  }, [targetTime]);
  if (!timeLeft||!targetTime) return null;
  const h=Math.floor(timeLeft/3600000), m=Math.floor((timeLeft%3600000)/60000), s=Math.floor((timeLeft%60000)/1000);
  // v312: kompakt PC-variant — én lav, sentrert pille (label + tall på samme
  // linje) som ligger MELLOM tittelen og tickeren, så den ikke skyver kjøp-panel
  // og premier nedover slik den fulle banner-boksen gjorde. Kun PC-spillsiden
  // sender compact; mobil bruker den fulle banneret uendret.
  if (compact) {
    const unit = { fontSize:17, fontWeight:"bold", color:"#C9A84C", fontFamily:"'Lato',Arial,sans-serif", fontVariantNumeric:"tabular-nums", lineHeight:1 };
    return (
      <div style={{
        display:"flex", alignItems:"center", justifyContent:"center", gap:12,
        width:"fit-content", maxWidth:"100%", margin:"8px auto 0",
        padding:"5px 16px", background:"#1a1200", border:"1px solid #C9A84C",
        borderRadius:18,
      }}>
        <span style={{ fontSize:10, color:"#a89058", letterSpacing:"0.12em", textTransform:"uppercase", fontFamily:"'Lato',Arial,sans-serif", whiteSpace:"nowrap" }}>⏱ Trekning starter om</span>
        <span style={{ display:"flex", gap:8 }}>
          {h>0&&<span style={unit}>{String(h).padStart(2,"0")}<small style={{ fontSize:11, color:"#888" }}> t</small></span>}
          <span style={unit}>{String(m).padStart(2,"0")}<small style={{ fontSize:11, color:"#888" }}> m</small></span>
          <span style={unit}>{String(s).padStart(2,"0")}<small style={{ fontSize:11, color:"#888" }}> s</small></span>
        </span>
      </div>
    );
  }
  return (
    <div style={S.countdownBanner}>
      <p style={S.countdownLabel}>⏱ Trekning starter om</p>
      <div style={S.countdownTimer}>
        {h>0&&<span style={S.countdownUnit}>{String(h).padStart(2,"0")}<small> t</small></span>}
        <span style={S.countdownUnit}>{String(m).padStart(2,"0")}<small> m</small></span>
        <span style={S.countdownUnit}>{String(s).padStart(2,"0")}<small> s</small></span>
      </div>
    </div>
  );
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function LoginScreen() {
  const [loading,setLoading]=useState(false); const [error,setError]=useState("");
  const handleLogin=async()=>{
    setLoading(true); setError("");
    try {
      const result=await signInWithPopup(auth,new GoogleAuthProvider());
      if (!ALLOWED_EMAILS.includes(result.user.email)) { await signOut(auth); setError("Du har ikke tilgang."); }
    } catch(e) { setError(e.message||"Innlogging feilet."); }
    setLoading(false);
  };
  return (
    <div style={S.loginScreen}>
      <div style={S.loginCard}>
        <div style={{ fontSize:52,marginBottom:12 }}>🍾</div>
        <h1 style={S.loginTitle}>Vinloddet</h1>
        <p style={S.loginRule}>Arrangør-innlogging</p>
        <button style={S.googleBtn} onClick={handleLogin} disabled={loading}>
          <span>🔑</span>{loading?"Logger inn…":"Logg inn med Google"}
        </button>
        {error&&<p style={S.loginError}>{error}</p>}
      </div>
    </div>
  );
}

// ─── VINNERSKJERM (deltaker) ──────────────────────────────────────────────────

// ─── MEDAL COMPONENT ──────────────────────────────────────────────────────────
function Medal({ rank }) {
  const colors = {
    1: { outer:"#C9A84C", inner:"#FFD700", text:"#7a4800", shine:"#fff8d0" },
    2: { outer:"#888",    inner:"#c0c0c0", text:"#333",    shine:"#f0f0f0" },
    3: { outer:"#7a4a1a", inner:"#cd7f32", text:"#4a2800", shine:"#ffd89a" },
  };
  const c = colors[rank] || { outer:"#555", inner:"#888", text:"#fff", shine:"#ccc" };
  if (rank > 3) return <span style={{ fontSize:13,fontWeight:"bold",color:"#888",minWidth:28,textAlign:"center" }}>{rank}.</span>;
  return (
    <svg width="32" height="36" viewBox="0 0 32 36" style={{ flexShrink:0 }}>
      {/* Ribbon */}
      <polygon points="10,2 22,2 26,12 16,10 6,12" fill={c.outer} opacity="0.9"/>
      <polygon points="10,2 16,10 6,12 4,6" fill={c.outer}/>
      <polygon points="22,2 16,10 26,12 28,6" fill={c.inner}/>
      {/* Circle */}
      <circle cx="16" cy="23" r="12" fill={c.outer}/>
      <circle cx="16" cy="23" r="10" fill={c.inner}/>
      <circle cx="16" cy="23" r="8" fill={c.inner} opacity="0.6"/>
      {/* Shine */}
      <ellipse cx="13" cy="19" rx="3" ry="2" fill={c.shine} opacity="0.5" transform="rotate(-30,13,19)"/>
      {/* Number */}
      <text x="16" y="27" textAnchor="middle" fontSize="9" fontWeight="900"
        fontFamily="Arial Black,sans-serif" fill={c.text}>{rank}</text>
    </svg>
  );
}

// ─── OPPSUMMERING: HISTORIKK-KOMMENTARER (v256) ─────────────────────────────
// Varme, personlige kommentarer per vinner på done-skjermen. To datakilder:
//  • Kveldens data (participants, live) — alltid solid: flest lodd / ett lodd.
//  • Historiske aggregater (history-collection) — e-post-basert.
// PRESISJON: vinst-tellinger matcher KUN på winnerEmail (e-post på vinner-
// oppføringen). Eldre history-runder mangler winnerEmail → telles IKKE, så
// vinst-statistikken "starter på null" nå og blir rikere etter hvert. Trofast/
// mange-lodd matcher på participants[].email (finnes alltid). E-post brukes KUN
// som intern nøkkel — rendres ALDRI i UI.
function vlNorm(s) { return (s == null ? "" : String(s)).trim().toLowerCase(); }

// v258: variant-pools. Hver kommentar-TYPE har flere formuleringer så
// oppsummeringssiden ikke ser ut som en mal når flere vinnere har samme status.
// {n} = tall, {ord} = ordenstall-ord (Andre/Tredje …).
const KOMMENTAR_VARIANTER = {
  endelig: [
    "Endelig din tur, etter mange forsøk!",
    "For en velfortjent gevinst — tålmodigheten lønte seg!",
    "Mange runder, og nå endelig napp!",
    "Det måtte jo skje til slutt — gratulerer!",
    "Lykken snudde i kveld!",
  ],
  forste: [
    "Første gevinst — gratulerer!",
    "Premiere! Aldri vunnet før.",
    "Jomfruturen ga napp!",
    "Første gang i vinnerlista!",
  ],
  flereGevinster: [
    "{ord} gevinst — på god vei!",
    "Vinner igjen! Gevinst nummer {n}.",
    "Heldiggrisen slår til for {n}. gang!",
    "Nok en gang i vinnerlista — {n}. gevinst!",
  ],
  trofast: [
    "Trofast deltaker — med på {n} trekninger",
    "Fast inventar i lotteriet — {n} runder!",
    "Alltid med — dette er runde {n}",
  ],
  mangeLodd: [
    "Kjøper alltid mange lodd!",
    "Satser friskt hver gang!",
    "Storkjøper av lodd!",
  ],
  ettLodd: [
    "Heldig — vant med bare ett lodd!",
    "Ett eneste lodd, og blink!",
    "Kun ett lodd — utrolig flaks!",
  ],
  flestLodd: [
    "Kjøpte flest lodd i kveld!",
    "Kveldens storkjøper!",
    "Flest lodd i kveld — fortjent!",
  ],
};

function vlOrdWord(n) {
  const o = { 2:"Andre", 3:"Tredje", 4:"Fjerde", 5:"Femte", 6:"Sjette", 7:"Sjuende", 8:"Åttende", 9:"Niende", 10:"Tiende" };
  return o[n] || `${n}.`;
}

function vlHash(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function vlFyllVariant(tpl, n) {
  return tpl.replace(/\{ord\}/g, vlOrdWord(n)).replace(/\{n\}/g, n != null ? String(n) : "");
}

function buildVinnerKommentarer({ winners, participants, history }) {
  const live = Array.isArray(participants) ? participants : [];
  const hist = Array.isArray(history) ? history : [];

  // Kveldens lodd-sum per e-post (og per navn som fallback hvis e-post mangler).
  const tonightByEmail = {};
  const tonightByName = {};
  live.forEach(p => {
    const e = vlNorm(p.email), n = vlNorm(p.name), t = Number(p.tickets) || 0;
    if (e) tonightByEmail[e] = (tonightByEmail[e] || 0) + t;
    if (n) tonightByName[n] = (tonightByName[n] || 0) + t;
  });
  const tonightVals = Object.values(tonightByEmail);
  const maxTonight = tonightVals.length ? Math.max(...tonightVals) : 0;
  const distinctBuyers = Object.keys(tonightByEmail).length;

  // Variant-valg: stabilt per vinner (seedet på navn+loddnr), men sporing av
  // sist brukte variant per type så to vinnere etter hverandre ikke får samme
  // formulering. lastVariantIdx deles på tvers av vinnerne i rekkefølge.
  const lastVariantIdx = {};

  return (Array.isArray(winners) ? winners : []).map(w => {
    const wname = vlNorm(w && w.name);
    // E-post: winnerEmail (v256+) ELLER match på kveldens deltaker-navn.
    let epost = vlNorm(w && w.winnerEmail);
    if (!epost && wname) {
      const m = live.find(p => vlNorm(p.name) === wname && vlNorm(p.email));
      if (m) epost = vlNorm(m.email);
    }

    const ticketsTonight = epost
      ? (tonightByEmail[epost] || 0)
      : (tonightByName[wname] || 0);

    // Historiske aggregater — kun hvis vi har e-post.
    let priorWins = 0, historyRounds = 0, histTickets = 0;
    if (epost) {
      hist.forEach(round => {
        const parts = Array.isArray(round.participants) ? round.participants : [];
        let deltok = false;
        parts.forEach(p => {
          if (vlNorm(p.email) === epost) { deltok = true; histTickets += (Number(p.tickets) || 0); }
        });
        if (deltok) historyRounds += 1;
        // Vinst telles KUN når vinner-oppføringen HAR winnerEmail (v256+).
        const wins = Array.isArray(round.winners) ? round.winners : [];
        wins.forEach(ww => {
          if (ww && ww.winnerEmail && vlNorm(ww.winnerEmail) === epost) priorWins += 1;
        });
      });
    }
    const avgTickets = historyRounds > 0 ? histTickets / historyRounds : 0;

    // Descriptors (type + evt. tall) — tekst velges etterpå fra variant-pool.
    // v258: strammere terskler så IKKE alle får en dramatisk historie. Mange
    // vinnere får 0-1 kommentar — det er mer naturlig enn at alle har en historie.
    const descs = [];
    let endeligShown = false;

    // 1) Vinst-status (gjensidig utelukkende). Kun med e-post.
    if (epost) {
      if (priorWins >= 1) {
        descs.push({ cat: "flereGevinster", n: priorWins + 1 });
      } else if (historyRounds >= 5) {            // var 3 — nå "virkelig mange forsøk"
        descs.push({ cat: "endelig" });
        endeligShown = true;
      } else if (historyRounds >= 2) {            // var 1 — brand-nye får ingenting
        descs.push({ cat: "forste" });
      }
    }

    // 2) Kveldens.
    if (ticketsTonight === 1) {
      descs.push({ cat: "ettLodd" });
    } else if (maxTonight > 0 && ticketsTonight === maxTonight && distinctBuyers >= 2) {
      descs.push({ cat: "flestLodd" });
    }

    // 3) Trofast deltaker (ikke når "endelig" allerede er vist — overlapp).
    if (epost && !endeligShown && historyRounds >= 3) {   // var 2
      descs.push({ cat: "trofast", n: historyRounds + 1 });
    }

    // 4) Kjøper alltid mange lodd.
    if (epost && historyRounds >= 2 && avgTickets >= 5) {
      descs.push({ cat: "mangeLodd", n: null });
    }

    // Velg variant per descriptor (maks 2 — ikke overlesset).
    const key = `${wname}#${w && w.ticketNum != null ? w.ticketNum : ""}`;
    return descs.slice(0, 2).map(d => {
      const pool = KOMMENTAR_VARIANTER[d.cat];
      let idx = vlHash(key + "|" + d.cat) % pool.length;
      if (pool.length > 1 && lastVariantIdx[d.cat] === idx) idx = (idx + 1) % pool.length;
      lastVariantIdx[d.cat] = idx;
      return vlFyllVariant(pool[idx], d.n);
    });
  });
}

// Leser history-collection (public read) og beregner kommentarer. Henter kun
// når enabled && det finnes vinnere — kveldens-kommentarer vises umiddelbart,
// historiske fylles inn når history er lastet.
function useWinnerComments(winners, participants, enabled = true) {
  const [history, setHistory] = useState(null);
  const n = winners ? winners.length : 0;
  useEffect(() => {
    if (!enabled || n === 0) return;
    let cancelled = false;
    getDocs(collection(db, "history"))
      .then(snap => { if (!cancelled) setHistory(snap.docs.map(d => d.data())); })
      .catch(() => { if (!cancelled) setHistory([]); });
    return () => { cancelled = true; };
  }, [enabled, n]);
  return useMemo(
    () => buildVinnerKommentarer({ winners: winners || [], participants: participants || [], history: history || [] }),
    [winners, participants, history]
  );
}

// Liten presentasjons-komponent for kommentar-linjene under en vinner.
function VinnerKommentarer({ comments, align = "center", marginTop = 6 }) {
  if (!comments || comments.length === 0) return null;
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:2, marginTop, alignItems: align==="center"?"center":"flex-start", width:"100%" }}>
      {comments.map((c, i) => (
        <span key={i} style={{
          fontFamily:"'Montserrat','Playfair Display',Georgia,serif",
          fontStyle:"italic", fontSize:12.5, lineHeight:1.25,
          color:"#C9A84C", textAlign:align,
        }}>{c}</span>
      ))}
    </div>
  );
}

function WinnerViewWithScroll({ lotteryState, prizes, settings, scrollNow, pcLayout = false, participants = [] }) {
  const [hasShown, setHasShown] = useState(false);
  useEffect(() => {
    if (!scrollNow) return;
    setHasShown(true);
    setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }), 400);
  }, [scrollNow]);
  return <WinnerView lotteryState={lotteryState} prizes={prizes} settings={settings} showConfetti={hasShown} pcLayout={pcLayout} participants={participants}/>;
}

function WinnerView({ lotteryState, prizes, settings, showConfetti, pcLayout = false, participants = [] }) {
  const winners = lotteryState?.winnerHistory || [];
  const winnerComments = useWinnerComments(winners, participants, true);
  const titleText = (()=>{
    const ts = lotteryState?.completedAt || lotteryState?.startedAt;
    if(!ts) return "Ukens vinnere!";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return `Vinnere ${d.toLocaleDateString("nb-NO",{ day:"numeric",month:"long" })}`;
  })();

  if (pcLayout) {
    // v60 FIX 3: vinner-kort som grid, 1-4 per rad avhengig av antall.
    // Lik luminans-baserte tekstfarger som premie-kortene (v59 FIX 4) slik
    // at både mørke og lyse kort-bakgrunner får tydelig kontrast.
    const n = winners.length;
    // v61 FIX 4: opptil 5 vinnere per rad (var 4).
    const cols = Math.min(Math.max(n, 1), 5);
    const cardBg = settings?.vinnerKortFarge || "#ffffff";
    const _lum = (hex) => {
      try {
        const h = (hex||"").replace('#','');
        if (h.length < 6) return 1;
        const r = parseInt(h.slice(0,2),16)/255, g = parseInt(h.slice(2,4),16)/255, b = parseInt(h.slice(4,6),16)/255;
        return 0.2126*r + 0.7152*g + 0.0722*b;
      } catch (e) { return 1; }
    };
    const dark = _lum(cardBg) < 0.5;
    const txtName  = dark ? "#f0f0f0" : "#111";
    const txtPrize = dark ? "#d4af37" : "#8B6914";
    const txtLodd  = dark ? "#aaa"    : "#888";
    return (
      <div style={{ maxWidth:1280, margin:"0 auto", padding:"4px 24px 0", boxSizing:"border-box" }}>
        <Confetti active={!!showConfetti}/>
        <h1 style={{ ...S.winnerTitle, margin:"0 0 24px", fontSize:28, textAlign:"center" }}>Siste treknings vinnere</h1>
        {/* v132: faste kort-bredder + sentrert grid i stedet for 1fr-strekk.
            Pars/flere ble ~620px brede med 1fr; nå 280px hver. Solo-vinner
            beholder sin bredere 400px (bevisst, mer prominent). */}
        <div style={{
          display:"grid",
          gridTemplateColumns: n === 1 ? "minmax(0, 400px)" : `repeat(${cols}, 280px)`,
          gap:20,
          justifyContent:"center",
          maxWidth:"100%",
          margin:"0 auto",
        }}>
          {winners.map((w,i)=>{
            const img = prizes.find(p=>p.name===w.prize)?.imageUrl;
            return (
              <div key={i} style={{
                position:"relative",
                background:cardBg,
                border:`1px solid ${dark?"#3a3a3a":"#e0e0e0"}`,
                borderRadius:10, padding:"22px 16px 22px",
                display:"flex", flexDirection:"column", alignItems:"center",
                textAlign:"center", gap:12, boxSizing:"border-box",
              }}>
                <div style={{
                  width:120, height:160, borderRadius:6,
                  background:"radial-gradient(ellipse at center, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.18) 100%)",
                  display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden",
                }}>
                  {img
                    ? <img src={img} alt={w.prize} style={{ width:"100%", height:"100%", objectFit:"contain" }}/>
                    : <span style={{ fontSize:52 }}>🍷</span>}
                </div>
                <p style={{ margin:0, fontSize:20, fontWeight:"bold", color:txtName, lineHeight:1.2 }}>{w.name}</p>
                <p style={{ margin:0, fontSize:14, color:txtPrize, lineHeight:1.35 }}>{w.prize}</p>
                <VinnerKommentarer comments={winnerComments[i]} align="center" marginTop={4}/>
                {/* v258: loddnr diskret i nedre høyre hjørne (var inline bak premie-navn). */}
                {w.ticketNum && <span style={{ position:"absolute", right:10, bottom:6, fontSize:11, color:txtLodd, opacity:0.85 }}>lodd #{w.ticketNum}</span>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding:"4px 16px 24px",display:"flex",flexDirection:"column",alignItems:"center",gap:4 }}>
      <Confetti active={!!showConfetti}/>
      <h1 style={{ ...S.winnerTitle,marginBottom:0,marginTop:-27,fontSize:22 }}>{titleText}</h1>
      <div style={{ width:"100%",maxWidth:420 }}>
        {winners.map((w,i)=>(
          <div key={i} style={{ ...S.winnerCard, position:"relative", paddingBottom:18, background:settings?.vinnerKortFarge||"#ffffff", borderColor:(settings?.vinnerKortFarge||"#ffffff")==="#ffffff"?"#e0e0e0":"#3a3a3a" }}>

            {(()=>{ const img=prizes.find(p=>p.name===w.prize)?.imageUrl; return img?(
              // v46: vignett-wrapper rundt vinner-bilde så transparent PNG
              // er synlig mot hvitt vinnerkort.
              <div style={{
                width:36, height:80, flexShrink:0, borderRadius:4,
                background:"radial-gradient(ellipse at center, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.18) 100%)",
                display:"flex", alignItems:"center", justifyContent:"center",
              }}>
                <img src={img} alt={w.prize} style={{ width:"100%",height:"100%",objectFit:"contain" }}/>
              </div>
            ):null; })()}
            <div style={{ flex:1 }}>
              <p style={S.winnerName}>{w.name}</p>
              <p style={S.winnerPrizeName}>{w.prize}</p>
              <VinnerKommentarer comments={winnerComments[i]} align="left" marginTop={4}/>
            </div>
            {/* v258: loddnr diskret i nedre høyre hjørne. */}
            {w.ticketNum?<span style={{ position:"absolute", right:10, bottom:5, fontSize:11, color:"#888" }}>lodd #{w.ticketNum}</span>:null}
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── INFO-MODAL ───────────────────────────────────────────────────────────────
// Viser informasjon om spillet, utvikler og abonnement.
// Brukes via InfoButton (❓-ikon) som legges på kjøpssiden.
function InfoModal({ onClose }) {
  // Stopp scroll på bakgrunnen mens modalen er åpen
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div onClick={onClose} style={{
      position:"fixed", inset:0, zIndex:1000,
      background:"rgba(0,0,0,0.85)",
      display:"flex", alignItems:"flex-start", justifyContent:"center",
      padding:"20px 12px", overflowY:"auto",
      animation:"infoFadeIn 0.25s ease-out"
    }}>
      <style>{`
        @keyframes infoFadeIn { 0%{opacity:0} 100%{opacity:1} }
        @keyframes infoSlideUp { 0%{transform:translateY(30px);opacity:0} 100%{transform:translateY(0);opacity:1} }
      `}</style>
      <div onClick={(e)=>e.stopPropagation()} style={{
        background:"linear-gradient(180deg,#1a0e04 0%,#0d0806 100%)",
        border:"2px solid #C9A84C",
        borderRadius:12,
        maxWidth:520, width:"100%",
        boxShadow:"0 0 40px rgba(201,168,76,0.3), 0 10px 40px rgba(0,0,0,0.8)",
        animation:"infoSlideUp 0.35s cubic-bezier(0.22,1,0.36,1) both",
        position:"relative"
      }}>
        {/* Lukk-knapp */}
        <button onClick={onClose} aria-label="Lukk" style={{
          position:"absolute", top:8, right:10,
          background:"transparent", border:"none",
          color:"#C9A84C", fontSize:28, lineHeight:1,
          cursor:"pointer", padding:"4px 10px",
          fontFamily:"Arial,sans-serif"
        }}>×</button>

        <div style={{ padding:"22px 22px 18px" }}>
          {/* Tittel */}
          <h2 style={{
            margin:"0 0 4px",
            fontFamily:"'Montserrat','Playfair Display',Georgia,serif",
            fontStyle:"italic",
            color:"#C9A84C",
            fontSize:22,
            letterSpacing:"0.08em",
            textAlign:"center",
            textShadow:"0 0 12px rgba(201,168,76,0.5)"
          }}>✦ Om Vinloddet ✦</h2>
          <p style={{
            margin:"0 0 16px", textAlign:"center",
            color:"#888", fontSize:11, letterSpacing:"0.2em",
            textTransform:"uppercase"
          }}>Versjon {APP_VERSION}</p>

          {/* Beskrivelse */}
          <Section title="Hva er dette?">
            En digital vinlotteri-løsning for bedrifter, klubber, foreninger og institusjoner.
            Deltakere kjøper lodd via Vipps, og trekninger skjer live med flotte animasjoner
            som skaper spenning og engasjement.
          </Section>

          <Section title="Fire trekningsmetoder">
            <ul style={infoListStyle}>
              <li><strong style={infoStrongStyle}>🎡 Lykkehjul</strong> — klassisk spinnehjul med deltakernavn</li>
              <li><strong style={infoStrongStyle}>🎰 Slot machine</strong> — bokstaver ruller til vinnerens navn</li>
              <li><strong style={infoStrongStyle}>🎟 Loddtrekning</strong> — lodd faller i en magisk hatt, vinneren stiger opp</li>
              <li><strong style={infoStrongStyle}>🎱 Lotto-kule</strong> — kuler virvler i en glasskule, vinneren heises opp</li>
            </ul>
          </Section>

          <Section title="Funksjoner">
            <ul style={infoListStyle}>
              <li>Sanntids-visning for alle deltakere samtidig</li>
              <li>Automatisk e-post til vinnere via EmailJS</li>
              <li>Vipps-integrasjon for loddsalg</li>
              <li>Komplett historikk og oversikt over tidligere runder</li>
              <li>Ukentlig automatisk åpning/lukking av loddsalg</li>
              <li>Scrollende melding (ticker) til deltakere</li>
              <li>Tilpassbart utseende (logo, farger, titler)</li>
              <li>Konfetti, lydeffekter og premievisning</li>
            </ul>
          </Section>

          <Section title="Utvikler">
            <p style={infoTextStyle}>
              Utviklet av <strong style={infoStrongStyle}>Baard Westgaard</strong>.
            </p>
            <p style={infoTextStyle}>
              Løsningen driftes på moderne skyplattform (Firebase) og er optimalisert
              for mobil, nettbrett og PC.
            </p>
          </Section>

          <Section title="Abonnement">
            <p style={infoTextStyle}>
              Vinloddet tilbys som <strong style={infoStrongStyle}>årlig abonnement</strong> til
              bedrifter, klubber og institusjoner. Abonnementet inkluderer:
            </p>
            <ul style={infoListStyle}>
              <li>Tilgang til alle trekningsmetoder og funksjoner</li>
              <li>Egen subdomene / egen branding-tilpasning</li>
              <li>Teknisk support og løpende oppdateringer</li>
              <li>Automatisk sikkerhetskopiering av data</li>
            </ul>
            <p style={{ ...infoTextStyle, marginTop:10 }}>
              Kontakt utvikler for pris og oppsett.
            </p>
          </Section>

          <div style={{
            marginTop:16, paddingTop:12,
            borderTop:"1px solid rgba(201,168,76,0.25)",
            textAlign:"center",
            fontSize:11, color:"#666",
            letterSpacing:"0.1em"
          }}>
            © {new Date().getFullYear()} · Vinloddet · v{APP_VERSION}
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper for seksjoner i InfoModal
function Section({ title, children }) {
  return (
    <div style={{ marginBottom:14 }}>
      <h3 style={{
        margin:"0 0 6px",
        fontSize:13,
        color:"#C9A84C",
        letterSpacing:"0.18em",
        textTransform:"uppercase",
        fontFamily:"'Inter',system-ui,sans-serif",
        fontWeight:"600"
      }}>{title}</h3>
      {typeof children === 'string'
        ? <p style={infoTextStyle}>{children}</p>
        : children}
    </div>
  );
}

const infoTextStyle = {
  margin:"0 0 6px", color:"#d6cfbf",
  fontSize:13, lineHeight:1.55,
  fontFamily:"'Inter',system-ui,sans-serif"
};
const infoListStyle = {
  margin:"0 0 0 18px", padding:0, color:"#d6cfbf",
  fontSize:13, lineHeight:1.7,
  fontFamily:"'Inter',system-ui,sans-serif"
};
const infoStrongStyle = { color:"#F4D06F", fontWeight:"600" };

// ─── INFO-KNAPP ───────────────────────────────────────────────────────────────
// ❓-ikon som legges i hjørnet på kjøpssiden. Åpner InfoModal.
function InfoButton() {
  const [open, setOpen] = useState(false);
  const isPc = useIsPcWidth();
  // v91 Fix 7: på mobil = 44×44 transparent klikk-flate (Apple HIG-treff)
  // med 22×22 synlig "?"-badge sentrert inne. På PC = 26×26 som før.
  if (isPc) {
    return (
      <>
        <button onClick={()=>setOpen(true)} aria-label="Om spillet" style={{
          position:"fixed", top:12, left:12, zIndex:100,
          width:26, height:26, borderRadius:"50%",
          background:"rgba(20,12,0,0.6)", border:"1px solid #6a5028",
          color:"#8a7848", fontSize:13, fontWeight:"600",
          cursor:"pointer",
          display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow:"0 1px 6px rgba(0,0,0,0.4)",
          fontFamily:"Georgia,serif", lineHeight:1,
        }}>?</button>
        {open && <InfoModal onClose={()=>setOpen(false)}/>}
      </>
    );
  }
  // v122: mindre badge (22→18, font 11→9) og nærmere øvre venstre hjørne
  // (12→4px margin). Touch-area 44→32 — fortsatt tappbar selv om litt under
  // Apple HIG 44px. Respekterer notch via safe-area-inset.
  return (
    <>
      <button onClick={()=>setOpen(true)} aria-label="Om spillet" style={{
        position:"fixed",
        top:"calc(4px + env(safe-area-inset-top, 0px))",
        left:"calc(4px + env(safe-area-inset-left, 0px))",
        zIndex:100,
        width:32, height:32,
        background:"transparent", border:"none", padding:0,
        cursor:"pointer",
        display:"flex", alignItems:"center", justifyContent:"center",
      }}>
        <span aria-hidden="true" style={{
          width:18, height:18, borderRadius:9,
          background:"rgba(20,12,0,0.6)", border:"1px solid #6a5028",
          color:"#8a7848", fontSize:9, fontWeight:"600",
          display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow:"0 1px 6px rgba(0,0,0,0.4)",
          fontFamily:"Georgia,serif", lineHeight:1,
        }}>?</span>
      </button>
      {open && <InfoModal onClose={()=>setOpen(false)}/>}
    </>
  );
}

// ─── ELEGANT WINNER CARD — lykkehjul-stil, brukes for lodd og slot ────────────
// Matcher eksakt stilen fra hjul-vinner-blokken:
//   ✦ Gratulerer ✦
//   Lodd nr.
//   [stort gylden loddnummer]
//   [vinnernavn i gullramme]
//   [premienavn i kursiv gull]
//   [region i courier]
function ElegantWinnerCard({ winnerName, winnerTicketNum, prizeName, prizeCountry, showTicketNum = false, framedName = true, noAnim = false, softBackdrop = true }) {
  // v216: compact-grenen fjernet, framedName + noAnim introdusert.
  // v217: prizeCountry-rendringen fjernet (land kun i blikkfang nå). softBackdrop
  // (default true) gir en myk radial gradient bak hele kortet for lesbarhet over
  // grafikk — hjul-callsites sender false fordi hjul-wrapperen har sin egen
  // lineære gradient-backdrop som dekker bunnen.
  const numSize = 'clamp(38px,9.5vw,57px)';
  const gratFontSize = 'clamp(16px,3.8vw,20px)';
  const loddNrSize = 'clamp(13px,3.2vw,16px)';
  const navnSize = showTicketNum ? 'clamp(22px,5.6vw,30px)' : 'clamp(26px,6.5vw,36px)';
  const premieSize = showTicketNum ? 'clamp(16px,4.6vw,22px)' : 'clamp(18px,5vw,24px)';
  const duHarVunnetSize = 'clamp(12px,2.8vw,14px)';
  // v270: på PC (≥640px) står premienavnet alltid på ÉN linje: nowrap +
  // max-content-bredde (linjen kan overflyte kortets 460px-ramme symmetrisk,
  // flex alignItems:center sentrerer overflowen — kortet selv beholdes smalt så
  // backdrop-gradienten ikke strekkes), capped på 88vw. Er teksten bredere enn
  // 88vw krympes fonten trinnvis (ned til 13px) til den får plass — ekstremt
  // lange navn brytes/klippes aldri. Mobil (<640px) beholder dagens wrap.
  const pcOneLine = window.innerWidth >= 640;
  const prizeRef = useRef(null);
  const [prizeFsPx, setPrizeFsPx] = useState(null);
  useEffect(() => {
    if (!pcOneLine) { setPrizeFsPx(null); return; }
    const el = prizeRef.current;
    if (!el) return;
    const base = showTicketNum ? 22 : 24; // px — matcher clamp-max i premieSize
    let fs = base;
    el.style.fontSize = fs + 'px';
    while (fs > 13 && el.scrollWidth > el.clientWidth) {
      fs -= 1;
      el.style.fontSize = fs + 'px';
    }
    setPrizeFsPx(fs < base ? fs : null);
  }, [prizeName, showTicketNum, pcOneLine]);
  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: 0,
      width: '100%', maxWidth: 460,
      // v217: ekstra padding sammen med radial gradient så bakgrunnen fader mykt
      // ut i kantene UTENFOR teksten. Uten softBackdrop holdes original kompakte
      // padding (brukt av hjul-wrappere som har egen backdrop).
      padding: softBackdrop ? '14px 20px 18px' : '0 12px 4px',
      boxSizing: 'border-box',
      // v217: myk radial mørk vinrød gradient bak innholdet — fader til transparent
      // i kantene så det ikke ser ut som en boks, bare en lesbarhet-skygge.
      // v276: kraftig økt dekkevne for lesbarhet over travle bakgrunner.
      // v277: trukket dekkevnen ned et hakk for å slippe mer av bakgrunnen gjennom.
      // Gull-inset-rammen fjernet — kun ytre drop-shadow beholdt for å løfte kortet.
      // v278: mellomting mellom v276 og v277. v279: hjul-kortet bruker også
      // denne (softBackdrop=true overalt). v280: et hakk lettere igjen — 0.88→
      // 0.78 senter, 0.82→0.70 ved 50 %, 0.58→0.46 ved 80 %. Premienavn-text-
      // shadow gjør den tyngre løftet. Dimensjoner uendret.
      background: softBackdrop
        ? 'radial-gradient(ellipse 130% 110% at 50% 50%, rgba(15,8,8,0.78) 0%, rgba(15,8,8,0.70) 50%, rgba(18,10,10,0.46) 80%, rgba(22,10,10,0) 100%)'
        : 'transparent',
      boxShadow: softBackdrop
        ? '0 10px 30px rgba(0,0,0,0.65), 0 2px 8px rgba(0,0,0,0.5)'
        : 'none',
      animation: noAnim ? 'none' : 'elegantCardIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both',
    }}>
      <style>{`
        @keyframes elegantCardIn {
          0% { transform: translateY(24px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes elegantBigNumIn {
          0% { transform: scale(0.3); opacity: 0; }
          60% { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      <div style={{
        fontFamily: "'Montserrat', 'Playfair Display', Georgia, serif",
        fontStyle: 'italic', fontSize: gratFontSize,
        color: '#C9A84C', letterSpacing: '4px', textTransform: 'uppercase',
        textShadow: '0 0 10px rgba(0,0,0,0.95), 0 2px 6px rgba(0,0,0,0.9)'
      }}>✦ Gratulerer ✦</div>

      {showTicketNum && winnerTicketNum != null && winnerTicketNum !== '' && winnerTicketNum !== '?' && (
        <>
          <div style={{
            fontFamily: "'Montserrat', 'Playfair Display', Georgia, serif",
            fontStyle: 'italic', fontSize: loddNrSize,
            color: '#F5E6D3', letterSpacing: '3px', textTransform: 'uppercase',
            opacity: 0.85, marginTop: 12, textShadow: '0 2px 6px rgba(0,0,0,0.95)'
          }}>Lodd nr.</div>
          <div style={{
            fontFamily: "'Impact', 'Arial Black', sans-serif",
            fontSize: numSize, lineHeight: 0.9, color: '#F4D06F',
            textShadow: '0 0 8px rgba(244, 208, 111, 0.7), 0 4px 10px rgba(0, 0, 0, 1)',
            WebkitTextStroke: '2px rgba(0,0,0,0.85)',
            letterSpacing: '2px',
            animation: 'elegantBigNumIn 0.7s cubic-bezier(0.34, 1.8, 0.64, 1) both'
          }}>{winnerTicketNum}</div>
        </>
      )}

      <div style={{
        fontFamily: "'Montserrat', 'Playfair Display', Georgia, serif",
        fontWeight: 900, fontSize: navnSize,
        color: '#F5E6D3',
        textShadow: '0 0 4px rgba(0,0,0,1), 0 0 8px rgba(0,0,0,0.95), 0 2px 6px rgba(0,0,0,1)',
        // v216: ramme gir innrammet navn på alle metoder. display:block sikrer at
        // lange 2-linjede navn omfavnes som ÉN ramme (ikke splittes per linje).
        // v223: mindre padding (4px 16px → 2px 10px) — strammere ramme.
        padding: framedName ? '2px 10px' : '2px 12px',
        marginTop: 14,
        background: framedName ? 'rgba(26,15,15,0.6)' : 'transparent',
        border: framedName ? '1px solid #C9A84C' : 'none',
        borderRadius: framedName ? 2 : 0,
        display: 'block',
        textAlign: 'center'
      }}>{winnerName}</div>

      {/* "Du har vunnet en" — vises kun når ticket-nummer er skjult */}
      {!showTicketNum && prizeName && (
        <div style={{
          fontFamily: "'Montserrat', 'Playfair Display', Georgia, serif",
          fontStyle: 'italic', fontSize: duHarVunnetSize,
          color: '#F5E6D3', opacity: 0.75, letterSpacing: '2px',
          textTransform: 'uppercase',
          marginTop: 12, textShadow: '0 2px 6px rgba(0,0,0,0.9)'
        }}>Du har vunnet en</div>
      )}

      {prizeName && (
        <div ref={prizeRef} style={{
          fontFamily: "'Montserrat', 'Playfair Display', Georgia, serif",
          fontStyle: 'italic',
          fontSize: prizeFsPx != null ? `${prizeFsPx}px` : premieSize,
          color: '#F4D06F',
          textAlign: 'center',
          // v276: stablet shadow så premienavnet er lesbart selv når det
          // (på PC) flyter ut av kortets backdrop med width:max-content.
          textShadow: '0 0 8px rgba(0,0,0,1), 0 0 16px rgba(0,0,0,0.9), 0 2px 6px rgba(0,0,0,0.95)',
          marginTop: 10,
          lineHeight: 1.1,
          ...(pcOneLine
            ? { whiteSpace: 'nowrap', width: 'max-content', maxWidth: '88vw' }
            : { maxWidth: '92vw' }),
        }}>{prizeName}</div>
      )}
      {/* v217: land/opprinnelse fjernet fra gratulerer-blokken. Vises fortsatt i
          blikkfang "NESTE PREMIE" (DrawIntroBlikkfang) som descriptive build-up. */}
    </div>
  );
}


// (WinnerCard, LiveWheel og DrawLayout fjernet — ubrukte)


// v183: tidsbasert animasjons-fremdrift (andre forsøk, med STALE-GUARD).
// Server (v177a) skriver drawnPrizes + drawStartedAt + premieVarighetMs.
// Klienten regner ut hvilken premie som er aktiv fra forløpt tid. Lærdom fra
// v179-incident: STRENGE krav (alle felter må finnes, ingen premieVarighetMs-
// fallback) + stale-guard (avvis trekninger eldre enn varighet + 5min) +
// fremtids-guard (klokke-skew). Returnerer {ready:false} → klienten faller
// tilbake til den gamle klient-motoren (manuell/legacy-trekning).
function useDrawProgress(lotteryState) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  const drawnPrizes = lotteryState?.drawnPrizes;
  const drawStartedAt = lotteryState?.drawStartedAt;
  const premieVarighetMs = lotteryState?.premieVarighetMs;

  // STRENGE KRAV — alle tre må være satt (ingen 22000-fallback som i v179).
  if (!drawnPrizes?.length || !drawStartedAt || !premieVarighetMs) {
    return { ready: false, reason: "mangler felter" };
  }
  // v184: er trekningen ferdig, ikke re-animer ved F5 — la klienten vise done-skjermen.
  if (lotteryState?.status === "done") {
    return { ready: false, reason: "trekning ferdig" };
  }
  const startMs = drawStartedAt.toMillis ? drawStartedAt.toMillis() : 0;
  if (!startMs) return { ready: false, reason: "ugyldig drawStartedAt" };

  // v190: NY STI — per-premie kumulativ tidsplan. Aktiveres KUN hvis serveren
  // skrev startOffsetMs (v190+). Gir konsekvent pause uavhengig av metode fordi
  // hver premies anim-vindu = faktisk animasjons-varighet (animMs), ikke fast 7s.
  if (drawnPrizes[0].startOffsetMs !== undefined) {
    const elapsedV190 = now - startMs;
    if (elapsedV190 < -60000) return { ready: false, reason: "fremtidig drawStartedAt" };
    const effElapsed = Math.max(0, elapsedV190);
    const drawTotalMs = lotteryState?.drawTotalMs ||
      drawnPrizes.length * (premieVarighetMs || 22000);
    // STALE-GUARD på drawTotalMs (v179-incident).
    if (effElapsed > drawTotalMs + 5 * 60 * 1000) {
      return { ready: false, reason: "stale (drawTotalMs)" };
    }
    // Ferdig-først (v187): låst done-tilstand, ingen phantom-fase etter siste premie.
    if (effElapsed >= drawTotalMs) {
      const sisteIdx = drawnPrizes.length - 1;
      const sisteDrawn = drawnPrizes[sisteIdx];
      return {
        ready: true, ferdig: true, aktivIdx: sisteIdx, fase: "ferdig", liveCount: null,
        winnerName: sisteDrawn?.winnerName,
        winnerTicketNum: sisteDrawn?.winnerTicketNum,
        method: sisteDrawn?.method || "hjul",
      };
    }
    // Aktiv premie = siste med startOffsetMs <= effElapsed.
    let aktivIdx = 0;
    for (let i = drawnPrizes.length - 1; i >= 0; i--) {
      if (drawnPrizes[i].startOffsetMs <= effElapsed) { aktivIdx = i; break; }
    }
    const currentDrawn = drawnPrizes[aktivIdx];
    const localT = effElapsed - currentDrawn.startOffsetMs;
    const animMs = currentDrawn.animMs || 7000;
    // v247: lik lengde på ALLE premie-presentasjoner (PRESENT_MS = 6s) og ALLE
    // gratulasjoner (WINNER_MS = 6s, server-styrt via slot). Server-slot per premie =
    // PRESENT_MS + animMs + WINNER_MS, og første premie får i tillegg INTRO_MS
    // foran når det er ≥2 premier. Klientens countdown må derfor matche server.
    // v294: INTRO_MS leses fra trekningsdokumentet (`introMs`, del av serverens
    // tidsskript — nå 6500) med fallback 8000 for trekninger startet på eldre
    // functions-versjon. Ingen speilet konstant lenger = ingen utakt-risiko.
    // PRESENT_MS er fortsatt speilet mot functions (6000) — MÅ endres i takt.
    const total = drawnPrizes.length;
    const INTRO_MS = lotteryState?.introMs ?? 8000;
    const PRESENT_MS = 6000;
    const COUNTDOWN_MS = (aktivIdx === 0 && total >= 2)
      ? (INTRO_MS + PRESENT_MS)
      : PRESENT_MS;
    let fase;
    let liveCount = null;
    if (localT < COUNTDOWN_MS) {
      fase = "countdown";
      liveCount = Math.ceil((COUNTDOWN_MS - localT) / 1000);
    } else if (localT < COUNTDOWN_MS + animMs) {
      fase = "animasjon";
    } else {
      fase = "vinner";
    }
    // Join mid-anim (v183): tilskuer som kommer inn 2+ sek inn i animasjonen hopper til vinner.
    const joinedMidAnim = (fase === "animasjon") && (localT > COUNTDOWN_MS + 2000);
    const effektivFase = joinedMidAnim ? "vinner" : fase;
    return {
      ready: true, ferdig: false, aktivIdx, fase: effektivFase, liveCount,
      localT, // v213: ms innenfor aktiv premie — brukes av blikkfangOverlay for to-fase-valg på premie 0 (intro vs next).
      winnerName: (effektivFase === "animasjon" || effektivFase === "vinner")
        ? currentDrawn?.winnerName : null,
      winnerTicketNum: currentDrawn?.winnerTicketNum,
      method: currentDrawn?.method || "hjul",
    };
  }

  const totalPremier = drawnPrizes.length;
  const totalVarighet = totalPremier * premieVarighetMs;
  const elapsed = now - startMs;

  // STALE-GUARD: trekning eldre enn (varighet + 5 min) → ignorer (v179-incident).
  const STALE_BUFFER_MS = 5 * 60 * 1000;
  if (elapsed > totalVarighet + STALE_BUFFER_MS) {
    return { ready: false, reason: "stale drawnPrizes" };
  }
  // FREMTIDS-GUARD: drawStartedAt > 1 min frem i tid → klokke-skew.
  if (elapsed < -60000) {
    return { ready: false, reason: "fremtidig drawStartedAt" };
  }

  const effElapsed = Math.max(0, elapsed);
  const aktivIdx = Math.floor(effElapsed / premieVarighetMs);

  // v187: FERDIG FØRST — beregn IKKE fase/liveCount etter siste premie. Tidligere
  // fortsatte iPremie (elapsed % premieVarighetMs) å sykle, traff periodisk
  // "countdown"-fasen og satte liveCount → LiveDrawView sin beep-effekt fyrte
  // (phantom nedtellings-pling uten synlig nedtelling). Nå låses ferdig-tilstanden.
  if (aktivIdx >= totalPremier) {
    const sisteIdx = totalPremier - 1;
    const sisteDrawn = drawnPrizes[sisteIdx];
    return {
      ready: true,
      ferdig: true,
      aktivIdx: sisteIdx,
      fase: "ferdig",
      liveCount: null,
      winnerName: sisteDrawn?.winnerName,
      winnerTicketNum: sisteDrawn?.winnerTicketNum,
      method: sisteDrawn?.method || "hjul",
    };
  }

  const iPremie = effElapsed % premieVarighetMs;
  const COUNTDOWN_MS = 5000;
  const ANIM_MS = 7000;
  let fase;
  let liveCount = null;
  if (iPremie < COUNTDOWN_MS) {
    fase = "countdown";
    liveCount = Math.ceil((COUNTDOWN_MS - iPremie) / 1000);
  } else if (iPremie < COUNTDOWN_MS + ANIM_MS) {
    fase = "animasjon";
  } else {
    fase = "vinner";
  }

  const currentDrawn = drawnPrizes[aktivIdx];
  // Join mid-premie: kommer tilskuer inn 2+ sek inn i animasjon-fasen, hopp til vinner.
  const joinedMidAnim = (fase === "animasjon") && (iPremie > COUNTDOWN_MS + 2000);
  const effektivFase = joinedMidAnim ? "vinner" : fase;

  return {
    ready: true,
    ferdig: false,
    aktivIdx,
    fase: effektivFase,
    liveCount,
    winnerName: (effektivFase === "animasjon" || effektivFase === "vinner")
      ? currentDrawn?.winnerName : null,
    winnerTicketNum: currentDrawn?.winnerTicketNum,
    method: currentDrawn?.method || "hjul",
  };
}

// ─── BLIKKFANG FØR TREKNING (v209) ───────────────────────────────────────────
// Fullskjerm-overlay med pulserende tekst, roterende lys-stråler og konfetti.
// Gjenbruker eksisterende Confetti (linje 700) + playWinFanfare — ingen ny avhengighet.
// Varianter:
//   intro    = egen intro-side FØR første premie (LiveDrawView, kun når total >= 2):
//     "Spenningen stiger" + "X flasker venter på nye eiere" + klubbnavn · uke +
//     "X deltakere". INGEN premie-info, INGEN bilde. 6s. konfetti+fanfare.
//   imminent = trekningstidspunkt nådd, venter på server (ParticipantView): ren intro.
//   next     = HVER premie (også premie 0 etter intro): "NESTE PREMIE" + bilde +
//     navn + land. 5s (premie 0) / 6s (øvrige). show-gate fader ut siste sek.
// v211: alle nedtellingstall fjernet (blikkfanget eier spenningen, ikke tallene).
// v212: finale-konsept fjernet; lengre blikkfang via useDrawProgress; ingen fade-IN.
// v213: "starting" splittet i to: ny "intro"-variant (egen 6s intro-side) + "next"
//   for selve premie-presentasjonen. Variant-swap intro→next på samme komponent-
//   instans bevarer bakgrunnen, så ingen flash. "Premie X av Y" fjernet fra "next".
// v290: sømløs holdeskjerm→intro-overgang. Holdeskjermen (imminent, i
// ParticipantView) og introen (i LiveDrawView) er TO ULIKE React-trær — App
// bytter view ved status idle/pending → drawing, så ingen React-key kan bevare
// DOM-en over byttet. I stedet overleveres koreografi-KLOKKA: imminent noterer
// når den monterte, og introen fortsetter linje-sekvensen derfra via negative
// animation-delays (allerede viste linjer står ferdig fra første frame, resten
// fortsetter på skjema). Verdien konsumeres av første intro per trekning og
// brukes kun når introen følger holdeskjermen direkte (< 5s gap) — manuell
// start uten holdeskjerm (og sen-innkommere) starter forfra som før.
let preDrawHold = null; // {startMs, endMs|null} — satt av imminent, konsumert av intro

function DrawIntroBlikkfang({ lotteryName, weekLabel, prizeCount, participantCount, ticketCount = 0, prizeName, prizeImage, prizeCountry, prizeIndex, totalPrizes, prizeTitle = "NESTE PREMIE", variant = "intro", show = true, introKey = 0, overdue = false, introRemainingMs = 8000 }) {
  // v288: sikkerhetsnett mot kuttet intro. Intro→next-byttet skjer på veggklokke
  // (localT >= 8000 fra drawStartedAt), mens linje-koreografien starter ved
  // DOM-mount (snapshot-levering). Kommer klienten sent inn i intro-vinduet
  // (ekstrem leveringsforsinkelse / refresh midt i introen), komprimeres
  // linje-tempoet til gjenværende vindu slik at ALLE linjene rekker fram —
  // fortsatt linje for linje, og selve byttet er fortsatt globalt synkront.
  // Normaltilfellet (fullt vindu, jf. INTRO_LEAD_MS server-side) gir scale 1
  // = identisk timing som før. Skalaen fryses per introKey (per trekning).
  const introPaceRef = useRef({ key: null, scale: 1, shift: 0 });
  if (variant === "intro" && introPaceRef.current.key !== introKey) {
    const rem = Math.max(0, introRemainingMs);
    // v290: kontinuitet fra holdeskjermen — hvor langt koreografien allerede
    // har spilt der (sekunder). Kun hvis introen følger holdeskjermen direkte.
    let shift = 0;
    if (preDrawHold && (preDrawHold.endMs == null || Date.now() - preDrawHold.endMs < 5000)) {
      shift = Math.max(0, (Date.now() - preDrawHold.startMs) / 1000);
    }
    preDrawHold = null; // konsumert — senere manuell start/ny trekning starter forfra
    // v295: koreografien strammet til 5.0s (siste delay 4.0s + 1.0s fade) i takt
    // med server-vinduet 5500; 0.5s margin før byttet. Skala-nevneren (5000) MÅ
    // matche koreografi-lengden.
    introPaceRef.current = { key: introKey, scale: Math.min(1, Math.max(0.25, (rem - 500) / 5000)), shift };
  }
  const introScale = introPaceRef.current.scale;
  const introShift = introPaceRef.current.shift;
  const INTRO_DELAYS = [0.8, 1.7, 2.4, 3.2, 4.0];
  // Negativ delay = CSS hopper inn i animasjonen: ferdig viste linjer står fra
  // første frame, delvis viste fortsetter midt i faden, resten kommer på skjema.
  const introAnim = (i) => ({ animation: `introFadeIn ${(1.0 * introScale).toFixed(2)}s ease-out ${(INTRO_DELAYS[i] * introScale - introShift).toFixed(2)}s forwards, goldShimmer 3s linear infinite` });
  // v291: ÉN kilde til sannhet for lyd og bilde — tonen utløses av selve
  // linje-hendelsen (animationstart på introFadeIn), ikke av en parallell
  // timer-kjede på AudioContext-klokka. Dermed følger tonene automatisk både
  // v288-komprimeringen (scale) og v290-skiftet. elapsedTime-filteret gjør at
  // linjer som allerede STÅR (negativ delay etter imminent→intro) er stille —
  // de fikk tonen sin da de tonet inn på holdeskjermen (v292), og skal ikke
  // utløse en forsinket klump av toner ved overgangen. Brukes av BÅDE intro-
  // og imminent-variantens linjer; dobbel-tone-vakta ligger i playIntroChord.
  const onIntroLineStart = (i) => (e) => {
    if (e.animationName !== "introFadeIn") return;
    if (e.elapsedTime > 0.25) return; // negativ delay = linja var allerede synlig
    playIntroChord(i);
  };
  // v290: holdeskjermen melder fra når den står (og når den forsvant), så
  // introen kan ta over koreografi-klokka. Overdue-grenen viser ANDRE linjer
  // ("Venter på deltakere…") — da skal introen starte forfra, ikke fortsette.
  useEffect(() => {
    if (variant !== "imminent" || overdue) return;
    preDrawHold = { startMs: Date.now(), endMs: null };
    return () => { if (preDrawHold) preDrawHold.endMs = Date.now(); };
  }, [variant, overdue]);
  const cfg = ({
    intro:    { rays:"9s",  confetti:true,  pulse:"1.4s", fanfare:true },
    imminent: { rays:"9s",  confetti:true,  pulse:"1.4s", fanfare:true },
    next:     { rays:"14s", confetti:false, pulse:"1.4s", fanfare:false },
  })[variant] || { rays:"9s", confetti:true, pulse:"1.4s", fanfare:true };
  // v212 (Endring A): ingen opacity-0 fade-IN lenger — overlayet er ugjennomsiktig
  // fra første frame, så spillet aldri glimter gjennom før blikkfanget. show-propen
  // styrer fortsatt fade-OUT (1→0) for NESTE-blikkfanget det siste sekundet.
  // v291: intro-tonene utløses ikke lenger her som en parallell timer-kjede —
  // de trigges av selve linje-hendelsen (onAnimationStart på hver intro-linje,
  // se onIntroLineStart). Effekten har kun cleanup-ansvar for intro: stopp
  // ev. klingende akkorder rent ved variant-swap (intro→next ved 8s) / unmount.
  useEffect(() => {
    if (variant === "intro") {
      return () => { stopIntroChords(); };
    }
    if (variant === "next") {
      // v245: trompetfanfare under HELE presentasjonen. prizeIndex i deps → ny
      // fanfare per premie; cleanup fader rent ut når spinning starter / unmount,
      // så fanfaren varer nøyaktig så lenge som premie-presentasjonen.
      const fanf = playPrizeFanfare();
      return () => { try { fanf.stop(); } catch (e) {} };
    }
    // v250/v292: imminent (svart holding-skjerm) spiller INGEN fanfare — men
    // linje-tonene utløses nå også der, av linjenes egne animationstart-
    // hendelser (én tone per linje idet den vises første gang, jf. v292).
    if (variant !== "imminent" && cfg.fanfare) { try { playWinFanfare(); } catch(e){} }
  }, [variant, introKey, prizeIndex]); // eslint-disable-line react-hooks/exhaustive-deps
  // v234: phase-state fjernet. Den drev tidligere stråle-intensitet og konfetti-timing
  // for intro, men strålene rendres ikke lenger for intro (v233 svart bakgrunn) og
  // konfetti er nå fjernet helt for intro (kun gnistrende stjerner igjen). Dermed var
  // phase-state død kode for intro. Andre varianter er uendret.
  const rayOpacity = 0.8;
  const showConfetti = variant !== "intro" && variant !== "imminent" && cfg.confetti;
  // v233: engangs-stjerner — hver stjerne popper opp og forsvinner ÉN gang
  // (starTwinkle kjører 1 forwards, ikke infinite), med delays spredt utover hele
  // 8s-introen (0-7s) så nye lyspunkter dukker opp på FERSKE, tilfeldige posisjoner
  // hele tiden uten at samme punkt blinker om igjen. 70 stjerner gir tett gnistring
  // gjennom hele introen. Regenereres per introKey (ny trekning) = nye posisjoner
  // hver gang.
  const starParticles = useMemo(() => Array.from({ length: 110 }, () => ({
    left: Math.random() * 100,
    top: Math.random() * 100,
    delay: Math.random() * 7,
    duration: 1.2 + Math.random() * 1.5,
    size: 1 + Math.random() * 2, // v251: mindre stjerner (1-3px) for ekte stjernehimmel
    silver: Math.random() < 0.4, // v235: ~40% sølv-stjerner, 60% gull
    // eslint-disable-next-line react-hooks/exhaustive-deps -- introKey er BEVISST eneste dep: tvinger re-randomisering av stjerner per premie
  })), [introKey]);
  const rays = "conic-gradient(from 0deg," + Array.from({length:18},(_,i)=>(i%2?"transparent":"rgba(201,168,76,0.13)")+" "+(i*20)+"deg "+((i+1)*20)+"deg").join(",") + ")";
  const isPrizeVariant = variant === "next";
  const imgSize = "clamp(150px,46vw,220px)";
  // v232: linje 3 er nå tre korte under-linjer (deltakere / premier / "Hvem vinner?")
  // med inline entall/flertall — egne consts (deltakerOrd/premieOrd) fjernet.
  return (
    <div style={{
      position:"fixed", inset:0, zIndex:300, padding:24, boxSizing:"border-box",
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center",
      // v233: intro-varianten har ren svart bakgrunn (stjernene gnistrer mot svart).
      // next-/imminent-variantene beholder den varme gull-gradienten uendret.
      background: (variant === "intro" || variant === "imminent") ? "#000000" : "radial-gradient(ellipse at center,#2a1500 0%,#1a0a0a 55%,#070404 100%)",
      overflow:"hidden",
      opacity: show ? 1 : 0, transition:"opacity 400ms ease", pointerEvents: show ? "auto" : "none",
    }}>
      <style>{`@keyframes blikkPulse{0%{transform:scale(1);opacity:0.92}50%{transform:scale(1.06);opacity:1}100%{transform:scale(1);opacity:0.92}} @keyframes blikkRays{from{transform:translate(-50%,-50%) rotate(0)}to{transform:translate(-50%,-50%) rotate(360deg)}} @keyframes introFadeIn{0%{opacity:0}100%{opacity:1}} @keyframes goldShimmer{0%{background-position:0% center}100%{background-position:200% center}} @keyframes starTwinkle{0%{opacity:0;transform:scale(0)}40%{opacity:1;transform:scale(1)}60%{opacity:1;transform:scale(1)}100%{opacity:0;transform:scale(0)}} .introStar{position:absolute;border-radius:50%;background:radial-gradient(circle,#FFFFFF 0%,#FFF4C2 40%,#F4D06F 70%,rgba(244,208,111,0) 100%);box-shadow:0 0 8px rgba(255,244,194,0.9),0 0 12px rgba(244,208,111,0.6);animation:starTwinkle ease-in-out 1 forwards} .introStarSilver{background:radial-gradient(circle,#FFFFFF 0%,#E8E8F0 40%,#C0C0D0 70%,rgba(192,192,208,0) 100%);box-shadow:0 0 8px rgba(255,255,255,0.9),0 0 12px rgba(200,200,220,0.6)} .introGoldText{background:linear-gradient(120deg,#B8860B 0%,#F4D06F 20%,#FFF4C2 35%,#FFD700 50%,#FFF4C2 65%,#F4D06F 80%,#B8860B 100%);background-size:200% auto;-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;filter:drop-shadow(0 2px 8px rgba(0,0,0,0.5))} .introEl,.introEl2,.introEl3,.introEl4,.introEl5{font-size:clamp(24px,5.5vw,52px);font-weight:700;line-height:1.15} .introEl{opacity:0;animation:introFadeIn 1.0s ease-out 0.8s forwards, goldShimmer 3s linear infinite} .introEl2{opacity:0;animation:introFadeIn 1.0s ease-out 1.7s forwards, goldShimmer 3s linear infinite} .introEl3{opacity:0;animation:introFadeIn 1.0s ease-out 2.4s forwards, goldShimmer 3s linear infinite} .introEl4{opacity:0;animation:introFadeIn 1.0s ease-out 3.2s forwards, goldShimmer 3s linear infinite} .introEl5{opacity:0;animation:introFadeIn 1.0s ease-out 4.0s forwards, goldShimmer 3s linear infinite} .introStarLoop{position:absolute;border-radius:50%;background:radial-gradient(circle,#FFFFFF 0%,#FFF4C2 40%,#F4D06F 70%,rgba(244,208,111,0) 100%);box-shadow:0 0 8px rgba(255,244,194,0.9),0 0 12px rgba(244,208,111,0.6);animation:starTwinkle ease-in-out infinite} .introStarLoopSilver{background:radial-gradient(circle,#FFFFFF 0%,#E8E8F0 40%,#C0C0D0 70%,rgba(192,192,208,0) 100%);box-shadow:0 0 8px rgba(255,255,255,0.9),0 0 12px rgba(200,200,220,0.6)} .introHold{font-size:clamp(24px,5.5vw,52px);font-weight:700;line-height:1.15;opacity:0;animation:introFadeIn 0.9s ease-out 0.2s forwards, goldShimmer 3s linear infinite} .introHoldSeq2,.introHoldSeq3,.introHoldSeq4,.introHoldSeq5{font-size:clamp(24px,5.5vw,52px);font-weight:700;line-height:1.15;opacity:0} .introHoldSeq2{animation:introFadeIn 1.0s ease-out 1.7s forwards, goldShimmer 3s linear infinite} .introHoldSeq3{animation:introFadeIn 1.0s ease-out 2.4s forwards, goldShimmer 3s linear infinite} .introHoldSeq4{animation:introFadeIn 1.0s ease-out 3.2s forwards, goldShimmer 3s linear infinite} .introHoldSeq5{animation:introFadeIn 1.0s ease-out 4.0s forwards, goldShimmer 3s linear infinite}`}</style>
      {variant !== "intro" && variant !== "imminent" && <div style={{ position:"absolute", top:"50%", left:"50%", width:"200vmax", height:"200vmax", background:rays, animation:`blikkRays ${cfg.rays} linear infinite`, pointerEvents:"none", opacity:rayOpacity, transition:"opacity 800ms ease" }}/>}
      {showConfetti && <Confetti active={true}/>}
      {isPrizeVariant ? (
        <div style={{ position:"relative", zIndex:2, width:"min(900px,95vw)", textAlign:"center" }}>
          {/* v214: textIndent kompenserer trailing letterSpacing 0.14em så
              "NESTE/FØRSTE/SISTE PREMIE" blir visuelt sentrert (ikke shiftet venstre). */}
          <div style={{ fontSize:"clamp(22px,6vw,38px)", color:"#C9A84C", letterSpacing:"0.14em", textIndent:"0.14em", fontWeight:800, marginBottom:18 }}>{prizeTitle}</div>
          {prizeImage
            ? <img src={prizeImage} alt={prizeName||""} style={{ width:imgSize, height:imgSize, objectFit:"contain", filter:"drop-shadow(0 6px 28px rgba(201,168,76,0.45))" }}/>
            : <div style={{ fontSize:"clamp(80px,26vw,110px)" }}>🍷</div>}
          <div style={{ marginTop:18, fontSize:"clamp(28px,7.5vw,56px)", color:"#fff", fontWeight:900, lineHeight:1.1, textShadow:"0 2px 22px rgba(201,168,76,0.5)", animation:`blikkPulse ${cfg.pulse} ease-in-out infinite` }}>{prizeName||""}</div>
          {prizeCountry && <div style={{ marginTop:10, fontSize:"clamp(15px,4vw,22px)", color:"#e8d9a8" }}>🌍 {prizeCountry}</div>}
        </div>
      ) : variant === "intro" ? (
        // v234: fem tekst-elementer på FASTE posisjoner (topp→bunn):
        //   "Spenningen stiger" (20%) → deltakere (42%) → lodd (52%) → premier (62%)
        //   → "Trekningen starter nå…" (80%).
        // Alle fader inn sekvensielt (introEl..introEl5: 0.8/1.7/2.4/3.2/4.0s, hver 1.0s)
        // og BLIR STÅENDE. Lett overlapp (fade 1.0s, data-linjer ~0.75s fra hverandre) gir
        // myk crossfade-følelse. ALLE har LIK fontstørrelse (clamp(24px,5.5vw,52px) via
        // CSS); de to setningene får fontWeight 800 for hierarki UTEN å endre størrelsen,
        // data-linjene fontWeight 700. Gull-shimmer på alle. key={introKey} remounter
        // DOM ved ny trekning. v233: svart bakgrunn + gnistrende engangs-stjerner (zIndex
        // 1, bak teksten). v234: konfetti fjernet — kun stjerner.
        <div key={introKey} style={{ position:"absolute", inset:0, zIndex:2 }}>
          <div className="introStarLayer" style={{ position:"absolute", inset:0, zIndex:1, pointerEvents:"none", overflow:"hidden" }}>
            {starParticles.map((s, i) => (
              <span key={i} className={s.silver ? "introStar introStarSilver" : "introStar"} style={{ left:`${s.left}%`, top:`${s.top}%`, animationDelay:`${s.delay}s`, animationDuration:`${s.duration}s`, width:s.size, height:s.size }}/>
            ))}
          </div>
          {/* v241: vuggingen (introSway) fjernet — teksten fader inn og blir
              stående HELT stille. Ytre div = posisjon + vertikal sentrering,
              indre div = tekst + introEl-klassen (kun introFadeIn + goldShimmer). */}
          <div style={{ position:"absolute", top:"20%", left:0, right:0, transform:"translateY(-50%)", zIndex:2 }}>
            <div className="introEl introGoldText" onAnimationStart={onIntroLineStart(0)} style={{ textAlign:"center", padding:"0 16px", boxSizing:"border-box", fontWeight:800, ...introAnim(0) }}>Spenningen stiger</div>
          </div>
          <div style={{ position:"absolute", top:"42%", left:0, right:0, transform:"translateY(-50%)", zIndex:2 }}>
            <div className="introEl2 introGoldText" onAnimationStart={onIntroLineStart(1)} style={{ textAlign:"center", padding:"0 16px", boxSizing:"border-box", ...introAnim(1) }}>{participantCount} {participantCount === 1 ? "deltaker" : "deltakere"}</div>
          </div>
          <div style={{ position:"absolute", top:"50%", left:0, right:0, transform:"translateY(-50%)", zIndex:2 }}>
            <div className="introEl3 introGoldText" onAnimationStart={onIntroLineStart(2)} style={{ textAlign:"center", padding:"0 16px", boxSizing:"border-box", ...introAnim(2) }}>{ticketCount} lodd</div>
          </div>
          <div style={{ position:"absolute", top:"58%", left:0, right:0, transform:"translateY(-50%)", zIndex:2 }}>
            <div className="introEl4 introGoldText" onAnimationStart={onIntroLineStart(3)} style={{ textAlign:"center", padding:"0 16px", boxSizing:"border-box", ...introAnim(3) }}>{totalPrizes} {totalPrizes === 1 ? "premie" : "premier"}</div>
          </div>
          <div style={{ position:"absolute", top:"80%", left:0, right:0, transform:"translateY(-50%)", zIndex:2 }}>
            <div className="introEl5 introGoldText" onAnimationStart={onIntroLineStart(4)} style={{ textAlign:"center", padding:"0 16px", boxSizing:"border-box", fontWeight:800, ...introAnim(4) }}>Trekningen starter nå…</div>
          </div>
        </div>
      ) : (
        // v250: imminent = sustained svart HOLDING-skjerm (samme visuelle språk som
        // intro, men for en ventetid av ukjent lengde 0-60s). Loopende stjerner
        // (introStarLoop, infinite) i stedet for engangs-stjerner, samtidig sustained
        // tekst (introHold = fader inn én gang og blir stående, IKKE den sekvensielle
        // 8s-koreografien), og "starter snart…" (ikke "nå"). v292: linje-toner
        // spilles også her (se kommentar ved linjene); fanfare-løs (se useEffect).
        // overdue (passert > 2 min, typisk 0 deltakere) → rolig "Venter på deltakere…".
        <div key={introKey} style={{ position:"absolute", inset:0, zIndex:2 }}>
          <div className="introStarLayer" style={{ position:"absolute", inset:0, zIndex:1, pointerEvents:"none", overflow:"hidden" }}>
            {starParticles.map((s, i) => (
              <span key={i} className={s.silver ? "introStarLoop introStarLoopSilver" : "introStarLoop"} style={{ left:`${s.left}%`, top:`${s.top}%`, animationDelay:`${s.delay}s`, animationDuration:`${s.duration}s`, width:s.size, height:s.size }}/>
            ))}
          </div>
          {overdue ? (
            <>
              <div style={{ position:"absolute", top:"44%", left:0, right:0, transform:"translateY(-50%)", zIndex:2 }}>
                <div className="introHold introGoldText" style={{ textAlign:"center", padding:"0 16px", boxSizing:"border-box", fontWeight:800 }}>Venter på deltakere…</div>
              </div>
              {(lotteryName || weekLabel) && (
                <div style={{ position:"absolute", top:"58%", left:0, right:0, textAlign:"center", zIndex:2, padding:"0 16px" }}>
                  <div style={{ fontSize:"clamp(15px,4vw,22px)", color:"#e8d9a8", fontWeight:600 }}>{[lotteryName, weekLabel].filter(Boolean).join(" · ")}</div>
                </div>
              )}
            </>
          ) : (
            // v287: linjene fases inn SEKVENSIELT (samme tider som intro-varianten:
            // 1.7/2.4/3.2/4.0s, strammet v295) i stedet for alle samtidig ved 0.2s. Ved autostart
            // (drawTime nådd → status flipper til drawing etter 0-10s) sto hele
            // siden ellers ferdig synlig i et blaff FØR den ekte introen spilte
            // linje-for-linje. Nå er ingen linje synlig før sin tur — korte vente-
            // tider viser bare stjerner + «Spenningen stiger». Linjene BLIR stående
            // (forwards) for ekte lange ventetider. Overdue-grenen er uendret.
            // v292: hver linje får sin tone idet den faktisk vises FØRSTE gang —
            // også her på holdeskjermen (samme onIntroLineStart som intro-varianten).
            // Etter v290 fortsetter introen sømløst der holdeskjermen slapp, så
            // linjer som tonet inn her får ALDRI ny animationstart i introen —
            // uten tone her ville de vært stumme hele veien (v291-symptomet for
            // «Spenningen stiger»). Dobbel tone ved overgangen hindres av vakta i
            // playIntroChord. Overdue-grenen («Venter på deltakere…») er lydløs.
            <>
              <div style={{ position:"absolute", top:"20%", left:0, right:0, transform:"translateY(-50%)", zIndex:2 }}>
                <div className="introHold introGoldText" onAnimationStart={onIntroLineStart(0)} style={{ textAlign:"center", padding:"0 16px", boxSizing:"border-box", fontWeight:800 }}>Spenningen stiger</div>
              </div>
              <div style={{ position:"absolute", top:"42%", left:0, right:0, transform:"translateY(-50%)", zIndex:2 }}>
                <div className="introHoldSeq2 introGoldText" onAnimationStart={onIntroLineStart(1)} style={{ textAlign:"center", padding:"0 16px", boxSizing:"border-box" }}>{participantCount} {participantCount === 1 ? "deltaker" : "deltakere"}</div>
              </div>
              <div style={{ position:"absolute", top:"50%", left:0, right:0, transform:"translateY(-50%)", zIndex:2 }}>
                <div className="introHoldSeq3 introGoldText" onAnimationStart={onIntroLineStart(2)} style={{ textAlign:"center", padding:"0 16px", boxSizing:"border-box" }}>{ticketCount} lodd</div>
              </div>
              <div style={{ position:"absolute", top:"58%", left:0, right:0, transform:"translateY(-50%)", zIndex:2 }}>
                <div className="introHoldSeq4 introGoldText" onAnimationStart={onIntroLineStart(3)} style={{ textAlign:"center", padding:"0 16px", boxSizing:"border-box" }}>{totalPrizes} {totalPrizes === 1 ? "premie" : "premier"}</div>
              </div>
              <div style={{ position:"absolute", top:"80%", left:0, right:0, transform:"translateY(-50%)", zIndex:2 }}>
                <div className="introHoldSeq5 introGoldText" onAnimationStart={onIntroLineStart(4)} style={{ textAlign:"center", padding:"0 16px", boxSizing:"border-box", fontWeight:800 }}>Trekningen starter snart…</div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── LIVE TREKNING (deltaker-visning) ────────────────────────────────────────
// v306: paginert liste UTEN scrollbar — navigering KUN via ekte klikkbare piler.
// (Erstatter v305s ScrollHintBox, der ▲/▼ var pointerEvents:none-hint som ikke
// gjorde noe ved klikk — rotårsaken til «døde piler».) Viser et vindu på perPage
// elementer fra en intern offset; ▲ blar opp én side, ▼ ned én side. ▲ skjules
// når offset=0, ▼ når man ser de siste. Pilene ligger som overlay med myk fade
// (samme gull-uttrykk som før), men er nå <button> med onClick. renderItem får
// (item, globalIndex) så f.eks. Loddkjøpere-nummereringen forblir korrekt.
function PagedList({ items, perPage, gridStyle, renderItem }) {
  const [offset, setOffset] = useState(0);
  const maxOffset = Math.max(0, items.length - perPage);
  const off = Math.min(offset, maxOffset);
  const canUp = off > 0;
  const canDown = off < maxOffset;
  const vis = items.slice(off, off + perPage);
  const arrowStyle = (pos) => ({
    position:"absolute", left:0, right:0, [pos]:0, height:20, padding:0,
    display:"flex", alignItems:"center", justifyContent:"center",
    border:"none", cursor:"pointer", color:"#C9A84C", fontSize:11, zIndex:3,
    background: pos==="top"
      ? "linear-gradient(to bottom, rgba(10,10,10,0.97), rgba(10,10,10,0))"
      : "linear-gradient(to top, rgba(10,10,10,0.97), rgba(10,10,10,0))",
  });
  return (
    <div style={{ position:"relative", flex:1, minHeight:0, overflow:"hidden" }}>
      <div style={gridStyle}>
        {vis.map((item, idx) => renderItem(item, off + idx))}
      </div>
      {canUp && <button type="button" aria-label="Vis forrige" style={arrowStyle("top")}
        onClick={()=>setOffset(Math.max(0, off - perPage))}>▲</button>}
      {canDown && <button type="button" aria-label="Vis flere" style={arrowStyle("bottom")}
        onClick={()=>setOffset(Math.min(maxOffset, off + perPage))}>▼</button>}
    </div>
  );
}

// v270: «Dine lodd» i sidefeltene under trekning (KUN PC) — erstatter v269s
// «Lykke til»-tekst. Viser spillerens egne loddnumre som brikker, midtstilt i
// det sorte sidefeltet mellom skjermkant og maskin/hjul. `sideWidth` (px) settes
// av kalleren — (skjermbredde − maskinbredde)/2, samme geometri som v269. Rører
// ikke maskin-/hjul-geometri eller fysikk. Identitet = e-posten spilleren tastet
// på kjøpssiden (localStorage "vl_email") — samme uverifiserte premiss som
// «Dine lodd»-boksene ellers i appen.
// Brikke-tilstander: normal (gull kant), vunnet tidligere i kvelden (dempet grå,
// fra wonNums), VINNER NÅ (fylt gull + pulserende glød). Gløden gates på
// revealWinner (spinDone hos kalleren) — vinnerloddet ligger i drawnPrizes fra
// første sekund, så uten gating ville sidefeltet røpet vinneren før maskinen.
// Ingen lodd → null (rene sorte felt, ingen feilmelding).
function MyTicketsSidebar({ visible, side, sideWidth, nums, wonNums, winnerNum, revealWinner }) {
  const isLeft = side === 'left';
  if (!nums.length) return null;
  return (
    <div style={{
      position:'fixed', top:0, bottom:0,
      [isLeft ? 'left' : 'right']: 0,
      width: sideWidth,
      zIndex:12, pointerEvents:'none',
      display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center',
      opacity: visible ? 1 : 0,
      transition:'opacity 500ms ease',
    }}>
      <style>{`@keyframes myTicketWinGlow{0%,100%{box-shadow:0 0 10px rgba(244,208,111,0.55),0 0 22px rgba(244,208,111,0.3);}50%{box-shadow:0 0 24px rgba(244,208,111,1),0 0 48px rgba(244,208,111,0.6),0 0 72px rgba(201,168,76,0.4);}}`}</style>
      <div style={{
        fontFamily:"'Montserrat','Playfair Display',Georgia,serif",
        fontStyle:'italic', fontWeight:700,
        fontSize:'clamp(12px,1.1vw,15px)',
        color:'#a89058', letterSpacing:'0.15em', textTransform:'uppercase',
        marginBottom:14, textShadow:'0 2px 6px rgba(0,0,0,0.9)',
      }}>Dine lodd</div>
      <div style={{
        display:'flex', flexWrap:'wrap', gap:8,
        justifyContent:'center', alignItems:'center',
        maxWidth:'86%',
      }}>
        {nums.map(n => {
          const isWinnerChip = revealWinner && winnerNum != null && Number(winnerNum) === n;
          const isWonEarlier = !isWinnerChip && wonNums.includes(n);
          return (
            <span key={n} style={{
              fontFamily:"'Inter',system-ui,sans-serif",
              fontVariantNumeric:'tabular-nums',
              fontWeight:700, fontSize:'clamp(14px,1.2vw,18px)',
              letterSpacing:'0.06em', lineHeight:1,
              padding:'7px 10px', borderRadius:8,
              color: isWinnerChip ? '#1a1200' : isWonEarlier ? '#8a8a8a' : '#F4D06F',
              background: isWinnerChip ? '#F4D06F' : isWonEarlier ? 'rgba(70,70,70,0.18)' : 'rgba(26,18,0,0.55)',
              border: `1px solid ${isWinnerChip ? '#F4D06F' : isWonEarlier ? '#555' : '#C9A84C'}`,
              opacity: isWonEarlier ? 0.5 : 1,
              animation: isWinnerChip ? 'myTicketWinGlow 1.5s ease-in-out infinite' : 'none',
            }}>#{n}</span>
          );
        })}
      </div>
    </div>
  );
}

function LiveDrawView({ lotteryState, prizes, participants, settings }) {
  const [showConfetti, setShowConfetti] = useState(false);
  const [spinDone, setSpinDone] = useState(false);
  const [wheelZoomStarted, setWheelZoomStarted] = useState(false); // v269/v270: skjul sidefelt-innholdet idet hjulet zoomer inn
  const drawProgress = useDrawProgress(lotteryState); // v183: tidsbasert sti når server-trekning er fersk
  // Lyd-aktivering: iOS/Safari krever at brukeren tapper noe for å la lyd spille.
  // Viser en overlay første gang, tapper spilleren = audio fungerer resten av sesjonen.
  // v168: audioActivated-state + activateAudio fjernet sammen med overlay-en.
  // Lyd-opplåsing håndteres av den globale unlock-listeneren på modul-nivå.
  const wheelCanvasRef = useRef(null);
  const stripCanvasRef = useRef(null);
  // v183: når drawProgress.ready (fersk server-trekning), kommer premie-indeks/
  // vinner/metode fra tidsbasert beregning; ellers legacy-feltene (fallback).
  const currentPrizeIdx = drawProgress.ready ? drawProgress.aktivIdx : (lotteryState?.currentPrizeIdx || 0);
  const currentWinnerName = drawProgress.ready ? drawProgress.winnerName : (lotteryState?.currentWinnerName || null);
  const currentWinnerTicketNum = drawProgress.ready
    ? (drawProgress.winnerTicketNum ?? null)
    : (lotteryState?.currentWinnerTicketNum ?? null);
  // v167: i miks-modus leses den per-premie-valgte metoden fra lottery/current
  // (skrevet av DrawScreen) så tilskuere ser SAMME animasjon som arrangøren.
  const trekningsMetode = drawProgress.ready
    ? drawProgress.method
    : (settings?.trekningsMetode === "mix"
        ? (lotteryState?.currentDrawMethod || "hjul")
        : (settings?.trekningsMetode || "hjul"));
  const currentPrize = prizes[currentPrizeIdx];
  // v22: bruk delt buildWheelPool slik at LiveDrawView (spiller) bygger
  // EKSAKT samme pool som DrawScreen (arrangør). Tidligere brukte denne
  // en flatMap som ignorerte phantom-tickets (deltakere med tickets >
  // ticketNumbers.length) og filtrerte ALDRI ut tidligere vinnere — det
  // ga divergens i n (typisk PC=38 vs mobil=36) når arrangør hadde
  // tatt en eller flere premier. Nå bruker begge views identisk logikk.
  // v188: i tidsbasert sti skriver serveren HELE winnerHistory ved start, så den
  // kan ikke brukes til å bygge hjul-poolen (da fjernes ALLE vinnere, og hjulet
  // finner aldri gjeldende vinner → findIndex=-1 → spinn avbrytes). Ekskluder i
  // stedet kun vinnere fra premier FØR aktivIdx — speiler fallback-stiens
  // "én vinner fjernes per premie". Lodd/Lotto/Banditt er upåvirket (de bruker
  // ikke poolen). Fallback/legacy-sti bruker fortsatt winnerHistory.
  const usedTicketNums = drawProgress.ready
    ? (lotteryState?.drawnPrizes || []).slice(0, drawProgress.aktivIdx).map(p => p.winnerTicketNum)
    : (lotteryState?.winnerHistory || []).map(w => w.ticketNum ?? null);
  const wheelTickets = buildWheelPool(participants, usedTicketNums);
  // v24: bruk delt computeMaxTicketNum slik at PC og mobil ALLTID gir samme
  // verdi (uavhengig av winnerHistory). Tidligere ble denne hentet fra
  // wheelTickets etter winner-filtering — det divergerte fra DrawScreen og
  // ga forskjellig totalTickets-prop til Lotto/Lodd-maskinene.
  const maxTicketNum = computeMaxTicketNum(participants);
  // v263: gjenværende loddnumre = SAMME kilde som hjulet (wheelTickets, der
  // alt vunne lodd alt er filtrert bort via usedTicketNums). Sendes til lotto +
  // lodd så de slutter å vise vunne numre ved premie 2, 3, … Den aktive premiens
  // vinner er fortsatt med (serveren har ikke spliced ham for denne premien).
  const remainingTicketNums = wheelTickets
    .map(t => t.ticketNum)
    .filter(n => Number.isFinite(n) && n > 0);
  // v270: spillerens egne lodd til «Dine lodd»-sidefeltene. Identitet = e-posten
  // fra kjøpssiden (localStorage "vl_email"), utledning = samme logikk som
  // emailTicketNumbers der (filter på e-post lowercase → flatMap(ticketNumbers)
  // → sortert). participants er allerede live via onSnapshot-prop — ingen ny
  // henting. Tom e-post / ingen treff → tom liste → ingenting rendres.
  const myEmail = (localStorage.getItem("vl_email") || "").trim().toLowerCase();
  const myTicketNums = myEmail
    ? (participants || [])
        .filter(p => (p.email || "").toLowerCase() === myEmail)
        .flatMap(p => p.ticketNumbers || [])
        .filter(n => Number.isFinite(n))
        .sort((a, b) => a - b)
    : [];
  // Fordel brikkene over begge sidefelt: første halvdel venstre, resten høyre.
  const myLeftNums = myTicketNums.slice(0, Math.ceil(myTicketNums.length / 2));
  const myRightNums = myTicketNums.slice(Math.ceil(myTicketNums.length / 2));
  // Reset spinDone when prize changes
  useEffect(()=>{ setSpinDone(false); setWheelZoomStarted(false); },[currentPrizeIdx, currentWinnerName]);
  const handleSpinDone = useCallback(()=>{
    playWinFanfare();
    setShowConfetti(true); setTimeout(()=>setShowConfetti(false),6000);
    setSpinDone(true);
  },[]);

  // Strip-lupe: kopier stripe fra hjulcanvas til strip-canvas kontinuerlig
  const stripRafRef = useRef(null);
  useEffect(()=>{
    const draw = () => {
      const wc = wheelCanvasRef.current;
      const sc = stripCanvasRef.current;
      if (!wc || !sc) { stripRafRef.current = requestAnimationFrame(draw); return; }
      const S = wc.width;
      const sw = sc.width || (S - 20);
      const sh = sc.height || 52;
      if (sc.width !== sw || sc.height !== sh) { sc.width = sw; sc.height = sh; }
      const ctx = sc.getContext("2d");
      ctx.clearRect(0, 0, sw, sh);
      // Kilde: smal stripe rundt toppen av hjulet (ved pilen)
      const zoom = 3.0;
      const srcW = Math.round(sw / zoom);
      const srcH = Math.round(sh / zoom);
      const srcX = Math.round(S/2 - srcW/2);
      const srcY = Math.round(S/2 - S*0.49); // topp av hjulet
      // Mørk bakgrunn
      ctx.fillStyle = "#0a0600";
      ctx.fillRect(0, 0, sw, sh);
      // Forstørret innhold
      ctx.drawImage(wc, srcX, srcY, srcW, srcH, 0, 0, sw, sh);
      // Rød midtlinje = pilen
      ctx.strokeStyle = "rgba(255,60,0,0.85)";
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(sw/2, 0); ctx.lineTo(sw/2, sh); ctx.stroke();
      // Gullramme
      const fg = ctx.createLinearGradient(0,0,sw,0);
      fg.addColorStop(0,"#6a4800"); fg.addColorStop(0.5,"#ffe566"); fg.addColorStop(1,"#6a4800");
      ctx.strokeStyle = fg; ctx.lineWidth = 2.5;
      ctx.strokeRect(1.25, 1.25, sw-2.5, sh-2.5);
      stripRafRef.current = requestAnimationFrame(draw);
    };
    stripRafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(stripRafRef.current);
  }, []); // eslint-disable-line
  const liveCount = drawProgress.ready ? drawProgress.liveCount : (lotteryState?.liveCount ?? null); // v183
  // v240: nedtellings-beepet (playCountdownBeep på liveCount<=5) er fjernet —
  // det spilte samtidig med premie-presentasjonen og rotet sammen med lyden der.
  // v245: presentasjonen bruker kun playPrizeFanfare (trompetfanfare).
  // liveCount beholdes (styrer fortsatt fade-out av next-blikkfanget siste sek).

  // v213: blikkfang-overlay DELT mellom hjul- og canvas-grenene. Variant-tre:
  //   premie 0 (total >= 2):
  //     localT 0-6000  → "intro" (egen 6s intro-side, ingen premie-info)
  //     localT 6000+   → "next" (5s premie-presentasjon)
  //   premie 0 (single-premie-trekning, total === 1):
  //     hele 6s        → "next" (intro hoppes over for å unngå spinn-overlapp)
  //   premie 1+:
  //     hele 6s        → "next"
  // Samme DrawIntroBlikkfang-instans renderer både "intro" og "next" — variant-
  // swap ved t=6s beholder bakgrunns-div-en, så ingen flash av spillet bak.
  const blikkfangOverlay = (drawProgress.ready && drawProgress.fase === "countdown") ? (() => {
    const idx = drawProgress.aktivIdx;
    const total = (lotteryState?.drawnPrizes?.length) || 0;
    const localT = drawProgress.localT || 0;
    // v294: intro-vinduets lengde leses fra trekningsdokumentet (serverens
    // tidsskript, nå 6500); fallback 8000 for trekninger fra eldre functions.
    // Samme verdi brukes i useDrawProgress (COUNTDOWN_MS) — felles kilde.
    const introMs = lotteryState?.introMs ?? 8000;
    const variant = (idx === 0 && localT < introMs && total >= 2) ? "intro" : "next";
    const show = variant === "next" ? (liveCount != null && liveCount >= 2) : true; // next: fade siste sek; intro: hele 5s
    // v214: tre tilstander for tittelen — kun tekst, ikke spesial-design.
    // Premie 0 → FØRSTE PREMIE (inkl. single-premie-trekninger).
    // Siste premie i flerpremie-trekning → SISTE PREMIE.
    // Alle i mellom → NESTE PREMIE.
    const prizeTitle = (idx === 0)
      ? "FØRSTE TREKNING"
      : ((idx === total - 1 && total > 1) ? "SISTE TREKNING" : "NESTE TREKNING");
    // v224: introKey identifiserer hver TREKNING (via drawStartedAt) så React
    // remounter intro-DOM ved ny trekning og sekvensielle fade-in-animasjoner
    // starter fra null hver gang.
    const introKey = lotteryState?.drawStartedAt?.toMillis?.() || lotteryState?.drawStartedAt?.seconds || 0;
    return (
      <DrawIntroBlikkfang variant={variant} show={show}
        lotteryName={settings?.lotteryName || ""}
        weekLabel={lotteryState?.weekLabel || ""}
        prizeCount={total || (prizes?.length) || 0}
        participantCount={new Set((participants||[]).map(p=>(p.email||p.name||p.id||"").toLowerCase())).size}
        ticketCount={(participants||[]).reduce((s,p)=>s+(p.ticketNumbers?.length||0),0)}
        prizeName={currentPrize?.name || ""}
        prizeImage={currentPrize?.imageUrl || null}
        prizeCountry={currentPrize?.country || null}
        prizeIndex={idx + 1}
        totalPrizes={total}
        prizeTitle={prizeTitle}
        introKey={introKey}
        introRemainingMs={introMs - localT}/>
    );
  })() : null;

  // Hjul — PC bruker LuckyWheelZoom, mobil bruker LuckyWheelZoomMobile.
  // Viewport-grense: < 640 px = mobil (matcher isMobileView ellers i appen).
  if (trekningsMetode === "hjul") {
    const prizeCountry = currentPrize?.country || currentPrize?.region || '';
    const isMob = window.innerWidth < 640;
    const Wheel = isMob ? LuckyWheelZoomMobile : LuckyWheelZoom;
    // v270: «Dine lodd»-brikker i sidefeltene — KUN PC. Sidefelt-bredde =
    // (skjerm − hjul-diameter)/2. Hjulets idle-radius beregnes med SAMME formel
    // som LuckyWheelZoom (widthFactor 0.46, PIN_Y, reservedBottom 30) — v269-
    // geometrien gjenbrukt. Skjules idet hjulet zoomer inn (onZoomStart) — i
    // GIANT-modus dekker hjulet sidefeltene. Uten zoom står brikkene også etter
    // spinDone, slik at vinner-gløden (gatet på spinDone) faktisk synes.
    const VPW = window.innerWidth, VPH = window.innerHeight;
    const pinY = Math.max(95, Math.round(VPH * 0.11));
    const idleR = Math.min(VPW * 0.46, (VPH - pinY - 30) * 0.5);
    const sideW = Math.max(0, (VPW - (idleR * 2 + 60)) / 2);
    const showSideTickets = !isMob && sideW >= 120 && myTicketNums.length > 0;
    // v211 (Alt X): hjul får samme blikkfang som canvas-metodene — pakk inn så
    // overlayet legges oppå hjulet under nedtelling.
    return (
      <>
        <Wheel
          key={`draw-${drawProgress.ready ? drawProgress.aktivIdx : currentPrizeIdx}`}
          tickets={wheelTickets}
          winnerName={currentWinnerName}
          winnerTicketNum={currentWinnerTicketNum}
          prizeName={currentPrize?.name || ''}
          prizeCountry={prizeCountry}
          liveCount={liveCount}
          showWinner={spinDone}
          onSpinDone={handleSpinDone}
          onCanvasReady={(c)=>{ wheelCanvasRef.current = c; }}
          onZoomStart={()=>setWheelZoomStarted(true)}
        />
        {showSideTickets && (
          <>
            <MyTicketsSidebar visible={!wheelZoomStarted} side="left" sideWidth={sideW} nums={myLeftNums} wonNums={usedTicketNums} winnerNum={currentWinnerTicketNum} revealWinner={spinDone}/>
            <MyTicketsSidebar visible={!wheelZoomStarted} side="right" sideWidth={sideW} nums={myRightNums} wonNums={usedTicketNums} winnerNum={currentWinnerTicketNum} revealWinner={spinDone}/>
          </>
        )}
        {blikkfangOverlay}
      </>
    );
  }

  const isMob = window.innerWidth < 640;
  // Alle nye trekningsmetoder (lodd, lotto, banditt) er canvas-basert med
  // samme layout-mønster: full bredde, heightOverride, nedtelling i bunnfeltet.
  const isCanvasDraw = trekningsMetode === "lodd" || trekningsMetode === "lotto" || trekningsMetode === "banditt";
  // Høyde: fyll mest mulig vertikalt på mobil.
  // For banditt spiller-mobil: fyll HELE skjermhøyden (full-bleed).
  //   Vinnerkort og nedtelling blir DOM-overlay oppå.
  // For andre metoder: reserver plass til vinnerkort under canvas.
  // Alle canvas-metoder (banditt, lotto, lodd) blir full-bleed på mobil
  // med gullramme tegnet inne i canvas
  const isCanvasMobile = isMob && (trekningsMetode === "banditt" || trekningsMetode === "lotto" || trekningsMetode === "lodd");
  // Canvas-spill på PC fyller også (nesten) hele skjermen — full høyde minus
  // litt plass for header/footer. Dette gir maskinen rikelig plass og lar oss
  // klistre den nederst.
  const isCanvasPCFullScreen = !isMob && (trekningsMetode === "banditt" || trekningsMetode === "lotto" || trekningsMetode === "lodd");
  const loddH = isCanvasMobile
    ? window.innerHeight   // full-bleed: canvas dekker hele skjermen
    : isCanvasPCFullScreen
    ? window.innerHeight   // PC full-skjerm canvas (helt ned til bunnen)
    : Math.min(
        window.innerHeight - (isMob ? 220 : 260),
        window.innerHeight * 0.82,
        720
      );
  const playerViewMode = isMob ? "player-mobile" : "player-pc";
  const adaptedTopAnim =
    trekningsMetode === "banditt"
      ? <SlotMachine key={`draw-${drawProgress.ready ? drawProgress.aktivIdx : currentPrizeIdx}`} winnerName={currentWinnerName} winnerTicketNum={currentWinnerTicketNum} onSpinDone={handleSpinDone} heightOverride={loddH} viewMode={playerViewMode} countdown={liveCount}/>
      : trekningsMetode === "lotto"
      ? <LottoDraw key={`draw-${drawProgress.ready ? drawProgress.aktivIdx : currentPrizeIdx}`} winnerName={currentWinnerName}
          winnerTicketNum={currentWinnerTicketNum||"?"}
          onSpinDone={handleSpinDone} heightOverride={loddH} totalTickets={maxTicketNum} availableTickets={remainingTicketNums} viewMode={playerViewMode} countdown={liveCount}/>
      : <LotteryDraw key={`draw-${drawProgress.ready ? drawProgress.aktivIdx : currentPrizeIdx}`} winnerName={currentWinnerName}
          winnerTicketNum={currentWinnerTicketNum||"?"}
          onSpinDone={handleSpinDone} heightOverride={loddH} totalTickets={maxTicketNum} availableTickets={remainingTicketNums} viewMode={playerViewMode}/>;

  // Topp-offset: alle canvas-trekninger på mobil starter helt øverst (full-bleed).
  // PC og hjul beholder sine offsets.
  // topOffset: 0 for canvas-spill (mobil og PC) slik at maskinen kan klistres
  // helt til bunn. 60 for andre PC-spill, 50 for andre mobil-spill.
  const topOffset = (isCanvasMobile || isCanvasPCFullScreen)
    ? 0
    : (isCanvasDraw && isMob ? 0 : (isMob ? 50 : 60));

  // v270: «Dine lodd»-sidefelt for canvas-spill (PC). Sidefelt-bredde =
  // (skjerm − maskinbredde)/2 så brikkene midtstilles i det sorte feltet ved
  // siden av den sentrerte maskinen. Maskinbredden utledes med SAMME formler
  // som maskinene selv (loddH = canvas-høyde på PC) — v269-geometrien gjenbrukt:
  //   lotto  = høyde 70% × 929/1023   banditt = høyde 80% × 941/1358
  //   lodd (hatt) = konservativ tilnærming (ingen enkel eksakt bredde).
  const machineW =
    trekningsMetode === "lotto" ? loddH * 0.70 * 929/1023 :
    trekningsMetode === "banditt" ? loddH * 0.80 * 941/1358 :
    Math.min(loddH * 0.60, 520);
  const sideW = Math.max(0, (window.innerWidth - machineW) / 2);
  const showSideTickets = !isMob && sideW >= 120 && myTicketNums.length > 0;

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:10,
      background:"radial-gradient(ellipse at center,#0d0800 0%,#0a0a0a 100%)",
      overflow:"hidden",
      border:"none", outline:"none", boxShadow:"none"
    }} onClick={()=>getAudioCtx()}>
      <Confetti active={showConfetti}/>
      {/* v211: blikkfang under HVER premies countdown — delt med hjul-grenen. */}
      {blikkfangOverlay}
      <style>{`@keyframes slideUp{0%{transform:translateY(120%);opacity:0}100%{transform:translateY(0);opacity:1}} @keyframes bottleDrop{0%{transform:translateY(-80px) rotate(-15deg);opacity:0;}60%{transform:translateY(8px) rotate(4deg);opacity:1;}80%{transform:translateY(-4px) rotate(-2deg);}100%{transform:translateY(0) rotate(0);opacity:1;}} @keyframes countPulse{0%{transform:scale(0.3);opacity:0;}50%{transform:scale(1.15);opacity:1;}75%{transform:scale(0.95);}100%{transform:scale(1);opacity:1;}}`}</style>

      {/* v168: lyd-aktiverings-overlay fjernet. Lyd låses opp av den globale
          unlock-listeneren (modul-nivå) på første klikk/touch hvor som helst —
          animasjonen vises nå umiddelbart uten å blokkere visningen. */}

      {/* Animasjon — øverst, på mobil helt i toppen for canvas-trekninger.
          For banditt på PC: ingen maxWidth — SlotMachine får full bredde slik at
          den er sentrert mot SAMME parent som gratulerer/nedtelling-overlayene. */}
      <div style={{
        position:"absolute", top:topOffset, left:0, right:0,
        display:"flex", alignItems:"flex-start", justifyContent:"center",
        padding: isCanvasDraw && isMob ? "0" : "0 8px", boxSizing:"border-box"
      }}>
        <div style={{
          width:"100%",
          maxWidth: isCanvasMobile
            ? "none"
            : (!isMob && (trekningsMetode === "banditt" || trekningsMetode === "lotto" || trekningsMetode === "lodd"))
              ? "none"
              : 480
        }}>
          {adaptedTopAnim}
        </div>
      </div>

      {/* v270: «Dine lodd»-brikker midtstilt i sidefeltene — KUN PC (!isMob).
          Mobil får ALDRI dette overlayet. Står synlig hele premien (også etter
          spinDone) slik at vinner-brikkens glød synes — selve gløden gates på
          spinDone (revealWinner) inne i MyTicketsSidebar, så vinneren røpes
          aldri før animasjonen er ferdig. */}
      {showSideTickets && (
        <>
          <MyTicketsSidebar visible={true} side="left" sideWidth={sideW} nums={myLeftNums} wonNums={usedTicketNums} winnerNum={currentWinnerTicketNum} revealWinner={spinDone}/>
          <MyTicketsSidebar visible={true} side="right" sideWidth={sideW} nums={myRightNums} wonNums={usedTicketNums} winnerNum={currentWinnerTicketNum} revealWinner={spinDone}/>
        </>
      )}

      {/* v211: nedtellingstall fjernet — blikkfanget eier nedtellingen (lyd beholdt). */}

      {/* Gratulerer — sentrert, alltid synlig */}
      {spinDone&&currentWinnerName&&(()=>{
        // v223: utvidet mobil-bredde fra 290 til 380px så navn fitter på én linje
        // ("Julie Knutsen" wrappet tidligere pga trang 290px-grense).
        const ww = isMob ? Math.min(380, window.innerWidth-16) : Math.min(460, window.innerWidth*0.9);
        const ll = Math.round((window.innerWidth - ww) / 2);
        // Lotto, lodd og banditt på mobil: gratulerer på toppen
        const useTopOverlay = isMob && (trekningsMetode === "lotto" || trekningsMetode === "lodd" || trekningsMetode === "banditt");
        // v222: TEST — top:0 for alle canvas-mobil-metoder. Hvis dette flytter
        // synlig opp fra v221, var 4/8 for subtilt. Hvis IKKE synlig forskjell,
        // er det cache eller noe annet som blokkerer.
        const topValue =
          trekningsMetode === "lodd" ? 0 :
          trekningsMetode === "banditt" ? 0 :
          0;
        // Banditt, Lotto og Lodd på PC: sentrert i øvre frigjorte plass over maskinen.
        // Bruker left:0,right:0 med flex i stedet for left:50%+translate, slik at
        // sentreringen er ROBUST og uavhengig av eventuelle parent-transforms.
        // Y-posisjon: 2% + 15px fra topp — gir litt luft mot maskinen.
        const isCanvasPCWithTop = !isMob && (trekningsMetode === "banditt" || trekningsMetode === "lotto" || trekningsMetode === "lodd");
        if (isCanvasPCWithTop) {
          return (
            <div style={{
              position:"absolute", top:"8px", left:0, right:0,
              zIndex:20, pointerEvents:"none",
              display:"flex", justifyContent:"center",
              animation:"slideUp 0.5s cubic-bezier(0.22,1,0.36,1) 1.5s both",
            }}>
              <div style={{ width: "min(460px, 94vw)" }}>
                <ElegantWinnerCard
                  winnerName={currentWinnerName}
                  winnerTicketNum={currentWinnerTicketNum}
                  prizeName={currentPrize?.name}
                  prizeCountry={currentPrize?.country || currentPrize?.region || ''}
                />
              </div>
            </div>
          );
        }
        const positionStyle = useTopOverlay
          ? { top: topValue }
          : { bottom: isMob ? 87 : 67 };
        return (
        <div style={{
          position:"absolute", ...positionStyle,
          left:ll, width:ww,
          zIndex:20, pointerEvents:"none",
          animation:"slideUp 0.5s cubic-bezier(0.22,1,0.36,1) 1.5s both",
        }}>
          <ElegantWinnerCard
            winnerName={currentWinnerName}
            winnerTicketNum={currentWinnerTicketNum}
            prizeName={currentPrize?.name}
            prizeCountry={currentPrize?.country || currentPrize?.region || ''}
          />
        </div>
        );
      })()}
    </div>
  );
}

// ─── DELTAKER-VISNING ─────────────────────────────────────────────────────────

// ─── SCROLLENDE MELDING (TICKER) ──────────────────────────────────────────────
// v83: tilbake til 2-spans-mønsteret med paddingRight som spacing
// (samme som v77 — "nesten perfekt" iflg. brukeren). To identiske spans
// inntil hverandre i en inline-block track; animasjon flytter -50% av
// track-bredden = nøyaktig én tekst+padding-bredde. Span 2 lander på
// span 1s posisjon ved loop-restart → sømløs handoff.
// Tekstens rendrede bredde måles dynamisk så hastigheten holdes konstant
// (~60 px/sek) uansett tekstlengde.
// v63 FIX 4: borderless-modus skjuler topp- og bunn-streken — brukes på
// PC-spillsiden så ticker flyter naturlig under tittelen uten skille-strek.
function Ticker({ message, borderless = false, noTopBorder = false, fontSize }) {
  // v84: Track-en bruker FULL bar-bredde per span. Hver sløyfe flytter teksten
  // gjennom hele den brune boksen fra høyre kant til venstre kant, slik at
  // animasjonen utnytter all tilgjengelig plass — ikke bare et smalt vindu
  // midt på (som var v83-bug-en).
  const wrapRef = useRef(null);
  const [duration, setDuration] = useState(20);
  const [barW, setBarW] = useState(0);

  useEffect(() => {
    if (!wrapRef.current) return;
    const update = () => {
      const w = wrapRef.current?.offsetWidth || 0;
      if (w <= 0) return;
      setBarW(w);
      const speed = 60; // piksler per sekund — teksten krysser én bar-bredde per syklus
      setDuration(Math.max(6, w / speed));
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [message]);

  if (!message) return null;
  const TICKER_FS = 13;
  // v134: valgfri fontSize-prop overstyrer default (brukt på PC for større
  // ticker). Mobil sender ingen prop → beholder 13px. Påvirker ikke scroll-
  // hastigheten (den måles fra bar-bredde, ikke font).
  const FS = fontSize || TICKER_FS;
  const itemStyle = {
    flex:"0 0 50%",          // hver span = 100% av bar-bredden (track er 200%)
    display:"block",
    paddingLeft:8,
    boxSizing:"border-box",
    fontSize:FS,
    color:"#C9A84C",
    fontWeight:500,
    whiteSpace:"nowrap",
  };
  return (
    <div ref={wrapRef} style={{
      overflow:"hidden", background:"#1a1200",
      borderTop:(borderless||noTopBorder)?"none":"1px solid #3a2800",
      borderBottom:borderless?"none":"1px solid #3a2800",
      padding:borderless?"4px 0":"6px 0",
      width:"100%", boxSizing:"border-box",
      textAlign:"left", position:"relative",
    }}>
      <style>{`
        @keyframes tickerLoopV84 {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
      {barW > 0 && (
        <div style={{
          display:"flex",
          width:"200%",
          animation:`tickerLoopV84 ${duration}s linear infinite`,
          willChange:"transform",
        }}>
          <span style={itemStyle}>{message}</span>
          <span style={itemStyle} aria-hidden="true">{message}</span>
        </div>
      )}
    </div>
  );
}

function ParticipantView({ lotteryState, settings, prizes, participants, onLogoClick, pcLayout = false, stickyTopOffset = 0, scrollToWinners = false }) {
  const [qty,setQty]=useState(1);
  const [name,setName]=useState(()=>localStorage.getItem("vl_name")||"");
  // v70 FIX 2: mobilnummer-felt — lagres som phone på deltaker-objektet
  // i Firestore og huskes lokalt sammen med navn/e-post.
  const [phone,setPhone]=useState(()=>localStorage.getItem("vl_phone")||"");
  const [email,setEmail]=useState(()=>localStorage.getItem("vl_email")||"");
  const [saving,setSaving]=useState(false);
  // v283: synkron dobbeltklikk-vakt. setState er asynkront, så `disabled={saving}`
  // alene rekker ikke å hindre at to raske klikk fyrer handleBuy to ganger i
  // samme event-loop. Ref-en flippes umiddelbart og blokker andre-klikket før
  // noen Firestore-/Vipps-handling skjer. Resettes i finally.
  const buyBusyRef = useRef(false);
  const [phase,setPhase]=useState("shop");
  const [myTickets,setMyTickets]=useState(0);
  const [myTicketNumbers,setMyTicketNumbers]=useState([]);
  // v69 FIX 2: faktisk beløp å betale (totalCost - brukKreditt) lagres ved
  // registrering så bekreftelses-siden kan vise korrekt sum.
  const [paidAmount,setPaidAmount]=useState(0);
  // v120: default = on for nye brukere. Lagrer eksplisitt valg i vl_rememberMe
  // slik at brukere som har skrudd av (lagret "false") beholder sitt valg.
  // Kun når key er fraværende treffer default-grenen (true).
  const [rememberMe,setRememberMe]=useState(()=>{
    const explicit = localStorage.getItem("vl_rememberMe");
    if (explicit !== null) return explicit === "true";
    return true;
  });
  // v305 (PC-spillside steg 2): start-indeks for premie-vinduet. Ved 6+ premier
  // viser raden 5 om gangen; pilene forskyver denne ±1.
  const [prizeStart,setPrizeStart]=useState(0);

  // v301 (premie-bibliotek steg 3): spilleren ser KUN premier valgt til
  // kommende trekning. Samme invariant som functions-filteret og PrizePanel:
  // medITrekning !== false ⇒ med (manglende felt = med, bakoverkompatibelt).
  // MÅ matche functions/index.js (utforTrekning + sendWinnerEmails) og
  // PrizePanel.erMed — endres ett sted, endres alle.
  const synligePremier = (prizes||[]).filter(p => p.medITrekning !== false);

  // v125: når forrige runde ble avlyst, hent siste runde MED vinnere fra
  // history (engangslesing, ikke onSnapshot — kun når lastClosedByAvlys).
  // Filtrerer bort avlyste docs (de har winners:[]), tar nyeste med vinnere.
  const [sisteVinnerRunde, setSisteVinnerRunde] = useState(null);
  useEffect(() => {
    if (!lotteryState?.lastClosedByAvlys) { setSisteVinnerRunde(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db,"history"));
        const found = snap.docs
          .map(d => d.data())
          .filter(d => !d.avlyst && Array.isArray(d.winners) && d.winners.length > 0)
          .sort((a,b) => (b.createdAt?.seconds||0) - (a.createdAt?.seconds||0))[0] || null;
        if (!cancelled) setSisteVinnerRunde(found);
      } catch (e) {
        console.warn("[siste-vinner] kunne ikke hente history:", e);
        if (!cancelled) setSisteVinnerRunde(null);
      }
    })();
    return () => { cancelled = true; };
  }, [lotteryState?.lastClosedByAvlys]);

  // v305: dynamisk sidebar-høyde (ResizeObserver + refs) fjernet — topp-boksene
  // har nå fast felles høyde PANEL_H (definert i pcLayout-grenen), dimensjonert
  // til 20 loddkjøper-rader. Ingen høyde-kobling til premie-bunnen lenger.

  // v256: historikk-kommentarer for done-skjermens vinnerliste (mobil-grenen
  // her; PC bruker WinnerView). enabled=!pcLayout så PC ikke henter history her.
  const winnerComments = useWinnerComments(lotteryState?.winnerHistory || [], participants, !pcLayout);

  // Lagre navn/mobil/epost i localStorage når de endres (kun hvis rememberMe er på)
  // v144: auto-stor forbokstav + strip ledende mellomrom ved input.
  const handleNameChange = (v) => { const c = capName((v||"").replace(/^\s+/,"")); setName(c); if(rememberMe) localStorage.setItem("vl_name",c); };
  // v138: strip alt som ikke er siffer, mellomrom, + eller - (godtar "+47 123 45 678").
  // Hindrer at navn/tekst tastes i mobil-feltet. Gjelder både PC og mobil (delt fn).
  const handlePhoneChange = (v) => { const cleaned=(v||"").replace(/[^0-9 +-]/g,"").slice(0,20); setPhone(cleaned); if(rememberMe) localStorage.setItem("vl_phone",cleaned); };
  const handleEmailChange = (v) => { setEmail(v); if(rememberMe) localStorage.setItem("vl_email",v); };
  const handleRememberToggle = () => {
    const next = !rememberMe; setRememberMe(next);
    localStorage.setItem("vl_rememberMe", String(next));
    if(next){ localStorage.setItem("vl_name",name); localStorage.setItem("vl_phone",phone); localStorage.setItem("vl_email",email); }
    else { localStorage.removeItem("vl_name"); localStorage.removeItem("vl_phone"); localStorage.removeItem("vl_email"); }
  };

  const ticketPrice = settings?.ticketPrice||10;
  const maxPerPlayer = settings?.maxTicketsPerPlayer||25;
  const status = lotteryState?.status;
  // Loddsalg ÅPENT kun når status eksplisitt er "idle" OG drawTime ikke er nådd.
  // Alt annet (done, drawing, ukjent) = stengt. Dette er den sikre standarden.
  const salesLocked = status !== "idle" ||
    lotteryState?.salgStengt === true ||
    (lotteryState?.drawTime && new Date(lotteryState.drawTime) <= new Date());
  // v209 B: trekning nært forestående — drawTime nådd, men status fortsatt "idle"
  // (serveren har ikke skrevet "drawing" ennå). Komponent-scope så BÅDE PC- og
  // mobil-grenen kan vise blikkfang i stedet for "Loddsalget er avsluttet"-flashen.
  // (status==="idle" utelukker allerede "done".)
  // v290: inkluderer "drawing_pending" (serverens claim-mellomtilstand, ~200ms
  // mellom claim og selve trekningsskrivingen). Uten den ble holdeskjermen
  // unmountet i gapet → svart blink + intro-restart ved autostart (bevist i
  // v289-diagnoseloggen). drawTime/autoDraw står urørt under pending.
  // v319: krever minst én premie valgt (synligePremier, samme filter som
  // v301-sperren og utforTrekning). Uten premier trekker serveren aldri —
  // den utsetter/avlyser innen ~1 min — så introen var en falsk alarm.
  const drawImminent = (status === "idle" || status === "drawing_pending") &&
    synligePremier.length > 0 &&
    !!lotteryState?.autoDraw && !!lotteryState?.drawTime &&
    (new Date(lotteryState.drawTime) <= new Date());
  // v249: sikkerhetsnett mot evig "GJØR DERE KLAR". Hvis trekningstidspunktet er
  // passert med > 2 min uten at status er blitt "drawing" (typisk 0 deltakere/
  // premier — serveren rydder normalt drawTime/utsetter innen ~1 min), viser vi en
  // rolig "venter"-melding i stedet for "TREKNINGEN STARTER SNART".
  const drawOverdue = drawImminent &&
    (Date.now() - new Date(lotteryState.drawTime).getTime() > 2 * 60 * 1000);
  // v320: "venter på premier"-vindu. drawTime er passert men 0 premier er valgt
  // (samme filter som v319/v301) — serveren trekker aldri i denne tilstanden
  // (ved autoDraw utsetter den innen ~1 min). Uten denne så spilleren et falskt
  // "Loddsalget er avsluttet"-glimt og kunne tro loddene var tapt. Ekte
  // avlysning/stengt salg (salgStengt/lastClosedByAvlys) og done er utelukket —
  // de viser sine egne meldinger som før.
  const venterPaaPremier = (status === "idle" || status === "drawing_pending") &&
    !lotteryState?.salgStengt &&
    !!lotteryState?.drawTime && (new Date(lotteryState.drawTime) <= new Date()) &&
    synligePremier.length === 0;

  const mobile = isMobile();
  // Vipps-lenke: bruk vippsLink hvis satt, ellers bygg fra vippsNumber
  const vippsUrl = settings?.vippsLink ||
    (settings?.vippsNumber ? `vipps://?acct=${settings.vippsNumber}` : null);

  // Faktiske lodd for denne e-posten (lest fra Firestore via participants-prop)
  // emailTickets er fasit. myTickets brukes kun lokalt som "optimistisk" visning
  // før Firestore-dataen har kommet inn. Vi tar MAX av de to — aldri summer dem.
  const emailTickets = email.trim()
    ? participants.filter(p=>p.email===email.trim().toLowerCase()).reduce((s,p)=>s+(p.tickets||0),0)
    : 0;
  const displayTickets = emailTickets > 0 ? emailTickets : myTickets;
  // Hent loddnummer fra Firestore for denne e-posten (vises selv etter reload)
  const emailTicketNumbers = email.trim()
    ? participants.filter(p=>p.email===email.trim().toLowerCase())
        .flatMap(p=>p.ticketNumbers||[])
        .sort((a,b)=>a-b)
    : [];
  const displayTicketNumbers = emailTicketNumbers.length > 0 ? emailTicketNumbers : myTicketNumbers;
  // Tilgodehavende for denne e-posten
  const [kreditt, setKreditt] = useState(0);
  useEffect(()=>{
    if(!email.trim()) { setKreditt(0); return; }
    const key=email.trim().toLowerCase();
    getDoc(doc(db,"credits",key)).then(s=>setKreditt(s.exists()?(s.data().amount||0):0)).catch(()=>setKreditt(0));
  },[email]);

  // v94 Fix 1: lås html+body overflow på mobil så HELE siden er låst — ingen
  // vertikal scroll uansett. Restaurer original-verdier ved unmount/PC-switch.
  useEffect(() => {
    if (pcLayout) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlH = html.style.height;
    const prevBodyH = body.style.height;
    const prevOverscroll = body.style.overscrollBehavior;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    html.style.height = "100%";
    body.style.height = "100%";
    body.style.overscrollBehavior = "contain";
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      html.style.height = prevHtmlH;
      body.style.height = prevBodyH;
      body.style.overscrollBehavior = prevOverscroll;
    };
  }, [pcLayout]);

  // v88: bottom-sheet for kjøp + side-drawers for "Dine lodd" og
  // "Loddkjøpere" + toast for kopier-til-utklippstavle. Alt mobil-only.
  // sheetDragY/sheetDragStartY: swipe-ned-gest på sheet-drag-handle.
  const [showBuySheet, setShowBuySheet] = useState(false);
  const [sheetDragY, setSheetDragY] = useState(0);
  const sheetDragStartY = useRef(null);
  const [showDineLodd, setShowDineLodd] = useState(false);
  const [showLoddkjopere, setShowLoddkjopere] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  // v155: valgfri andrelinje (f.eks. vippsnummer) som rendres større/uthevet.
  const [toastValue, setToastValue] = useState("");
  const toastTimerRef = useRef(null);
  const nameInputRef = useRef(null);
  const emailInputRef = useRef(null);

  // v88: toast-helper for bekreftelses-meldinger ("Kopiert!" etc.). 2 sek
  // varighet. Plassert FØR phase==="waiting"-grenen så waiting-skjermen
  // kan kalle copyVippsNumber uten TDZ-feil.
  // v155: showToast(title, value?) — bakoverkompatibel. Uten value oppfører
  // den seg som før (enlinje). Med value rendres value som stor uthevet
  // andrelinje. v155: varighet 2000→2500ms så brukeren rekker å lese.
  const showToast = (msg, value="") => {
    setToastMsg(msg);
    setToastValue(value);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => { setToastMsg(""); setToastValue(""); }, 2500);
  };
  // v154: dismiss toast når fanen blir synlig igjen. setTimeout pauses mens
  // fanen er i bakgrunnen (f.eks. når Vipps-appen åpnes), så toast-en hang
  // til brukeren kom tilbake. visibilitychange + clearTimeout rydder den.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToastMsg(""); setToastValue("");
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);
  // v284: lukk kjøpsarket automatisk når salget ikke lenger er åpent.
  // NB: avlysning beholder status:"idle" men setter salgStengt:true (se
  // avlys-handleren ~9710), så vi lytter på BEGGE — status (trekning
  // startet) og salgStengt (avlyst/stengt). Uten dette ble arket stående
  // til brukeren refreshet siden. Samme lukking som X-knappen (closeBuySheet).
  // Må ligge FØR phase==="waiting"-early-return (rules-of-hooks).
  const lotteryStatus = lotteryState?.status;
  const lotterySalgStengt = !!lotteryState?.salgStengt;
  useEffect(() => {
    if ((lotteryStatus && lotteryStatus !== "idle") || lotterySalgStengt) {
      setShowBuySheet(false); setSheetDragY(0);
    }
  }, [lotteryStatus, lotterySalgStengt]);
  // v153: copyVippsNumber-funksjonen fjernet — den var død kode (aldri kalt
  // siden v126). Synkron kopiering håndteres nå av copyToClipboardSync (modul-
  // nivå) i handleBuy.

  if (phase==="waiting") {
    // v69 FIX 1: auto-skalering på DINE LODD-listen. Default 16 px,
    // minimum 10 px — skaleres ned trinnvis basert på antall lodd så de
    // får plass uten å wrappe stygt. Sidebar-pillen i salgs-vyen bruker
    // fortsatt fast 52 px grid (FIX 1 påvirker bare bekreftelses-siden).
    const _ln = displayTicketNumbers.length;
    const loddFs = _ln <= 10 ? 16 : _ln <= 20 ? 14 : _ln <= 30 ? 12 : _ln <= 50 ? 11 : 10;
    // v69 FIX 2: QR-data for bekreftelses-siden — samme kilder som
    // floating QR (uploaded image eller generert URL).
    const hasQr = !!(settings?.vippsQrImage || vippsUrl);
    return (
      <div style={S.waitingScreen}>
        <div style={{ fontSize:72 }}>🍾</div>
        <h2 style={S.waitingTitle}>Lodd registrert!</h2>
        {/* v284: Vipps-påminnelsen vises kun når det faktisk er noe å betale.
            paidAmount = restBetales fra handleBuy (totalCost - kredittBrukt).
            Fullt tilgode-dekket kjøp (rest 0) får nøytral bekreftelse i stedet. */}
        {paidAmount > 0 ? (
          <p style={{ margin:0,fontSize:16,fontWeight:"bold",color:"#FF5B24",animation:"vippsBlink 2s ease-in-out infinite" }}>Husk å fullføre betalingen i Vipps</p>
        ) : (
          <p style={{ margin:0,fontSize:16,fontWeight:"bold",color:"#4caf50" }}>Betalt med tilgodehavende</p>
        )}
        <style>{`@keyframes vippsBlink{0%,100%{opacity:1}50%{opacity:0.35}}`}</style>
        {/* v69 FIX 2: beløp å betale (etter kreditt-bruk) — gull-fremhevet. */}
        {paidAmount > 0 && (
          <p style={{ margin:"6px 0 0", fontSize:16, color:"#aaa", textAlign:"center" }}>
            Du skal betale <span style={{ color:"#C9A84C", fontWeight:"bold", fontSize:22 }}>{paidAmount},-</span>
          </p>
        )}
        {/* v69 FIX 2: QR-kode + Vipps-nummer for direkte betaling.
            v89: PC viser QR (skanne med mobil). Mobil viser statisk Vipps-
            nummer-info — nummeret er allerede auto-kopiert ved registrering
            (handleBuy success-gren). Ingen klikk-interaksjon her. */}
        {hasQr && paidAmount > 0 && !pcLayout && settings?.vippsNumber && (
          <div style={{
            marginTop:14, padding:"12px 16px",
            background:"#1a1200", border:"1px solid #C9A84C",
            borderRadius:10,
            display:"flex", flexDirection:"column", alignItems:"center", gap:4,
            color:"#C9A84C", maxWidth:380, width:"100%", boxSizing:"border-box",
          }}>
            <span style={{ display:"block", fontSize:12, color:"#999", letterSpacing:"0.08em" }}>Vipps:</span>
            <span style={{ display:"block", fontSize:24, fontWeight:"bold", color:"#C9A84C", fontVariantNumeric:"tabular-nums", letterSpacing:"0.02em" }}>{settings.vippsNumber}</span>
            <span style={{ fontSize:11, color:"#8a7848", letterSpacing:"0.06em" }}>
              Nummeret er kopiert til utklippstavlen
            </span>
          </div>
        )}
        {hasQr && paidAmount > 0 && pcLayout && (
          <div style={{ marginTop:10, padding:"14px 16px", background:"#0d0d0d", border:"1px solid #2a2000", borderRadius:8, textAlign:"center" }}>
            {settings?.vippsQrImage ? (
              <img src={settings.vippsQrImage} alt="Vipps QR-kode"
                style={{ width:180, height:180, objectFit:"contain", background:"#fff", borderRadius:6, padding:6 }}/>
            ) : (
              <QRCode url={vippsUrl} size={180}/>
            )}
            {settings?.vippsNumber && <p style={{ margin:"10px 0 0", fontSize:13, color:"#c0c0c0", fontWeight:"bold" }}>Vipps {settings.vippsNumber}</p>}
          </div>
        )}
        {/* v137: "Du har X lodd"-pillen fjernet også på PC (var redundant —
            DINE LODD-boksen under viser antallet). Mobil fjernet i v127. */}
        {/* v69 FIX 3: grønn bg + gull-ramme på DINE LODD-listen — matcher
            sidebar-boksen i salgs-vyen for visuell konsistens. */}
        {displayTicketNumbers.length>0&&(
          <div style={{
            margin:"6px 0 0", padding:"12px 16px",
            background:"rgba(46,125,50,0.12)",
            border:"1px solid #3a2800", borderRadius:8,
            textAlign:"center", maxWidth:560, width:"100%", boxSizing:"border-box",
          }}>
            <p style={{ margin:"0 0 6px",fontSize:11,color:"#a89058",letterSpacing:"0.15em",textTransform:"uppercase",fontWeight:600 }}>Dine lodd ({displayTicketNumbers.length})</p>
            <p style={{
              margin:0, fontSize:loddFs, color:"#f0f0f0",
              letterSpacing:"0.1em", fontWeight:"bold",
              fontVariantNumeric:"tabular-nums",
              lineHeight:1.4, wordBreak:"break-word",
            }}>
              {displayTicketNumbers.map(n=>`#${n}`).join("  ·  ")}
            </p>
          </div>
        )}
        {/* v69 FIX 4: lysere tekst (var #555) for lesbarhet. */}
        <p style={{ color:"#c0c0c0",fontSize:13,textAlign:"center" }}>
          {lotteryState?.drawTime
            ? `⏱ Trekning: ${new Date(lotteryState.drawTime).toLocaleString("nb-NO",{weekday:"long",day:"numeric",month:"long",hour:"2-digit",minute:"2-digit"})}`
            : "Arrangøren starter trekningen snart 🤞"}
        </p>
        {/* v137: gul tilbake-knapp på både PC og mobil via delt S.backBtnGold.
            Tidligere hadde PC en grå ghost-variant med hover-handlere. */}
        <button style={S.backBtnGold} onClick={()=>setPhase("shop")}>← Tilbake</button>
      </div>
    );
  }

  const handleBuy=async()=>{
    // Synkrone valideringer — ingen await, så user-gesture-context bevares.
    if (!name.trim()){showAlert("Skriv inn navnet ditt!");return;}
    if (!email.trim()||!email.includes("@")){showAlert("Skriv inn en gyldig e-postadresse!");return;}
    if (salesLocked){showAlert("Loddsalget er avsluttet!");return;}
    // v157: maxPerPlayer-sjekken er synkron (participants er lokal state) →
    // flyttet hit, FØR clipboard/deep-link, så vi ikke åpner Vipps når grensa
    // allerede er nådd. Note (v283): klient-state, kan være litt stale — verste
    // tilfelle 1-2 ekstra lodd som arrangør kan justere. Vanntett krever
    // denormalisert participantsByEmail-doc (eget steg).
    const existing=participants.filter(p=>p.email===email.trim().toLowerCase()).reduce((s,p)=>s+(p.tickets||0),0);
    if (existing+qty>maxPerPlayer){showAlert(`Maks ${maxPerPlayer} lodd per person. Du har allerede ${existing}.`);return;}
    // v283: dobbeltklikk-vakt FØR Vipps-åpning. Synkron ref blokkerer to raske
    // klikk i samme event-loop. Validerings-failures over treffer ikke ref-en.
    if (buyBusyRef.current) return;
    buyBusyRef.current = true;
    // v153/v157: clipboard + Vipps deep-link SYNKRONT i klikk-gesten, FØR
    // første await. iOS Safari mister gesture-context ved await — derfor må
    // BÅDE clipboard-kopiering OG app-launch initieres her (ikke etter
    // Firestore-skrivingene). Firestore-skriving skjer rett etter; ved feil
    // viser vi en tydelig "ikke betal"-melding (Vipps er da allerede åpnet).
    if (mobile && settings?.vippsNumber) {
      const copied = copyToClipboardSync(String(settings.vippsNumber));
      if (copied) showToast("Vippsnummer kopiert", String(settings.vippsNumber));
    }
    // v317: husk om vi faktisk hoppet til Vipps i denne klikk-flyten. Da kan
    // mobil-nettleseren ha avbrutt det pågående kjopLodd-kallet ved retur, og
    // et uspesifikt 'internal' i catch er falsk alarm (kjøpet gikk gjennom).
    let vippsHoppet = false;
    if (mobile) {
      hapticSuccess();
      setShowBuySheet(false);
      setSheetDragY(0);
      openVippsPaymentMobile(settings);
      vippsHoppet = true;
    }
    setSaving(true);
    try {
      // v286 (fase 1): kjøpet flyttet SERVER-SIDE — callable kjopLodd i
      // functions/index.js gjør alt v283-transaksjonen gjorde (atomisk lodd +
      // kreditt-trekk + transaksjonslogg + deltaker), pluss server-lest pris
      // og server-håndhevet maxPerPlayer (collection-query i admin-tx).
      // Klient-valideringene over står som rask UX-sjekk; serveren er nå
      // autoritativ. Samme garanti som v283: callable-feil = ingen lodd skrevet.
      const kjopLoddFn = httpsCallable(getFunctions(app, "europe-west1"), "kjopLodd", { timeout: 30000 });
      const res = await kjopLoddFn({
        lotteryId: "current", qty,
        name: name.trim(), email: email.trim(), phone: phone.trim(),
      });
      const result = res.data;
      // ETTER KALL — synkroniser klient-state (samme observerbar oppfoersel som for).
      const restBetales = result.restBetales;
      setKreditt(result.nyKreditt);
      setMyTicketNumbers(prev=>[...prev, ...result.ticketNumbers]);
      setMyTickets(prev=>prev+qty);
      setPaidAmount(restBetales);
      if (result.txBruk > 0) {
        if (result.paid) showAlert(`✅ Lodd betalt med tilgodehavende! (${result.txBruk},- brukt)`);
        else showAlert(`✅ ${result.txBruk},- av tilgodehavende brukt. Restbeløp å betale: ${restBetales},-`);
      }
      // v157: clipboard + Vipps deep-link + sheet-close er flyttet til toppen
      // av handleBuy (synkron gesture-context). Her gjenstaar kun overgangen
      // til kvitteringssiden ved vellykket registrering.
      setTimeout(()=>setPhase("waiting"),600);
    } catch(e) {
      // v65: detaljert logging for aa diagnostisere "Noe gikk galt" i framtida.
      console.error("Kjøpsfeil:", e);
      console.error("  Firebase code:", e?.code);
      console.error("  Firebase message:", e?.message);
      console.error("  Auth state:", auth?.currentUser ? `uid=${auth.currentUser.uid} anon=${auth.currentUser.isAnonymous}` : "ingen auth");
      // v286: spesifikke feilkoder fra kjopLodd-callablen (HttpsError-message
      // kommer som e.message) faar pene norske meldinger; alle andre faar den
      // eksisterende generiske "Lodd ble IKKE registrert"-alerten.
      if (e?.message === "salget-stengt") {
        showAlert("⛔ Loddsalget stengte akkurat\nDu har IKKE fått lodd — ikke betal i Vipps. Kontakt arrangøren hvis du allerede har betalt.");
      } else if (e?.message === "maks-lodd") {
        showAlert("⛔ Maks antall lodd per person er nådd\nKjøpet ble IKKE registrert — ikke betal i Vipps.");
      } else if (e?.message === "ugyldig-antall") {
        showAlert("⛔ Ugyldig antall lodd — kjøpet ble ikke registrert. Velg minst 1 lodd og prøv igjen.");
      } else if (e?.message === "ugyldig-navn" || e?.message === "ugyldig-epost" || e?.message === "ugyldig-telefon") {
        showAlert("⛔ Ugyldige opplysninger — kjøpet ble IKKE registrert. Sjekk navn, e-post og telefon og prøv igjen. Ikke betal i Vipps før kjøpet er registrert.");
      } else if (vippsHoppet && ["internal","deadline-exceeded","unavailable","cancelled"]
          .some(c => e?.code === c || e?.code === `functions/${c}`)) {
        // v317/v318: FALSK ALARM. Vi hoppet til Vipps i denne flyten, og mobil-
        // nettleseren avbrøt det pågående kjopLodd-kallet da siden ble forlatt.
        // Avhengig av timing rapporteres avbruddet som 'internal' (rått avbrutt),
        // 'deadline-exceeded' (brukeren ble lenge i Vipps, bekreftet i praksis),
        // 'unavailable' eller 'cancelled' (ustabilt nett). Kjøpet gikk likevel
        // gjennom på serveren — alle EKTE serverfeil pakkes nå i
        // 'functions/unknown' (se kjopLodd i functions/index.js), så disse
        // kodene her betyr utelukkende at klienten aldri fikk svaret.
        // Vis derfor en rolig, positiv melding og gå til kvitteringssiden
        // («Dine lodd» fylles fra participants-snapshot). Vi ber IKKE om «prøv
        // igjen» — det er nettopp det som skaper dobbeltbetaling i dag.
        setTimeout(()=>setPhase("waiting"),600);
        showAlert("✅ Takk! Fullfør betalingen i Vipps.\nLoddene dine dukker opp under «Dine lodd» om et øyeblikk.");
      } else {
        const errCode = e?.code || "ukjent";
        const errMsg = e?.message || String(e);
        // Vipps kan allerede vaere aapnet (deep-link skjer i gesten over),
        // saa gjor det tydelig at lodd IKKE ble registrert og at de ikke skal betale.
        showAlert(`⛔ Lodd ble IKKE registrert: ${errCode}\n\n${errMsg}\n\nIkke betal i Vipps før du har prøvd igjen. Kontakt arrangøren hvis problemet vedvarer.`);
      }
    } finally {
      setSaving(false);
      buyBusyRef.current = false;
    }
  };

  // v88: CTA-klikk åpner ALLTID bottom-sheet (ingen ett-trykks-kjøp).
  // Brukeren må selv bekrefte antall lodd og registrere inne i sheet.
  // Fokus på første tomme felt etter at sheet-glider er ferdig.
  const handleCTAClick = () => {
    hapticTap();
    if (saving) return;
    setShowBuySheet(true);
    setTimeout(() => {
      if (!name.trim()) nameInputRef.current?.focus();
      else if (!email.trim() || !email.includes("@")) emailInputRef.current?.focus();
    }, 360);
  };

  // v87: lukk sheet etter vellykket registrering (kalles fra handleBuy's
  // success-gren via setTimeout — flyttes inn der under).
  const closeBuySheet = () => { setShowBuySheet(false); setSheetDragY(0); };

  // v87: swipe-ned-gest for å lukke sheet. Touch-events kun på drag-handle/
  // header-området så scrolling inne i sheet ikke trigger lukking.
  const onSheetTouchStart = (e) => { sheetDragStartY.current = e.touches[0].clientY; };
  const onSheetTouchMove = (e) => {
    if (sheetDragStartY.current == null) return;
    const dy = e.touches[0].clientY - sheetDragStartY.current;
    if (dy > 0) setSheetDragY(dy);
  };
  const onSheetTouchEnd = () => {
    const dy = sheetDragY;
    sheetDragStartY.current = null;
    if (dy > 120) closeBuySheet();
    else setSheetDragY(0);
  };

  // v58–v61: PC-spillside (Alt A). Aktiveres når innerWidth >= 900.
  // v61 restruktur: ticker rett under tittel; bilder uten max-cap; premier
  // og vinnere som grid (opptil 5 per rad); QR som flytende fast-element i
  // nedre høyre hjørne (FloatingVippsQR); deltakerliste sticky i sidebar.
  // Mobil-grenen under er bit-for-bit uendret.
  if (pcLayout) {
    // Felles tittel-renderer (samme som mobil-flowen lenger ned).
    const renderTittel = (pcMultiplier = 1) => {
      const sz = (settings?.tittelStorrelse||26) * pcMultiplier;
      const align = settings?.tittelJustering==="venstre"?"left":settings?.tittelJustering==="høyre"?"right":"center";
      const baseStyle = { margin:0, fontSize:sz, fontWeight:"700", letterSpacing:"0.06em",
        fontFamily: settings?.tittelFont || "'Playfair Display',Georgia,serif",
        textAlign:align, lineHeight:1.15 };
      const eff = settings?.tittelEffekt||"gull";
      const erGrad = eff !== "ingen" && EFFEKT_GRADIENTS[eff];
      const erShad = EFFEKT_SHADOW[eff];
      const gradStyle = { ...baseStyle, color:"transparent",
        backgroundImage: EFFEKT_GRADIENTS[eff]||EFFEKT_GRADIENTS["gull"],
        backgroundClip:"text", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
        filter: EFFEKT_FILTER[eff] };
      const shadowStyle = { ...baseStyle, color:settings?.tittelFarge||"#C9A84C", textShadow:EFFEKT_SHADOW[eff] };
      const plainStyle = { ...baseStyle, color:settings?.tittelFarge||"#C9A84C" };
      const relieffStyle = { ...baseStyle, color:settings?.tittelFarge||"#C9A84C", WebkitTextFillColor:settings?.tittelFarge||"#C9A84C", textShadow:EFFEKT_RELIEFF };
      const outlinePx = settings?.tittelOutline||0;
      const outlineFarge = settings?.tittelOutlineFarge||"#ffffff";
      const baseWithOutline = outlinePx > 0 ? { WebkitTextStroke:`${outlinePx}px ${outlineFarge}` } : {};
      const st = settings?.tittelEffekt==="relieff" ? { ...relieffStyle, ...baseWithOutline }
               : erGrad ? { ...gradStyle, ...baseWithOutline }
               : erShad ? { ...shadowStyle, ...baseWithOutline }
               : { ...plainStyle, ...baseWithOutline };
      const linje1 = settings?.tittelLinje1 || settings?.lotteryName || "Vinloddet";
      const linje2 = settings?.tittelLinje2 || "";
      return (<>
        <h1 style={st}>{linje1}</h1>
        {linje2&&<h1 style={{ ...st,marginTop:2 }}>{linje2}</h1>}
      </>);
    };
    // v61 FIX 2: QR er flyttet ut av sidebaren til en flytende fast-positioned
    // boks i nedre høyre hjørne (FloatingVippsQR — modul-scope). isDone
    // styrer fortsatt hvilke status-bannere som vises i hovedinnholdet.
    const isDone = status === "done";
    // v59 FIX 4: luminans-basert tekstfarge på premie-kortene.
    // Hvit/lys card-bg → mørk tekst (som før); mørk card-bg → lys tekst
    // for tydelig kontrast mot dempet bakgrunn.
    const _hexLum = (hex) => {
      try {
        const h = (hex||"").replace('#','');
        if (h.length < 6) return 1;
        const r = parseInt(h.slice(0,2),16)/255;
        const g = parseInt(h.slice(2,4),16)/255;
        const b = parseInt(h.slice(4,6),16)/255;
        return 0.2126*r + 0.7152*g + 0.0722*b;
      } catch (e) { return 1; }
    };
    const cardBg = settings?.premieKortFarge || "#ffffff";
    const isDarkCard = _hexLum(cardBg) < 0.5;
    const txtName  = isDarkCard ? "#f0f0f0" : "#222";
    const txtMeta  = isDarkCard ? "#aaa"    : "#888";
    const txtDesc  = isDarkCard ? "#c0c0c0" : "#666";

    // v61 FIX 6: aggregert deltakerliste for sidebar (per e-post, sortert
    // synkende på loddantall).
    const deltakerByEmail = {};
    participants.forEach(p => {
      const k = (p.email || p.name || p.id || "").toLowerCase();
      if (!deltakerByEmail[k]) deltakerByEmail[k] = { name: p.name || "Ukjent", tickets: 0 };
      deltakerByEmail[k].tickets += (p.tickets || 0);
      if (!deltakerByEmail[k].name && p.name) deltakerByEmail[k].name = p.name;
    });
    // v64 FIX 3: sortering: flest lodd først, alfabetisk ved likhet.
    const deltakerList = Object.values(deltakerByEmail).sort((a,b) => (b.tickets - a.tickets) || a.name.localeCompare(b.name, "nb"));
    // v64 dynamisk kolonner — v68 erstattet med fast 2-kol topp-30-grid.
    // v67 FIX 1+4: 3-kolonners grid med to symmetriske sidekol gir
    // automatisk skjerm-sentrert hovedinnhold i midten.
    // v68 FIX 3: sidebar utvidet 280 → 320 px for å gi 2-kolonners
    // Loddkjøpere-grid (topp 30) god plass. Dine lodd får tilsvarende
    // bredde for symmetri og kan vise opptil 4 kolonner med lodd.
    // v304: én premieboks bred (186px) — sidefeltene flukter med premie 1 og 5.
    const SIDEBAR_W = 186;
    // v305: felles fast høyde for alle tre topp-boksene. v311: dimensjonert til
    // å vise 15 loddkjøper-rader uten pil. PANEL_ROW_H = én Loddkjøpere-rad
    // (padding 3+3 + tekst ~15 + 1px border ≈ 22), PANEL_HEAD_H = header (font 10
    // + margin 10 ≈ 24), padding 14 topp + 14 bunn. Listene scroller internt ved
    // flere (med pil-indikator via ScrollHintBox).
    const PANEL_ROW_H = 22;
    const PANEL_HEAD_H = 24;
    const PANEL_PAD = 14;
    // v311: krympet fra 20 → 15 rader så HELE normal-visningen (header, ticker,
    // de tre boksene, «Ukens premier» og gullrammens nedre kant) får plass på én
    // 1080p PC-skjerm uten vertikal scroll. Færre rader vises uten pil — listene
    // pagineres som før via PagedList. Loddkjøpere viser nå 15 uten pil, Dine
    // lodd ~22 (11 pille-rader × 2). PANEL_H = 28 + 24 + 330 = 382 (var 492).
    const PANEL_LIST_H = 15 * PANEL_ROW_H; // 330 — eksakt 15 rader
    const PANEL_H = PANEL_PAD * 2 + PANEL_HEAD_H + PANEL_LIST_H; // 382

    // v306: gullramme rundt spill-innholdet (topp-rad + premier), i samme gull-
    // uttrykk som mobil-trekningsrammen (linje ~12705: gull-gradient + glød).
    // Border-image støtter ikke avrundede hjørner, så vi bruker padding-box-
    // trikset: ytre div er gull-gradient med padding (= rammetykkelse) og radius;
    // indre div har app-bakgrunn + mindre radius.
    // v310: rammen FYLLER skjermhøyden — minHeight calc(100vh − topp/bunn-margin),
    //   men beholder min-height (ikke fast height) så den vokser når innholdet er
    //   høyere enn skjermen (mange lodd/premier) uten dobbel scroll. Ytre+indre er
    //   flex column så indre fyller høyden; innholdet fordeles via en main-gruppe
    //   med marginTop:auto (skyves ned med luft over) — header/ticker blir øverst,
    //   premier/vinnere i nedre del. RAMME_VPAD ≈ topp-margin + bunn-luft.
    const RAMME_VPAD = 96; // wrapper-padding + ramme-margin + litt pusterom
    const GULL_GRADIENT = "linear-gradient(145deg,#fff5a0 0%,#C9A84C 20%,#8B6B1A 50%,#C9A84C 80%,#fff5a0 100%)";
    const GULL_RAMME_YTRE = {
      maxWidth: 1180, width:"100%", margin:"18px auto 0", boxSizing:"border-box",
      background: GULL_GRADIENT, padding:5, borderRadius:18,
      boxShadow:"0 0 20px rgba(201,168,76,0.3)",
      minHeight: `calc(100vh - ${RAMME_VPAD}px)`,
      display:"flex", flexDirection:"column",
    };
    const GULL_RAMME_INDRE = {
      background: settings?.bakgrunnsFarge || "#0a0a0a",
      borderRadius:14, padding:"14px 22px 30px", boxSizing:"border-box",
      flex:1, display:"flex", flexDirection:"column",
    };
    // v311: RAMME_MAIN fyller resthøyden (flex:1, kolonne) i stedet for å
    // skyves ned som helhet. Da kan banner-statene ligge ØVERST rett under
    // tickeren (liten margin), mens vinner-seksjonene (avlyst/done) skyves
    // NED via marginTop:auto KUN på selve vinner-blokkene. Normal-visningen
    // (grid + premier) flyter da naturlig fra toppen.
    const RAMME_MAIN = { width:"100%", flex:1, display:"flex", flexDirection:"column" };

    const showCenteredLockedBanner = salesLocked && !isDone;
    return (
      <>
        {/* v63 FIX 5: tettere wrapper-padding (bottom 80 → 40) for å bevare
            vertikal plass på 1080p PC-skjermer. */}
        <div style={{ maxWidth:1500, margin:"0 auto", padding:"8px 24px 40px", width:"100%", boxSizing:"border-box" }}>
          {/* v142: scrollbar skjult som default, gull-tynet ved hover. Strategi:
              track/gutter holdes konstant 6px (når scroll finnes) og thumb er
              TRANSPARENT som default → gull ved hover. Dette unngår innholds-
              jumping (bredden endres aldri) OG lar thumb-fargen fade. Firefox
              styres via scrollbar-color (transparent→gull) i samme klasse. */}
          <style dangerouslySetInnerHTML={{ __html: `
            .vl-sidebar-scroll, .vl-sidebar-scroll * { scrollbar-width: thin; scrollbar-color: transparent transparent; }
            .vl-sidebar-scroll:hover, .vl-sidebar-scroll:hover * { scrollbar-color: rgba(201,168,76,0.4) transparent; }
            .vl-sidebar-scroll::-webkit-scrollbar, .vl-sidebar-scroll *::-webkit-scrollbar { width:6px; height:6px; }
            .vl-sidebar-scroll::-webkit-scrollbar-track, .vl-sidebar-scroll *::-webkit-scrollbar-track { background:transparent; }
            .vl-sidebar-scroll::-webkit-scrollbar-thumb, .vl-sidebar-scroll *::-webkit-scrollbar-thumb { background:transparent; border-radius:3px; transition:background 0.2s; }
            .vl-sidebar-scroll:hover::-webkit-scrollbar-thumb, .vl-sidebar-scroll:hover *::-webkit-scrollbar-thumb { background:rgba(201,168,76,0.4); }
            .vl-sidebar-scroll:hover::-webkit-scrollbar-thumb:hover, .vl-sidebar-scroll:hover *::-webkit-scrollbar-thumb:hover { background:rgba(201,168,76,0.7); }
          ` }} />
          <InfoButton/>
          {/* v307: gullrammen omslutter NÅ ALT spill-innhold — tittel, uke/dato,
              ticker, banner-statene OG boksene/premiene. Åpnes her, lukkes etter
              spill-innhold-gaten (rett før «Siste vinnere»). */}
          <div style={GULL_RAMME_YTRE}>
           <div style={GULL_RAMME_INDRE}>
          {/* HEADER (uke + tittel) sentrert over hele 1280-bredden. */}
          {/* v313: komprimert topp — mindre vertikal padding (12/8 → 6/4),
              tettere uke-label (margin/font) og tittel-multiplikator 1.7 → 1.5.
              Henter inn høyden som den kompakte nedtellings-pillen legger til,
              så HELE gullrammen får plass på en 1080p-skjerm med nedtelling
              synlig. Strammingen gjelder ALLTID (ikke betinget av nedtelling)
              så toppen ser lik ut med og uten pille — ingen layout-hopp ved
              trekningsstart. */}
          <header style={{ ...S.shopHeader, padding:"6px 20px 4px", borderBottom:"none", cursor:"pointer", userSelect:"none" }} onClick={onLogoClick}>
            {lotteryState?.weekLabel && <p style={{
              margin:"-2px 0 4px", fontSize:10, color:"#fff",
              letterSpacing:"0.25em", textTransform:"uppercase", textAlign:"center",
              fontWeight:"600", textShadow:"0 0 12px rgba(255,255,255,0.3)",
              fontFamily:"'Inter', system-ui, -apple-system, sans-serif",
              fontVariantNumeric:"lining-nums tabular-nums",
            }}>{lotteryState.weekLabel}</p>}
            {renderTittel(1.5)}
          </header>
          {/* v312: kompakt nedtelling mellom tittel og ticker (kun PC). Lav pille
              som ikke forskyver kjøp-panel/premier — erstatter den fulle banner-
              boksen som tidligere lå i midtkolonnen over kjøp-panelet. */}
          {lotteryState?.drawTime && status==='idle' && (
            <Countdown targetTime={lotteryState.drawTime} compact/>
          )}
          {/* Ticker rett under tittelen — borderless.
              v130: økt marginTop 6→28 så tittel og ticker puster mer.
              v133: begrenset til maxWidth 760 (matcher banner-bredden) og
              sentrert, så ticker ikke spenner over hele 1500px-containeren. */}
          {/* v304: ticker like bred som «Kjøp lodd» (586px = 3 premiebokser+gap). */}
          {/* v313: marginTop 28 → 14 — del av topp-komprimeringen. */}
          <div style={{ width:"100%", maxWidth:586, marginTop:14, marginLeft:"auto", marginRight:"auto" }}>
            <Ticker message={lotteryState?.tickerMessage||""} borderless={true} fontSize={17}/>
          </div>

          {/* v310: hovedinnhold skyves ned i rammens høyde (marginTop:auto). */}
          <div style={RAMME_MAIN}>
          {/* v60 FIX 1: "Trekning er avsluttet"-banner sentrert i hovedinnholdet. */}
          {isDone && (
            <div style={{
              margin:"22px auto 8px", maxWidth:760, padding:"28px 32px",
              background:"linear-gradient(135deg,#1f1300 0%,#160d00 100%)",
              border:"1px solid #C9A84C", borderRadius:10,
              textAlign:"center", boxShadow:"0 4px 18px rgba(201,168,76,0.18)",
            }}>
              <p style={{ margin:0, fontSize:24, fontWeight:700, color:"#C9A84C", letterSpacing:"0.08em" }}>🏆 Trekning er avsluttet</p>
              <p style={{ margin:"10px 0 0", fontSize:14, color:"#aaa", lineHeight:1.5 }}>
                {lotteryState?.saleOpenTime
                  ? `Ny runde åpner ${new Date(lotteryState.saleOpenTime).toLocaleString("nb-NO",{ weekday:"long",day:"numeric",month:"long",hour:"2-digit",minute:"2-digit" })}${lotteryState?.repeatWeekly?" (ukentlig)":""}`
                  : "Ny runde åpner etter at arrangøren starter nytt loddsalg"}
              </p>
            </div>
          )}

          {/* Salgs-stengt-banner (ikke-done) sentrert i hovedinnholdet. */}
          {/* v209 B: trekning nært forestående → blikkfang (fjerner "avsluttet"-flashen) */}
          {drawImminent && (
            <DrawIntroBlikkfang variant="imminent" overdue={drawOverdue}
              lotteryName={settings?.lotteryName || ""}
              weekLabel={lotteryState?.weekLabel || ""}
              prizeCount={(prizes?.length) || 0}
              totalPrizes={(prizes?.length) || 0}
              ticketCount={(participants||[]).reduce((s,p)=>s+(p.ticketNumbers?.length||0),0)}
              introKey={lotteryState?.drawTime || 0}
              participantCount={new Set((participants||[]).map(p=>(p.email||p.name||p.id||"").toLowerCase())).size}/>
          )}
          {/* v320: betryggende ventemelding i stedet for falskt "avsluttet"-glimt
              når drawTime er passert uten valgte premier (PC-gren). */}
          {venterPaaPremier && (
            <div style={{
              margin:"22px auto 8px", maxWidth:660, padding:"22px 28px",
              background:"#0d1a0d", border:"1px solid #2a5a2a", borderRadius:8,
              textAlign:"center",
            }}>
              <p style={{ margin:0, fontSize:18, fontWeight:600, color:"#4caf50" }}>
                ⏳ Venter på premier
              </p>
              <p style={{ margin:"8px 0 0", fontSize:13, color:"#aaa", lineHeight:1.5 }}>
                Trekningen starter så snart arrangøren har lagt inn premiene.
                Loddene dine er trygge og gjelder fortsatt.
              </p>
            </div>
          )}
          {showCenteredLockedBanner && !drawImminent && !venterPaaPremier && (
            <div style={{
              margin:"22px auto 8px", maxWidth:660, padding:"22px 28px",
              background:"#1a0d00", border:"1px solid #8B4500", borderRadius:8,
              textAlign:"center",
            }}>
              <p style={{ margin:0, fontSize:18, fontWeight:600, color:"#C9A84C" }}>
                {lotteryState?.salgStengt && lotteryState?.lastClosedByAvlys
                  ? AVLYS_TITTEL
                  : lotteryState?.salgStengt ? "🔒 Loddsalget er ikke åpent ennå" : "🔒 Loddsalget er avsluttet"}
              </p>
              <p style={{ margin:"8px 0 0", fontSize:13, color:"#aaa", lineHeight:1.5 }}>
                {lotteryState?.salgStengt && lotteryState?.lastClosedByAvlys
                  ? avlysForklaringFor(lotteryState?.avlysReason)
                  : lotteryState?.salgStengt && lotteryState?.saleOpenTime
                    ? `Salget åpner ${new Date(lotteryState.saleOpenTime).toLocaleString("nb-NO",{ weekday:"long",day:"numeric",month:"long",hour:"2-digit",minute:"2-digit" })}${lotteryState?.repeatWeekly?" (ukentlig)":""}`
                    : lotteryState?.drawTime
                      ? `⏱ Trekning: ${new Date(lotteryState.drawTime).toLocaleString("nb-NO",{weekday:"long",day:"numeric",month:"long",hour:"2-digit",minute:"2-digit"})}`
                      : "Arrangøren har ikke fastsatt trekningstidspunkt ennå"}
              </p>
              {lotteryState?.salgStengt && lotteryState?.lastClosedByAvlys && (
                <p style={{ margin:"10px 0 0", fontSize:12, color:"#888", textAlign:"center" }}>{AVLYS_VENTER}</p>
              )}
            </div>
          )}

          {/* v67: 3-kolonners grid med symmetriske 280 px sidekol-er. Sidebar-
              toppene starter rett under ticker (logo/topbilde/countdown/
              noPrizes er flyttet inn i MIDTEN-kolonnen), så Dine lodd og
              Loddkjøpere er optisk balansert. Hovedinnhold = midten flyter
              automatisk midt på skjermen siden sidekol-ene er like brede. */}
          {/* v136: hele 3-kolonne-grid (sidebars + kjøp-panel + premier) skjules
              ved avlysning — paritet med mobil. "Siste vinnere" rendres separat
              UNDER grid-en (utenfor, så grid-auto-placement ikke brytes). */}
          {!isDone && !lotteryState?.lastClosedByAvlys && (
            <>
            <div style={{
              display:"grid",
              // v304: rett inn topp-raden mot 5-premie-rutenettet under.
              // Premie-rad = 5×186 + 4×14 = 986px. Topp-rad speiler den:
              //   [186px Dine lodd][14 gap][586px Kjøp lodd][14 gap][186px Loddkjøpere]
              //   = 186 + 14 + 586 + 14 + 186 = 986px.
              // Venstre boks ligger over premie 1, kjøp-panel over premie 2–4,
              // høyre boks over premie 5. SIDEBAR_W = 186 (én premieboks).
              gridTemplateColumns:`${SIDEBAR_W}px 586px ${SIDEBAR_W}px`,
              gap:14, alignItems:"start", marginTop:0,
              maxWidth:986, marginLeft:"auto", marginRight:"auto",
            }}>
              {/* VENSTRE — Dine lodd. v305: sticky fjernet — fast PANEL_H, flukter
                  med høyre boks og premie-raden (sticky løsnet boksen fra raden). */}
              <div style={{ alignSelf:"start" }}>
                {(() => {
                  // v67 FIX 4: fast 280 px boks. Lodd-kolonner (1-4) skaleres
                  // basert på antall: 12 lodd per kolonne, så nye kolonner.
                  const loddCount = displayTicketNumbers.length;
                  // v140: fast pille-størrelse (60px) uansett antall lodd. Boksen
                  // vokser/scroller i stedet (v139 overflowY:auto). 2 piller per
                  // rad i 160px-sidebar: 60×2 + 6 gap = 126 < ~136px innhold.
                  const PILL_W = 60;
                  const PILL_FS = 13;
                  // v307: lavere vertikal padding (8→5) krymper KUN pille-høyden
                  // (tallstørrelsen PILL_FS er uendret). v311: PANEL_H krympet →
                  // ~11 rader × 2 = 22 lodd vises uten pil-ned.
                  const PILL_PADY = 5;
                  const COL_GAP = 6;
                  return (
                    // v69 FIX 3: grønn-tonet bakgrunn + gull-ramme — konsistent
                    // med DINE LODD-listen på bekreftelses-siden.
                    // v305: fast PANEL_H (lik de andre topp-boksene); pille-listen
                    // scroller internt med pil-indikator via ScrollHintBox.
                    <div style={{
                      width:"100%",
                      height: PANEL_H,
                      background:"rgba(46,125,50,0.12)",
                      border:"1px solid #3a2800", borderRadius:8,
                      padding:`${PANEL_PAD}px 12px`, display:"flex", flexDirection:"column",
                      boxSizing:"border-box",
                    }}>
                      <p style={{ margin:"0 0 10px", fontSize:10, color:"#a89058", letterSpacing:"0.14em", textTransform:"uppercase", fontWeight:700, textAlign:"center" }}>🎟 Dine lodd ({loddCount})</p>
                      {loddCount === 0 ? (
                        <span style={{ fontSize:11, color:"#555", fontStyle:"italic", textAlign:"center", marginTop:10 }}>Ingen lodd ennå</span>
                      ) : (
                        <PagedList
                          items={displayTicketNumbers}
                          perPage={22}
                          gridStyle={{
                            // v141: radvis fyll (var kolonnevis). 2 faste kolonner.
                            display:"grid",
                            gridTemplateColumns:`repeat(2, ${PILL_W}px)`,
                            gridAutoFlow:"row",
                            rowGap:4, columnGap:COL_GAP,
                            justifyContent:"center",
                            alignContent:"start",
                          }}
                          renderItem={(n) => (
                            <span key={n} style={{
                              fontSize:PILL_FS, color:"#f5efe0", fontWeight:600,
                              padding:`${PILL_PADY}px 4px`, textAlign:"center",
                              background:"#0a1a0a", border:"1px solid #1a3a1a",
                              borderRadius:3, fontVariantNumeric:"tabular-nums",
                              boxSizing:"border-box", whiteSpace:"nowrap",
                            }}>#{n}</span>
                          )}
                        />
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* MIDTEN — Logo, topbilde, countdown, noPrizes, kjøp-panel, premier */}
              <div style={{ minWidth:0 }}>
                {settings?.logoUrl && !settings?.skjulLogo && (
                  <div style={{ width:"100%", textAlign:"center", padding:"0 0 8px" }}>
                    <img src={settings.logoUrl} alt="Logo"
                      style={{
                        height: settings?.logoHoyde || 80,
                        width: "auto", maxWidth: "100%",
                        display: "inline-block",
                        transform: `translate(${settings?.logoPosX||0}%, ${settings?.logoPosY||0}%) scale(${(settings?.logoSkala||100)/100})`,
                        transformOrigin: "center",
                      }}/>
                  </div>
                )}
                {settings?.headerImageUrl && !settings?.skjulTopbilde && (
                  <div style={{ width:"100%", textAlign:"center", overflow:"hidden", marginBottom:10 }}>
                    <img src={settings.headerImageUrl} alt=""
                      style={{
                        height: settings?.topBildeHoyde || 180,
                        width: "auto", maxWidth: "100%",
                        display: "inline-block",
                        transform: `translate(${settings?.topbildePosX||0}%, ${settings?.topbildePosY||0}%) scale(${(settings?.topbildeSkala||100)/100})`,
                        transformOrigin: "center",
                      }}/>
                  </div>
                )}
                {/* v312: nedtellingen er flyttet OPP mellom tittel og ticker
                    (kompakt pille) så den ikke skyver kjøp-panel/premier ned. */}
                {!lotteryState?.lastClosedByAvlys && synligePremier.length===0 && status==="idle" && (
                  <div style={{ margin:"12px auto 8px", maxWidth:560, padding:"14px 18px", background:"#1a1200", border:"1px solid #3a2800", borderRadius:8, textAlign:"center" }}>
                    <p style={{ margin:"0 0 8px", fontSize:13, color:"#8a6a00" }}>⏳ Premier for denne trekningen er foreløpig ikke publisert</p>
                    <p style={{ margin:0, fontSize:11, color:"#555" }}>Arrangøren publiserer premier innen trekning starter</p>
                  </div>
                )}
                {!salesLocked && (
                  /* v304: kjøp-panel fyller 586px-kolonnen (3 premiebokser+gap).
                     v306: minHeight PANEL_H + flex-kolonne med space-between —
                     feltene beholder størrelsen, men fordeles med luft over hele
                     høyden i stedet for å klumpe seg øverst. */
                  <div style={{ ...S.buyPanel, padding:18, margin:"0 auto 14px", maxWidth:"100%", minHeight:PANEL_H, boxSizing:"border-box", display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
                    <p style={S.buyPanelTitle}>Kjøp lodd</p>
                    {/* v71 FIX 1: Navn + mobilnummer side om side (50/50).
                        E-post fortsatt full bredde under. */}
                    <div style={{ display:"flex", gap:8 }}>
                      <input style={{ ...S.input, color:"#e8e0d0", flex:1, minWidth:0 }} placeholder="Ditt navn" value={name} onChange={e=>handleNameChange(e.target.value)}/>
                      <input style={{ ...S.input, color:"#e8e0d0", flex:1, minWidth:0 }} placeholder="Ditt mobilnummer" type="tel" inputMode="numeric" pattern="[0-9 +]*" value={phone} onChange={e=>handlePhoneChange(e.target.value)}/>
                    </div>
                    <input style={{ ...S.input, color:"#e8e0d0" }} placeholder="Din e-post" type="email" value={email} onChange={e=>handleEmailChange(e.target.value)}/>
                    {/* v75: position:relative-wrapper omslutter NÅ både Husk-
                        toggle-raden OG antall-seksjonen, slik at QR-en med
                        top:0 right:0 mekanisk lander på samme høyde som
                        toppen av Husk-toggelen — ingen pixel-gjetting. */}
                    <div style={{ position:"relative" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                        <div onClick={handleRememberToggle}
                          style={{ width:36, height:20, borderRadius:10, background:rememberMe?"#C9A84C":"#2a2a2a", cursor:"pointer", position:"relative", transition:"background 0.2s", flexShrink:0 }}>
                          <div style={{ position:"absolute", top:2, left:rememberMe?18:2, width:16, height:16, borderRadius:"50%", background:"#fff", transition:"left 0.2s" }}/>
                        </div>
                        <span style={{ fontSize:11, color:"#999", cursor:"pointer", userSelect:"none" }} onClick={handleRememberToggle}>
                          Husk navn, mobil og e-post til neste gang
                        </span>
                      </div>
                      <p style={{ ...S.buyLabel, color:"#aaa" }}>Antall lodd à {ticketPrice},- (maks {maxPerPlayer} per person)</p>
                      {kreditt>0 && (
                        <div style={{ background:"#0d2a0d", border:"1px solid #1a5a1a", borderRadius:6, padding:"6px 12px", marginBottom:8, textAlign:"center" }}>
                          <span style={{ fontSize:12, color:"#4caf50", fontWeight:"bold" }}>🎟 Tilgodehavende: {kreditt},- (trekkes automatisk ved kjøp)</span>
                        </div>
                      )}
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginBottom:8 }}>
                        <button style={S.qtyBtn} onClick={()=>setQty(Math.max(1,qty-1))}>−</button>
                        <span style={S.qtyNum}>{qty}</span>
                        <button style={S.qtyBtn} onClick={()=>setQty(Math.min(maxPerPlayer,qty+1))}>+</button>
                      </div>
                      <div style={{ display:"flex", gap:6, marginBottom:12, justifyContent:"center" }}>
                        {[1,3,5,10,20].filter(n=>n<=maxPerPlayer).map(n=>(
                          <button key={n} style={{ ...S.qtyPreset, ...(qty===n?S.qtyActive:{}) }} onClick={()=>setQty(n)}>{n}</button>
                        ))}
                      </div>
                      {/* v147: QR-kode fjernet fra kjøps-vinduet — den klemte ut
                          tilgodehavende-banneret og er overflødig her (vises på
                          kvitteringssiden etter registrering). */}
                    </div>
                    {qty*ticketPrice >= 100 && (
                      <div style={{ background:"#1a1200", border:"1px solid #8a6500", borderRadius:4, padding:"8px 12px", marginBottom:8, fontSize:12, color:"#C9A84C" }}>
                        💰 Du er i ferd med å kjøpe {qty} lodd for <strong>{qty*ticketPrice},-</strong>
                      </div>
                    )}
                    {/* v137: 3-linjers knapp. v138: kun 2 linjer på PC —
                        kopierings-hinten ga ikke mening her siden PC bruker
                        QR-kode (mobil-knappen beholder alle 3 linjer). */}
                    <button
                      style={{ ...S.vippsBtn, flexDirection:"column", gap:3, paddingTop:10, paddingBottom:10 }}
                      onClick={handleBuy} disabled={saving}>
                      <span style={{ fontSize:15, fontWeight:"bold", color:"#fff" }}>
                        {saving ? "Registrerer…" : `Registrer ${qty} lodd — ${qty*ticketPrice},-`}
                      </span>
                      {settings?.vippsNumber && !saving && (
                        <span style={{ fontSize:14, fontWeight:"normal", color:"#fff" }}>
                          Vipps: {settings.vippsNumber}
                        </span>
                      )}
                    </button>
                  </div>
                )}
                {/* v302 (PC-spillside steg 1): «Ukens premier» er flyttet UT av
                    midt-kolonnen til en egen full-bredde-blokk under grid-en (se
                    nedenfor). Midt-kolonnen klemte raden til ~684px og tillot kun
                    ~3 kort; den nye blokken har fast 5-kolonners layout. */}
              </div>

              {/* HØYRE — Loddkjøpere. v305: fast PANEL_H (lik de to andre topp-
                  boksene), dimensjonert til 20 rader uten scroll; ScrollHintBox
                  gir intern scroll + pil-indikator ved flere. */}
              <div style={{
                alignSelf:"start", minWidth:0,
                width:"100%",
                height: PANEL_H,
                background:"#101010", border:"1px solid #2a2a2a", borderRadius:8,
                padding:`${PANEL_PAD}px 12px`,
                display:"flex", flexDirection:"column", boxSizing:"border-box",
              }}>
                <p style={{ margin:"0 0 10px", fontSize:10, color:"#C9A84C", letterSpacing:"0.14em", textTransform:"uppercase", fontWeight:700 }}>🎟 Loddkjøpere</p>
                {deltakerList.length === 0 ? (
                  <p style={{ margin:0, fontSize:11, color:"#666" }}>Ingen har kjøpt lodd ennå.</p>
                ) : (
                  <PagedList
                    items={deltakerList}
                    perPage={15}
                    gridStyle={{
                      display:"grid",
                      gridTemplateColumns:"minmax(0, 1fr)",
                      gridAutoFlow:"row",
                      rowGap:2,
                      alignContent:"start",
                    }}
                    renderItem={(d, gi) => (
                      <div key={gi} style={{
                        display:"flex", alignItems:"baseline", gap:4,
                        padding:"3px 0", minWidth:0,
                        borderBottom:"1px solid #1f1f1f",
                      }}>
                        <span style={{ fontSize:10, color:"#666", minWidth:18, fontVariantNumeric:"tabular-nums", whiteSpace:"nowrap" }}>{gi+1}.</span>
                        <span style={{ fontSize:11, color:"#ddd", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:"1 1 auto", minWidth:0 }}>{d.name}</span>
                        <span style={{ fontSize:10, color:"#C9A84C", fontWeight:600, whiteSpace:"nowrap" }}>{d.tickets}</span>
                      </div>
                    )}
                  />
                )}
              </div>
            </div>

            {/* v306: «UKENS PREMIER» — nå INNE i gullrammen, under topp-raden.
                Opptil 5 premier midtstilt, ingen tomme plasser; piler ved 6+. */}
            {synligePremier.length>0 && (() => {
            const bottleH = Math.max(140, Math.round((settings?.flaskeHoyde||160) * 0.85));
            const bottleW = Math.max(93,  Math.round((settings?.bildeBredde||80)  * 0.85));
            const sortert = [...synligePremier].sort((a,b)=>(parseInt(b.price)||0)-(parseInt(a.price)||0));
            const CARD_W = 186;
            const CARD_MINH = bottleH + 96; // felles min-høyde → jevn underkant
            // v305 (steg 2): vindu på maks 5 med start-indeks. 6+ → piler ±1.
            const total = sortert.length;
            const maxStart = Math.max(0, total - 5);
            const effStart = Math.min(prizeStart, maxStart);
            const vindu = sortert.slice(effStart, effStart + 5);
            const harPiler = total > 5;
            const canPrev = effStart > 0;
            const canNext = effStart < maxStart;
            const pilStyle = (enabled) => ({
              flexShrink:0, width:38, height:38, alignSelf:"center",
              borderRadius:"50%", cursor: enabled ? "pointer" : "default",
              background: enabled ? "rgba(201,168,76,0.12)" : "transparent",
              border:`1px solid ${enabled ? "#C9A84C" : "#2a2a2a"}`,
              color: enabled ? "#C9A84C" : "#3a3a3a",
              fontSize:18, fontWeight:"bold", lineHeight:1,
              display:"flex", alignItems:"center", justifyContent:"center",
              transition:"all 0.15s",
              // Reserver pil-plassen (skjult) ved ≤5 så raden ikke hopper.
              visibility: harPiler ? "visible" : "hidden",
            });
            return (
              <div style={{ maxWidth:1120, margin:"4px auto 0", width:"100%", boxSizing:"border-box" }}>
                <p style={{ ...S.sectionLabel, fontSize:24, color:"#b8943c", letterSpacing:"0.16em", textAlign:"center", marginBottom:14 }}>✦ UKENS PREMIER ✦</p>
                {/* v305: piler (◄ ►) ved 6+ scroller ett kort av gangen; selve
                    kort-raden er midtstilt. Pil-plassen reserveres (visibility:
                    hidden) ved ≤5 så raden ikke forskyves. */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
                  <button type="button" aria-label="Forrige premie" style={pilStyle(canPrev)}
                    disabled={!canPrev} onClick={()=>setPrizeStart(Math.max(0, effStart-1))}>◄</button>
                  <div style={{
                    display:"flex", flexWrap:"nowrap", justifyContent:"center",
                    alignItems:"stretch", gap:14,
                  }}>
                  {vindu.map((p) => {
                    return (
                      <div key={p.id} style={{
                        flex:`0 0 ${CARD_W}px`, width:CARD_W,
                        background: cardBg,
                        border: `1px solid ${isDarkCard?"#3a3a3a":"#e8e8e8"}`,
                        borderRadius: 8, padding: "13px 9px",
                        display: "flex", flexDirection: "column", alignItems: "center",
                        textAlign: "center", gap: 6, boxSizing: "border-box",
                        minHeight: CARD_MINH,
                      }}>
                        <div style={{ height:bottleH, width:bottleW, overflow:"hidden",
                          background:"radial-gradient(ellipse at center, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.18) 100%)",
                          borderRadius:4, display:"flex", alignItems:"center", justifyContent:"center",
                          position:"relative" }}>
                          {p.imageUrl
                            ? <img src={p.imageUrl} alt={p.name} style={{ width:"100%", height:"100%", objectFit:"contain" }}/>
                            : <span style={{ fontSize:36 }}>🍷</span>}
                          {p.price && (
                            <span style={{
                              position:"absolute", bottom:4, left:"50%",
                              transform:"translateX(-50%)",
                              background:"rgba(0,0,0,0.78)", color:"#d4af37",
                              fontSize:11, fontWeight:"bold",
                              padding:"2px 8px", borderRadius:10,
                              border:"1px solid rgba(212,175,55,0.4)",
                              whiteSpace:"nowrap", letterSpacing:"0.02em",
                              boxShadow:"0 1px 4px rgba(0,0,0,0.5)",
                            }}>{p.price},-</span>
                          )}
                        </div>
                        {p.url ? (
                          <a href={p.url} target="_blank" rel="noreferrer"
                            onClick={e=>e.stopPropagation()}
                            style={{ margin:"2px 0 0", fontSize:14, fontWeight:"bold", color:txtName, lineHeight:1.25, textDecoration:"none", cursor:"pointer", display:"block" }}
                            onMouseEnter={e=>{ e.currentTarget.style.textDecoration="underline"; }}
                            onMouseLeave={e=>{ e.currentTarget.style.textDecoration="none"; }}>{p.name}</a>
                        ) : (
                          <p style={{ margin:"2px 0 0", fontSize:14, fontWeight:"bold", color:txtName, lineHeight:1.25 }}>{p.name}</p>
                        )}
                        {p.country && <p style={{ margin:0, fontSize:11, color:txtMeta }}>🌍 {p.country}</p>}
                        {p.description && <p style={{ margin:"2px 0 0", fontSize:11, color:txtDesc, lineHeight:1.45 }}>{p.description}</p>}
                      </div>
                    );
                  })}
                  </div>
                  <button type="button" aria-label="Neste premie" style={pilStyle(canNext)}
                    disabled={!canNext} onClick={()=>setPrizeStart(Math.min(maxStart, effStart+1))}>►</button>
                </div>
              </div>
            );
          })()}
            </>
          )}

          {/* v308: «Siste vinnere» (avlyst) ligger nå INNENFOR gullrammen — den
              lå utenfor i v307, så rammen favnet ikke alt i avlyst-tilstand.
              Ramme-lukkingen er flyttet til ETTER denne blokken. */}
          {/* v136: "Siste vinnere" ved avlysning — flyttet UT av grid-en (var i
              MIDTEN i v135) så grid-gating ikke skjuler den. Tittel matcher
              mobil-stil (v125): #d4af37, letterSpacing 0.1em, weight 600 —
              oppskalert font på PC (18/13 vs mobil 14/12). Hopp over hvis ingen
              tidligere vinnere (sisteVinnerRunde===null). */}
          {!isDone && lotteryState?.lastClosedByAvlys && sisteVinnerRunde && (() => {
            const vBg = settings?.vinnerKortFarge || "#ffffff";
            const vDark = _hexLum(vBg) < 0.5;
            const vWinners = sisteVinnerRunde.winners || [];
            return (
              <div style={{ maxWidth:1040, width:"100%", marginTop:"auto", marginLeft:"auto", marginRight:"auto", marginBottom:0 }}>
                <h3 style={{ textAlign:"center", margin:"0 0 2px", color:"#d4af37", fontSize:18, letterSpacing:"0.1em", fontWeight:600 }}>🏆 SISTE VINNERE</h3>
                <p style={{ textAlign:"center", margin:"0 0 16px", color:"#888", fontSize:13 }}>fra {sisteVinnerRunde.weekLabel}</p>
                <div style={{
                  display:"grid",
                  gridTemplateColumns: vWinners.length === 1 ? "minmax(0, 400px)" : `repeat(${Math.min(vWinners.length, 3)}, 280px)`,
                  gap:20, justifyContent:"center", maxWidth:"100%", margin:"0 auto",
                }}>
                  {vWinners.map((w,i)=>{
                    const img = prizes.find(p=>p.name===w.prize)?.imageUrl;
                    return (
                      <div key={i} style={{
                        background:vBg,
                        border:`1px solid ${vDark?"#3a3a3a":"#e0e0e0"}`,
                        borderRadius:10, padding:"22px 16px 18px",
                        display:"flex", flexDirection:"column", alignItems:"center",
                        textAlign:"center", gap:12, boxSizing:"border-box",
                      }}>
                        <div style={{
                          width:120, height:160, borderRadius:6,
                          background:"radial-gradient(ellipse at center, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.18) 100%)",
                          display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden",
                        }}>
                          {img ? <img src={img} alt={w.prize} style={{ width:"100%", height:"100%", objectFit:"contain" }}/> : <span style={{ fontSize:52 }}>🍷</span>}
                        </div>
                        <p style={{ margin:0, fontSize:20, fontWeight:"bold", color:vDark?"#f0f0f0":"#111", lineHeight:1.2 }}>{w.name}</p>
                        <p style={{ margin:0, fontSize:14, color:vDark?"#d4af37":"#8B6914", lineHeight:1.35 }}>{w.prize}</p>
                        {w.ticketNum && <p style={{ margin:0, fontSize:12, color:vDark?"#aaa":"#888" }}>lodd #{w.ticketNum}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* v309: done-vinnerne («Siste treknings vinnere») rendres nå INNE i
              gullrammen. Lå før som søsken-komponent i App (WinnerViewWithScroll)
              → havnet utenfor rammen i done-tilstand. */}
          {isDone && (
            <div style={{ marginTop:"auto", width:"100%" }}>
              <WinnerViewWithScroll lotteryState={lotteryState} prizes={prizes} settings={settings} scrollNow={scrollToWinners} pcLayout={true} participants={participants}/>
            </div>
          )}
          </div>{/* v310: slutt RAMME_MAIN */}
           </div>
          </div>
        </div>

        {/* v147: forstørr-QR-modal fjernet — den ble kun åpnet fra QR-en i
            kjøps-vinduet, som nå er fjernet. */}
      </>
    );
  }

  return (
    // v93 Fix 2: flex-column-lock på mobil-spillside. Hele mobil-vyen er
    // {viewport-height - arrangør-topbar} høy. Topp-seksjonen (header +
    // ticker) er flexShrink:0 → låst øverst. Midt-seksjonen (premier +
    // drawer-knapper) er flex:1 + overflow-y:auto → eneste som scroller.
    // Bunn-seksjonen (CTA-bar) er flexShrink:0 → låst nederst. Tidligere
    // brukte vi position:sticky som hadde edge-cases på iOS Safari ved
    // address-bar-collapse — flex-column-lock fjerner dem.
    <div style={{
      ...S.shopView,
      padding:"0 16px",
      height:`calc(100dvh - ${stickyTopOffset}px)`,
      display:"flex", flexDirection:"column",
      overflow:"hidden",
    }}>
      <InfoButton/>
      {/* TOPP — låst øverst i flex-column. */}
      <div style={{
        flexShrink:0,
        background: settings?.bakgrunnsFarge || "#0a0a0a",
        paddingTop: stickyTopOffset > 0 ? 0 : "env(safe-area-inset-top, 0px)",
        borderBottom:"none",
        boxShadow:"0 4px 14px rgba(0,0,0,0.45)",
        marginLeft:-16, marginRight:-16,
        paddingLeft:16, paddingRight:16,
      }}>
      <header style={{ ...S.shopHeader, padding:"12px 20px 8px", cursor:"pointer", userSelect:"none", borderBottom:"none" }} onClick={onLogoClick}>
        {lotteryState?.weekLabel&&<p style={{
          margin:"-4px 0 2px",
          fontSize:10,
          color:"#fff",
          letterSpacing:"0.25em",
          textTransform:"uppercase",
          textAlign:"center",
          fontWeight:"600",
          textShadow:"0 0 12px rgba(255,255,255,0.3)",
          fontFamily:"'Inter', system-ui, -apple-system, sans-serif",
          fontVariantNumeric:"lining-nums tabular-nums"
        }}>{lotteryState.weekLabel}</p>}
        {(() => {
          // v159: tegn-basert auto-skalering — lange titler krympes så de
          // holder seg ~2 linjer i den 100dvh-låste TOPP-seksjonen, slik at
          // de ikke skyver MIDTRE/drawer-knappene ut av viewport. Korte titler
          // beholder arrangørens valgte tittelStorrelse.
          const linje1 = settings?.tittelLinje1 || settings?.lotteryName || "Vinloddet";
          const linje2 = settings?.tittelLinje2 || "";
          const rawSz = settings?.tittelStorrelse || 26;
          const titleLen = (linje1 || "").length;
          const usableW = (typeof window !== "undefined" ? window.innerWidth : 360) - 32;
          const fitSz = (2 * usableW) / (0.55 * Math.max(titleLen, 1)); // Playfair ≈ 0.55·fontSize/tegn
          const sz = Math.max(15, Math.min(rawSz, fitSz));
          const align = settings?.tittelJustering==="venstre"?"left":settings?.tittelJustering==="høyre"?"right":"center";
          const baseStyle = {
            margin:0, fontSize:sz, fontWeight:"700", letterSpacing:"0.06em",
            // v55 FIX 5: text-transform:uppercase fjernet fra spillsiden —
            // tittelen rendres med arrangørens faktiske store/små bokstaver.
            fontFamily: settings?.tittelFont || "'Playfair Display',Georgia,serif",
            textAlign:align, lineHeight:1.15,
          };
          // v55 FIX 1: bruker de delte, profesjonaliserte effekt-definisjonene.
          const effGrads = EFFEKT_GRADIENTS;
          const effShadows = EFFEKT_SHADOW;
          const eff = settings?.tittelEffekt||"gull";
          const erGrad = eff !== "ingen" && effGrads[eff];
          const erShad = effShadows[eff];
          const gradStyle = { ...baseStyle, color:"transparent",
            backgroundImage: effGrads[eff]||effGrads["gull"],
            backgroundClip:"text", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
            filter: EFFEKT_FILTER[eff],
          };
          const shadowStyle = { ...baseStyle, color:settings?.tittelFarge||"#C9A84C", textShadow:effShadows[eff] };
          const plainStyle = { ...baseStyle, color:settings?.tittelFarge||"#C9A84C" };
          const relieffStyle = { ...baseStyle, color:settings?.tittelFarge||"#C9A84C", WebkitTextFillColor:settings?.tittelFarge||"#C9A84C",
            textShadow:EFFEKT_RELIEFF };
          const outlinePx = settings?.tittelOutline||0;
          const outlineFarge = settings?.tittelOutlineFarge||"#ffffff";
          const baseWithOutline = outlinePx > 0 ? { WebkitTextStroke:`${outlinePx}px ${outlineFarge}` } : {};
          const st = settings?.tittelEffekt==="relieff" ? { ...relieffStyle, ...baseWithOutline }
                   : erGrad ? { ...gradStyle, ...baseWithOutline }
                   : erShad ? { ...shadowStyle, ...baseWithOutline }
                   : { ...plainStyle, ...baseWithOutline };
          return (<>
            <h1 style={st}>{linje1}</h1>
            {linje2&&<h1 style={{ ...st,marginTop:2 }}>{linje2}</h1>}
          </>);
        })()}
      </header>
      {/* v55 FIX 2: logo/topbilde styres med proporsjonal skala (settings.
          logoSkala/topbildeSkala, 100=normal) + fri X/Y-forskyvning via CSS
          transform. Defaultene (100 / 0) gir uendret visning for eksisterende
          oppsett. overflow:hidden på wrapperen klipper ved skala > 100%. */}
      {settings?.logoUrl&&!settings?.skjulLogo&&(
        <div style={{ width:"100%",textAlign:"center",padding:"14px 0 0",overflow:"hidden" }}>
          <img src={settings.logoUrl} alt="Logo"
            style={{ maxHeight:80, width:"100%", objectFit:"contain", display:"block",
              transform:`translate(${settings?.logoPosX||0}%, ${settings?.logoPosY||0}%) scale(${(settings?.logoSkala||100)/100})`,
              transformOrigin:"center" }}/>
        </div>
      )}
      {settings?.headerImageUrl&&!settings?.skjulTopbilde&&(
        <div style={{ width:"100%", overflow:"hidden" }}>
          <img src={settings.headerImageUrl} alt=""
            style={{ width:"100%", maxHeight:180, objectFit:"cover", display:"block",
              transform:`translate(${settings?.topbildePosX||0}%, ${settings?.topbildePosY||0}%) scale(${(settings?.topbildeSkala||100)/100})`,
              transformOrigin:"center" }}/>
        </div>
      )}
      {/* v287: når tickeren er aktiv overlapper dens marginTop:-2 nedtellings-
          boksens bunnramme — gi boksen luft under KUN da. Ticker av → 0 (som før). */}
      {lotteryState?.drawTime&&status==='idle'&&(
        <div style={{ marginBottom: lotteryState?.tickerMessage ? 8 : 0 }}>
          <Countdown targetTime={lotteryState.drawTime}/>
        </div>
      )}
      {/* v161: -2px topp/bunn for å stramme ticker-footprint i TOPP. */}
      <div style={{ marginTop:-2, marginBottom:-2 }}><Ticker message={lotteryState?.tickerMessage||""}/></div>
      </div>{/* v93: lukker TOPP-seksjonen */}

      {/* v123: avlys/lukket-banner plassert HER (var tidligere etter premier).
          Brukeren ser den nå som første element under sticky-topp. */}
      {/* v209 B: trekning nært forestående → blikkfang (fjerner "avsluttet"-flashen, mobil-gren) */}
      {drawImminent && (
        <DrawIntroBlikkfang variant="imminent" overdue={drawOverdue}
          lotteryName={settings?.lotteryName || ""}
          weekLabel={lotteryState?.weekLabel || ""}
          prizeCount={(prizes?.length) || 0}
          totalPrizes={(prizes?.length) || 0}
          ticketCount={(participants||[]).reduce((s,p)=>s+(p.ticketNumbers?.length||0),0)}
          introKey={lotteryState?.drawTime || 0}
          participantCount={new Set((participants||[]).map(p=>(p.email||p.name||p.id||"").toLowerCase())).size}/>
      )}
      {/* v320: betryggende ventemelding i stedet for falskt "avsluttet"-glimt
          når drawTime er passert uten valgte premier (mobil-gren). */}
      {venterPaaPremier && (
        <div style={{ ...S.salesLockedBanner, background:"#0d1a0d", border:"1px solid #2a5a2a", color:"#4caf50" }}>
          ⏳ Venter på premier
          <p style={{ margin:"6px 0 0",fontSize:12,color:"#888" }}>
            Trekningen starter så snart arrangøren har lagt inn premiene.
            Loddene dine er trygge og gjelder fortsatt.
          </p>
        </div>
      )}
      {salesLocked && !drawImminent && !venterPaaPremier && (
        <div style={S.salesLockedBanner}>
          {status==="done"
            ? "🏆 Trekning er avsluttet"
            : lotteryState?.salgStengt && lotteryState?.lastClosedByAvlys
              ? AVLYS_TITTEL
              : lotteryState?.salgStengt ? "🔒 Loddsalget er ikke åpent ennå" : "🔒 Loddsalget er avsluttet"}
          <p style={{ margin:"6px 0 0",fontSize:12,color:"#888" }}>
            {status==="done"
              ? lotteryState?.saleOpenTime
                ? `Ny runde åpner ${new Date(lotteryState.saleOpenTime).toLocaleString("nb-NO",{ weekday:"long",day:"numeric",month:"long",hour:"2-digit",minute:"2-digit" })}${lotteryState?.repeatWeekly?" (ukentlig)":""}`
                : "Ny runde åpner etter at arrangøren starter nytt loddsalg"
              : lotteryState?.salgStengt && lotteryState?.lastClosedByAvlys
                ? avlysForklaringFor(lotteryState?.avlysReason)
                : lotteryState?.salgStengt && lotteryState?.saleOpenTime
                  ? `Salget åpner ${new Date(lotteryState.saleOpenTime).toLocaleString("nb-NO",{ weekday:"long",day:"numeric",month:"long",hour:"2-digit",minute:"2-digit" })}${lotteryState?.repeatWeekly?" (ukentlig)":""}`
                  : lotteryState?.drawTime
                    ? `⏱ Trekning: ${new Date(lotteryState.drawTime).toLocaleString("nb-NO",{weekday:"long",day:"numeric",month:"long",hour:"2-digit",minute:"2-digit"})}`
                    : "Arrangøren har ikke fastsatt trekningstidspunkt ennå"}
          </p>
          {lotteryState?.lastClosedByAvlys && (
            <p style={{ margin:"8px 0 0", fontSize:11, color:"#666", textAlign:"center" }}>
              {AVLYS_VENTER}
            </p>
          )}
        </div>
      )}

      {/* MIDTRE — INGEN scroll (v94 Fix 1). Premier beholder horisontal scroll
          innen sin rad; salesLockedBanner og drawer-knapper rendres uten
          vertikal scroll. Hvis innholdet ikke får plass i 100dvh blir det
          klippet, men siden er aldri scrollbar vertikalt. */}
      <div style={{
        flex:1, minHeight:0,
        overflow:"hidden",
        marginLeft:-16, marginRight:-16,
        paddingLeft:16, paddingRight:16,
        paddingBottom:8,
        display:"flex", flexDirection:"column",
      }}>

      {!lotteryState?.lastClosedByAvlys&&synligePremier.length===0&&status==="idle"&&(
        <div style={{ margin:"8px 16px",padding:"14px 16px",background:"#1a1200",border:"1px solid #3a2800",borderRadius:8,textAlign:"center" }}>
          <p style={{ margin:"0 0 8px",fontSize:13,color:"#8a6a00" }}>⏳ Premier for denne trekningen er foreløpig ikke publisert</p>
          <p style={{ margin:"0 0 10px",fontSize:11,color:"#555" }}>Arrangøren publiserer premier innen trekning starter</p>
        </div>
      )}
      {!lotteryState?.lastClosedByAvlys&&synligePremier.length>0&&status!=="done"&&(() => {
        // v88: mobil-premier som HORISONTAL scroll-karusell. Hver kort er
        // 80vw så neste premie peeker inn fra høyre (signal om at det er
        // mer). scroll-snap-type:x mandatory gir snap-til-premie. Fade-
        // gradient på venstre/høyre kant antyder scrollbar innhold.
        // ?-ikonet er ~20% mindre enn v86 (24→19) per spec.
        const _hexLum = (hex) => {
          try {
            const h = hex.replace("#",""); if (h.length !== 6) return 1;
            const r = parseInt(h.slice(0,2),16)/255;
            const g = parseInt(h.slice(2,4),16)/255;
            const b = parseInt(h.slice(4,6),16)/255;
            return 0.2126*r + 0.7152*g + 0.0722*b;
          } catch (e) { return 1; }
        };
        const cardBg = settings?.premieKortFarge || "#ffffff";
        const isDarkCard = _hexLum(cardBg) < 0.5;
        const txtName = isDarkCard ? "#f0f0f0" : "#222";
        const txtMeta = isDarkCard ? "#aaa" : "#888";
        const txtDesc = isDarkCard ? "#c0c0c0" : "#666";
        // v91 Fix 4: 2 kort per rad → smalere kort → mindre flaske.
        const bottleH = Math.max(110, Math.round((settings?.flaskeHoyde||160) * 0.85));
        const bottleW = Math.max(70, Math.round((settings?.bildeBredde || 80) * 0.85));
        return (
          // v96 Fix 2: økt top-padding (8 → 24) for å gi mer luft mellom
          // sticky-header og premier-raden. flex:1 + flexDirection:column
          // lar drawer-knappene skyves nedover med marginTop:auto.
          <div style={{ ...S.prizesSection, padding:"12px 0 0", minHeight:0, flex:1, display:"flex", flexDirection:"column" }}>
            <p style={{ ...S.sectionLabel, fontSize:12, color:"#b8943c", letterSpacing:"0.16em", textAlign:"center", marginBottom:2 }}>✦ UKENS PREMIER ✦</p>
            {/* v161: vertikal scroll-fallback. På trange iPhone-viewports der
                premie-kortene blir høyere enn tilgjengelig MIDTRE-høyde kan
                brukeren nå scrolle vertikalt inni denne wrapperen i stedet for
                at kortene klippes. flex:1+minHeight:0 binder høyden til MIDTRE;
                den indre .vl-mob-prizes-scroll beholder horisontal scroll (X),
                denne håndterer Y. Scrollbar skjult visuelt. */}
            <div className="vl-mob-prizes-vscroll" style={{ position:"relative", flex:1, minHeight:0, overflowY:"auto", WebkitOverflowScrolling:"touch", scrollbarWidth:"none" }}>
              <div className="vl-mob-prizes-scroll" style={{
                display:"flex", flexWrap:"nowrap", overflowX:"auto",
                scrollSnapType:"x mandatory",
                gap:12, padding:"4px 16px 8px",
                WebkitOverflowScrolling:"touch",
                scrollbarWidth:"none",
              }}>
                <style>{`.vl-mob-prizes-scroll::-webkit-scrollbar{display:none}.vl-mob-prizes-vscroll::-webkit-scrollbar{display:none}`}</style>
                {[...synligePremier].sort((a,b)=>(parseInt(b.price)||0)-(parseInt(a.price)||0)).map((p)=>(
                  // v91 Fix 4: ~50% bredde så to premier vises samtidig. Bruker
                  // calc(50% - 6px) så 2 kort + 12px gap = 100%. scroll-snap
                  // beholdt så horisontal scroll snapper jevnt.
                  <div key={p.id} style={{
                    flex:"0 0 calc(50% - 6px)", maxWidth:280,
                    scrollSnapAlign:"start",
                    background: cardBg,
                    border: `1px solid ${isDarkCard?"#3a3a3a":"#e8e8e8"}`,
                    borderRadius: 10,
                    padding: "12px 10px",
                    display:"flex", flexDirection:"column", alignItems:"center",
                    textAlign:"center", gap:4, boxSizing:"border-box",
                  }}>
                    {/* v99 DIAGNOSE: i v98 lå pris og ?-ikon som absolute
                        children av image-area-containeren (full kort-bredde,
                        ~129px på 375px-viewport). Bottle-wrap (med radial-
                        gradient = den "grå rammen") er bare 70px bred,
                        sentrert i image-area. Bottle-wrap høyre-kant ligger
                        ved ~100px, mens ?-ikonet med right:14 og width:20
                        spannte fra ~95 til ~115 — altså strakk det seg ~15px
                        UT i card-bg-området til høyre for grå rammen. Bruker
                        så dette som "stikker halvveis utenfor".
                        FIX: pris og ?-ikon flyttes INN i bottle-wrapperen
                        som absolute children av denne. Da betyr right:4 nå
                        4px fra HØYRE KANT AV GRÅ RAMMEN — garantert helt
                        inni. Bottle-wrap har overflow:hidden så elementene
                        kan ikke stikke utenfor. */}
                    <div style={{
                      position:"relative",
                      height:bottleH, width:bottleW, maxWidth:"60%",
                      // v102: overflow:hidden → visible så pris-pillen kan
                      // henge ned over kanten av grå rammen (bottom:-10).
                      // Børbg er fortsatt clipped av borderRadius (default
                      // background-clip:padding-box respekterer radius).
                      overflow:"visible",
                      background:"radial-gradient(ellipse at center, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.18) 100%)",
                      borderRadius:6,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      flexShrink:0,
                      margin:"0 auto",
                    }}>
                      {p.imageUrl
                        ? <img src={p.imageUrl} alt={p.name} style={{ width:"100%", height:"100%", objectFit:"contain" }}/>
                        : <span style={{ fontSize:36 }}>🍷</span>}
                      {/* v102 Fix 3: pris-pille henger ned OVER overgangen
                          grå→hvit. v122: bottom -10 → -15 (5px ned). */}
                      {p.price && (
                        <span style={{
                          position:"absolute",
                          bottom:-15, left:"50%",
                          transform:"translateX(-50%)",
                          background:"rgba(0,0,0,0.85)", color:"#d4af37",
                          fontSize:10, fontWeight:"bold",
                          padding:"2px 7px", borderRadius:7,
                          border:"1px solid rgba(212,175,55,0.4)",
                          whiteSpace:"nowrap", letterSpacing:"0.02em",
                          boxShadow:"0 1px 4px rgba(0,0,0,0.4)",
                          fontVariantNumeric:"tabular-nums",
                          zIndex:1,
                        }}>{p.price},-</span>
                      )}
                      {/* v102 Fix 1: ?-ikon fjernet — vin-navnet under er nå
                          klikkbart med samme Vinmonopolet-funksjon. */}
                    </div>
                    {/* v102 Fix 2: vin-navnet er klikkbart hvis p.url finnes.
                        Vanlig svart tekst (uendret utseende), :active gir
                        subtil opacity-fade for trykk-feedback. Hvis ingen
                        URL: rendres som vanlig <p> uten interaksjon.
                        marginTop:14 (var 6) gir plass til pris-pillen som
                        henger ned over grå/hvit-overgangen. */}
                    <style>{`.vl-prize-name-link:active{opacity:0.55;transition:opacity .08s}`}</style>
                    {p.url ? (
                      <a href={p.url} target="_blank" rel="noreferrer" className="vl-prize-name-link"
                        onClick={e=>e.stopPropagation()}
                        style={{
                          margin:"14px 0 0", fontSize:13, fontWeight:"bold", color:txtName,
                          lineHeight:1.25, wordBreak:"break-word",
                          textDecoration:"none", cursor:"pointer", display:"block",
                          WebkitTapHighlightColor:"rgba(212,175,55,0.2)",
                        }}>{p.name}</a>
                    ) : (
                      <p style={{ margin:"14px 0 0", fontSize:13, fontWeight:"bold", color:txtName, lineHeight:1.25, wordBreak:"break-word" }}>{p.name}</p>
                    )}
                    {p.country && <p style={{ margin:0, fontSize:10, color:txtMeta }}>🌍 {p.country}</p>}
                    {p.description && <p style={{ margin:"2px 0 0", fontSize:10, color:txtDesc, lineHeight:1.4 }}>{p.description}</p>}
                  </div>
                ))}
              </div>
              {/* v100 Fix 2: fade-gradientene på sidene fjernet — premie-
                  kortene har nå rene, skarpe kanter. Scroll-snap-funksjonen
                  beholdt så horisontal swipe fortsatt snapper jevnt. */}
            </div>
            {/* v100 Fix 2: fade-gradientene på sidene fjernet — premie-
                kortene har nå rene, skarpe kanter. */}
          </div>
        );
      })()}

      {/* v125: ved avlyst forrige runde — vis siste runde MED vinnere i stedet
          for premier. Egen overflowY:auto så lange lister kan scrolles innen
          det låste viewportet. Bilde slås opp i gjeldende prizes — kan mangle
          hvis premien er endret siden, faller da tilbake til ingen bilde. */}
      {lotteryState?.lastClosedByAvlys && sisteVinnerRunde && (
        <div style={{
          flex:1, minHeight:0,
          overflowY:"auto",
          WebkitOverflowScrolling:"touch",
          overscrollBehavior:"contain",
          padding:"16px 16px 16px",
          marginLeft:-16, marginRight:-16,
        }}>
          <div style={{ width:"100%", maxWidth:420, margin:"0 auto" }}>
            <h3 style={{ textAlign:"center", margin:"0 0 2px", color:"#d4af37", fontSize:14, letterSpacing:"0.1em", fontWeight:600 }}>🏆 SISTE VINNERE</h3>
            <p style={{ textAlign:"center", margin:"0 0 16px", color:"#888", fontSize:12 }}>fra {sisteVinnerRunde.weekLabel}</p>
            {sisteVinnerRunde.winners.map((w,i)=>(
              <div key={i} style={{
                ...S.winnerCard,
                background: settings?.vinnerKortFarge || "#ffffff",
                borderColor: (settings?.vinnerKortFarge||"#ffffff")==="#ffffff" ? "#e0e0e0" : "#3a3a3a",
              }}>
                {(()=>{ const img=prizes.find(p=>p.name===w.prize)?.imageUrl; return img ? (
                  <div style={{
                    width:36, height:80, flexShrink:0, borderRadius:4,
                    background:"radial-gradient(ellipse at center, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.18) 100%)",
                    display:"flex", alignItems:"center", justifyContent:"center",
                  }}>
                    <img src={img} alt={w.prize} style={{ width:"100%", height:"100%", objectFit:"contain" }}/>
                  </div>
                ) : null; })()}
                <div style={{ flex:1 }}>
                  <p style={S.winnerName}>{w.name}</p>
                  <p style={S.winnerPrizeName}>{w.prize}{w.ticketNum ? <span style={{ fontSize:11,color:"#888",marginLeft:6 }}>lodd #{w.ticketNum}</span> : null}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* v103: vinnerlista inne i midtre flex:1-seksjonen når trekning er
          avsluttet. Erstatter den tidligere søsken-renderingen av
          WinnerViewWithScroll under ParticipantView, som havnet utenfor det
          låste 100dvh-viewportet. Egen overflowY:auto-container gjør at
          listen kan scrolles vertikalt innenfor det låste vinduet. */}
      {status==="done" && (() => {
        const winners = lotteryState?.winnerHistory || [];
        if (winners.length === 0) return null;
        return (
          <div style={{
            flex:1, minHeight:0,
            overflowY:"auto",
            WebkitOverflowScrolling:"touch",
            overscrollBehavior:"contain",
            padding:"12px 16px 16px",
            marginLeft:-16, marginRight:-16,
          }}>
            <div style={{ width:"100%", maxWidth:420, margin:"0 auto" }}>
              {/* v104: kompakt overskrift over vinnerlista. Sentence-case
                  uten textTransform — passer på én linje selv på iPhone SE
                  (~288px usable bredde). */}
              <p style={{
                margin:"0 0 12px",
                fontSize:18, fontWeight:600, color:"#C9A84C",
                letterSpacing:"0.04em", textAlign:"center",
                lineHeight:1.2, whiteSpace:"nowrap",
              }}>Siste treknings vinnere</p>
              {winners.map((w,i)=>(
                <div key={i} style={{
                  ...S.winnerCard,
                  position:"relative",
                  paddingBottom:18,
                  background: settings?.vinnerKortFarge || "#ffffff",
                  borderColor: (settings?.vinnerKortFarge||"#ffffff")==="#ffffff" ? "#e0e0e0" : "#3a3a3a",
                }}>
                  {(()=>{ const img=prizes.find(p=>p.name===w.prize)?.imageUrl; return img ? (
                    <div style={{
                      width:36, height:80, flexShrink:0, borderRadius:4,
                      background:"radial-gradient(ellipse at center, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.18) 100%)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                    }}>
                      <img src={img} alt={w.prize} style={{ width:"100%", height:"100%", objectFit:"contain" }}/>
                    </div>
                  ) : null; })()}
                  <div style={{ flex:1 }}>
                    <p style={S.winnerName}>{w.name}</p>
                    <p style={S.winnerPrizeName}>{w.prize}</p>
                    <VinnerKommentarer comments={winnerComments[i]} align="left" marginTop={4}/>
                  </div>
                  {w.ticketNum?<span style={{ position:"absolute", right:10, bottom:5, fontSize:11, color:"#888" }}>lodd #{w.ticketNum}</span>:null}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* v88: inline ticket-numbers, Leaderboard og participantStrip er
          flyttet ut av hovedinnholdet — finnes nå i side-drawers ("Dine
          lodd" venstre + "Loddkjøpere" høyre) som åpnes via tab-knapper. */}

      </div>{/* v93: lukker MIDTRE scroll-seksjonen */}

      {/* v159: drawer-trigger-knapper flyttet HIT — ut av MIDTRE (overflow:
          hidden) og opp til flexShrink:0-søsken rett over CTA-baren. Tidligere
          lå de nederst i MIDTRE via marginTop:auto og ble klippet bort når
          lang tittel/mange premier presset MIDTRE for lavt. Som flexShrink:0
          krymper MIDTRE (premiene) i stedet, og knappene er alltid synlige.
          Gating uendret: kun ved åpent salg. */}
      {status !== "done" && !salesLocked && (
        <div style={{ display:"flex", gap:12, flexShrink:0, padding:"16px 0 4px" }}>
          <button
            onClick={()=>{ hapticTick(); setShowDineLodd(true); }}
            style={{
              flex:"1 1 0", minWidth:0, minHeight:40,
              background:"#1a1200", color:"#C9A84C",
              border:"1px solid #C9A84C", borderRadius:8,
              fontSize:12, fontWeight:600, letterSpacing:"0.06em",
              padding:"8px 8px", cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center",
              whiteSpace:"nowrap",
            }}>🎫 Dine lodd</button>
          <button
            onClick={()=>{ hapticTick(); setShowLoddkjopere(true); }}
            style={{
              flex:"1 1 0", minWidth:0, minHeight:40,
              background:"#1a1200", color:"#C9A84C",
              border:"1px solid #C9A84C", borderRadius:8,
              fontSize:12, fontWeight:600, letterSpacing:"0.06em",
              padding:"8px 8px", cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center",
              whiteSpace:"nowrap",
            }}>🏆 Loddkjøpere</button>
        </div>
      )}

      {/* v93 Fix 2: BUNN — låst nederst i flex-column. Ikke lenger
          position:fixed (var v86) — nå statisk innenfor flex-column.
          status="done" → skjules helt. v122: salesLocked → wrapper
          fjernes helt (inkl. gull-border) siden lock-tilstand allerede
          formidles av top-banneret; bunn-indikator var duplikat.
          safe-area-inset-bottom holder iPhone-home-indicator unna
          knappens nederste kant. */}
      {status !== "done" && !salesLocked && (
        <div style={{
          flexShrink:0,
          background:"rgba(0,0,0,0.92)",
          borderTop:"none",
          boxShadow:"0 -4px 18px rgba(0,0,0,0.6)",
          padding:`6px 14px calc(10px + env(safe-area-inset-bottom, 0px))`,
          marginLeft:-16, marginRight:-16,
        }}>
          <button
            onClick={handleCTAClick}
            disabled={saving}
            style={{
              ...S.vippsBtn, width:"100%", padding:"11px 14px",
              opacity: saving ? 0.6 : 1, minHeight:46, fontSize:16, gap:10,
            }}>
            <span style={{ fontSize:20 }}>🅥</span>
            {saving ? "Registrerer…" : `Kjøp lodd · ${ticketPrice},-`}
          </button>
        </div>
      )}

      {/* v87: bottom-sheet med fullt kjøps-skjema. Rendres alltid (også
          showBuySheet=false) for å støtte glide-animasjon — translateY(100%)
          skjuler den under viewport. Backdrop tones inn ved åpning.
          Lukk-mekanismer: X-knapp, klikk på backdrop, swipe ned >120px. */}
      {!pcLayout && (
        <>
          <div
            onClick={closeBuySheet}
            style={{
              position:"fixed", inset:0, background:"rgba(0,0,0,0.6)",
              zIndex:120,
              opacity: showBuySheet ? 1 : 0,
              pointerEvents: showBuySheet ? "auto" : "none",
              transition:"opacity 0.3s ease-out",
            }}/>
          <div style={{
            position:"fixed", bottom:0, left:0, right:0,
            maxHeight:"90svh", zIndex:121,
            background:"#0f0f0f",
            borderTopLeftRadius:16, borderTopRightRadius:16,
            borderTop:"1px solid #C9A84C",
            boxShadow:"0 -8px 32px rgba(0,0,0,0.7)",
            transform: !showBuySheet ? "translateY(100%)" : `translateY(${sheetDragY}px)`,
            transition: (sheetDragY > 0 && sheetDragStartY.current != null)
              ? "none"
              : "transform 0.3s ease-out",
            display:"flex", flexDirection:"column",
            boxSizing:"border-box",
            paddingBottom:"env(safe-area-inset-bottom, 0px)",
          }}>
            {/* drag-handle — eneste område som lytter til touch-gester */}
            <div onTouchStart={onSheetTouchStart} onTouchMove={onSheetTouchMove} onTouchEnd={onSheetTouchEnd}
              style={{ padding:"10px 0 4px", display:"flex", justifyContent:"center",
                cursor:"grab", touchAction:"none", flexShrink:0 }}>
              <div style={{ width:42, height:4, borderRadius:2, background:"#555" }}/>
            </div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
              padding:"4px 18px 8px", flexShrink:0 }}>
              <p style={{ margin:0, fontSize:11, color:"#C9A84C",
                letterSpacing:"0.2em", textTransform:"uppercase", fontWeight:600 }}>Kjøp lodd</p>
              <button onClick={closeBuySheet} aria-label="Lukk"
                style={{ width:36, height:36, borderRadius:18, background:"transparent",
                  border:"1px solid #3a3a3a", color:"#aaa", fontSize:20, cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>×</button>
            </div>
            {/* scrollable form-innhold — v89: navn + mobil 50/50 side-by-side,
                e-post på egen linje under (matcher PC-versjonen fra v71). */}
            <div style={{ overflowY:"auto", overflowX:"hidden", padding:"4px 18px 18px", flex:1 }}>
              <div style={{ display:"flex", gap:8 }}>
                <input ref={nameInputRef}
                  style={{ ...S.input, color:"#e8e0d0", fontSize:16, flex:"1 1 0", minWidth:0 }}
                  placeholder="Ditt navn" value={name}
                  onChange={e=>handleNameChange(e.target.value)}/>
                <input
                  style={{ ...S.input, color:"#e8e0d0", fontSize:16, flex:"1 1 0", minWidth:0 }}
                  placeholder="Mobilnummer" type="tel" inputMode="numeric" pattern="[0-9 +]*" value={phone}
                  onChange={e=>handlePhoneChange(e.target.value)}/>
              </div>
              <input ref={emailInputRef}
                style={{ ...S.input, color:"#e8e0d0", fontSize:16 }}
                placeholder="E-postadresse" type="email" value={email}
                onChange={e=>handleEmailChange(e.target.value)}/>
              <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:12 }}>
                <div onClick={handleRememberToggle}
                  style={{ width:36,height:20,borderRadius:10,
                    background:rememberMe?"#C9A84C":"#2a2a2a",
                    cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0 }}>
                  <div style={{ position:"absolute",top:2,left:rememberMe?18:2,
                    width:16,height:16,borderRadius:"50%",background:"#fff",
                    transition:"left 0.2s" }}/>
                </div>
                <span style={{ fontSize:11,color:"#999",cursor:"pointer",userSelect:"none" }} onClick={handleRememberToggle}>
                  Husk navn, mobil og e-post til neste gang
                </span>
              </div>
              <p style={{ ...S.buyLabel,color:"#aaa" }}>
                Antall lodd à {ticketPrice},- (maks {maxPerPlayer} per person)
              </p>
              {kreditt>0&&(
                <div style={{ background:"#0d2a0d",border:"1px solid #1a5a1a",
                  borderRadius:6,padding:"6px 12px",marginBottom:8,textAlign:"center" }}>
                  <span style={{ fontSize:12,color:"#4caf50",fontWeight:"bold" }}>
                    🎟 Tilgodehavende: {kreditt},- (trekkes automatisk ved kjøp)
                  </span>
                </div>
              )}
              <div style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:10,marginBottom:8 }}>
                <button style={{ ...S.qtyBtn,width:44,height:44,fontSize:24 }}
                  onClick={()=>{ hapticTick(); setQty(Math.max(1,qty-1)); }}>−</button>
                <span style={S.qtyNum}>{qty}</span>
                <button style={{ ...S.qtyBtn,width:44,height:44,fontSize:24 }}
                  onClick={()=>{ hapticTick(); setQty(Math.min(maxPerPlayer,qty+1)); }}>+</button>
              </div>
              <div style={{ display:"flex",gap:6,marginBottom:14,justifyContent:"center" }}>
                {[1,3,5,10,20].filter(n=>n<=maxPerPlayer).map(n=>(
                  <button key={n}
                    style={{ ...S.qtyPreset,minWidth:44,minHeight:44,padding:"10px 14px",
                      fontSize:14,...(qty===n?S.qtyActive:{}) }}
                    onClick={()=>{ hapticTick(); setQty(n); }}>{n}</button>
                ))}
              </div>
              {qty*ticketPrice >= 100 && (
                <div style={{ background:"#1a1200",border:"1px solid #8a6500",borderRadius:4,
                  padding:"8px 12px",marginBottom:10,fontSize:12,color:"#C9A84C" }}>
                  💰 Du er i ferd med å kjøpe {qty} lodd for <strong>{qty*ticketPrice},-</strong>
                </div>
              )}
              {/* v126: 📝-ikon fjernet. Knappen stabler vertikalt: hovedtekst
                  + vippsnummer + kopierings-hint (de to siste kun når nummer
                  finnes og vi ikke er i Registrerer…-tilstand). */}
              <button
                style={{ ...S.vippsBtn, flexDirection:"column", gap:3, paddingTop:10, paddingBottom:10 }}
                onClick={()=>{ hapticTap(); handleBuy(); }}
                disabled={saving}>
                <span style={{ fontSize:15, fontWeight:"bold", color:"#fff" }}>
                  {saving ? "Registrerer…" : `Registrer ${qty} lodd — ${qty*ticketPrice},-`}
                </span>
                {settings?.vippsNumber && !saving && (
                  <>
                    <span style={{ fontSize:14, fontWeight:"normal", color:"#fff" }}>
                      Vipps: {settings.vippsNumber}
                    </span>
                    <span style={{ fontSize:10, color:"rgba(255,255,255,0.85)" }}>
                      Nummeret kopieres — lim inn i Vipps
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {/* v92 Fix 3: drawer-trigger-knapper UNDER premie-raden, én 50%-bred
          knapp under hvert av de to synlige premie-kortene. De gamle
          vertikale sticky-tabbene på sidene er fjernet. */}

      {/* v88: VENSTRE drawer — "Dine lodd". Backdrop + drawer som glir
          inn fra venstre. Lukkes ved backdrop-klikk, X-knapp eller
          implisitt swipe (translateX-tracking). */}
      {!pcLayout && (
        <>
          <div onClick={()=>setShowDineLodd(false)}
            style={{
              position:"fixed", inset:0, background:"rgba(0,0,0,0.6)",
              zIndex:140,
              opacity: showDineLodd ? 1 : 0,
              pointerEvents: showDineLodd ? "auto" : "none",
              transition:"opacity 0.3s ease-out",
            }}/>
          <div style={{
            position:"fixed", top:0, bottom:0, left:0,
            // v94 Fix 3: drawer-bredde 50% av viewport — den andre halvdelen
            // forblir synlig med dimmet backdrop.
            width:"50vw", zIndex:141,
            background:"#0f0f0f", borderRight:"1px solid #C9A84C",
            transform: showDineLodd ? "translateX(0)" : "translateX(-100%)",
            transition:"transform 0.3s ease-out",
            boxShadow:"4px 0 18px rgba(0,0,0,0.6)",
            display:"flex", flexDirection:"column",
            paddingTop:"env(safe-area-inset-top, 0px)",
            paddingBottom:"env(safe-area-inset-bottom, 0px)",
          }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
              padding:"14px 16px", borderBottom:"1px solid #2a2000", flexShrink:0 }}>
              {/* v89: drawer-tittel viser antall lodd (tab-en gjør det ikke). */}
              <p style={{ margin:0, fontSize:13, color:"#C9A84C",
                letterSpacing:"0.14em", textTransform:"uppercase", fontWeight:600 }}>
                🎫 Dine lodd{displayTickets > 0 ? ` (${displayTickets})` : ""}
              </p>
              <button onClick={()=>setShowDineLodd(false)} aria-label="Lukk"
                style={{ width:36, height:36, borderRadius:18, background:"transparent",
                  border:"1px solid #3a3a3a", color:"#aaa", fontSize:20, cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>×</button>
            </div>
            <div style={{ overflowY:"auto", padding:"12px 10px 20px", flex:1 }}>
              {displayTickets > 0 ? (
                <>
                  {displayTicketNumbers.length > 0 ? (
                    // v94 Fix 3: drawer er 50vw (~160-220px på telefon),
                    // minmax(54px) gir ~2 kolonner. På bredere viewports
                    // fyller den auto-magisk flere kolonner.
                    <div style={{
                      display:"grid",
                      gridTemplateColumns:"repeat(auto-fill, minmax(54px, 1fr))",
                      gap:6,
                    }}>
                      {displayTicketNumbers.map(n => (
                        <div key={n} style={{
                          aspectRatio:"1 / 1",
                          background:"rgba(46,125,50,0.14)",
                          border:"1.5px solid #C9A84C",
                          borderRadius:8,
                          display:"flex", alignItems:"center", justifyContent:"center",
                          color:"#f5e7a8", fontWeight:"bold",
                          fontSize: n >= 1000 ? 14 : n >= 100 ? 17 : 20,
                          fontVariantNumeric:"tabular-nums",
                          letterSpacing:"0.02em",
                          boxShadow:"0 2px 8px rgba(0,0,0,0.4) inset",
                        }}>
                          #{n}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize:12, color:"#888" }}>Loddnummer kommer etter registrering.</p>
                  )}
                </>
              ) : (
                <p style={{ fontSize:13, color:"#888", textAlign:"center", marginTop:24 }}>
                  Du har ingen lodd i denne trekningen ennå. Trykk «Kjøp lodd» nederst for å registrere.
                </p>
              )}
            </div>
          </div>
        </>
      )}

      {/* v88: HØYRE drawer — "Loddkjøpere" topp 30 med ranking. */}
      {!pcLayout && (
        <>
          <div onClick={()=>setShowLoddkjopere(false)}
            style={{
              position:"fixed", inset:0, background:"rgba(0,0,0,0.6)",
              zIndex:140,
              opacity: showLoddkjopere ? 1 : 0,
              pointerEvents: showLoddkjopere ? "auto" : "none",
              transition:"opacity 0.3s ease-out",
            }}/>
          <div style={{
            position:"fixed", top:0, bottom:0, right:0,
            // v94 Fix 3: drawer-bredde 50% av viewport.
            width:"50vw", zIndex:141,
            background:"#0f0f0f", borderLeft:"1px solid #C9A84C",
            transform: showLoddkjopere ? "translateX(0)" : "translateX(100%)",
            transition:"transform 0.3s ease-out",
            boxShadow:"-4px 0 18px rgba(0,0,0,0.6)",
            display:"flex", flexDirection:"column",
            paddingTop:"env(safe-area-inset-top, 0px)",
            paddingBottom:"env(safe-area-inset-bottom, 0px)",
          }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
              padding:"14px 16px", borderBottom:"1px solid #2a2000", flexShrink:0 }}>
              <p style={{ margin:0, fontSize:12, color:"#C9A84C",
                letterSpacing:"0.18em", textTransform:"uppercase", fontWeight:600 }}>🏆 Loddkjøpere</p>
              <button onClick={()=>setShowLoddkjopere(false)} aria-label="Lukk"
                style={{ width:36, height:36, borderRadius:18, background:"transparent",
                  border:"1px solid #3a3a3a", color:"#aaa", fontSize:20, cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>×</button>
            </div>
            <div style={{ overflowY:"auto", padding:"12px 14px", flex:1 }}>
              {(() => {
                // Topp 30 sortert på lodd-antall desc, alfabetisk tiebreak.
                const byEmail = {};
                participants.forEach(p => {
                  const k = (p.email||p.name||p.id||"").toLowerCase();
                  if (!byEmail[k]) byEmail[k] = { name: p.name||"Ukjent", tickets: 0 };
                  byEmail[k].tickets += (p.tickets||0);
                });
                const list = Object.values(byEmail)
                  .sort((a,b)=>(b.tickets-a.tickets) || a.name.localeCompare(b.name, "nb"))
                  .slice(0, 30);
                if (!list.length) return (
                  <p style={{ fontSize:13, color:"#888", textAlign:"center", marginTop:24 }}>
                    Ingen har kjøpt lodd ennå.
                  </p>
                );
                const medals = ["🥇","🥈","🥉"];
                return (
                  <>
                    <p style={{ margin:"0 0 10px", fontSize:11, color:"#888",
                      letterSpacing:"0.12em", textTransform:"uppercase" }}>
                      {list.length} {list.length === 1 ? "deltaker" : "deltakere"}
                    </p>
                    {list.map((p, i) => (
                      // v94 Fix 3: kompakt layout for smal 50vw drawer.
                      <div key={i} style={{
                        display:"flex", alignItems:"center", gap:6,
                        padding:"6px 8px", marginBottom:3,
                        background: i === 0 ? "#1a1200" : "transparent",
                        border:`1px solid ${i === 0 ? "#C9A84C" : "#1a1a1a"}`,
                        borderRadius:6,
                      }}>
                        <span style={{ fontSize:12, minWidth:20, color:"#888",
                          fontVariantNumeric:"tabular-nums" }}>
                          {medals[i] || `${i+1}.`}
                        </span>
                        <span style={{ flex:1, fontSize:11, color:"#e8e0d0",
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", minWidth:0 }}>
                          {p.name}
                        </span>
                        <span style={{ fontSize:10, color:"#C9A84C", fontVariantNumeric:"tabular-nums", whiteSpace:"nowrap", flexShrink:0 }}>
                          {p.tickets}
                        </span>
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* v88: toast/snackbar — vises etter "Kopiert!" og lignende handlinger. */}
      {!pcLayout && toastMsg && (
        <div style={{
          position:"fixed",
          bottom:`calc(96px + env(safe-area-inset-bottom, 0px))`,
          left:"50%", transform:"translateX(-50%)",
          background:"#1a1200", color:"#C9A84C",
          padding: toastValue ? "16px 32px" : "10px 22px", borderRadius:24,
          maxWidth:"90vw",
          fontSize:13, fontWeight:600, letterSpacing:"0.05em",
          border:"1px solid #C9A84C", zIndex:200,
          boxShadow:"0 4px 18px rgba(0,0,0,0.6)",
          pointerEvents:"none",
          animation:"vlToastIn 0.2s ease-out",
          whiteSpace:"pre-line", textAlign:"center", lineHeight:1.4,
        }}>
          <style>{`@keyframes vlToastIn { from { opacity:0; transform: translate(-50%, 8px); } to { opacity:1; transform: translate(-50%, 0); } }`}</style>
          {toastValue ? (
            <>
              {/* v155: tittel på én linje (nowrap), nummeret stort og uthevet under */}
              <div style={{ fontSize:18, fontWeight:600, color:"#C9A84C", whiteSpace:"nowrap" }}>{toastMsg}</div>
              <div style={{ fontSize:25, fontWeight:"bold", color:"#d4af37", marginTop:4, fontVariantNumeric:"tabular-nums" }}>{toastValue}</div>
            </>
          ) : (
            toastMsg
          )}
        </div>
      )}
    </div>
  );
}

// ─── PREMIEHÅNDTERING ─────────────────────────────────────────────────────────
function PrizeManager({ onPrizeChange, pcLayout = false }) {
  const [prizes,setPrizes]=useState([]); const [editId,setEditId]=useState(null);
  const [editForm,setEditForm]=useState({}); const [saving,setSaving]=useState(false);
  const [newForm,setNewForm]=useState({ name:"",country:"",price:"",description:"",url:"",imageUrl:"" });
  // v43: status for bakgrunns-fjernings-prosessering (per skjema).
  const [editBgRemoving, setEditBgRemoving] = useState(false);
  const [newBgRemoving, setNewBgRemoving] = useState(false);
  // v45: "✓ Klar"-indikator som blinker grønt etter vellykket prosessering
  // slik at brukeren vet at det er trygt å lagre. Timeren rydder den selv.
  const [editBgDone, setEditBgDone] = useState(false);
  const [newBgDone, setNewBgDone] = useState(false);
  const flashBgDone = (formType) => {
    const setter = formType === "edit" ? setEditBgDone : setNewBgDone;
    setter(true);
    setTimeout(() => setter(false), 3000);
  };
  // Anti-loop: vi auto-trigger bakgrunns-fjerning når en NY Vinmonopolet-URL
  // settes, men vi må huske hvilken URL vi sist prosesserte for ikke å
  // re-prosessere etter at vi har erstattet imageUrl med data-URI.
  const lastProcessedUrlRef = useRef({ edit:null, new:null });

  // v43-44: kjør bakgrunns-fjerning. Returnerer { dataUri } eller { error }.
  const runBgRemoval = async (url, formType) => {
    if (!url || url.startsWith("data:")) return { error:"load" };
    const setter = formType === "edit" ? setEditBgRemoving : setNewBgRemoving;
    setter(true);
    try {
      return await removeWhiteBackground(url);
    } finally {
      setter(false);
    }
  };

  // v44: bedre feilmeldinger basert på feilkode.
  const showBgRemovalError = (code) => {
    if (code === "load") {
      showErrorToast("Kunne ikke laste bildet. Sjekk at URL-en er korrekt.");
    } else if (code === "tainted") {
      showErrorToast("Dette bildet kan ikke prosesseres. Last det ned manuelt og bruk fil-opplasting i stedet.");
    } else {
      showErrorToast("Bakgrunns-fjerning feilet. Prøv en annen URL eller last opp filen manuelt.");
    }
  };

  // v43-44: auto-trigger på Vinmonopolet-URLer. URL lagres umiddelbart i
  // form, bakgrunns-fjerning kjører i bakgrunnen. Hvis prosessering lykkes
  // erstattes imageUrl med data-URI; ellers beholdes original URL og toast
  // viser konkret feil-kode.
  const setImageUrl = async (newUrl, formType) => {
    const setForm = formType === "edit" ? setEditForm : setNewForm;
    setForm(f => ({ ...f, imageUrl: newUrl }));
    if (!isVinmonopoletUrl(newUrl)) return;
    if (lastProcessedUrlRef.current[formType] === newUrl) return;
    lastProcessedUrlRef.current[formType] = newUrl;
    const result = await runBgRemoval(newUrl, formType);
    if (result.dataUri) {
      // v45: bruk funksjonell update slik at vi alltid leser FRISK state-
      // verdi — selv om brukeren har endret feltet siden vi startet, kan
      // vi sammenligne mot newUrl og trygt erstatte.
      setForm(f => f.imageUrl === newUrl ? { ...f, imageUrl: result.dataUri } : f);
      flashBgDone(formType);
    } else {
      showBgRemovalError(result.error);
    }
  };
  useEffect(()=>{
    const unsub=onSnapshot(collection(db,"prizes"),snap=>{
      const p=snap.docs.map(d=>({ id:d.id,...d.data() })).sort((a,b)=>(a.createdAt?.seconds||0)-(b.createdAt?.seconds||0));
      setPrizes(p); onPrizeChange&&onPrizeChange(p);
    });
    return unsub;
  },[onPrizeChange]);
  const startEdit=p=>{ setEditId(p.id); setEditForm({ name:p.name,country:p.country||"",price:p.price||"",description:p.description||"",url:p.url||"",imageUrl:p.imageUrl||"" }); };
  const saveEdit=async id=>{
    try { await updateDoc(doc(db,"prizes",id),editForm); setEditId(null); }
    catch(e) { console.error("Feil ved lagring av premie:", e); showErrorToast("Kunne ikke lagre premie."); }
  };
  const deletePrize=async id=>{
    if(await showConfirm("Slette denne premien?"))
      try { await deleteDoc(doc(db,"prizes",id)); }
      catch(e) { showErrorToast("Kunne ikke slette premie."); }
  };
  // v300 (premie-bibliotek steg 2): toggle "med i kommende trekning". Manglende
  // felt tolkes som MED (samme invariant som functions-filteret: medITrekning
  // !== false ⇒ med). Skriver eksplisitt boolean så av-tilstand persisteres.
  const erMed = p => p.medITrekning !== false;
  const antallValgt = prizes.filter(erMed).length;
  // v319: når en premie legges til/velges er "utsatt/avlyst uten premier"-
  // varselet (autoDrawSkipped, skrevet av server-autoDraw) utdatert — rydd det.
  // v322: varsle i tillegg hvis runden har et PASSERT trekningstidspunkt uten
  // at trekning startet (idle + autoDraw av — serveren rører aldri slike
  // runder, så tidspunktet blir liggende og spillersiden viser "avsluttet").
  // Kun varsel, ingen stille mutering av drawTime — arrangøren rydder selv.
  const clearAutoDrawSkipped = async () => {
    try {
      const snap = await getDoc(doc(db,"lottery","current"));
      const d = snap.exists() ? snap.data() : null;
      if (d && d.status === "idle" && !d.autoDraw &&
          d.drawTime && new Date(d.drawTime) <= new Date()) {
        const t = new Date(d.drawTime).toLocaleString("nb-NO",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"});
        showErrorToast(`Trekningstidspunktet ${t} er passert uten at trekning ble startet. Sett et nytt tidspunkt eller fjern det.`);
      }
      await updateDoc(doc(db,"lottery","current"),{ autoDrawSkipped: deleteField() });
    }
    catch(e) { /* varselet er kosmetisk — ignorer nettverksfeil */ }
  };
  const toggleMedITrekning = async p => {
    try {
      const nyMed = !erMed(p);
      await updateDoc(doc(db,"prizes",p.id),{ medITrekning: nyMed });
      if (nyMed) clearAutoDrawSkipped();
    }
    catch(e) { console.error("Feil ved toggling av medITrekning:", e); showErrorToast("Kunne ikke endre utvalg."); }
  };
  const handlePrizeImageUpload = async (e, isEdit=false) => {
    const file = e.target.files[0]; if (!file) return;
    if (file.size > 2*1024*1024) { showAlert("Bildet er for stort (maks 2 MB)"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (isEdit) setEditForm(f=>({ ...f, imageUrl:ev.target.result }));
      else setNewForm(f=>({ ...f, imageUrl:ev.target.result }));
    };
    reader.readAsDataURL(file);
  };
  const addPrize=async()=>{
    if(!newForm.name.trim()){showAlert("Navn mangler!");return;}
    setSaving(true);
    try {
      await addDoc(collection(db,"prizes"),{ ...newForm,medITrekning:true,createdAt:serverTimestamp() });
      setNewForm({ name:"",country:"",price:"",description:"",url:"",imageUrl:"" });
      clearAutoDrawSkipped();
    } catch(e) {
      console.error("Feil ved legg til premie:", e);
      showErrorToast("Kunne ikke legge til premie. Sjekk internett.");
    }
    setSaving(false);
  };
  const fields=[{key:"name",label:"Navn *"},{key:"country",label:"Land"},{key:"price",label:"Pris kr"},{key:"description",label:"Beskrivelse"},{key:"imageUrl",label:"Bilde-URL"},{key:"url",label:"Vinmonopolet-lenke"}];
  // v41: Vinmonopolet-søk. Vinmonopolet har ikke et fritt browser-tilgjengelig
  // API (krever auth-nøkkel + CORS-proxy). Pragmatisk løsning: åpne deres
  // søke-side i ny fane med pre-utfylt q-parameter. Brukeren kopierer info
  // manuelt tilbake til premie-feltene. Funksjonen er tilgjengelig i begge
  // skjemaer (rediger eksisterende + legg til ny) og på alle plattformer.
  const openVinmonopolet = (query) => {
    const q = (query || "").trim();
    const url = q
      ? `https://www.vinmonopolet.no/search/?q=${encodeURIComponent(q)}`
      : "https://www.vinmonopolet.no/search/";
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const vinmonopoletBtnStyle = {
    flexShrink:0, padding:"6px 10px", height:32,
    background:"#7a1a1a", border:"1px solid #a04040",
    color:"#ffe6e6", borderRadius:4, cursor:"pointer",
    fontSize:11, fontWeight:"bold", whiteSpace:"nowrap",
    display:"flex", alignItems:"center", gap:4,
  };

  // v48: PC-prototype-grenen. To kort: "🏆 Eksisterende premier" og
  // "➕ Legg til ny premie". Større headers (14 px gull caps), luftig
  // liste med stor rang-badge + større bilder + tydelige rediger/slett-
  // knapper. Add-skjema er 2-kolonners: tekst-felter til venstre,
  // bilde-relaterte felter + forhåndsvisning til høyre. Maks bredde
  // 1320 px sentrert. All eksisterende logikk (Vinmonopolet-søk,
  // bakgrunns-fjerning, race-condition-disable fra v45) bevart.
  if (pcLayout) {
    const pcInput = {
      ...S.input, marginBottom:0,
      background:"#0a0a0a", border:"1px solid #2a2a2a",
      borderRadius:5, padding:"10px 12px", fontSize:13, color:"#f5efe0",
    };
    const pcLabel = {
      display:"block", margin:"0 0 6px", fontSize:11,
      color:"#888", textTransform:"uppercase",
      letterSpacing:"0.1em", fontWeight:600,
    };
    const pcBgBtn = (busy) => ({
      flexShrink:0, padding:"0 12px", height:40,
      background:busy?"#1a1a1a":"#1a4a1a",
      border:`1px solid ${busy?"#444":"#2a6a2a"}`,
      color:busy?"#666":"#a8e8a8",
      borderRadius:5, cursor:busy?"wait":"pointer",
      fontSize:12, fontWeight:"bold", whiteSpace:"nowrap",
    });
    const pcSokBtn = {
      flexShrink:0, padding:"0 14px", height:40,
      background:"#7a1a1a", border:"1px solid #a04040",
      color:"#ffe6e6", borderRadius:5, cursor:"pointer",
      fontSize:12, fontWeight:"bold", whiteSpace:"nowrap",
    };

    return (
      <div style={{ padding:0, maxWidth:1320, margin:"0 auto" }}>
        <style>{`
          .vl-pc-pm-card{
            background:linear-gradient(145deg,#141414 0%,#0e0e0e 100%);
            border:1px solid #2a2a2a;border-left:3px solid #C9A84C;
            border-radius:10px;padding:22px 24px;margin-bottom:16px;
            box-shadow:0 3px 10px rgba(0,0,0,0.4);
          }
          .vl-pc-pm-title{
            margin:0 0 18px;font-size:14px;color:#C9A84C;font-weight:700;
            letter-spacing:0.12em;text-transform:uppercase;
            display:flex;align-items:center;gap:8px;
          }
          .vl-pc-pm-row{
            display:flex;align-items:center;gap:16px;
            padding:14px 0;border-bottom:1px solid #1f1f1f;
          }
          .vl-pc-pm-row:last-child{border-bottom:none;}
          .vl-pc-pm-rank{
            flex-shrink:0;width:40px;height:40px;border-radius:50%;
            display:flex;align-items:center;justify-content:center;
            background:linear-gradient(145deg,#C9A84C33 0%,#C9A84C10 100%);
            border:1px solid #C9A84C55;
            color:#C9A84C;font-weight:bold;font-size:16px;
          }
          .vl-pc-pm-img{
            flex-shrink:0;width:48px;height:72px;object-fit:contain;
            border-radius:4px;padding:2px;
          }
          .vl-pc-pm-img-empty{
            flex-shrink:0;width:48px;height:72px;border-radius:4px;
            border:1px dashed #333;display:flex;align-items:center;
            justify-content:center;font-size:18px;color:#444;
          }
          .vl-pc-pm-iconbtn{
            flex-shrink:0;padding:0;width:36px;height:36px;
            display:flex;align-items:center;justify-content:center;
            border-radius:6px;cursor:pointer;font-size:14px;
            transition:all 0.15s;
          }
          .vl-pc-pm-editbtn{
            background:#1a1200;border:1px solid #3a2a00;color:#C9A84C;
          }
          .vl-pc-pm-editbtn:hover{background:#2a1d00;border-color:#C9A84C;}
          .vl-pc-pm-delbtn{
            background:#2a0d0d;border:1px solid #5a1a1a;color:#e57373;
          }
          .vl-pc-pm-delbtn:hover{background:#3a1010;border-color:#e57373;}
        `}</style>

        {/* KORT 1: Eksisterende premier */}
        <div className="vl-pc-pm-card">
          <p className="vl-pc-pm-title">🏆 Eksisterende premier {prizes.length>0&&<span style={{ color:"#666", fontWeight:400, letterSpacing:"0.05em", textTransform:"none", fontSize:13 }}>· {prizes.length} stk</span>}</p>
          {prizes.length>0&&(
            <p style={{ margin:"-8px 0 16px", fontSize:13, fontWeight:600, color:antallValgt>0?"#4caf50":"#e57373", letterSpacing:"0.03em" }}>
              {antallValgt} av {prizes.length} valgt til trekning
            </p>
          )}
          {prizes.length === 0 ? (
            <p style={{ margin:0, color:"#666", fontSize:13 }}>Ingen premier definert ennå. Legg til den første nedenfor.</p>
          ) : prizes.map((p,i)=>(
            <div key={p.id} style={{ borderBottom:i===prizes.length-1?"none":"1px solid #1f1f1f", padding:"14px 0" }}>
              {editId===p.id ? (
                <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                  <p style={{ margin:0, fontSize:11, color:"#C9A84C", textTransform:"uppercase", letterSpacing:"0.1em", fontWeight:600 }}>Redigerer premie {i+1}</p>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                      <div>
                        <label style={pcLabel}>Navn *</label>
                        <div style={{ display:"flex", gap:8 }}>
                          <input style={{ ...pcInput, flex:1 }} placeholder="Navn på premie" value={editForm.name||""} onChange={e=>setEditForm({ ...editForm, name:e.target.value })}/>
                          <button type="button" style={pcSokBtn} title="Søk på Vinmonopolet" onClick={()=>openVinmonopolet(editForm.name)}>🍷 Søk</button>
                        </div>
                      </div>
                      <div>
                        <label style={pcLabel}>Land</label>
                        <input style={pcInput} placeholder="F.eks. Italia" value={editForm.country||""} onChange={e=>setEditForm({ ...editForm, country:e.target.value })}/>
                      </div>
                      <div>
                        <label style={pcLabel}>Pris kr</label>
                        <input style={pcInput} placeholder="F.eks. 199" value={editForm.price||""} onChange={e=>setEditForm({ ...editForm, price:e.target.value })}/>
                      </div>
                      <div>
                        <label style={pcLabel}>Beskrivelse</label>
                        <input style={pcInput} placeholder="Kort beskrivelse" value={editForm.description||""} onChange={e=>setEditForm({ ...editForm, description:e.target.value })}/>
                      </div>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                      <div>
                        <label style={pcLabel}>Bilde-URL</label>
                        <div style={{ display:"flex", gap:8 }}>
                          <input style={{ ...pcInput, flex:1 }} placeholder="https://… eller data:image/…" value={editForm.imageUrl||""} onChange={e=>setImageUrl(e.target.value, "edit")}/>
                          <button type="button" style={pcBgBtn(editBgRemoving)}
                            title="Fjern hvit bakgrunn fra dette bildet"
                            disabled={editBgRemoving || !editForm.imageUrl || (editForm.imageUrl||"").startsWith("data:")}
                            onClick={async()=>{
                              const r = await runBgRemoval(editForm.imageUrl, "edit");
                              if (r.dataUri) { setEditForm(f=>({ ...f, imageUrl:r.dataUri })); flashBgDone("edit"); }
                              else showBgRemovalError(r.error);
                            }}>
                            {editBgRemoving ? "⏳ Behandler…" : "🪄 Fjern bakgrunn"}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label style={pcLabel}>Last opp fil</label>
                        <input type="file" accept="image/*" style={{ fontSize:12, color:"#aaa", display:"block" }} onChange={e=>handlePrizeImageUpload(e, true)}/>
                      </div>
                      <div>
                        <label style={pcLabel}>Vinmonopolet-lenke</label>
                        <input style={pcInput} placeholder="https://www.vinmonopolet.no/…" value={editForm.url||""} onChange={e=>setEditForm({ ...editForm, url:e.target.value })}/>
                      </div>
                      {editForm.imageUrl && (
                        <div>
                          <label style={pcLabel}>Forhåndsvisning</label>
                          <div style={{ width:100, height:100, border:"1px solid #2a2a2a", borderRadius:6, display:"flex", alignItems:"center", justifyContent:"center", background:"#0a0a0a" }}>
                            <img src={editForm.imageUrl} alt="" style={{ maxWidth:96, maxHeight:96, objectFit:"contain" }}/>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {(editBgRemoving || editBgDone) && (
                    <p style={{
                      margin:0, fontSize:12, fontWeight:"bold",
                      color: editBgRemoving ? "#C9A84C" : "#4caf50",
                    }}>
                      {editBgRemoving ? "⏳ Behandler bilde — vent til 'Lagre' aktiveres…" : "✓ Bildet er klart for lagring"}
                    </p>
                  )}
                  <div style={{ display:"flex", gap:10 }}>
                    <button onClick={()=>saveEdit(p.id)} disabled={editBgRemoving}
                      title={editBgRemoving ? "Vent til bakgrunns-fjerning er ferdig" : ""}
                      style={{
                        padding:"9px 18px", background:"#0d2a0d", border:"1px solid #1a4a1a",
                        color:"#4caf50", borderRadius:5, fontSize:13, fontWeight:"bold",
                        cursor: editBgRemoving ? "wait" : "pointer",
                        opacity: editBgRemoving ? 0.5 : 1,
                      }}>
                      {editBgRemoving ? "⏳ Vent…" : "✓ Lagre"}
                    </button>
                    <button onClick={()=>setEditId(null)}
                      style={{
                        padding:"9px 18px", background:"transparent", border:"1px solid #3a3a3a",
                        color:"#888", borderRadius:5, fontSize:13, cursor:"pointer",
                      }}>
                      Avbryt
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                  <button type="button"
                    title={erMed(p)?"Med i kommende trekning — klikk for å ta av":"Ikke med — klikk for å ta med i trekning"}
                    onClick={()=>toggleMedITrekning(p)}
                    style={{
                      flexShrink:0, width:26, height:26, borderRadius:6, cursor:"pointer",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:15, fontWeight:"bold",
                      background:erMed(p)?"#0d2a0d":"#1a1a1a",
                      border:`1px solid ${erMed(p)?"#2a6a2a":"#444"}`,
                      color:erMed(p)?"#4caf50":"#555",
                    }}>{erMed(p)?"✓":""}</button>
                  <span className="vl-pc-pm-rank" style={erMed(p)?{}:{ opacity:0.4 }}>{i+1}</span>
                  {p.imageUrl
                    ? <img src={p.imageUrl} alt={p.name} className="vl-pc-pm-img" style={erMed(p)?{}:{ opacity:0.4 }}/>
                    : <div className="vl-pc-pm-img-empty" style={erMed(p)?{}:{ opacity:0.4 }}>🍷</div>
                  }
                  <div style={{ flex:1, minWidth:0, ...(erMed(p)?{}:{ opacity:0.4 }) }}>
                    <p style={{ margin:0, fontSize:15, fontWeight:"bold", color:"#f5efe0" }}>{p.name}</p>
                    <p style={{ margin:"3px 0 0", fontSize:12, color:"#888" }}>
                      {p.country}{p.country&&p.price?" · ":""}{p.price?`${p.price},-`:""}
                      {p.description && <span style={{ color:"#666" }}>{(p.country||p.price)?" · ":""}{p.description}</span>}
                    </p>
                  </div>
                  <button className="vl-pc-pm-iconbtn vl-pc-pm-editbtn" title="Rediger" onClick={()=>startEdit(p)}>✏️</button>
                  <button className="vl-pc-pm-iconbtn vl-pc-pm-delbtn" title="Slett" onClick={()=>deletePrize(p.id)}>✕</button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* KORT 2: Legg til ny premie */}
        <div className="vl-pc-pm-card">
          <p className="vl-pc-pm-title">➕ Legg til ny premie</p>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div>
                <label style={pcLabel}>Navn *</label>
                <div style={{ display:"flex", gap:8 }}>
                  <input style={{ ...pcInput, flex:1 }} placeholder="Navn på premie" value={newForm.name} onChange={e=>setNewForm({ ...newForm, name:e.target.value })}/>
                  <button type="button" style={pcSokBtn} title="Søk på Vinmonopolet" onClick={()=>openVinmonopolet(newForm.name)}>🍷 Søk</button>
                </div>
              </div>
              <div>
                <label style={pcLabel}>Land</label>
                <input style={pcInput} placeholder="F.eks. Italia" value={newForm.country} onChange={e=>setNewForm({ ...newForm, country:e.target.value })}/>
              </div>
              <div>
                <label style={pcLabel}>Pris kr</label>
                <input style={pcInput} placeholder="F.eks. 199" value={newForm.price} onChange={e=>setNewForm({ ...newForm, price:e.target.value })}/>
              </div>
              <div>
                <label style={pcLabel}>Beskrivelse</label>
                <input style={pcInput} placeholder="Kort beskrivelse" value={newForm.description} onChange={e=>setNewForm({ ...newForm, description:e.target.value })}/>
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div>
                <label style={pcLabel}>Bilde-URL</label>
                <div style={{ display:"flex", gap:8 }}>
                  <input style={{ ...pcInput, flex:1 }} placeholder="https://… eller data:image/…" value={newForm.imageUrl||""} onChange={e=>setImageUrl(e.target.value, "new")}/>
                  <button type="button" style={pcBgBtn(newBgRemoving)}
                    title="Fjern hvit bakgrunn fra dette bildet"
                    disabled={newBgRemoving || !newForm.imageUrl || (newForm.imageUrl||"").startsWith("data:")}
                    onClick={async()=>{
                      const r = await runBgRemoval(newForm.imageUrl, "new");
                      if (r.dataUri) { setNewForm(f=>({ ...f, imageUrl:r.dataUri })); flashBgDone("new"); }
                      else showBgRemovalError(r.error);
                    }}>
                    {newBgRemoving ? "⏳ Behandler…" : "🪄 Fjern bakgrunn"}
                  </button>
                </div>
              </div>
              <div>
                <label style={pcLabel}>Last opp fil</label>
                <input type="file" accept="image/*" style={{ fontSize:12, color:"#aaa", display:"block" }} onChange={e=>handlePrizeImageUpload(e, false)}/>
              </div>
              <div>
                <label style={pcLabel}>Vinmonopolet-lenke</label>
                <input style={pcInput} placeholder="https://www.vinmonopolet.no/…" value={newForm.url} onChange={e=>setNewForm({ ...newForm, url:e.target.value })}/>
              </div>
              <div>
                <label style={pcLabel}>Forhåndsvisning</label>
                <div style={{ width:100, height:100, border:"1px solid #2a2a2a", borderRadius:6, display:"flex", alignItems:"center", justifyContent:"center", background:"#0a0a0a" }}>
                  {newForm.imageUrl
                    ? <img src={newForm.imageUrl} alt="" style={{ maxWidth:96, maxHeight:96, objectFit:"contain" }}/>
                    : <span style={{ fontSize:24, color:"#333" }}>🍷</span>
                  }
                </div>
              </div>
            </div>
          </div>
          {(newBgRemoving || newBgDone) && (
            <p style={{
              margin:"14px 0 0", fontSize:12, fontWeight:"bold",
              color: newBgRemoving ? "#C9A84C" : "#4caf50",
            }}>
              {newBgRemoving ? "⏳ Behandler bilde — vent til 'Legg til' aktiveres…" : "✓ Bildet er klart for lagring"}
            </p>
          )}
          <button onClick={addPrize} disabled={saving || newBgRemoving}
            title={newBgRemoving ? "Vent til bakgrunns-fjerning er ferdig" : ""}
            style={{
              marginTop:18, padding:"11px 22px",
              background:"#1a1200", border:"1px solid #C9A84C",
              color:"#C9A84C", borderRadius:6, fontSize:13, fontWeight:"bold",
              letterSpacing:"0.05em",
              cursor: (saving || newBgRemoving) ? "wait" : "pointer",
              opacity: (saving || newBgRemoving) ? 0.5 : 1,
            }}>
            {saving ? "Lagrer…" : newBgRemoving ? "⏳ Vent på bilde-prosessering…" : "+ Legg til premie"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding:16 }}>
      <p style={S.sectionLabel}>Premier</p>
      {prizes.length>0&&(
        <p style={{ margin:"-6px 0 12px", fontSize:13, fontWeight:600, color:antallValgt>0?"#4caf50":"#e57373" }}>
          {antallValgt} av {prizes.length} valgt til trekning
        </p>
      )}
      {prizes.map((p,i)=>(
        <div key={p.id} style={S.prizeEditorCard}>
          {editId===p.id?(
            <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
              <p style={{ margin:0,fontSize:10,color:"#C9A84C",textTransform:"uppercase" }}>{i+1}. premie</p>
              {fields.map(f=>(
                <div key={f.key}>
                  {f.key === "name" ? (
                    <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:8 }}>
                      <input style={{ ...S.input, marginBottom:0, flex:1 }} placeholder={f.label} value={editForm[f.key]} onChange={e=>setEditForm({ ...editForm,[f.key]:e.target.value })}/>
                      <button type="button" style={vinmonopoletBtnStyle}
                        title="Søk på Vinmonopolet"
                        onClick={()=>openVinmonopolet(editForm.name)}>🍷 Søk</button>
                    </div>
                  ) : f.key === "imageUrl" ? (
                    <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:8 }}>
                      <input style={{ ...S.input, marginBottom:0, flex:1 }} placeholder={f.label}
                        value={editForm[f.key]||""}
                        onChange={e=>setImageUrl(e.target.value, "edit")}/>
                      <button type="button"
                        title="Fjern hvit bakgrunn fra dette bildet"
                        disabled={editBgRemoving || !editForm.imageUrl || (editForm.imageUrl||"").startsWith("data:")}
                        onClick={async()=>{
                          const r = await runBgRemoval(editForm.imageUrl, "edit");
                          if (r.dataUri) { setEditForm(f=>({ ...f, imageUrl:r.dataUri })); flashBgDone("edit"); }
                          else showBgRemovalError(r.error);
                        }}
                        style={{
                          flexShrink:0, padding:"6px 10px", height:32,
                          background:editBgRemoving?"#1a1a1a":"#1a4a1a",
                          border:`1px solid ${editBgRemoving?"#444":"#2a6a2a"}`,
                          color:editBgRemoving?"#666":"#a8e8a8",
                          borderRadius:4, cursor:editBgRemoving?"wait":"pointer",
                          fontSize:11, fontWeight:"bold", whiteSpace:"nowrap",
                        }}>
                        {editBgRemoving ? "⏳ Behandler…" : "🪄 Fjern bakgrunn"}
                      </button>
                    </div>
                  ) : (
                    <input style={S.input} placeholder={f.label} value={editForm[f.key]} onChange={e=>setEditForm({ ...editForm,[f.key]:e.target.value })}/>
                  )}
                  {f.key==="imageUrl"&&(
                    <input type="file" accept="image/*" style={{ fontSize:11,color:"#aaa",marginBottom:6,display:"block" }}
                      onChange={e=>handlePrizeImageUpload(e,true)}/>
                  )}
                </div>
              ))}
              {editForm.imageUrl&&<img src={editForm.imageUrl} alt="" style={{ height:60,objectFit:"contain",marginBottom:4 }}/>}
              {/* v45: vis "Behandler…" eller "✓ Klar" status så brukeren
                  vet at det er trygt å lagre. Lagre-knappen er disabled
                  mens prosessering pågår for å unngå race condition. */}
              {(editBgRemoving || editBgDone) && (
                <p style={{
                  margin:"0 0 8px", fontSize:11, fontWeight:"bold",
                  color: editBgRemoving ? "#C9A84C" : "#4caf50",
                }}>
                  {editBgRemoving
                    ? "⏳ Behandler bilde — vent til 'Lagre' aktiveres…"
                    : "✓ Bildet er klart for lagring"}
                </p>
              )}
              <div style={{ display:"flex",gap:8 }}>
                <button style={{ ...S.saveBtn, opacity: editBgRemoving ? 0.5 : 1, cursor: editBgRemoving ? "wait" : "pointer" }}
                  disabled={editBgRemoving}
                  title={editBgRemoving ? "Vent til bakgrunns-fjerning er ferdig" : ""}
                  onClick={()=>saveEdit(p.id)}>
                  {editBgRemoving ? "⏳ Vent…" : "✓ Lagre"}
                </button>
                <button style={S.cancelBtn} onClick={()=>setEditId(null)}>Avbryt</button>
              </div>
            </div>
          ):(
            <div style={{ display:"flex",alignItems:"center",gap:10 }}>
              <button type="button"
                title={erMed(p)?"Med i kommende trekning — trykk for å ta av":"Ikke med — trykk for å ta med i trekning"}
                onClick={()=>toggleMedITrekning(p)}
                style={{
                  flexShrink:0, width:26, height:26, borderRadius:6, cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:15, fontWeight:"bold",
                  background:erMed(p)?"#0d2a0d":"#1a1a1a",
                  border:`1px solid ${erMed(p)?"#2a6a2a":"#444"}`,
                  color:erMed(p)?"#4caf50":"#555",
                }}>{erMed(p)?"✓":""}</button>
              <span style={{ ...S.rankBadge, ...(erMed(p)?{}:{ opacity:0.4 }) }}>{i+1}</span>
              {/* v46: fjernet background:"#fff" — transparent PNG fra
                  bakgrunns-fjerning vises nå mot mørkt dashboard-tema. */}
              {p.imageUrl&&<img src={p.imageUrl} alt={p.name} style={{ width:22,height:52,objectFit:"contain",borderRadius:2,padding:2, ...(erMed(p)?{}:{ opacity:0.4 }) }}/>}
              <div style={{ flex:1, ...(erMed(p)?{}:{ opacity:0.4 }) }}>
                <p style={{ margin:0,fontSize:13,fontWeight:"bold",color:"#f5efe0" }}>{p.name}</p>
                <p style={{ margin:0,fontSize:11,color:"#666" }}>{p.country}{p.country&&p.price?" · ":""}{p.price?`${p.price},-`:""}</p>
              </div>
              <button style={S.editBtn} onClick={()=>startEdit(p)}>✏️</button>
              <button style={S.deleteBtn} onClick={()=>deletePrize(p.id)}>✕</button>
            </div>
          )}
        </div>
      ))}
      <div style={{ background:"#0d0d0d",borderRadius:4,padding:14,border:"1px solid #3a3a3a",marginTop:8 }}>
        <p style={S.buyLabel}>Legg til premie</p>
        {fields.map(f=>(
          <div key={f.key}>
            {f.key === "name" ? (
              <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:8 }}>
                <input style={{ ...S.input, marginBottom:0, flex:1 }} placeholder={f.label} value={newForm[f.key]} onChange={e=>setNewForm({ ...newForm,[f.key]:e.target.value })}/>
                <button type="button" style={vinmonopoletBtnStyle}
                  title="Søk på Vinmonopolet"
                  onClick={()=>openVinmonopolet(newForm.name)}>🍷 Søk</button>
              </div>
            ) : f.key === "imageUrl" ? (
              <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:8 }}>
                <input style={{ ...S.input, marginBottom:0, flex:1 }} placeholder={f.label}
                  value={newForm[f.key]||""}
                  onChange={e=>setImageUrl(e.target.value, "new")}/>
                <button type="button"
                  title="Fjern hvit bakgrunn fra dette bildet"
                  disabled={newBgRemoving || !newForm.imageUrl || (newForm.imageUrl||"").startsWith("data:")}
                  onClick={async()=>{
                    const r = await runBgRemoval(newForm.imageUrl, "new");
                    if (r.dataUri) { setNewForm(f=>({ ...f, imageUrl:r.dataUri })); flashBgDone("new"); }
                    else showBgRemovalError(r.error);
                  }}
                  style={{
                    flexShrink:0, padding:"6px 10px", height:32,
                    background:newBgRemoving?"#1a1a1a":"#1a4a1a",
                    border:`1px solid ${newBgRemoving?"#444":"#2a6a2a"}`,
                    color:newBgRemoving?"#666":"#a8e8a8",
                    borderRadius:4, cursor:newBgRemoving?"wait":"pointer",
                    fontSize:11, fontWeight:"bold", whiteSpace:"nowrap",
                  }}>
                  {newBgRemoving ? "⏳ Behandler…" : "🪄 Fjern bakgrunn"}
                </button>
              </div>
            ) : (
              <input style={S.input} placeholder={f.label} value={newForm[f.key]} onChange={e=>setNewForm({ ...newForm,[f.key]:e.target.value })}/>
            )}
            {f.key==="imageUrl"&&(
              <input type="file" accept="image/*" style={{ fontSize:11,color:"#aaa",marginBottom:6,display:"block" }}
                onChange={e=>handlePrizeImageUpload(e,false)}/>
            )}
          </div>
        ))}
        {newForm.imageUrl&&<img src={newForm.imageUrl} alt="" style={{ height:50,objectFit:"contain",marginBottom:6 }}/>}
        {/* v45: status-indikator + disablet legg-til-knapp under prosessering. */}
        {(newBgRemoving || newBgDone) && (
          <p style={{
            margin:"0 0 8px", fontSize:11, fontWeight:"bold",
            color: newBgRemoving ? "#C9A84C" : "#4caf50",
          }}>
            {newBgRemoving
              ? "⏳ Behandler bilde — vent til 'Legg til' aktiveres…"
              : "✓ Bildet er klart for lagring"}
          </p>
        )}
        <button style={{ ...S.addBtn, opacity: (saving || newBgRemoving) ? 0.5 : 1, cursor: (saving || newBgRemoving) ? "wait" : "pointer" }}
          onClick={addPrize}
          disabled={saving || newBgRemoving}
          title={newBgRemoving ? "Vent til bakgrunns-fjerning er ferdig" : ""}>
          {saving ? "Lagrer…" : newBgRemoving ? "⏳ Vent på bilde-prosessering…" : "+ Legg til premie"}
        </button>
      </div>
    </div>
  );
}

// ─── REGNSKAP ─────────────────────────────────────────────────────────────────

// ─── KREDITT-OVERSIKT ─────────────────────────────────────────────────────────
// v117: mountet for første gang i Regnskap-tab (PC + mobil). CSV-eksport +
// Skriv ut. Print bruker enkel strategi: samme liste rendres, men @media print
// styler om farger til svart-på-hvitt og skjuler knapper/delete-ikoner.
// v119: minimerbar boks (default kollapset). Header er klikkbar. Print-listen
// rendres alltid i DOM med display:none når kollapset; @media print over-
// styrer til block !important slik at utskrift fungerer uavhengig av state.
function KredittOversikt({ pcLayout = false }) {
  const [credits, setCredits] = useState([]);
  const [expanded, setExpanded] = useState(false);
  // v252: per-spiller transaksjonshistorikk. (v253: innskudd-skjema fjernet —
  // tilgode oppstår kun via avlysning; ingen vei for å øke saldo manuelt.)
  const [histFor, setHistFor] = useState(null); // e-post hvis historikk er åpen
  const [histRows, setHistRows] = useState([]);
  const [histBusy, setHistBusy] = useState(false);
  useEffect(()=>{
    const unsub = onSnapshot(collection(db,"credits"),snap=>{
      setCredits(snap.docs.map(d=>({ id:d.id,...d.data() })).sort((a,b)=>(b.amount||0)-(a.amount||0)));
    });
    return unsub;
  },[]);
  // v252: skjul 0-saldoer — handleBuy setter brukt-opp tilgode til 0 (setDoc)
  // i stedet for å slette (delete er strammet til arrangør-only i rules).
  const visibleCredits = credits.filter(c=>(c.amount||0)>0);
  const total = visibleCredits.reduce((s,c)=>s+(c.amount||0),0);

  // v252: lazy-last transaksjonslogg for én spiller (toggle).
  const toggleHistorikk = async (email) => {
    if(histFor === email){ setHistFor(null); setHistRows([]); return; }
    setHistFor(email); setHistBusy(true); setHistRows([]);
    try {
      const snap = await getDocs(collection(db,"credits",email,"transactions"));
      setHistRows(snap.docs.map(d=>d.data()).sort((a,b)=>(b.dato?.seconds||0)-(a.dato?.seconds||0)));
    } catch(e){ console.error("Historikk-feil:", e); }
    finally { setHistBusy(false); }
  };
  const txLabel = (t)=> t==="innskudd" ? "➕ Innskudd" : t==="avlysning" ? "↩ Avlysning" : t==="brukt" ? "🎟 Brukt" : t==="etterregistrert" ? "✅ Etterregistrert" : t;
  const txColor = (t)=> t==="brukt" ? "#e57373" : "#4caf50";

  const formatKortDato = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d.getTime())) return "—";
    return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
  };

  const exportCSV = (e) => {
    e?.stopPropagation();
    const rows = [["Navn","Email","Beløp","Sist oppdatert"]];
    visibleCredits.forEach(c => rows.push([c.name||"", c.email||c.id, c.amount||0, formatKortDato(c.lastUpdated)]));
    const csv = rows.map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob(["﻿"+csv],{type:"text/csv;charset=utf-8"});
    const url = URL.createObjectURL(blob);
    const today = new Date();
    const fname = `vinloddet-tilgodehavende-${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}.csv`;
    const a = document.createElement("a"); a.href=url; a.download=fname; a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = (e) => { e?.stopPropagation(); window.print(); };

  const wrapperStyle = pcLayout
    ? {
        marginTop:16,
        background:"linear-gradient(145deg,#141414 0%,#0e0e0e 100%)",
        border:"1px solid #2a2a2a", borderLeft:"3px solid #C9A84C",
        borderRadius:10, padding:"22px 24px",
        boxShadow:"0 3px 10px rgba(0,0,0,0.4)",
      }
    : { marginTop:16, padding:14, background:"#0d0d0d", border:"1px solid #1a4a1a", borderRadius:6 };

  const titleStyle = pcLayout
    ? { margin:0, fontSize:14, color:"#C9A84C", fontWeight:700,
        letterSpacing:"0.12em", textTransform:"uppercase",
        display:"flex", alignItems:"center", gap:8 }
    : { margin:0, fontSize:10, color:"#4caf50", textTransform:"uppercase",
        letterSpacing:"0.1em", fontWeight:"bold" };

  const chevronColor = pcLayout ? "#C9A84C" : "#4caf50";

  const printStyles = `
    .vl-print-title-block { display: none; }
    @media print {
      body * { visibility: hidden; }
      .vl-print-area, .vl-print-area * { visibility: visible; }
      .vl-print-area {
        position: absolute; left: 0; top: 0; width: 100%;
        padding: 20px; background: white !important; color: black !important;
        font-family: Arial, sans-serif !important;
        border: none !important; box-shadow: none !important;
      }
      .vl-print-area * {
        background: white !important; color: black !important;
        border-color: #ccc !important; box-shadow: none !important;
      }
      .vl-print-area .vl-no-print { display: none !important; }
      .vl-print-area .vl-print-list { display: block !important; }
      .vl-print-title-block { display: block !important; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 2px solid #000 !important; }
      .vl-print-title-block h2 { font-size: 18pt; margin: 0 0 4px 0; font-weight: bold; }
      .vl-print-title-block p { font-size: 10pt; margin: 0; }
    }
  `;

  return (
    <div className="vl-print-area" style={wrapperStyle}>
      <style dangerouslySetInnerHTML={{ __html: printStyles }} />
      <div className="vl-print-title-block">
        <h2>Spillers tilgodehavende — Vinloddet</h2>
        <p>Utskrift {formatKortDato(new Date())} · Totalt {total},-</p>
      </div>
      <div onClick={()=>setExpanded(v=>!v)}
        style={{ display:"flex",alignItems:"center",justifyContent:"space-between",
                 marginBottom: expanded ? 10 : 0, flexWrap:"wrap", gap:8,
                 cursor:"pointer", userSelect:"none" }}>
        <p style={titleStyle}>
          <span style={{ color:chevronColor, fontSize:10, marginRight:6, display:"inline-block", width:10 }}>
            {expanded ? "▼" : "▶"}
          </span>
          🎟 Spillers tilgodehavende
        </p>
        <div style={{ display:"flex",gap:8,alignItems:"center",flexWrap:"wrap" }}>
          {expanded && visibleCredits.length > 0 && (
            <>
              <button className="vl-no-print" onClick={exportCSV}
                style={{ padding:"5px 12px",background:"#0d2a0d",border:"1px solid #1a4a1a",color:"#4caf50",borderRadius:4,cursor:"pointer",fontSize:11,fontWeight:"bold" }}>
                ⬇ CSV
              </button>
              <button className="vl-no-print" onClick={handlePrint}
                style={{ padding:"5px 12px",background:"#2a2410",border:"1px solid #5a4a1a",color:"#C9A84C",borderRadius:4,cursor:"pointer",fontSize:11,fontWeight:"bold" }}>
                🖨 Skriv ut
              </button>
            </>
          )}
          <span style={{ fontSize:12,color:"#4caf50",fontWeight:"bold" }}>Totalt: {total},-</span>
        </div>
      </div>
      <div className="vl-print-list" style={{ display: expanded ? "block" : "none" }}>
        {visibleCredits.length === 0 ? (
          <p style={{ margin:0, color:"#666", fontSize:13 }}>Ingen tilgodehavende registrert ennå.</p>
        ) : visibleCredits.map(c=>(
          <div key={c.id}>
            <div style={{ display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:"1px solid #1a2a1a",flexWrap:"wrap" }}>
              <span style={{ flex:1,fontSize:12,color:"#ccc",minWidth:120 }}>{c.name||c.id}</span>
              <span style={{ fontSize:11,color:"#555",minWidth:140 }}>{c.email||c.id}</span>
              <span style={{ fontSize:11,color:"#888",minWidth:80,textAlign:"right" }}>{formatKortDato(c.lastUpdated)}</span>
              <span style={{ fontSize:13,color:"#4caf50",fontWeight:"bold",minWidth:50,textAlign:"right" }}>{c.amount},-</span>
              <button className="vl-no-print" onClick={(e)=>{ e.stopPropagation(); toggleHistorikk(c.id); }}
                title="Vis transaksjonshistorikk"
                style={{ padding:"3px 8px",background:histFor===c.id?"#2a2410":"#141414",border:"1px solid #3a3a2a",color:"#C9A84C",borderRadius:3,cursor:"pointer",fontSize:10 }}>🕓</button>
              <button className="vl-no-print" onClick={async(e)=>{
                e.stopPropagation();
                if(!await showConfirm(`Nullstille tilgodehavende for ${c.name||c.id}?`)) return;
                await deleteDoc(doc(db,"credits",c.id));
              }} style={{ padding:"3px 8px",background:"#2a0d0d",border:"1px solid #5a1a1a",color:"#e57373",borderRadius:3,cursor:"pointer",fontSize:10 }}>✕</button>
            </div>
            {histFor===c.id && (
              <div className="vl-no-print" style={{ padding:"6px 0 10px 12px", borderBottom:"1px solid #1a2a1a" }}>
                {histBusy ? (
                  <p style={{ margin:0, fontSize:11, color:"#666" }}>Laster historikk…</p>
                ) : histRows.length===0 ? (
                  <p style={{ margin:0, fontSize:11, color:"#666" }}>Ingen transaksjoner registrert.</p>
                ) : histRows.map((t,i)=>(
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"3px 0", fontSize:11 }}>
                    <span style={{ minWidth:90, color:txColor(t.type), fontWeight:"bold" }}>{txLabel(t.type)}</span>
                    <span style={{ minWidth:70, textAlign:"right", color:txColor(t.type) }}>{t.type==="brukt"?"-":"+"}{t.beløp},-</span>
                    <span style={{ minWidth:90, textAlign:"right", color:"#888" }}>saldo: {t.saldoEtter},-</span>
                    <span style={{ minWidth:90, color:"#666" }}>{formatKortDato(t.dato)}</span>
                    {t.ref && <span style={{ color:"#555", fontStyle:"italic" }}>{t.ref}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Regnskap({ participants, prizes, settings, pcLayout = false }) {
  const [accumulated, setAccumulated] = useState({ totalInntekt:0, totalUtgift:0, manuellKorreksjon:0 });
  const [korreksjonInput, setKorreksjonInput] = useState("");
  const [editKorreksjon, setEditKorreksjon] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(()=>{
    const unsub=onSnapshot(doc(db,"settings","regnskap"),snap=>{ if(snap.exists()) setAccumulated(snap.data()); });
    return unsub;
  },[]);
  const rundeInntekt = participants.filter(p=>p.paid).reduce((s,p)=>s+(p.amount||0),0);
  const rundeUtgift = prizes.reduce((s,p)=>s+(parseInt(p.price)||0),0);
  const ubetalte = participants.filter(p=>!p.paid).reduce((s,p)=>s+(p.amount||0),0);
  const totalInntekt = (accumulated.totalInntekt||0) + rundeInntekt;
  const totalUtgift = (accumulated.totalUtgift||0) + rundeUtgift;
  const korreksjon = accumulated.manuellKorreksjon||0;
  const beholdning = totalInntekt - totalUtgift + korreksjon;
  const cBeholdning = useCountUp(Math.abs(beholdning));
  const cInntekt = useCountUp(totalInntekt);
  const cUtgift = useCountUp(totalUtgift);
  const lagreKorreksjon = async()=>{
    const val = parseFloat(korreksjonInput.replace(",",".")) || 0;
    if(val===0){setEditKorreksjon(false);return;}
    setSaving(true);
    await setDoc(doc(db,"settings","regnskap"),{ ...accumulated, manuellKorreksjon:(korreksjon+val) },{ merge:true });
    setKorreksjonInput(""); setEditKorreksjon(false); setSaving(false);
  };
  // v47: PC-prototype-grenen. Stor kassebeholdnings-tile øverst, så
  // 2-kolonners grid for "Denne runden" + "Totalt alle runder", deretter
  // Premier + Manuell korreksjon side om side. Hovedoverskriftene er store
  // (18-22 px gull caps) med ikoner; verditall er 30-38 px for visuell vekt.
  if (pcLayout) {
    return (
      <div style={{ padding:0 }}>
        <style>{`
          .vl-pc-rg-card{
            background:linear-gradient(145deg,#141414 0%,#0e0e0e 100%);
            border:1px solid #2a2a2a;border-left:3px solid #C9A84C;
            border-radius:10px;padding:22px 24px;margin-bottom:16px;
            box-shadow:0 3px 10px rgba(0,0,0,0.4);
          }
          .vl-pc-rg-title{
            margin:0 0 14px;font-size:14px;color:#C9A84C;font-weight:700;
            letter-spacing:0.12em;text-transform:uppercase;
            display:flex;align-items:center;gap:8px;
          }
          .vl-pc-rg-row{
            display:flex;align-items:center;justify-content:space-between;
            padding:10px 0;border-bottom:1px solid #1f1f1f;
          }
          .vl-pc-rg-row:last-child{border-bottom:none;}
          .vl-pc-rg-label{font-size:13px;color:#aaa;}
          .vl-pc-rg-val{font-size:18px;font-weight:bold;font-family:'Inter',sans-serif;}
        `}</style>

        {/* HERO: Total kassebeholdning — full-bredde fremhevet kort */}
        <div className="vl-pc-rg-card" style={{
          background:`linear-gradient(135deg, ${beholdning>=0?"#C9A84C22":"#e5737322"} 0%, ${beholdning>=0?"#1a1500":"#1a0a0a"} 60%, #0c0c0c 100%)`,
          borderLeft:`4px solid ${beholdning>=0?"#C9A84C":"#e57373"}`,
          padding:"28px 32px", display:"flex", alignItems:"center", gap:24,
        }}>
          <div style={{
            fontSize:48, width:80, height:80, flexShrink:0,
            display:"flex", alignItems:"center", justifyContent:"center",
            background:`linear-gradient(145deg, ${beholdning>=0?"#C9A84C33":"#e5737333"} 0%, ${beholdning>=0?"#C9A84C10":"#e5737310"} 100%)`,
            border:`1px solid ${beholdning>=0?"#C9A84C55":"#e5737355"}`,
            borderRadius:16, boxShadow:`0 0 20px ${beholdning>=0?"#C9A84C55":"#e5737355"}`,
          }}>{/* v54 FIX 3: 🏦 (bank-bygg) → 💰 (pengesekk) — representerer
              kasse/penger tydeligere enn et bygg-ikon. */}💰</div>
          <div style={{ flex:1 }}>
            <p style={{ margin:0, fontSize:11, color:"#888", textTransform:"uppercase", letterSpacing:"0.18em", fontWeight:600 }}>Total kassebeholdning</p>
            <p style={{
              margin:"6px 0 0", fontSize:42, fontWeight:800,
              color:beholdning>=0?"#C9A84C":"#e57373",
              letterSpacing:"-0.02em",
              textShadow:`0 0 20px ${beholdning>=0?"#C9A84C55":"#e5737355"}`,
            }}>{beholdning<0?"-":""}{cBeholdning},-</p>
          </div>
        </div>

        {/* 2-kolonner: Denne runden + Totalt alle runder */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
          <div className="vl-pc-rg-card">
            <p className="vl-pc-rg-title">📊 Denne runden</p>
            <div className="vl-pc-rg-row">
              <span className="vl-pc-rg-label">💰 Inntekter (betalte lodd)</span>
              <span className="vl-pc-rg-val" style={{ color:"#4caf50" }}>{rundeInntekt},-</span>
            </div>
            <div className="vl-pc-rg-row">
              <span className="vl-pc-rg-label">🍷 Utgifter (premier)</span>
              <span className="vl-pc-rg-val" style={{ color:"#e57373" }}>{rundeUtgift},-</span>
            </div>
            <div className="vl-pc-rg-row">
              <span className="vl-pc-rg-label">📋 Ubetalte lodd</span>
              <span className="vl-pc-rg-val" style={{ color:"#888" }}>{ubetalte},-</span>
            </div>
          </div>
          <div className="vl-pc-rg-card">
            <p className="vl-pc-rg-title">📈 Totalt alle runder</p>
            <div className="vl-pc-rg-row">
              <span className="vl-pc-rg-label">💰 Sum inntekter</span>
              <span className="vl-pc-rg-val" style={{ color:"#4caf50" }}>{cInntekt},-</span>
            </div>
            <div className="vl-pc-rg-row">
              <span className="vl-pc-rg-label">🍷 Sum utgifter</span>
              <span className="vl-pc-rg-val" style={{ color:"#e57373" }}>{cUtgift},-</span>
            </div>
            {korreksjon !== 0 && (
              <div className="vl-pc-rg-row">
                <span className="vl-pc-rg-label">✏️ Manuell korreksjon</span>
                <span className="vl-pc-rg-val" style={{ color:korreksjon>=0?"#4caf50":"#e57373" }}>{korreksjon>0?"+":""}{korreksjon},-</span>
              </div>
            )}
          </div>
        </div>

        {/* 2-kolonner: Premier denne runden + Manuell korreksjon */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
          <div className="vl-pc-rg-card">
            <p className="vl-pc-rg-title">🍷 Premier denne runden</p>
            {prizes.length === 0 ? (
              <p style={{ margin:0, color:"#666", fontSize:13 }}>Ingen premier definert ennå.</p>
            ) : prizes.map((p,i)=>(
              <div key={p.id} className="vl-pc-rg-row">
                <span className="vl-pc-rg-label">{i+1}. {p.name}</span>
                <span className="vl-pc-rg-val" style={{ color:"#e57373", fontSize:15 }}>{p.price?`${p.price},-`:"—"}</span>
              </div>
            ))}
          </div>
          <div className="vl-pc-rg-card">
            <p className="vl-pc-rg-title">✏️ Manuell korreksjon</p>
            {!editKorreksjon ? (
              <>
                <p style={{ margin:"0 0 12px", fontSize:12, color:"#999", lineHeight:1.5 }}>
                  Justér kassebeholdningen for svinn, registreringsfeil eller andre engangs-poster.
                </p>
                <button
                  onClick={()=>setEditKorreksjon(true)}
                  style={{
                    padding:"8px 14px", background:"#1a1200", border:"1px solid #C9A84C",
                    color:"#C9A84C", borderRadius:5, cursor:"pointer", fontSize:12, fontWeight:"bold",
                  }}>
                  + Legg til korreksjon
                </button>
              </>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                <p style={{ margin:0, fontSize:11, color:"#888", lineHeight:1.5 }}>
                  Positivt = inntekt. Negativt = utgift/svinn. Eks: <code style={{ color:"#e57373" }}>-150</code> for 150 kr svinn.
                </p>
                <input style={{ ...S.input, marginBottom:0, maxWidth:200 }}
                  placeholder="Beløp (f.eks. -150)" type="number"
                  value={korreksjonInput}
                  onChange={e=>setKorreksjonInput(e.target.value)}/>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={lagreKorreksjon} disabled={saving}
                    style={{
                      padding:"7px 14px", background:"#0d2a0d", border:"1px solid #1a4a1a",
                      color:"#4caf50", borderRadius:4, cursor:"pointer", fontSize:12, fontWeight:"bold",
                    }}>
                    {saving ? "Lagrer…" : "✓ Legg til"}
                  </button>
                  <button onClick={()=>setEditKorreksjon(false)}
                    style={{
                      padding:"7px 14px", background:"transparent", border:"1px solid #3a3a3a",
                      color:"#888", borderRadius:4, cursor:"pointer", fontSize:12,
                    }}>
                    Avbryt
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding:16 }}>
      <div style={{ ...S.regnskapRow,background:"#1a1200",border:"1px solid #C9A84C",marginBottom:16 }}>
        <span style={{ ...S.regnskapLabel,color:"#C9A84C",fontWeight:"bold" }}>🏦 Total kassebeholdning</span>
        <span style={{ ...S.regnskapVal,color:beholdning>=0?"#C9A84C":"#e57373",fontSize:22 }}>{beholdning<0?"-":""}{cBeholdning},-</span>
      </div>
      <p style={S.sectionLabel}>Denne runden</p>
      <div style={{ display:"flex",flexDirection:"column",gap:6,marginBottom:16 }}>
        <div style={S.regnskapRow}><span style={S.regnskapLabel}>💰 Inntekter (betalte lodd)</span><span style={{ ...S.regnskapVal,color:"#4caf50" }}>{rundeInntekt},-</span></div>
        <div style={S.regnskapRow}><span style={S.regnskapLabel}>🍷 Utgifter (premier)</span><span style={{ ...S.regnskapVal,color:"#e57373" }}>{rundeUtgift},-</span></div>
        <div style={S.regnskapRow}><span style={S.regnskapLabel}>📋 Ubetalte lodd</span><span style={{ ...S.regnskapVal,color:"#888" }}>{ubetalte},-</span></div>
      </div>
      <p style={S.sectionLabel}>Totalt alle runder</p>
      <div style={{ display:"flex",flexDirection:"column",gap:6,marginBottom:16 }}>
        <div style={S.regnskapRow}><span style={S.regnskapLabel}>💰 Sum inntekter</span><span style={{ ...S.regnskapVal,color:"#4caf50" }}>{cInntekt},-</span></div>
        <div style={S.regnskapRow}><span style={S.regnskapLabel}>🍷 Sum utgifter</span><span style={{ ...S.regnskapVal,color:"#e57373" }}>{cUtgift},-</span></div>
        {korreksjon!==0&&<div style={S.regnskapRow}><span style={S.regnskapLabel}>✏️ Manuell korreksjon</span><span style={{ ...S.regnskapVal,color:korreksjon>=0?"#4caf50":"#e57373" }}>{korreksjon>0?"+":""}{korreksjon},-</span></div>}
      </div>
      {prizes.length>0&&(<>
        <p style={S.sectionLabel}>Premier denne runden</p>
        {prizes.map((p,i)=>(<div key={p.id} style={{ ...S.regnskapRow,marginBottom:4 }}><span style={S.regnskapLabel}>{i+1}. {p.name}</span><span style={{ ...S.regnskapVal,color:"#e57373",fontSize:14 }}>{p.price?`${p.price},-`:"—"}</span></div>))}
      </>)}
      <div style={{ background:"#0d0d0d",border:"1px solid #3a3a3a",borderRadius:4,padding:14,marginTop:12 }}>
        <p style={{ ...S.sectionLabel,marginBottom:8 }}>✏️ Manuell korreksjon</p>
        {!editKorreksjon?(
          <button style={S.editBtn} onClick={()=>setEditKorreksjon(true)}>Legg til korreksjon (svinn, feil, etc.)</button>
        ):(
          <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
            <p style={{ margin:0,fontSize:11,color:"#666" }}>Positivt = inntekt. Negativt = utgift/svinn. Eks: -150 for 150 kr svinn.</p>
            <input style={S.input} placeholder="Beløp (f.eks. -150)" value={korreksjonInput} onChange={e=>setKorreksjonInput(e.target.value)} type="number"/>
            <div style={{ display:"flex",gap:8 }}>
              <button style={S.saveBtn} onClick={lagreKorreksjon} disabled={saving}>{saving?"Lagrer…":"✓ Legg til"}</button>
              <button style={S.cancelBtn} onClick={()=>setEditKorreksjon(false)}>Avbryt</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PURRING-KNAPP ────────────────────────────────────────────────────────────
function PurringKnapp({ week, participants }) {
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  // v206: purring sendes nå server-side via Resend (callable sendReminderEmails).
  // Klienten resolver mottakere; serveren leser mal fra settings + sender.
  const sendPurring = async () => {
    const recipients = participants
      .filter(p => p.email)
      .map(p => ({ email: p.email, name: capName(p.name), tickets: p.tickets, amount: p.amount }));
    if (recipients.length === 0) { showAlert("Ingen ubetalte deltakere med e-post."); return; }
    setSending(true);
    try {
      const fn = httpsCallable(getFunctions(app, "europe-west1"), "sendReminderEmails");
      const res = await fn({ type: "unpaid", recipients, weekLabel: week.weekLabel || "" });
      const { sent = 0, failed = 0, skipped = 0 } = res.data || {};
      if (failed > 0) {
        showAlert(`Sendt ${sent} purring. ${failed} feilet${skipped > 0 ? `, ${skipped} hoppet over` : ""}.`);
      } else {
        setDone(true); setTimeout(()=>setDone(false),3000);
      }
    } catch (err) {
      console.error("sendReminderEmails feilet:", err);
      showAlert(`Purring feilet: ${err?.message || "ukjent feil"}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <button style={{ padding:"3px 10px",background:"#1a0a0a",border:"1px solid #5a2a2a",color:"#e57373",borderRadius:4,cursor:"pointer",fontSize:10,fontWeight:"bold" }}
      onClick={sendPurring} disabled={sending}>
      {sending?"Sender…":done?"✅ Sendt!":"📧 Send purring"}
    </button>
  );
}

// ─── PREMIE-HENTING PURRING ─────────────────────────────────────────────────
function PremieHentingPurring({ vinner, weekLabel }) {
  const [sending,setSending]=useState(false);
  const [done,setDone]=useState(false);
  // v206: premie-henting-purring sendes nå server-side via Resend. Beholder
  // history-oppslaget (vinner har kun navn i historikk → finn e-post via navn).
  const sendPurring = async () => {
    setSending(true);
    try {
      const histSnap = await getDocs(collection(db,"history"));
      let email = null;
      histSnap.docs.forEach(d=>{ const data=d.data(); (data.participants||[]).forEach(p=>{ if(p.name===vinner.name&&p.email) email=p.email; }); });
      if(!email){ showAlert(`Fant ingen e-post for ${vinner.name}`); return; }
      const fn = httpsCallable(getFunctions(app, "europe-west1"), "sendReminderEmails");
      const res = await fn({ type:"unclaimed", recipients:[{ email, name:capName(vinner.name), prize:vinner.prize }], weekLabel: weekLabel || "" });
      const { sent = 0 } = res.data || {};
      if (sent > 0) { setDone(true); setTimeout(()=>setDone(false),3000); }
      else showAlert("Sending feilet.");
    } catch (err) {
      console.error("sendReminderEmails feilet:", err);
      showAlert(`Sending feilet: ${err?.message || "ukjent feil"}`);
    } finally {
      setSending(false);
    }
  };
  return (
    <button onClick={sendPurring} disabled={sending}
      style={{ fontSize:10,padding:"2px 8px",borderRadius:10,cursor:"pointer",fontWeight:"bold",
        background:"#1a1a2a",color:"#64b5f6",border:"1px solid #2a2a5a",whiteSpace:"nowrap" }}>
      {sending?"Sender…":done?"✅ Sendt!":"📧 Purr henting"}
    </button>
  );
}

// ─── UBETALTE FRA HISTORIKK ───────────────────────────────────────────────────
function UbetalteHistorikk({ settings }) {
  const [history, setHistory] = useState([]);
  useEffect(()=>{
    const unsub = onSnapshot(collection(db,"history"), snap=>{
      setHistory(snap.docs.map(d=>({ id:d.id,...d.data() })).sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0)));
    });
    return unsub;
  },[]);

  const alleUbetalte = [];
  history.forEach(week=>{
    // v285: avlyste runder hører ikke hjemme i ubetalt-panelet — det finnes
    // ingen betalingsplikt for et avlyst lotteri, så verken listing eller
    // purring skal tilbys. Runden vises fortsatt i Statistikk-historikken.
    if (week.avlyst) return;
    const dato = week.createdAt ? (week.createdAt.toDate ? week.createdAt.toDate() : new Date(week.createdAt)) : null;
    const datoStr = dato ? dato.toLocaleString("nb-NO",{ day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit" }) : (week.weekLabel||"");
    (week.participants||[]).forEach((p,i)=>{
      if(!p.paid && p.email) alleUbetalte.push({ ...p, weekLabel:week.weekLabel, datoStr, weekId:week.id, origIdx:i });
    });
  });

  if(!alleUbetalte.length) return (
    <div style={{ marginTop:16 }}>
      <p style={{ margin:"0 0 8px",fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:"0.1em" }}>Ubetalte lodd fra tidligere runder</p>
      <p style={{ color:"#4caf50",fontSize:13 }}>✅ Ingen ubetalte lodd fra tidligere runder</p>
    </div>
  );

  const totalUbetalt = alleUbetalte.reduce((s,p)=>s+(p.amount||0),0);
  return (
    <div style={{ marginTop:20 }}>
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8 }}>
        <p style={{ margin:0,fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:"0.1em" }}>
          Ubetalte lodd fra tidligere runder
        </p>
        <span style={{ fontSize:12,color:"#e57373",fontWeight:"bold" }}>{totalUbetalt},-</span>
      </div>
      <button
        style={{ marginBottom:10,padding:"6px 14px",background:"#0d2a0d",border:"1px solid #1a4a1a",color:"#4caf50",borderRadius:4,cursor:"pointer",fontSize:11,fontWeight:"bold" }}
        onClick={async()=>{
          if(!await showConfirm(`Merk alle ${alleUbetalte.length} ubetalte fra tidligere runder som betalt?

Dette oppdaterer historikken og kan ikke angres.`)) return;
          const byWeek = {};
          alleUbetalte.forEach(p=>{ if(!byWeek[p.weekId]) byWeek[p.weekId]=[]; byWeek[p.weekId].push(p.origIdx); });
          for(const [weekId,idxs] of Object.entries(byWeek)){
            const snap=await getDoc(doc(db,"history",weekId));
            if(!snap.exists()) continue;
            const parts=[...snap.data().participants];
            idxs.forEach(idx=>{ if(parts[idx]) parts[idx]={ ...parts[idx],paid:true }; });
            await updateDoc(doc(db,"history",weekId),{ participants:parts });
          }
        }}>
        ✓ Merk alle som betalt
      </button>
      {alleUbetalte.map((p,i)=>(
        <div key={i} style={{ display:"grid",gridTemplateColumns:"1fr auto auto auto auto",gap:"4px 8px",padding:"10px 12px",background:"#0d0d0d",borderRadius:4,marginBottom:4,border:"1px solid #3a3a3a",alignItems:"center" }}>
          <div>
            <div style={{ fontSize:12,color:"#ccc",fontWeight:"bold" }}>{p.name}</div>
            {p.email&&<div style={{ fontSize:10,color:"#666",marginTop:1 }}>{p.email}</div>}
            <div style={{ fontSize:10,color:"#555",marginTop:1 }}>📅 {p.datoStr} · {p.weekLabel}</div>
          </div>
          <span style={{ fontSize:12,color:"#C9A84C" }}>{p.tickets} lodd</span>
          <span style={{ fontSize:12,color:"#e57373" }}>{p.amount},-</span>
          <span onClick={async()=>{
            const snap=await getDoc(doc(db,"history",p.weekId));
            if(!snap.exists()) return;
            const parts=[...snap.data().participants];
            if(parts[p.origIdx]) parts[p.origIdx]={ ...parts[p.origIdx],paid:true };
            await updateDoc(doc(db,"history",p.weekId),{ participants:parts });
          }} style={{ fontSize:10,padding:"3px 7px",borderRadius:3,background:"#2a0d0d",color:"#e57373",cursor:"pointer",border:"1px solid #5a1a1a",whiteSpace:"nowrap" }}>
            Merk betalt
          </span>
          <PurringKnapp week={{ weekLabel:p.weekLabel }} participants={[p]}/>
        </div>
      ))}
    </div>
  );
}

// ─── STATISTIKK ───────────────────────────────────────────────────────────────
function Statistikk({ pcLayout = false }) {
  const [history,setHistory]=useState([]);
  const [expanded,setExpanded]=useState({});
  // v52 FIX 4: uke-filter for historikken (kun PC-prototypen). "all" = alle uker.
  const [filterWeek,setFilterWeek]=useState("all");
  // v314 (fase 3 steg 2): fremdrift for godkjennBetaling. Per-deltaker nøkkel
  // "weekId:email"; «godkjenn alle» nøkkel = weekId.
  const [godkjennBusy,setGodkjennBusy]=useState({});
  useEffect(()=>{
    const unsub=onSnapshot(collection(db,"history"),snap=>{
      setHistory(snap.docs.map(d=>({ id:d.id,...d.data() })).sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0)));
    });
    return unsub;
  },[]);

  // v52 FIX 4: synlig delmengde basert på dropdown-valget.
  const visibleHistory = filterWeek==="all" ? history : history.filter(w=>w.id===filterWeek);

  // v54 FIX 2: formatér trekningstidspunktet (createdAt) som "dd.mm.yyyy"
  // til dropdown-filteret, slik at filteret matcher "Trekningsdato"-labelen.
  const formatTrekningsdato = (ts) => {
    if (!ts) return "Ukjent dato";
    const d = ts.seconds ? new Date(ts.seconds*1000) : new Date(ts);
    if (isNaN(d.getTime())) return "Ukjent dato";
    return `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}.${d.getFullYear()}`;
  };

  const allExpanded = history.length>0 && history.every(w=>expanded[w.id]);
  const toggleAllExpanded = () => {
    if (allExpanded) { setExpanded({}); }
    else { const all={}; history.forEach(w=>{ all[w.id]=true; }); setExpanded(all); }
  };
  const merkAllePremierHentet = async (week) => {
    const updated=(week.winners||[]).map(w=>({ ...w,premieHentet:true }));
    await updateDoc(doc(db,"history",week.id),{ winners:updated });
  };
  // v53: toggle hentet-status for én enkelt premie (delt mellom PC- og
  // mobil-grenen av vinner-raden så onClick-logikken ikke dupliseres).
  const togglePremieHentet = async (week, i) => {
    const updated=(week.winners||[]).map((ww,wj)=>wj===i?{ ...ww,premieHentet:!ww.premieHentet }:ww);
    await updateDoc(doc(db,"history",week.id),{ winners:updated });
  };
  const merkAlleBetalt = async (week) => {
    const updatedParts=week.participants.map(p=>({ ...p,paid:true }));
    await updateDoc(doc(db,"history",week.id),{ participants:updatedParts });
    const totalDelta=week.participants.filter(p=>!p.paid).reduce((s,p)=>s+(p.amount||0),0);
    if(totalDelta>0){
      const regSnap=await getDoc(doc(db,"settings","regnskap"));
      const ex=regSnap.exists()?regSnap.data():{ totalInntekt:0,totalUtgift:0,manuellKorreksjon:0 };
      await setDoc(doc(db,"settings","regnskap"),{ ...ex,totalInntekt:(ex.totalInntekt||0)+totalDelta },{ merge:true });
    }
  };
  // v314 (fase 3 steg 2): oversett serverens HttpsError-koder til norsk tekst.
  const godkjennFeilTekst = (e) => {
    const msg = (e && e.message) || "";
    if (msg.includes("ingen-ubetalte-rader")) return "Allerede godkjent eller ingen ubetalt rad.";
    if (msg.includes("ikke-avlyst")) return "Gjelder kun avlyste runder.";
    if (msg.includes("ukjent-uke")) return "Fant ikke runden.";
    if (msg.includes("ugyldig-epost")) return "Ugyldig e-post.";
    if (msg.includes("permission-denied") || msg.toLowerCase().includes("arrangør")) return "Kun arrangører kan godkjenne betaling.";
    return "Noe gikk galt ved godkjenning. Prøv igjen.";
  };
  // v314: per-deltaker «Merk betalt» på avlyste uker. Kaller godkjennBetaling
  // (server-side, atomisk, idempotent). onSnapshot på history fjerner knappen
  // automatisk når raden blir paid/tilgodeGitt.
  const godkjennBetalingFor = async (week, email, navn) => {
    const key = `${week.id}:${email}`;
    setGodkjennBusy(b=>({ ...b,[key]:true }));
    try {
      const fn = httpsCallable(getFunctions(app,"europe-west1"),"godkjennBetaling");
      const res = await fn({ weekId: week.id, email });
      const kreditert = res.data?.kreditert ?? 0;
      await showAlert(`✅ Betaling godkjent.\n${kreditert} kr lagt til ${navn||email} sin tilgode.`);
    } catch(e) {
      console.error("godkjennBetaling feilet:", e);
      await showAlert(`⛔ Kunne ikke godkjenne.\n${godkjennFeilTekst(e)}`);
    } finally {
      setGodkjennBusy(b=>{ const n={ ...b }; delete n[key]; return n; });
    }
  };
  // v314: «Godkjenn alle» — loop over UNIKE ubetalte e-poster i en avlyst uke,
  // ett kall per e-post (sekvensielt). Allerede-godkjente fanges som
  // 'ingen-ubetalte-rader' og hoppes over (ikke vist som feil).
  const godkjennAlleBetalinger = async (week) => {
    setGodkjennBusy(b=>({ ...b,[week.id]:true }));
    try {
      const epostListe = [...new Set(
        (week.participants||[])
          .filter(p=>p.paid===false && p.tilgodeGitt!==true && p.email)
          .map(p=>p.email.trim().toLowerCase())
      )];
      let antall=0, totalKr=0, hoppet=0, feilet=0;
      const fn = httpsCallable(getFunctions(app,"europe-west1"),"godkjennBetaling");
      for (const email of epostListe) {
        try {
          const res = await fn({ weekId: week.id, email });
          antall++;
          totalKr += res.data?.kreditert ?? 0;
        } catch(e) {
          if ((e?.message||"").includes("ingen-ubetalte-rader")) { hoppet++; }
          else { feilet++; console.error("godkjennBetaling (alle) feilet for", email, e); }
        }
      }
      const feilTekst = feilet>0 ? `\n${feilet} feilet — se konsoll.` : "";
      const hoppTekst = hoppet>0 ? `\n${hoppet} var allerede godkjent (hoppet over).` : "";
      await showAlert(`✅ Godkjent ${antall} betaling(er), totalt ${totalKr} kr.${hoppTekst}${feilTekst}`);
    } finally {
      setGodkjennBusy(b=>{ const n={ ...b }; delete n[week.id]; return n; });
    }
  };
  const exportCSV = () => {
    const rows = [["Runde","Dato","Vinner","Premie","Deltakernavn","Lodd","Beløp","Betalt"]];
    history.forEach(week => {
      (week.winners||[]).forEach(w => {
        rows.push([week.weekLabel||"",formatNorwegianDate(week.createdAt),w.name,w.prize,"","",""]);
      });
      (week.participants||[]).forEach(p => {
        rows.push([week.weekLabel||"",formatNorwegianDate(week.createdAt),"","",p.name,p.tickets,p.amount||0,p.paid?"Ja":"Nei"]);
      });
    });
    const csv = rows.map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff"+csv],{type:"text/csv;charset=utf-8"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url; a.download="vinloddet-statistikk.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // v47: PC-prototype wrapper-stil — full-bredde kort med stor overskrift
  // som matcher de andre Regnskap-kortene.
  const wrapperStyle = pcLayout
    ? {
        background:"linear-gradient(145deg,#141414 0%,#0e0e0e 100%)",
        border:"1px solid #2a2a2a", borderLeft:"3px solid #C9A84C",
        borderRadius:10, padding:"22px 24px",
        boxShadow:"0 3px 10px rgba(0,0,0,0.4)",
      }
    : { padding:16 };
  const titleStyle = pcLayout
    ? { margin:"0 0 14px", fontSize:14, color:"#C9A84C", fontWeight:700,
        letterSpacing:"0.12em", textTransform:"uppercase",
        display:"flex", alignItems:"center", gap:8 }
    : { ...S.sectionLabel, margin:0 };
  return (
    <div style={wrapperStyle}>
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,flexWrap:"wrap",gap:8 }}>
        <p style={titleStyle}>{pcLayout ? "📜 Historikk — alle runder" : "Historikk — alle runder"}</p>
        <div style={{ display:"flex",gap:8,flexWrap:"wrap",alignItems:"center" }}>
          {/* v52 FIX 4: rundefilter — kun PC-prototypen.
              v53 FIX 5: "Trekningsdato:"-label foran dropdownen.
              v54 FIX 2: dropdownen viser nå faktiske trekningsdatoer
              (dd.mm.yyyy) i stedet for ukenummer, så den matcher labelen. */}
          {pcLayout&&history.length>0&&(
            <span style={{ display:"flex",alignItems:"center",gap:6 }}>
              <span style={{ fontSize:11,color:"#999",textTransform:"uppercase",letterSpacing:"0.08em",fontWeight:600,whiteSpace:"nowrap" }}>Trekningsdato:</span>
              <select value={filterWeek} onChange={e=>setFilterWeek(e.target.value)}
                style={{ padding:"5px 10px",background:"#0d0d0d",border:"1px solid #3a3a3a",color:"#C9A84C",
                  borderRadius:4,cursor:"pointer",fontSize:11,fontWeight:"bold" }}>
                <option value="all">Alle datoer ({history.length})</option>
                {history.map(w=>(
                  <option key={w.id} value={w.id}>{formatTrekningsdato(w.createdAt)}</option>
                ))}
              </select>
            </span>
          )}
          {history.length>0&&(
            <button style={{ padding:"5px 12px",background:"#1a1a2a",border:"1px solid #3a3a5a",color:"#aaa",borderRadius:4,cursor:"pointer",fontSize:11 }}
              onClick={toggleAllExpanded}>{allExpanded?"▲ Skjul alle":"▼ Utvid alle"}</button>
          )}
          {history.length>0&&(
            <button style={{ padding:"5px 12px",background:"#0d2a0d",border:"1px solid #1a4a1a",color:"#4caf50",borderRadius:4,cursor:"pointer",fontSize:11,fontWeight:"bold" }}
              onClick={exportCSV}>⬇ Eksport CSV</button>
          )}
        </div>
      </div>
      {history.length===0&&<p style={{ color:"#777",fontSize:13 }}>Statistikk lagres automatisk etter trekning.</p>}
      {/* v52 FIX 4: itererer over filtrert delmengde i stedet for hele history. */}
      {visibleHistory.map((week,wi)=>(
        <div key={week.id} style={{ background:"#0d0d0d",border:"1px solid #3a3a3a",borderRadius:4,marginBottom:10,overflow:"hidden" }}>
          <div style={{ padding:14,cursor:"pointer" }} onClick={()=>setExpanded(e=>({ ...e,[week.id]:!e[week.id] }))}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start" }}>
              <div>
                <p style={{ margin:"0 0 2px",fontSize:13,color:week.avlyst?"#e57373":"#C9A84C",fontWeight:"bold" }}>{week.weekLabel||`Runde ${wi+1}`}</p>
                {/* v52 FIX 2: lysere trekningstidspunkt på PC — var #555
                    (nesten uleselig), nå #aaa (samme grep som v50 FIX 1). */}
                {week.createdAt&&<p style={{ margin:"0 0 4px",fontSize:10,color:pcLayout?"#aaa":"#555" }}>📅 {formatNorwegianDate(week.createdAt)}</p>}
                {week.avlyst&&week.refundert>0&&(
                  <div style={{ display:"inline-flex",alignItems:"center",gap:6,background:"#2a0a0a",border:"1px solid #8B0000",borderRadius:4,padding:"3px 10px",marginTop:2 }}>
                    <span style={{ fontSize:11,color:"#ff6b6b",fontWeight:"bold" }}>💸 Skyldes tilbake: {week.refundert},-</span>
                    <span style={{ fontSize:10,color:"#e57373" }}>— husk å refundere deltakerne!</span>
                  </div>
                )}
              </div>
              {/* v52 FIX 5: på PC flyttes "Merk alle premier som hentet" opp
                  hit — øverst til høyre i runde-raden, ved siden av chevron. */}
              <div style={{ display:"flex",alignItems:"center",gap:10,flexShrink:0 }}>
                {pcLayout&&(week.winners||[]).some(w=>!w.premieHentet)&&(
                  <button onClick={e=>{ e.stopPropagation(); merkAllePremierHentet(week); }}
                    style={{ padding:"4px 11px",background:"#0d2a0d",border:"1px solid #1a4a1a",color:"#4caf50",borderRadius:4,cursor:"pointer",fontSize:10,fontWeight:"bold",whiteSpace:"nowrap" }}>
                    ✓ Merk alle premier som hentet
                  </button>
                )}
                <span style={{ color:"#555",fontSize:12 }}>{expanded[week.id]?"▲":"▼"}</span>
              </div>
            </div>
            {(week.winners||[]).length>0&&(
              <div onClick={e=>e.stopPropagation()}>
                {/* v53 FIX 2+4: på PC rendres vinner-raden som grid med faste
                    kolonner — [nr+navn 220px] [loddnr 90px] [premie 1fr]
                    [status 210px høyrejustert]. Det gir loddnumrene samme
                    x-posisjon som deltaker-listens LODDNR-kolonne (begge
                    starter 220px inn), og premie-navnet ligger på fast x
                    uansett om status-cellen har én eller to knapper. Mobil
                    beholder den gamle flex-raden uendret. */}
                {(week.winners||[]).map((w,i)=> pcLayout ? (
                  <div key={i} style={{ display:"grid",gridTemplateColumns:"220px 90px 1fr 210px",columnGap:12,alignItems:"center",marginTop:6 }}>
                    {/* v52 FIX 1: nøytralt nummer, ingen medalje-rangering. */}
                    <span style={{ display:"flex",alignItems:"center",gap:8,minWidth:0 }}>
                      <span style={{ fontSize:12,fontWeight:"bold",color:"#888",width:22,textAlign:"center",flexShrink:0 }}>{i+1}.</span>
                      <span style={{ fontSize:12,color:"#f5efe0",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{w.name}</span>
                    </span>
                    {/* v53 FIX 1: lysere loddnr-farge (dempet gull #d6c074). */}
                    <span style={{ fontSize:10,color:"#d6c074",whiteSpace:"nowrap" }}>{w.ticketNum?`#${w.ticketNum}`:"—"}</span>
                    <span style={{ fontSize:11,color:"#aaa",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{w.prize}</span>
                    <span style={{ display:"flex",alignItems:"center",gap:6,justifyContent:"flex-end" }}>
                      <span onClick={()=>togglePremieHentet(week,i)}
                        style={{ fontSize:10,padding:"2px 8px",borderRadius:10,cursor:"pointer",fontWeight:"bold",
                          background:w.premieHentet?"#0d2a0d":"#2a1200",color:w.premieHentet?"#4caf50":"#e57373",
                          border:`1px solid ${w.premieHentet?"#1a4a1a":"#5a2a00"}`,whiteSpace:"nowrap" }}>
                        {w.premieHentet?"✓ Hentet":"⏳ Ikke hentet"}
                      </span>
                      {!w.premieHentet&&<PremieHentingPurring vinner={w} weekLabel={week.weekLabel}/>}
                    </span>
                  </div>
                ) : (
                  <div key={i} style={{ display:"flex",alignItems:"center",gap:8,marginTop:6,flexWrap:"wrap" }}>
                    <Medal rank={i+1}/>
                    <span style={{ flex:1,fontSize:12,color:"#f5efe0",minWidth:80 }}>
                      {w.name}{w.ticketNum?<span style={{ fontSize:10,color:"#C9A84C",marginLeft:6 }}>#{w.ticketNum}</span>:null}
                    </span>
                    <span style={{ fontSize:11,color:"#888",flex:1 }}>{w.prize}</span>
                    <span onClick={()=>togglePremieHentet(week,i)}
                      style={{ fontSize:10,padding:"2px 8px",borderRadius:10,cursor:"pointer",fontWeight:"bold",
                      background:w.premieHentet?"#0d2a0d":"#2a1200",color:w.premieHentet?"#4caf50":"#e57373",
                      border:`1px solid ${w.premieHentet?"#1a4a1a":"#5a2a00"}`,whiteSpace:"nowrap" }}>
                      {w.premieHentet?"✓ Hentet":"⏳ Ikke hentet"}
                    </span>
                    {!w.premieHentet&&<PremieHentingPurring vinner={w} weekLabel={week.weekLabel}/>}
                  </div>
                ))}
                {/* v52 FIX 5: original plassering nederst beholdes kun for mobil. */}
                {!pcLayout&&(week.winners||[]).some(w=>!w.premieHentet)&&(
                  <button onClick={()=>merkAllePremierHentet(week)}
                    style={{ marginTop:8,padding:"3px 10px",background:"#0d2a0d",border:"1px solid #1a4a1a",color:"#4caf50",borderRadius:4,cursor:"pointer",fontSize:10,fontWeight:"bold" }}>
                    ✓ Merk alle premier som hentet
                  </button>
                )}
              </div>
            )}
          </div>
          {expanded[week.id]&&(week.participants||[]).length>0&&(
            <div style={{ borderTop:"1px solid #3a3a3a",padding:"12px 14px" }}>
              {/* v54 FIX 4: "Deltakere (…)" er en UNDER-overskrift, ikke en
                  hoved-seksjonsoverskrift — derfor 12px fet lysere grå (#999)
                  i stedet for 14px gull. Konsistent med kolonne-headerne. */}
              <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:pcLayout?12:8,flexWrap:"wrap",gap:6 }}>
                <p style={pcLayout
                  ? { margin:0,fontSize:12,color:"#999",fontWeight:"bold",textTransform:"uppercase",letterSpacing:"0.06em" }
                  : { margin:0,fontSize:10,color:"#888",textTransform:"uppercase",letterSpacing:"0.1em" }}>
                  Deltakere ({week.participants.length} stk · {week.participants.reduce((s,p)=>s+(p.tickets||0),0)} lodd)
                </p>
                <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}>
                  {/* v314: «Merk alle betalt» (merkAlleBetalt) bokfører INNTEKT
                      og gir IKKE kreditt — gjelder kun IKKE-avlyste uker. På
                      avlyste uker er det godkjennBetaling-flyten (kreditt) som
                      gjelder, så denne skjules der. */}
                  {!week.avlyst && week.participants.some(p=>!p.paid)&&(
                    <button onClick={()=>merkAlleBetalt(week)}
                      style={{ padding:"3px 10px",background:"#0d2a0d",border:"1px solid #1a4a1a",color:"#4caf50",borderRadius:4,cursor:"pointer",fontSize:10,fontWeight:"bold" }}>
                      ✓ Merk alle betalt
                    </button>
                  )}
                  {/* v314: «Godkjenn alle betalinger» — kun avlyste uker. Looper
                      over unike ubetalte e-poster og kaller godkjennBetaling
                      (kreditt = amount − kredittBrukt, idempotent). */}
                  {week.avlyst && week.participants.some(p=>p.paid===false && p.tilgodeGitt!==true)&&(
                    <button onClick={()=>godkjennAlleBetalinger(week)} disabled={!!godkjennBusy[week.id]}
                      style={{ padding:"3px 10px",background:"#0d2a0d",border:"1px solid #1a4a1a",color:godkjennBusy[week.id]?"#777":"#4caf50",borderRadius:4,cursor:godkjennBusy[week.id]?"default":"pointer",fontSize:10,fontWeight:"bold" }}>
                      {godkjennBusy[week.id] ? "Godkjenner…" : "✅ Godkjenn alle betalinger"}
                    </button>
                  )}
                  {/* v285: ingen betalingspurring for avlyste runder — det
                      finnes ingen betalingsplikt for et avlyst lotteri. */}
                  {!week.avlyst && week.participants.some(p=>!p.paid)&&(
                    <PurringKnapp week={week} participants={week.participants.filter(p=>!p.paid)}/>
                  )}
                </div>
              </div>
              {/* v52 FIX 3: på PC får navnet en fast kolonne-bredde (220 px)
                  og loddnumrene sin egen kolonne, slik at loddnumrene alltid
                  starter på samme x-posisjon uansett navne-lengde. Mobil
                  beholder den gamle 4-kolonners layouten der loddnumrene
                  står inline etter navnet. */}
              {/* v53 FIX 3: kolonne-headers på PC — 11px fet lysere grå
                  (#999) i stedet for 9px #444; litt større rad-gap.
                  Navn-kolonnen er 220px (samme som vinner-radens kol 1) så
                  LODDNR-kolonnen starter på nøyaktig samme x i begge lister.
                  LODDNR holdes som 1fr så lange loddnummer-rekker ikke
                  klippes. */}
              <div style={{ display:"grid",gridTemplateColumns:pcLayout?"220px 1fr auto auto auto":"1fr auto auto auto",gap:pcLayout?"6px 12px":"4px 12px" }}>
                {(pcLayout?["Navn","Loddnr","Lodd","Beløp","Betalt"]:["Navn","Lodd","Beløp","Betalt"]).map(h=><span key={h} style={pcLayout
                  ? { fontSize:11,color:"#999",textTransform:"uppercase",fontWeight:"bold",letterSpacing:"0.05em" }
                  : { fontSize:9,color:"#444",textTransform:"uppercase" }}>{h}</span>)}
                {week.participants
                  .map((p,origIdx)=>({ ...p,origIdx }))
                  .sort((a,b)=>(b.tickets||0)-(a.tickets||0))
                  .map((p)=>(
                  <><span key={`n${p.origIdx}`} style={{ fontSize:12,color:"#ccc",alignSelf:"center",
                    ...(pcLayout?{ overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }:{}) }}>
                    {p.name}
                    {!pcLayout&&p.ticketNumbers&&p.ticketNumbers.length>0&&(
                      <span style={{ fontSize:9,color:"#666",marginLeft:6 }}>
                        {p.ticketNumbers.map(n=>`#${n}`).join(" ")}
                      </span>
                    )}
                  </span>
                  {pcLayout&&(
                    /* v53 FIX 1: lysere loddnr-farge — var #666, nå dempet
                       gull #d6c074 (samme tone som vinner-loddnumrene). */
                    <span key={`ln${p.origIdx}`} style={{ fontSize:10,color:"#d6c074",alignSelf:"center" }}>
                      {p.ticketNumbers&&p.ticketNumbers.length>0
                        ? p.ticketNumbers.map(n=>`#${n}`).join(" ")
                        : "—"}
                    </span>
                  )}
                  <span key={`t${p.origIdx}`} style={{ fontSize:12,color:"#C9A84C",textAlign:"right",alignSelf:"center" }}>{p.tickets}</span>
                  <span key={`a${p.origIdx}`} style={{ fontSize:12,color:"#888",textAlign:"right",alignSelf:"center" }}>{p.amount||0},-</span>
                  {week.avlyst ? (
                    /* v314: avlyste uker — betaling godkjennes via
                       godkjennBetaling (kreditt), IKKE den inntektsbokførende
                       toggelen. Betalt-status er kun en etikett her. */
                    (p.paid || p.tilgodeGitt) ? (
                      <span key={`p${p.origIdx}`} style={{ fontSize:11,color:"#4caf50",textAlign:"center",padding:"2px 4px",borderRadius:2,background:"#0d2a0d",alignSelf:"center" }}>✓ Betalt</span>
                    ) : (() => {
                      const pEmail=(p.email||"").trim().toLowerCase();
                      const bKey=`${week.id}:${pEmail}`;
                      const busy=!!godkjennBusy[bKey];
                      return (
                        <button key={`p${p.origIdx}`} disabled={busy||!pEmail}
                          onClick={()=>godkjennBetalingFor(week,pEmail,p.name)}
                          style={{ fontSize:10,fontWeight:"bold",color:busy?"#777":"#4caf50",textAlign:"center",cursor:busy?"default":"pointer",padding:"2px 8px",borderRadius:4,background:"#0d2a0d",border:"1px solid #1a4a1a",alignSelf:"center",whiteSpace:"nowrap" }}>
                          {busy ? "Godkjenner…" : "Merk betalt"}
                        </button>
                      );
                    })()
                  ) : (
                  <span key={`p${p.origIdx}`}
                    style={{ fontSize:11,color:p.paid?"#4caf50":"#e57373",textAlign:"center",cursor:"pointer",padding:"2px 4px",borderRadius:2,background:p.paid?"#0d2a0d":"#2a0d0d",alignSelf:"center" }}
                    title="Klikk for å endre betalingsstatus"
                    onClick={async()=>{
                      const newPaid=!p.paid;
                      const updatedParts=week.participants.map((pp,pi)=>pi===p.origIdx?{ ...pp,paid:newPaid }:pp);
                      await updateDoc(doc(db,"history",week.id),{ participants:updatedParts });
                      if(p.amount){
                        const regSnap=await getDoc(doc(db,"settings","regnskap"));
                        const ex=regSnap.exists()?regSnap.data():{ totalInntekt:0,totalUtgift:0,manuellKorreksjon:0 };
                        const delta=newPaid?(p.amount||0):-(p.amount||0);
                        await setDoc(doc(db,"settings","regnskap"),{ ...ex,totalInntekt:Math.max(0,(ex.totalInntekt||0)+delta) },{ merge:true });
                      }
                    }}>
                    {p.paid?"✓ Betalt":"⏳ Ubetalt"}
                  </span>
                  )}</>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
// ─── INNSTILLINGER ────────────────────────────────────────────────────────────
function SettingsPanel({ lotteryState, onDataChanged, pcLayout = false, settingsTab: settingsTabProp, setSettingsTab: setSettingsTabProp }) {
  const [form,setForm]=useState({
    vippsNumber:"", vippsLink:"", vippsQrImage:"", ticketPrice:10, maxTicketsPerPlayer:25,
    lotteryName:"Vinloddet", logoUrl:"", headerImageUrl:"",
    // Tittelkontroll
    tittelLinje1:"", tittelLinje2:"",
    tittelFarge:"#C9A84C", tittelOutlineFarge:"#ffffff", tittelStorrelse:26, tittelSentrert:true,
    tittelFont:"'Playfair Display',Georgia,serif",
    tittelEffekt:"gull",
    tittelJustering:"midtstilt",
    tittelOutline:false,
    // Bildestorrelse
    flaskeHoyde:160, bildeBredde:80,
    skjulLogo:false,
    skjulTopbilde:false,
    bakgrunnsFarge:"#0a0a0a",
    premieKortFarge:"#ffffff",
    vinnerKortFarge:"#ffffff",
    topBildeHoyde:180,
    logoHoyde:56,
    // v55 FIX 2: logo/topbilde styres nå med proporsjonal skala (50-300%,
    // 100=normal) + fri X/Y-forskyvning (-100..+100%, 0=sentrert). De gamle
    // høyde-feltene (logoHoyde/topBildeHoyde) beholdes i Firestore men brukes
    // ikke lenger i UI eller spiller-rendering.
    logoSkala:100, topbildeSkala:100,
    logoPosX:0, logoPosY:0,
    topbildePosX:0, topbildePosY:0,
    // E-post (v208: EmailJS serviceId/publicKey/template-IDer fjernet — server-Resend bruker malene under + avsender)
    emailjsAvsender:"",
    emailjsMelding:"Gratulerer! Du har vunnet {{premieNavn}} i {{lotteriNavn}}. Premien hentes hos arrangøren. Lykke til neste gang!",
    emailjsPurreMelding:"Vi minner om at du har ubetalte lodd. Vennligst betal til Vipps {{vippsNummer}} så snart som mulig. Takk!",
    emailjsPremieHentingMelding:"Du har vunnet {{premieNavn}} i {{ukeNavn}}, men premien er ennå ikke hentet. Ta kontakt med arrangøren for å hente premien din. Hilsen {{lotteriNavn}}",
    emailjsAvlysMelding:"Vinlotteriet for {{uke}} ble avlyst. Du har {{belop}} kr i tilgodehavende som trekkes automatisk fra ved neste loddkjøp.\n\nMvh\nVinloddet",
    emailjsAutoSend:true, // v145: deprecated felles-toggle, beholdt som fallback
    emailjsAutoSendVinner:true, // v145: auto-send vinner-mail ved trekning
    emailjsAutoSendAvlys:true,  // v145: auto-send avlys-mail ved avlysning
    trekningsMetode:"hjul",
    // v26: auto = 5 sek mellom premier; manuell = vent på space (PC) eller
    // dobbelttrykk (mobil). Kun arrangør-enheten lytter.
    trekningsForlop:"auto",
    // v40: bakgrunns-mønster for spiller-vyen. URL kan være data-URI (fra
    // file-opplasting) eller ekstern lenke. Mobiles arrangør-vy får
    // tilsvarende UI i fremtidig runde — spiller-vyen renderer her nå.
    bgPatternUrl:"",
    bgPatternSize:80,
    bgPatternOpacity:25,
    bgPatternEnabled:false,
    // v41: tilfeldig plassering. 0 = stivt rutenett (CSS background-repeat),
    // 100 = full jitter. bgPatternSeed gir deterministisk plassering på
    // tvers av klienter (samme mønster vises for alle spillere).
    bgPatternRandomness:0,
    bgPatternSeed:42,

  });
  const [weekLabel,setWeekLabel]=useState(lotteryState?.weekLabel||"");
  const [drawTime,setDrawTime]=useState(lotteryState?.drawTime||"");
  const [autoDrawEnabled,setAutoDrawEnabled]=useState(lotteryState?.autoDraw||false);
  const [saleOpenTime,setSaleOpenTime]=useState(lotteryState?.saleOpenTime||"");
  const [saving,setSaving]=useState(false);
  const [saveConfirm,setSaveConfirm]=useState(false);
  // v37 (PC-prototype): auto-save status. "idle" | "saving" | "saved".
  // Vises som diskret indikator øverst i settings-panelet.
  const [pcSaveStatus, setPcSaveStatus] = useState("idle");
  const [pcLastSaved, setPcLastSaved] = useState(null);
  // v42: pre-konverter ekstern bgPattern-URL til base64 så SVG <image>
  // (for random plassering) ikke kræsjer på CORS.
  const bgPatternResolved = useResolvedPatternUrl(form.bgPatternUrl);
  // v55 FIX 2: bakgrunns-fjerning på logo/topbilde — "✓ Klar"-blink-status.
  const [bildeBgDone, setBildeBgDone] = useState({});
  // v62: åpen modal for klikk-for-farge chroma-key. Null = lukket; ellers
  // form-nøkkelen for bildet ("logoUrl" / "headerImageUrl").
  const [chromaKeyImage, setChromaKeyImage] = useState(null);
  // Synkroniser fra lotteryState
  useEffect(()=>{
    if(lotteryState?.weekLabel !== undefined) setWeekLabel(lotteryState.weekLabel||"");
    if(lotteryState?.drawTime !== undefined) setDrawTime(lotteryState.drawTime||"");
    if(lotteryState?.autoDraw !== undefined) setAutoDrawEnabled(!!lotteryState.autoDraw);
    if(lotteryState?.saleOpenTime !== undefined) setSaleOpenTime(lotteryState.saleOpenTime||"");
  },[lotteryState?.weekLabel, lotteryState?.drawTime, lotteryState?.autoDraw, lotteryState?.saleOpenTime]);
  useEffect(()=>{
    const load=async()=>{
      const s=await getDoc(doc(db,"settings","config"));
      if(s.exists()) setForm(f=>({ ...f,...s.data() }));
    };
    load();
  },[]);
  const save=async()=>{
    setSaving(true);
    try {
      // v167/v170: trekningsMetode/Mix + trekningsForlop/Pause eies av Trekning-
      // dropdownen — ekskluder dem her så et lagre-klikk ikke overskriver
      // dropdownens valg med stale form. v323: minLodd eies også av dropdownen.
      const { trekningsMetode:_tm, trekningsMetodeMix:_tmx, trekningsForlop:_tf, trekningsPause:_tp, minLodd:_ml, ...cfgToSave } = form;
      await setDoc(doc(db,"settings","config"),cfgToSave,{ merge:true });
      await updateDoc(doc(db,"lottery","current"),{
        weekLabel,
        drawTime:drawTime||null,
        autoDraw:autoDrawEnabled&&!!drawTime,
        saleOpenTime:saleOpenTime||null
      });
      setSaveConfirm(true); setTimeout(()=>setSaveConfirm(false),2500);
    } catch(e) {
      console.error("Feil ved lagring av innstillinger:", e);
      showErrorToast("Innstillinger ble ikke lagret. Sjekk internett.");
    }
    setSaving(false);
  };

  // v37 (PC-prototype): full auto-save av ALLE felt med 700 ms debounce.
  // Erstatter manuell "Lagre innstillinger"-knapp. Status-indikator vises
  // øverst i panelet. KUN aktiv i PC-prototype (pcLayout=true) — klassisk
  // mobil-OrganizerView bruker fortsatt manuell save-knapp.
  const pcAutoSaveTimerRef = useRef(null);
  const pcAutoSaveFirstRunRef = useRef(true);
  useEffect(() => {
    if (!pcLayout) return;
    if (pcAutoSaveFirstRunRef.current) {
      pcAutoSaveFirstRunRef.current = false;
      return;
    }
    if (pcAutoSaveTimerRef.current) clearTimeout(pcAutoSaveTimerRef.current);
    setPcSaveStatus("saving");
    pcAutoSaveTimerRef.current = setTimeout(async () => {
      try {
        // v167/v170: ekskluder trekningsMetode/Mix + trekningsForlop/Pause (eies av dropdownen). v323: + minLodd.
        const { trekningsMetode:_tm, trekningsMetodeMix:_tmx, trekningsForlop:_tf, trekningsPause:_tp, minLodd:_ml, ...cfgToSave } = form;
        await setDoc(doc(db,"settings","config"), cfgToSave, { merge:true });
        await updateDoc(doc(db,"lottery","current"), {
          weekLabel,
          drawTime: drawTime || null,
          autoDraw: autoDrawEnabled && !!drawTime,
          saleOpenTime: saleOpenTime || null,
        });
        setPcSaveStatus("saved");
        setPcLastSaved(new Date());
        setTimeout(() => setPcSaveStatus("idle"), 2000);
      } catch(e) {
        console.error("Auto-save feilet:", e);
        showErrorToast("Kunne ikke auto-lagre. Sjekk internett.");
        setPcSaveStatus("idle");
      }
    }, 700);
    return () => { if (pcAutoSaveTimerRef.current) clearTimeout(pcAutoSaveTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, weekLabel, drawTime, autoDrawEnabled, saleOpenTime, pcLayout]);

  // ─── AUTOLAGRING AV TREKNINGSFORLØP ────────────────────────────────────────
  // v170: autosave for trekningsForlop fjernet — forløp + pause eies nå av
  // Trekning-dropdownen (skriver direkte til settings/config). trekningsMetode
  // ble flyttet i v167. SettingsPanel skriver ingen av disse feltene lenger.

  // Bildeoppasting til base64
  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0]; if (!file) return;
    if (file.size > 2*1024*1024) { showAlert("Bildet er for stort (maks 2 MB)"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f=>({ ...f, [field]:ev.target.result }));
    reader.readAsDataURL(file);
  };

  // Nullstill historikk
  const nullstillHistorikk = async () => {
    if (!await showConfirm("Slett ALT og start fra scratch?\nDette sletter:\n• Alle deltakere\n• All historikk (tidligere runder)\n• Regnskap (inntekt/utgift)\n• Loddnummerering starter på 1\nTilgodehavende beholdes.\nDette kan ikke angres.")) return;
    // Slett historikk
    const histSnap = await getDocs(collection(db,"history"));
    await Promise.all(histSnap.docs.map(d=>deleteDoc(d.ref)));
    // Slett alle deltakere
    const partSnap = await getDocs(collection(db,"participants"));
    await Promise.all(partSnap.docs.map(d=>deleteDoc(d.ref)));
    // Nullstill regnskap
    await setDoc(doc(db,"settings","regnskap"),{ totalInntekt:0, totalUtgift:0, manuellKorreksjon:0 });
    // Reset lottery state
    const now = new Date();
    await updateDoc(doc(db,"lottery","current"),{
      status:"idle", salgStengt:false, lastClosedByAvlys:false,
      winner:null, winnerHistory:[], winnerEmailSent:false,
      winnerEmailServerStats:deleteField(), // v287: ikke vis forrige rundes e-post-status
      weekLabel:`Uke ${getWeekNumber(now)} ${now.getFullYear()}`,
      startedAt:null, drawTime:null, currentPrizeIdx:0, currentWinnerName:null,
      autoDraw:null, saleOpenTime:null, repeatWeekly:false,
      nextTicketNumber:deleteField(),
      // v181: rydd server-trekning-feltene ved full nullstilling.
      drawnPrizes:deleteField(), drawStartedAt:deleteField(), premieVarighetMs:deleteField(), drawTotalMs:deleteField(), drawSource:deleteField(), // v192/v198: rydd total-varighet + draw-kilde
      autoDrawSkipped:deleteField(), // v319: rydd utsatt/avlyst-uten-premier-varselet
      avlysReason:deleteField() // v325: rydd server-avlysningsgrunn (for-faa-lodd)
    });
    await setDoc(doc(db,"counters","tickets"),{ next:1 });
  };

  // Nullstill regnskap
  const nullstillRegnskap = async () => {
    if (!await showConfirm("Nullstille kun regnskapet?\nDette nullstiller:\n• Akkumulert inntekt\n• Akkumulert utgift\n• Manuell korreksjon\nHistorikk, deltakere og lotteri-status beholdes.\nDette kan ikke angres.")) return;
    await setDoc(doc(db,"settings","regnskap"),{ totalInntekt:0, totalUtgift:0, manuellKorreksjon:0 });
  };

  // Tittelforhåndsvisning
  const currentFont = form.tittelFont || "'Playfair Display',Georgia,serif";
  // v55 FIX 1: profesjonaliserte tittel-effekter. Metaller (gull/sølv) har
  // flerstegs-gradient med skarp høylys-stripe midt i; fargene har mettet
  // dyp-til-lys gradient; ild er en rik gul→oransje→rød overgang. effektFilter
  // legger drop-shadow (dybde) / fargeglød på gradient-effektene siden
  // background-clip:text ikke kan kombineres med text-shadow.
  const effektGradients = EFFEKT_GRADIENTS;
  const effektShadow = EFFEKT_SHADOW;
  const effektFilter = EFFEKT_FILTER;
  const erGradient = form.tittelEffekt && form.tittelEffekt !== "ingen" && effektGradients[form.tittelEffekt];
  const erShadow = form.tittelEffekt && effektShadow[form.tittelEffekt];
  const tekstJustering = form.tittelJustering==="høyre"?"right":form.tittelJustering==="venstre"?"left":"center";
  const tittelStyle = {
    margin:0,
    fontSize: form.tittelStorrelse||26,
    fontWeight:"700",
    color: erGradient ? "transparent" : (form.tittelFarge||"#C9A84C"),
    letterSpacing:"0.06em",
    // v55 FIX 5: text-transform fjernet — tittelen beholder casing fra
    // input-feltet ("Oslo Janitsjars", ikke "OSLO JANITSJARS").
    fontFamily: currentFont,
    textAlign: tekstJustering,
    lineHeight: 1.15,
    ...(erGradient ? {
      backgroundImage: effektGradients[form.tittelEffekt],
      backgroundClip:"text", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
      // v55 FIX 1: drop-shadow/glød gir dybde til gradient-teksten.
      filter: effektFilter[form.tittelEffekt],
    } : {}),
    ...(erShadow ? { textShadow: effektShadow[form.tittelEffekt], color: form.tittelFarge||"#C9A84C" } : {}),
    // Relieff: 3D-emboss — høylys oppe-venstre + skygge nede-høyre.
    ...(form.tittelEffekt==="relieff" ? {
      color: form.tittelFarge||"#C9A84C",
      textShadow:EFFEKT_RELIEFF,
      WebkitTextFillColor: form.tittelFarge||"#C9A84C",
    } : {}),
    // Kontur — valgfri farge og tykkelse
    ...((form.tittelOutline||0) > 0 ? {
      WebkitTextStroke:`${form.tittelOutline}px ${form.tittelOutlineFarge||"#ffffff"}`,
    } : {}),
  };

  // v50 FIX 3: i PC-dashboardet løftes undertab-state til OrganizerView slik
  // at undermenyene kan rendres inline i hovedtab-raden. Mobil/klassisk vy
  // bruker fortsatt egen lokal state. stabs ligger nå i SETTINGS_SUBTABS.
  const [settingsTabLocal, setSettingsTabLocal] = useState("lotteri");
  const settingsTab = settingsTabProp != null ? settingsTabProp : settingsTabLocal;
  const setSettingsTab = setSettingsTabProp || setSettingsTabLocal;
  const stabs = SETTINGS_SUBTABS;

  // v37: PC-prototype intro-tekster per undertabb (FIX F).
  const pcIntroTexts = {
    lotteri: "Grunnleggende informasjon om dette lotteriet.",
    utseende: "Farger, logo og visuell stil for spillerne.",
    betaling: "Innstillinger for Vipps-betaling.",
    // v51: tom for PC — intro-teksten er integrert i hvert kort i stedet
    // for å unngå "løsrevet" tekst over kortene.
    epost: "",
    trekning: "Trekningsforløp og automatisering mellom premier.",
    system: "Tekniske innstillinger, rettigheter og nullstilling.",
  };
  // v38: per-tab max-bredde — korte tabber (Vipps, System) sentreres ~700 px,
  // radio-tabber (Trekning) ~900 px, lange tabber (Utseende, E-post) ~1200 px.
  // v54 FIX 5: løftet hit (var lokalt i IIFE) slik at intro-tekst-raden kan
  // bruke SAMME maks-bredde og sentrering som kort-wrapperen — da henger ikke
  // intro-teksten løst i venstre marg lenger.
  const pcMaxWidthByTab = {
    lotteri: 900, utseende: 1200, betaling: 700,
    epost: 1200, trekning: 900, system: 700,
  };
  const pcMaxWidth = pcLayout ? (pcMaxWidthByTab[settingsTab] || 1200) : undefined;
  return (
    <div style={{ padding:"0 0 16px" }} className={pcLayout ? "vl-pc-settings" : ""}>
      {/* v37 PC-mode: CSS-overrides som begrenser input-bredde (FIX B) og
          gir kortene mer kontrast (FIX C). Kun aktiv når pcLayout=true. */}
      {pcLayout && (
        <style>{`
          .vl-pc-settings input[type="text"],
          .vl-pc-settings input[type="email"],
          .vl-pc-settings input[type="url"],
          .vl-pc-settings input:not([type]){max-width:400px;}
          .vl-pc-settings input[type="number"]{max-width:140px;}
          .vl-pc-settings input[type="color"]{max-width:60px;}
          .vl-pc-settings select{max-width:240px;}
          .vl-pc-settings textarea{max-width:720px;}
          .vl-pc-settings .vl-pc-card{
            background:linear-gradient(145deg,#141414 0%,#0e0e0e 100%);
            border:1px solid #2a2a2a;border-left:3px solid #C9A84C;
            border-radius:8px;padding:18px 20px;margin-bottom:14px;
            box-shadow:0 2px 8px rgba(0,0,0,0.35);
          }
          /* v54 FIX 4: enhetlig hoved-seksjonsoverskrift — 14px / 0.12em,
             samme som .vl-pc-rg-title, .vl-pc-pm-title og .vl-pc-ep-title. */
          .vl-pc-settings .vl-pc-card-title{
            margin:0 0 12px;font-size:14px;color:#C9A84C;font-weight:700;
            letter-spacing:0.12em;text-transform:uppercase;
          }
          /* v38: 2x2-grid for trekningsmetode-radio-knapper. */
          .vl-pc-settings .vl-pc-radios-2col{
            display:grid !important;
            grid-template-columns:1fr 1fr;
            gap:10px !important;
          }
          /* v38: destruktive System-knapper får sterkere rød profil
             og forsterket hover for å markere alvor. */
          .vl-pc-settings .vl-pc-danger-btn{
            background:rgba(90,26,26,0.18) !important;
            border:1px solid #5a1a1a !important;
            color:#e57373 !important;
            padding:12px 16px !important;
            border-radius:6px !important;
            cursor:pointer;font-size:13px;font-weight:600;
            text-align:left;transition:all 0.15s;
            display:flex;align-items:center;gap:10px;
          }
          .vl-pc-settings .vl-pc-danger-btn:hover{
            background:rgba(208,64,64,0.28) !important;
            border-color:#ff5050 !important;
            color:#ff7070 !important;
            box-shadow:0 2px 8px rgba(208,64,64,0.25);
          }
          /* v39: Utseende-tab kompaktering — mest mulig synlig uten scroll.
             v55 FIX 1: 5 → 6 kolonner, lavere knapper og mindre tekst slik
             at de 12 effektene fyller akkurat 2 jevne rader. */
          .vl-pc-settings .vl-pc-effekt-grid{
            grid-template-columns:repeat(6,1fr) !important;
            gap:3px !important;
            margin-bottom:8px !important;
          }
          .vl-pc-settings .vl-pc-effekt-grid > div{
            padding:4px 2px !important;
            font-size:8px !important;
          }
          .vl-pc-settings .vl-pc-tittel-preview{
            padding:8px 10px !important;
            max-height:90px;
            overflow:hidden;
          }
          .vl-pc-settings .vl-pc-bilde-preview img{
            max-height:90px !important;
            object-position:center !important;
          }
          .vl-pc-settings .vl-pc-farger-row{
            display:grid;
            grid-template-columns:160px 1fr;
            align-items:center;
            gap:12px;
            padding:6px 0;
            border-bottom:1px solid #1a1a1a;
          }
          .vl-pc-settings .vl-pc-farger-row:last-child{border-bottom:none;}
        `}</style>
      )}
      {/* Settings subtabs. v50 FIX 3: skjult i PC-dashboardet — der rendres
          undermenyene inline i hovedtab-raden. Mobil/klassisk vy beholder
          denne egne raden. */}
      {!pcLayout && (
        <div style={{ display:"flex",overflowX:"auto",borderBottom:"1px solid #3a3a3a",marginBottom:16 }}>
          {stabs.map(t=>(
            <button key={t.id}
              style={{ flex:"0 0 auto",padding:"10px 14px",background:"none",border:"none",
                color:settingsTab===t.id?"#C9A84C":"#888",cursor:"pointer",fontSize:11,
                fontWeight:settingsTab===t.id?"bold":"normal",
                borderBottom:settingsTab===t.id?"2px solid #C9A84C":"2px solid transparent",
                whiteSpace:"nowrap" }}
              onClick={()=>setSettingsTab(t.id)}>{t.label}</button>
          ))}
        </div>
      )}

      {/* v37 PC: intro-tekst + auto-save-status (FIX E, FIX F).
          v54 FIX 5: raden er nå begrenset til samme maks-bredde og
          sentrering som kort-wrapperen under (maxWidth:pcMaxWidth +
          margin:0 auto), så intro-teksten ligger rett over kortene
          i stedet for å henge løst ute i venstre marg. */}
      {pcLayout && (
        <div style={{
          padding:"0 16px", marginBottom:14, display:"flex",
          alignItems:"center", justifyContent:"space-between", gap:16,
          maxWidth:pcMaxWidth, margin:"0 auto 14px", boxSizing:"border-box",
        }}>
          <p style={{ margin:0, fontSize:12, color:"#999", lineHeight:1.5 }}>
            {pcIntroTexts[settingsTab] || ""}
          </p>
          <div style={{ fontSize:11, color:"#666", whiteSpace:"nowrap", minHeight:16 }}>
            {pcSaveStatus === "saving" && <span style={{ color:"#C9A84C" }}>⏳ Lagrer…</span>}
            {pcSaveStatus === "saved" && <span style={{ color:"#4caf50" }}>✓ Lagret</span>}
            {pcSaveStatus === "idle" && pcLastSaved && (
              <span>Sist lagret: {new Date(pcLastSaved).toLocaleTimeString("nb-NO",{hour:"2-digit",minute:"2-digit"})}</span>
            )}
          </div>
        </div>
      )}

      {(()=>{
        // v54 FIX 5: pcMaxWidth beregnes nå i komponent-scope (delt med
        // intro-tekst-raden over).
        return (
      <div style={{ padding:"0 16px", maxWidth:pcMaxWidth, margin:pcLayout?"0 auto":undefined, boxSizing:"border-box" }}>

        {settingsTab==="lotteri"&&(<>
          {/* v37 PC: kort 1 "Identitet" + kort 2 "Priser og begrensninger"
              (FIX C). Live preview-boks under (FIX D). */}
          {pcLayout ? (<>
            <div className="vl-pc-card">
              <p className="vl-pc-card-title">🏷 Identitet</p>
              <label style={S.inputLabel}>Navn på lotteriet</label>
              <input style={S.input} value={form.lotteryName} onChange={e=>setForm({ ...form,lotteryName:e.target.value })}/>
              <label style={S.inputLabel}>Ukenavn / undertittel</label>
              <input style={S.input} placeholder="Uke 15 · Bedrift AS" value={weekLabel} onChange={e=>setWeekLabel(e.target.value)}/>
            </div>
            <div className="vl-pc-card">
              <p className="vl-pc-card-title">💰 Priser og begrensninger</p>
              <label style={S.inputLabel}>Loddpris (kr)</label>
              <input style={S.input} type="number" min="1" value={form.ticketPrice} onChange={e=>setForm({ ...form,ticketPrice:parseInt(e.target.value)||10 })}/>
              <label style={S.inputLabel}>Maks lodd per spiller</label>
              <input style={S.input} type="number" min="1" max="100" value={form.maxTicketsPerPlayer} onChange={e=>setForm({ ...form,maxTicketsPerPlayer:parseInt(e.target.value)||25 })}/>
            </div>
            {/* FIX D: live preview av tittel slik spillerne ser den. */}
            <div className="vl-pc-card" style={{ borderLeftColor:"#a78bfa" }}>
              <p className="vl-pc-card-title" style={{ color:"#a78bfa" }}>👁 Slik ser spillerne det</p>
              <div style={{
                padding:"18px 20px",
                background:"radial-gradient(ellipse at top, #1a1410 0%, #0a0806 100%)",
                borderRadius:6, border:"1px solid #2a2a2a",
                textAlign:"center",
              }}>
                <div style={{
                  fontFamily:"'Playfair Display',Georgia,serif",
                  fontSize:22, color:"#C9A84C", letterSpacing:"0.08em",
                  textTransform:"uppercase", fontWeight:"bold",
                  textShadow:"0 0 12px rgba(201,168,76,0.4)",
                }}>{form.lotteryName||"Vinloddet"}</div>
                {weekLabel && (
                  <div style={{ marginTop:6, fontSize:11, color:"#888", letterSpacing:"0.1em", textTransform:"uppercase" }}>
                    {weekLabel}
                  </div>
                )}
                <div style={{ marginTop:14, fontSize:12, color:"#aaa" }}>
                  {form.ticketPrice} kr/lodd · maks {form.maxTicketsPerPlayer} per spiller
                </div>
              </div>
            </div>
          </>) : (
            <div style={S.settingsCard}>
              <p style={S.sectionLabel}>Generelt</p>
              <label style={S.inputLabel}>Navn på lotteriet</label>
              <input style={S.input} value={form.lotteryName} onChange={e=>setForm({ ...form,lotteryName:e.target.value })}/>
              <label style={S.inputLabel}>Ukenavn / undertittel</label>
              <input style={S.input} placeholder="Uke 15 · Bedrift AS" value={weekLabel} onChange={e=>setWeekLabel(e.target.value)}/>
              <label style={S.inputLabel}>Loddpris (kr)</label>
              <input style={S.input} type="number" min="1" value={form.ticketPrice} onChange={e=>setForm({ ...form,ticketPrice:parseInt(e.target.value)||10 })}/>
              <label style={S.inputLabel}>Maks lodd per spiller</label>
              <input style={S.input} type="number" min="1" max="100" value={form.maxTicketsPerPlayer} onChange={e=>setForm({ ...form,maxTicketsPerPlayer:parseInt(e.target.value)||25 })}/>
            </div>
          )}

        </>)}

        {settingsTab==="utseende"&&(()=>{
          // v55 FIX 6: de fire kortene defineres som variabler og arrangeres
          // ulikt for PC vs mobil. PC: Venstre=[Tittel+Bakgrunn],
          // Høyre=[Bilder+Farger] — gir jevnest kolonnehøyde etter at Farger
          // ble forenklet (FIX 4) og Bilder vokste (FIX 2). Mobil beholder
          // EKSAKT samme stable-rekkefølge: Tittel → Bilder → Farger → Bakgrunn.
          const tittelCard = (
          /* ── TITTEL ── */
          <div style={S.settingsCard}>
            <p style={S.sectionLabel}>✏️ Tittel</p>

            {/* To tekstlinjer side om side */}
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10 }}>
              <div>
                <label style={S.inputLabel}>Linje 1</label>
                <input style={{ ...S.input,marginBottom:0 }} placeholder={form.lotteryName||"Mitt lotteri"} value={form.tittelLinje1||""} onChange={e=>setForm({ ...form,tittelLinje1:e.target.value })}/>
              </div>
              <div>
                <label style={S.inputLabel}>Linje 2 (valgfri)</label>
                <input style={{ ...S.input,marginBottom:0 }} placeholder="Undertittel" value={form.tittelLinje2||""} onChange={e=>setForm({ ...form,tittelLinje2:e.target.value })}/>
              </div>
            </div>

            {/* Font + størrelse + justering på én rad */}
            <div style={{ display:"grid",gridTemplateColumns:"1fr auto auto",gap:8,alignItems:"end",marginBottom:12 }}>
              <div>
                <label style={S.inputLabel}>Font</label>
                {pcLayout ? (
                  // v39: custom FontPicker med per-font-preview i popup.
                  <FontPicker
                    value={form.tittelFont||"'Playfair Display',Georgia,serif"}
                    onChange={v => setForm({ ...form, tittelFont:v })}
                    options={TITLE_FONTS}
                  />
                ) : (
                  <select style={{ ...S.input,marginBottom:0,cursor:"pointer" }}
                    value={form.tittelFont||"'Playfair Display',Georgia,serif"}
                    onChange={e=>setForm({ ...form,tittelFont:e.target.value })}>
                    {TITLE_FONTS.map(f=><option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                )}
              </div>
              <div style={{ textAlign:"center" }}>
                <label style={S.inputLabel}>Str.</label>
                <div style={{ display:"flex",alignItems:"center",gap:4 }}>
                  <button type="button" style={{ ...S.qtyBtn,width:30,height:30,fontSize:16 }}
                    onClick={()=>setForm(f=>({ ...f,tittelStorrelse:Math.max(14,(f.tittelStorrelse||26)-1) }))}>−</button>
                  <span style={{ fontSize:13,color:"#f5efe0",minWidth:28,textAlign:"center" }}>{form.tittelStorrelse||26}</span>
                  <button type="button" style={{ ...S.qtyBtn,width:30,height:30,fontSize:16 }}
                    onClick={()=>setForm(f=>({ ...f,tittelStorrelse:Math.min(80,(f.tittelStorrelse||26)+1) }))}>+</button>
                </div>
              </div>
              <div>
                <label style={S.inputLabel}>Just.</label>
                <div style={{ display:"flex",gap:3 }}>
                  {[["venstre","⬅"],["midtstilt","⬛"],["høyre","➡"]].map(([j,ic])=>(
                    <button key={j} type="button"
                      style={{ width:30,height:30,borderRadius:4,border:"1px solid #333",cursor:"pointer",fontSize:14,
                        background:form.tittelJustering===j?"#2a2000":"#1a1a1a",
                        color:form.tittelJustering===j?"#C9A84C":"#666" }}
                      onClick={()=>setForm(f=>({ ...f,tittelJustering:j }))}>
                      {ic}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Teksteffekt — 4 per rad */}
            <label style={{ ...S.inputLabel,marginBottom:6 }}>Effekt</label>
            <div className={pcLayout ? "vl-pc-effekt-grid" : ""} style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:5,marginBottom:10 }}>
              {[
                { id:"ingen",    label:"Ingen",    bg:"#1a1a1a", color:"#888" },
                { id:"gull",     label:"✦ Gull",   bg:"linear-gradient(135deg,#f6d97a,#C9A84C,#8a6500)" },
                { id:"sølv",     label:"✦ Sølv",   bg:"linear-gradient(135deg,#e0e0e0,#b0b0b0,#888)", color:"#222" },
                { id:"rød",      label:"✦ Rød",    bg:"linear-gradient(135deg,#ff6b6b,#c0392b,#8B0000)" },
                { id:"blå",      label:"✦ Blå",    bg:"linear-gradient(135deg,#74b9ff,#0984e3,#1a3a6a)" },
                { id:"grønn",    label:"✦ Grønn",  bg:"linear-gradient(135deg,#55efc4,#00b894,#006450)" },
                { id:"lilla",    label:"✦ Lilla",  bg:"linear-gradient(135deg,#d6a4ff,#a855f7,#6b21a8)" },
                { id:"ild",      label:"✦ Ild",    bg:"linear-gradient(135deg,#ffd700,#ff8c00,#ff4500)" },
                { id:"glød-gull",label:"◎ Gull",   bg:"#1a1200", color:"#C9A84C" },
                { id:"glød-hvit",label:"◎ Hvit",   bg:"#111",    color:"#fff" },
                { id:"glød-rød", label:"◎ Rød",    bg:"#1a0000", color:"#ff5050" },
                { id:"relieff",  label:"⬛ Relief", bg:"#222",    color:"#C9A84C" },
              ].map(eff=>(
                <div key={eff.id}
                  style={{ padding:"6px 4px",borderRadius:4,background:eff.bg,cursor:"pointer",
                    fontSize:9,fontWeight:"bold",textAlign:"center",
                    color:eff.color||"#fff",
                    border:form.tittelEffekt===eff.id?"2px solid #fff":"2px solid transparent",
                    textShadow:"0 1px 2px rgba(0,0,0,0.8)" }}
                  onClick={()=>setForm(f=>({ ...f,tittelEffekt:eff.id }))}>
                  {eff.label}
                </div>
              ))}
            </div>

            {/* Farge + hvit kant på én rad */}
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12 }}>
              <div>
                <label style={S.inputLabel}>Tekstfarge</label>
                <div style={{ display:"flex",gap:6,alignItems:"center" }}>
                  {/* v56 FIX 3: egendefinert tekstfarge med regnbue-kant,
                      konsistent med "Egendefinert" i Farger-seksjonen. */}
                  <RegnbueFargevelger size={36}
                    value={form.tittelFarge||"#C9A84C"}
                    onChange={e=>setForm({ ...form,tittelFarge:e.target.value })}
                    title="Egendefinert tekstfarge"/>
                  <div style={{ display:"flex",gap:4,flexWrap:"wrap" }}>
                    {["#C9A84C","#ffffff","#ff4444","#4caf50","#2196f3","#ff9800"].map(c=>(
                      <div key={c} onClick={()=>setForm(f=>({ ...f,tittelFarge:c }))}
                        style={{ width:22,height:22,borderRadius:3,background:c,cursor:"pointer",
                          border:(form.tittelFarge||"#C9A84C")===c?"2px solid #fff":"1px solid #555" }}/>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label style={S.inputLabel}>Kontur</label>
                <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:5 }}>
                  <span style={{ fontSize:10,color:"#777",whiteSpace:"nowrap" }}>{form.tittelOutline||0}px</span>
                  <input type="range" min="0" max="5" step="0.1"
                    value={form.tittelOutline||0}
                    onChange={e=>setForm(f=>({ ...f,tittelOutline:parseFloat(e.target.value) }))}
                    style={{ flex:1,accentColor:"#C9A84C" }}/>
                  {/* v56 FIX 3: kontur-farge med samme regnbue-kant. */}
                  <RegnbueFargevelger size={26}
                    value={form.tittelOutlineFarge||"#ffffff"}
                    onChange={e=>setForm(f=>({ ...f,tittelOutlineFarge:e.target.value }))}
                    title="Kontur-farge"/>
                </div>
              </div>
            </div>

            {/* Forhåndsvisning */}
            <div className={pcLayout ? "vl-pc-tittel-preview" : ""} style={{ background:"#0a0a0a",padding:"14px 12px",borderRadius:4,border:"1px solid #333" }}>
              <div style={tittelStyle}>{form.tittelLinje1||form.lotteryName||"Vinloddet"}</div>
              {form.tittelLinje2&&<div style={{ ...tittelStyle,marginTop:2 }}>{form.tittelLinje2}</div>}
            </div>
          </div>
          );

          // v55 FIX 2: Bilder-kortet — logo og topbilde stables (hver fikk
          // flere kontroller). Per bilde: synlig-toggle, fil/URL, 🪄 Fjern
          // bakgrunn (chroma-key via removeWhiteBackground), proporsjonal
          // Skala-slider (50-300%) og fri X/Y-posisjon (-100..+100%).
          const bilderCard = (
          /* ── BILDER ── */
          <div style={S.settingsCard}>
            <p style={S.sectionLabel}>🖼 Bilder</p>
            <p style={{ margin:"0 0 12px",fontSize:11,color:"#999" }}>Last opp fil (maks 2 MB) eller lim inn URL. Skalér, posisjonér og fjern bakgrunn per bilde.</p>

            {[
              { key:"logoUrl",        label:"Logo",     skalaKey:"logoSkala",     posXKey:"logoPosX",     posYKey:"logoPosY",     skjulKey:"skjulLogo",     baseH:90,  fit:"contain", radius:0 },
              { key:"headerImageUrl", label:"Topbilde", skalaKey:"topbildeSkala", posXKey:"topbildePosX", posYKey:"topbildePosY", skjulKey:"skjulTopbilde", baseH:120, fit:"cover",   radius:4 },
            ].map(({ key,label,skalaKey,posXKey,posYKey,skjulKey,baseH,fit,radius },idx)=>{
              const isHidden = form[skjulKey];
              const skala = form[skalaKey] ?? 100;
              const posX = form[posXKey] ?? 0;
              const posY = form[posYKey] ?? 0;
              const done = !!bildeBgDone[key];
              return (
                <div key={key} style={{ borderTop:idx>0?"1px solid #2a2a2a":"none", paddingTop:idx>0?12:0, marginTop:idx>0?12:0 }}>
                  {/* Label + synlig-toggle */}
                  <div style={{ display:"flex",alignItems:"center",marginBottom:6 }}>
                    <label style={{ ...S.inputLabel,margin:0,flex:1 }}>{label}</label>
                    <span style={{ display:"flex",alignItems:"center",gap:4,cursor:"pointer" }}
                      onClick={()=>setForm(f=>({ ...f,[skjulKey]:!f[skjulKey] }))}>
                      <span style={{ width:28,height:16,borderRadius:8,background:isHidden?"#e57373":"#2a2a2a",position:"relative",display:"inline-block",flexShrink:0 }}>
                        <span style={{ position:"absolute",top:1,left:isHidden?13:1,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left 0.15s" }}/>
                      </span>
                      <span style={{ fontSize:9,color:"#888" }}>{isHidden?"Skjult":"Vis"}</span>
                    </span>
                  </div>
                  {/* Fil-upload */}
                  <input type="file" accept="image/*"
                    style={{ fontSize:11,color:"#aaa",display:"block",marginBottom:6,width:"100%",
                      background:"#111",border:"1px solid #333",borderRadius:4,padding:"4px" }}
                    onChange={e=>handleImageUpload(e,key)}/>
                  {/* URL-input */}
                  <input style={{ ...S.input,marginBottom:6,fontSize:11,padding:"5px 8px" }}
                    placeholder="https://..." value={form[key]||""}
                    onChange={e=>setForm({ ...form,[key]:e.target.value })}/>
                  {/* v62: erstattet auto-hvit "Fjern bakgrunn"-knapp med
                      generell klikk-for-farge picker. Logo/topbilde har
                      sjelden hvit bakgrunn (oftere svart, blå, etc.), så
                      brukeren må eksplisitt peke ut bakgrunnsfargen via
                      ChromaKeyModal. Vinmonopolet-flaskenes auto-hvit-flyt
                      i PrizeManager er uendret. */}
                  <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:6 }}>
                    <button type="button"
                      disabled={!form[key]}
                      onClick={()=>setChromaKeyImage(key)}
                      style={{ flexShrink:0,padding:"5px 10px",height:30,
                        background:"#1a4a1a",
                        border:"1px solid #2a6a2a",
                        color:!form[key]?"#555":"#a8e8a8",
                        borderRadius:4,cursor:!form[key]?"not-allowed":"pointer",
                        fontSize:11,fontWeight:"bold",whiteSpace:"nowrap" }}>
                      🎯 Velg bakgrunns-farge
                    </button>
                    {done && <span style={{ fontSize:10,color:"#4caf50",fontWeight:"bold" }}>✓ Klar</span>}
                  </div>
                  {/* Forhåndsvisning — med skala + X/Y-forskyvning */}
                  {form[key]&&(
                    <div style={{ background:"#111",borderRadius:4,overflow:"hidden",marginBottom:8,border:"1px solid #2a2a2a",
                      height:baseH,display:"flex",alignItems:"center",justifyContent:"center" }}>
                      <img src={form[key]} alt={label}
                        style={{ maxWidth:"100%",maxHeight:"100%",objectFit:fit,borderRadius:radius,
                          transform:`translate(${posX}%, ${posY}%) scale(${skala/100})`, transformOrigin:"center" }}/>
                    </div>
                  )}
                  {/* Skala-slider */}
                  <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:5 }}>
                    <span style={{ fontSize:10,color:"#777",whiteSpace:"nowrap",minWidth:78 }}>Skala {skala}%</span>
                    <input type="range" min="50" max="300" step="5" value={skala}
                      onChange={e=>setForm(f=>({ ...f,[skalaKey]:parseInt(e.target.value) }))}
                      style={{ flex:1,accentColor:"#C9A84C" }}/>
                  </div>
                  {/* X-posisjon */}
                  <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:5 }}>
                    <span style={{ fontSize:10,color:"#777",whiteSpace:"nowrap",minWidth:78 }}>X {posX>0?"+":""}{posX}%</span>
                    <input type="range" min="-100" max="100" step="1" value={posX}
                      onChange={e=>setForm(f=>({ ...f,[posXKey]:parseInt(e.target.value) }))}
                      style={{ flex:1,accentColor:"#C9A84C" }}/>
                  </div>
                  {/* Y-posisjon */}
                  <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                    <span style={{ fontSize:10,color:"#777",whiteSpace:"nowrap",minWidth:78 }}>Y {posY>0?"+":""}{posY}%</span>
                    <input type="range" min="-100" max="100" step="1" value={posY}
                      onChange={e=>setForm(f=>({ ...f,[posYKey]:parseInt(e.target.value) }))}
                      style={{ flex:1,accentColor:"#C9A84C" }}/>
                  </div>
                </div>
              );
            })}

            {/* Premie-flaskenes høyde/bredde — uendret (gjelder ikke logo/topbilde) */}
            <div style={{ borderTop:"1px solid #2a2a2a",paddingTop:10,marginTop:12 }}>
              <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                <label style={{ ...S.inputLabel,margin:0,whiteSpace:"nowrap",minWidth:60 }}>↕ Høyde</label>
                <span style={{ fontSize:11,color:"#C9A84C",minWidth:36 }}>{form.flaskeHoyde||160}px</span>
                <input type="range" min="80" max="400" step="10" value={form.flaskeHoyde||160}
                  onChange={e=>setForm({ ...form,flaskeHoyde:parseInt(e.target.value) })}
                  style={{ flex:1,accentColor:"#C9A84C" }}/>
              </div>
              <p style={{ margin:"4px 0 0",fontSize:10,color:"#666" }}>Premie-flaskenes høyde og bredde (uavhengig av hverandre).</p>
              <div style={{ display:"flex",alignItems:"center",gap:8,marginTop:8 }}>
                <label style={{ ...S.inputLabel,margin:0,whiteSpace:"nowrap",minWidth:60 }}>↔ Bredde</label>
                <span style={{ fontSize:11,color:"#C9A84C",minWidth:36 }}>{form.bildeBredde||80}px</span>
                <input type="range" min="40" max="250" step="5" value={form.bildeBredde||80}
                  onChange={e=>setForm({ ...form,bildeBredde:parseInt(e.target.value) })}
                  style={{ flex:1,accentColor:"#C9A84C" }}/>
              </div>
            </div>
          </div>
          );

          // v55 FIX 4: forenklet fargevalg — kun [Egendefinert][Svart][Hvit].
          // Egendefinert ligger først med regnbue-ramme som tydelig signaliserer
          // at den åpner en color-picker.
          const fargerCard = (
          /* ── FARGER ── */
          <div style={S.settingsCard}>
            <p style={S.sectionLabel}>🎨 Farger</p>
            {[
              { label:"Bakgrunn",     key:"bakgrunnsFarge",  def:"#0a0a0a" },
              { label:"Premiebokser", key:"premieKortFarge", def:"#ffffff" },
              { label:"Vinnerbokser", key:"vinnerKortFarge", def:"#ffffff" },
            ].map(({ label,key,def })=>{
              const val = (form[key]||def).toLowerCase();
              const isBlack = val==="#000000";
              const isWhite = val==="#ffffff";
              const isCustom = !isBlack && !isWhite;
              return (
                <div key={key} style={{ display:"flex",alignItems:"center",gap:10,marginBottom:12 }}>
                  <span style={{ fontSize:11,color:"#aaa",minWidth:88,flexShrink:0 }}>{label}</span>
                  <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                    {/* Egendefinert — først, regnbue-ramme */}
                    <label title="Egendefinert farge — klikk for fargevelger"
                      style={{ position:"relative",width:30,height:30,borderRadius:6,cursor:"pointer",padding:3,boxSizing:"border-box",
                        background:"linear-gradient(135deg,#ff0040,#ff8c00,#ffd000,#33d17a,#3584e4,#9141ac)",
                        boxShadow:isCustom?"0 0 0 2px #fff":"none" }}>
                      <span style={{ display:"block",width:"100%",height:"100%",borderRadius:3,
                        background:isCustom?(form[key]||def):"#1a1a1a" }}/>
                      <input type="color" value={isCustom?(form[key]||def):def}
                        onChange={e=>setForm(f=>({ ...f,[key]:e.target.value }))}
                        style={{ position:"absolute",inset:0,width:"100%",height:"100%",opacity:0,cursor:"pointer" }}/>
                    </label>
                    {/* Svart */}
                    <div onClick={()=>setForm(f=>({ ...f,[key]:"#000000" }))} title="Svart"
                      style={{ width:30,height:30,borderRadius:6,background:"#000000",cursor:"pointer",boxSizing:"border-box",
                        border:isBlack?"2px solid #C9A84C":"1px solid #555" }}/>
                    {/* Hvit */}
                    <div onClick={()=>setForm(f=>({ ...f,[key]:"#ffffff" }))} title="Hvit"
                      style={{ width:30,height:30,borderRadius:6,background:"#ffffff",cursor:"pointer",boxSizing:"border-box",
                        border:isWhite?"2px solid #C9A84C":"1px solid #555" }}/>
                  </div>
                </div>
              );
            })}
          </div>
          );

          const bakgrunnCard = (
          /* ── BAKGRUNN / MØNSTER ── */
          <div style={S.settingsCard}>
            <p style={S.sectionLabel}>🎨 Bakgrunn / mønster</p>
            <p style={{ margin:"0 0 12px", fontSize:11, color:"#999", lineHeight:1.5 }}>
              Last opp et lite bilde (f.eks. hjerte, stjerne, logo) som repeteres som tapet på spiller-siden.
            </p>

            {/* På/av-toggle */}
            <label style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14, cursor:"pointer" }}
              onClick={()=>setForm(f=>({ ...f, bgPatternEnabled: !f.bgPatternEnabled }))}>
              <span style={{ width:36, height:20, borderRadius:10, background:form.bgPatternEnabled?"#C9A84C":"#2a2a2a", position:"relative", display:"inline-block", flexShrink:0, transition:"background 0.15s" }}>
                <span style={{ position:"absolute", top:3, left:form.bgPatternEnabled?18:3, width:14, height:14, borderRadius:"50%", background:"#fff", transition:"left 0.15s" }}/>
              </span>
              <span style={{ fontSize:12, color:"#ccc" }}>Vis bakgrunnsmønster</span>
            </label>

            <label style={S.inputLabel}>Last opp bilde</label>
            <input type="file" accept="image/*"
              style={{ fontSize:11, color:"#aaa", display:"block", marginBottom:8, width:"100%",
                background:"#111", border:"1px solid #333", borderRadius:4, padding:"4px" }}
              onChange={e=>handleImageUpload(e, "bgPatternUrl")}/>

            <label style={S.inputLabel}>Eller URL</label>
            <input style={{ ...S.input, fontSize:11, padding:"5px 8px" }}
              placeholder="https://..." value={form.bgPatternUrl||""}
              onChange={e=>setForm({ ...form, bgPatternUrl:e.target.value })}/>

            {/* v55 FIX 3: minimum 20 → 10 px på PC-prototypen. */}
            <label style={S.inputLabel}>Størrelse: {form.bgPatternSize||80} px</label>
            <input type="range" min={pcLayout?"10":"20"} max="200" step="5"
              value={form.bgPatternSize||80}
              onChange={e=>setForm(f=>({ ...f, bgPatternSize:parseInt(e.target.value)||80 }))}
              style={{ width:"100%", accentColor:"#C9A84C", marginBottom:8 }}/>

            <label style={S.inputLabel}>Synlighet: {form.bgPatternOpacity||25}%</label>
            <input type="range" min="5" max="100" step="5"
              value={form.bgPatternOpacity||25}
              onChange={e=>setForm(f=>({ ...f, bgPatternOpacity:parseInt(e.target.value)||25 }))}
              style={{ width:"100%", accentColor:"#C9A84C", marginBottom:8 }}/>

            <label style={S.inputLabel}>🎲 Tilfeldig plassering: {form.bgPatternRandomness||0}%</label>
            <input type="range" min="0" max="100" step="5"
              value={form.bgPatternRandomness||0}
              onChange={e=>setForm(f=>({ ...f, bgPatternRandomness:parseInt(e.target.value)||0 }))}
              style={{ width:"100%", accentColor:"#C9A84C", marginBottom:4 }}/>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
              <span style={{ fontSize:10, color:"#666", flex:1 }}>0% = stivt rutenett · 100% = full spredning</span>
              <button type="button"
                onClick={()=>setForm(f=>({ ...f, bgPatternSeed: Math.floor(Math.random()*100000)+1 }))}
                title="Generer nytt tilfeldig mønster (samme seed = samme mønster overalt)"
                style={{
                  padding:"3px 8px", background:"#1a1a1a", border:"1px solid #444",
                  borderRadius:3, color:"#aaa", fontSize:10, cursor:"pointer",
                }}>🔀 Ny seed ({form.bgPatternSeed||42})</button>
            </div>

            {/* Forhåndsvisning */}
            {form.bgPatternUrl && (
              <>
                <label style={S.inputLabel}>Forhåndsvisning</label>
                <div style={{
                  width:"100%", height:120, borderRadius:6, border:"1px solid #2a2a2a",
                  background: form.bakgrunnsFarge || "#0a0a0a",
                  position:"relative", overflow:"hidden",
                }}>
                  {(()=>{
                    const randomness = form.bgPatternRandomness || 0;
                    // v42: random-mode krever pre-konvertert base64 (CORS-fix).
                    // Hvis fetch ikke har fullført ennå, eller hvis det feilet,
                    // faller vi tilbake til vanlig CSS-repeat (uten random).
                    if (randomness > 0 && bgPatternResolved.resolved) {
                      const svg = generatePatternSVG({
                        url: bgPatternResolved.resolved,
                        imageSize: form.bgPatternSize || 80,
                        randomness,
                        seed: form.bgPatternSeed || 42,
                      });
                      return (
                        <div style={{
                          position:"absolute", inset:0,
                          backgroundImage: svg ? `url("${svg.dataUri}")` : "none",
                          backgroundRepeat:"repeat",
                          backgroundSize:`${svg?.tileSize||600}px`,
                          opacity:(form.bgPatternOpacity||25)/100,
                        }}/>
                      );
                    }
                    return (
                      <div style={{
                        position:"absolute", inset:0,
                        backgroundImage:`url(${form.bgPatternUrl})`,
                        backgroundRepeat:"repeat",
                        backgroundSize:`${form.bgPatternSize||80}px`,
                        opacity:(form.bgPatternOpacity||25)/100,
                      }}/>
                    );
                  })()}
                </div>
                {/* v42: brukervennlige statusmeldinger ved CORS/fetch-issues. */}
                {(form.bgPatternRandomness || 0) > 0 && bgPatternResolved.loading && (
                  <p style={{ margin:"6px 0 0", fontSize:10, color:"#888" }}>⏳ Henter bilde for tilfeldig plassering…</p>
                )}
                {(form.bgPatternRandomness || 0) > 0 && bgPatternResolved.error && (
                  <p style={{ margin:"6px 0 0", fontSize:10, color:"#e57373", lineHeight:1.5 }}>
                    ⚠️ Dette bildet kan ikke brukes som tilfeldig mønster pga. CORS-restriksjoner. Last ned bildet og last det opp via fil-feltet over, eller bruk en annen URL. Stivt rutenett (0%) fungerer fortsatt.
                  </p>
                )}
              </>
            )}
          </div>
          );

          // v55 FIX 6: PC = to balanserte kolonner (Tittel+Bakgrunn /
          // Bilder+Farger). Mobil = uendret stable-rekkefølge.
          return pcLayout ? (
            <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
              <div style={{ flex:"1 1 50%", minWidth:0 }}>{tittelCard}{bakgrunnCard}</div>
              <div style={{ flex:"1 1 50%", minWidth:0 }}>{bilderCard}{fargerCard}</div>
            </div>
          ) : (
            <>{tittelCard}{bilderCard}{fargerCard}{bakgrunnCard}</>
          );
        })()}

        {settingsTab==="betaling"&&(<>
          <div style={S.settingsCard}>
            <p style={S.sectionLabel}>💜 Vipps og betaling</p>
            <label style={S.inputLabel}>Vipps-nummer</label>
            <input style={S.input} placeholder="98765432" value={form.vippsNumber} onChange={e=>setForm({ ...form,vippsNumber:e.target.value.replace(/\D/g,"").slice(0,8) })} type="tel"/>
            <label style={S.inputLabel}>Vipps betalingslenke (for QR-kode på PC)</label>
            <input style={S.input} placeholder="https://qr.vipps.no/..." value={form.vippsLink||""} onChange={e=>setForm({ ...form,vippsLink:e.target.value })}/>
            <p style={{ margin:"-6px 0 10px",fontSize:10,color:"#777",lineHeight:1.5 }}>
              💡 Åpne Vipps → Profil → Del betalingslenke → kopier URL. Deltakere på PC skanner QR-koden.
            </p>
            {form.vippsLink&&(
              <div style={{ marginTop:8,textAlign:"center" }}>
                <p style={{ margin:"0 0 8px",fontSize:11,color:"#999" }}>Forhåndsvisning QR:</p>
                <QRCode url={form.vippsLink} size={120}/>
              </div>
            )}

            {/* v50 FIX 4: opplastbart QR-kode-bilde. Lagres som data-URI i
                settings/config.vippsQrImage. Vises kun for PC-spillere på
                betalingssiden — mobil-spillere bruker Vipps-nummeret direkte. */}
            <div style={{ borderTop:"1px solid #2a2a2a", marginTop:14, paddingTop:12 }}>
              <label style={S.inputLabel}>Vipps QR-kode (bilde)</label>
              <p style={{ margin:"0 0 8px",fontSize:10,color:"#777",lineHeight:1.5 }}>
                QR-koden vises kun for PC-spillere som kan skanne den med mobilen for å betale. Mobil-spillere bruker Vipps-nummeret direkte.
              </p>
              <input type="file" accept="image/png,image/jpeg,image/webp"
                style={{ fontSize:11,color:"#aaa",display:"block",marginBottom:8,width:"100%",
                  background:"#111",border:"1px solid #333",borderRadius:4,padding:"4px" }}
                onChange={e=>{
                  const file = e.target.files[0]; if(!file) return;
                  if(file.size > 1024*1024){ showAlert("Bildet er for stort (maks 1 MB)"); e.target.value=""; return; }
                  const reader = new FileReader();
                  reader.onload = ev => setForm(f=>({ ...f, vippsQrImage:ev.target.result }));
                  reader.readAsDataURL(file);
                }}/>
              {form.vippsQrImage&&(
                <div style={{ textAlign:"center" }}>
                  <img src={form.vippsQrImage} alt="Vipps QR-kode"
                    style={{ maxWidth:160,maxHeight:160,objectFit:"contain",border:"1px solid #2a2a2a",borderRadius:4,background:"#fff",padding:4 }}/>
                  <div>
                    <button type="button"
                      onClick={()=>setForm(f=>({ ...f, vippsQrImage:"" }))}
                      style={{ marginTop:8,padding:"4px 10px",background:"#2a0d0d",border:"1px solid #5a1a1a",
                        color:"#e57373",borderRadius:4,cursor:"pointer",fontSize:11 }}>
                      ✕ Fjern QR-bilde
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>)}

        {settingsTab==="epost"&&(pcLayout ? (<>
          {/* v51: PC-prototype-grenen for E-post-tabben. Tre kort med store
              gull-overskrifter (matcher Regnskap/Premier-stilen), integrerte
              beskrivelser i hvert kort (ingen løsrevet intro-tekst), og
              variabel-info som monospace kode-chips med tooltip-forklaring.
              Layout: EmailJS-kortet i full bredde øverst, deretter Vinner-mal
              og Purre-mal STABLET vertikalt — Purre-malen har to textareas +
              to variabel-blokker og er vesentlig høyere enn Vinner-malen, så
              side-om-side ville gitt svært ujevne kolonner og smale felter. */}
          <style>{`
            .vl-pc-ep-card{
              background:linear-gradient(145deg,#141414 0%,#0e0e0e 100%);
              border:1px solid #2a2a2a;border-left:3px solid #C9A84C;
              border-radius:10px;padding:20px 22px;margin-bottom:16px;
              box-shadow:0 3px 10px rgba(0,0,0,0.4);
            }
            .vl-pc-ep-title{
              /* v54 FIX 4: 15px/0.1em → 14px/0.12em for tab-overgripende
                 konsistens med de andre hoved-seksjonsoverskriftene. */
              margin:0;font-size:14px;color:#C9A84C;font-weight:700;
              letter-spacing:0.12em;text-transform:uppercase;
              display:flex;align-items:center;gap:10px;
            }
            .vl-pc-ep-title .ico{ font-size:22px;line-height:1; }
            .vl-pc-ep-desc{
              margin:6px 0 16px;font-size:12px;color:#8a8a8a;line-height:1.6;
            }
            .vl-pc-ep-field{ margin-bottom:14px; }
            .vl-pc-ep-field:last-child{ margin-bottom:0; }
            .vl-pc-ep-label{
              display:block;margin:0 0 5px;font-size:10px;color:#999;
              text-transform:uppercase;letter-spacing:0.07em;font-weight:600;
            }
            .vl-pc-settings .vl-pc-ep-card input,
            .vl-pc-settings .vl-pc-ep-card textarea{
              margin-bottom:0;max-width:none;
              background:#0a0a0a;border:1px solid #2a2a2a;
            }
            .vl-pc-ep-card textarea{
              width:100%;box-sizing:border-box;resize:vertical;
              line-height:1.5;
            }
            .vl-pc-ep-vars{
              background:#0a0a0a;border:1px solid #242424;border-radius:6px;
              padding:9px 11px;margin-top:8px;
            }
            .vl-pc-ep-vars-h{
              margin:0 0 7px;font-size:9px;color:#6a6a6a;font-weight:600;
              text-transform:uppercase;letter-spacing:0.1em;
            }
            .vl-pc-ep-vars code{
              font-family:'SF Mono',Menlo,Consolas,monospace;font-size:11px;
              background:#1a1500;color:#C9A84C;border:1px solid #3a2e0a;
              border-radius:3px;padding:2px 6px;margin:0 4px 4px 0;
              display:inline-block;cursor:help;
            }
            .vl-pc-ep-note{
              margin:10px 0 0;font-size:11px;color:#777;line-height:1.55;
              padding:8px 10px;background:#11110c;border-radius:5px;
              border-left:2px solid #3a2e0a;
            }
          `}</style>

          {/* KORT 1: Avsender (v208: EmailJS-nøkler/template-IDer fjernet — e-post går via server-Resend) */}
          <div className="vl-pc-ep-card">
            <p className="vl-pc-ep-title"><span className="ico">📧</span> Avsender</p>
            <p className="vl-pc-ep-desc">
              Navnet som vises som avsender i alle e-poster (vinner, purring, avlysning). E-post sendes via Resend fra vinner@vinloddet.no.
            </p>
            <div className="vl-pc-ep-field">
              <label className="vl-pc-ep-label">Avsendernavn (vises i e-posten)</label>
              <input style={S.input} placeholder="Fredagskaffen i DIO" value={form.emailjsAvsender||""} onChange={e=>setForm({ ...form,emailjsAvsender:e.target.value })}/>
            </div>
          </div>

          {/* KORT 2: Vinner-mal */}
          <div className="vl-pc-ep-card">
            <p className="vl-pc-ep-title"><span className="ico">🏆</span> Mal for vinner-e-post</p>
            <p className="vl-pc-ep-desc">
              Sendes til vinnere etter trekning. Slå av auto-utsending hvis du heller vil sende manuelt fra trekningsskjermen.
            </p>
            {(() => { const vinnerPa = (form.emailjsAutoSendVinner ?? form.emailjsAutoSend) !== false; return (<>
            <label style={{ display:"flex",alignItems:"center",gap:10,marginBottom:6,cursor:"pointer" }}
              onClick={async()=>{
                const newVal = !vinnerPa;
                setForm(f=>({ ...f,emailjsAutoSendVinner:newVal }));
                await setDoc(doc(db,"settings","config"),{ emailjsAutoSendVinner:newVal },{ merge:true });
              }}>
              <span style={{ width:36,height:20,borderRadius:10,background:vinnerPa?"#C9A84C":"#2a2a2a",position:"relative",display:"inline-block",flexShrink:0,transition:"background 0.15s" }}>
                <span style={{ position:"absolute",top:3,left:vinnerPa?18:3,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left 0.15s" }}/>
              </span>
              <span style={{ fontSize:12,color:"#ccc" }}>Auto-send varsel ved trekning</span>
            </label>
            <p style={{ margin:"0 0 14px",fontSize:11,color:"#888",lineHeight:1.5 }}>
              {vinnerPa
                ? "Sender e-post til vinnere automatisk når trekningen er fullført. (Endring påvirker kun fremtidige trekninger.)"
                : "⚠️ Av — knappen for manuell sending vises etter trekning. (Endring påvirker kun fremtidige trekninger.)"}
            </p>
            </>); })()}
            <div className="vl-pc-ep-field">
              <label className="vl-pc-ep-label">Melding til vinner</label>
              <textarea style={{ ...S.input,height:96 }}
                placeholder="Gratulerer! Du har vunnet {{premieNavn}} i {{lotteriNavn}}..."
                value={form.emailjsMelding||""}
                onChange={e=>setForm({ ...form,emailjsMelding:e.target.value })}/>
            </div>
            <div className="vl-pc-ep-vars">
              <p className="vl-pc-ep-vars-h">Tilgjengelige variabler</p>
              <code title="Navnet på vinneren">{"{{vinnerNavn}}"}</code>
              <code title="Navnet på premien — slås automatisk sammen ved flere gevinster">{"{{premieNavn}}"}</code>
              <code title="Navnet på lotteriet">{"{{lotteriNavn}}"}</code>
              <code title="Uke-/runde-etiketten">{"{{ukeNavn}}"}</code>
            </div>
            <p className="vl-pc-ep-note">
              💡 Vinner samme person flere premier får én samlet e-post: «en flaske Château Margaux og en flaske Barolo 2019». Skriv bare <code style={{ fontFamily:"'SF Mono',Menlo,Consolas,monospace",color:"#C9A84C" }}>{"{{premieNavn}}"}</code> — formatet settes automatisk.
            </p>
          </div>

          {/* KORT 3: Purre-mal */}
          <div className="vl-pc-ep-card">
            <p className="vl-pc-ep-title"><span className="ico">🔔</span> Mal for purre-e-post</p>
            <p className="vl-pc-ep-desc">
              Brukes til betalingspurringer og påminnelser om uavhentede premier.
            </p>
            <div className="vl-pc-ep-field">
              <label className="vl-pc-ep-label">Melding — betalingspurring</label>
              <textarea style={{ ...S.input,height:96 }}
                placeholder="Hei {{mottakerNavn}}! Vi minner om at du har ubetalte lodd..."
                value={form.emailjsPurreMelding||""}
                onChange={e=>setForm({ ...form,emailjsPurreMelding:e.target.value })}/>
              <div className="vl-pc-ep-vars">
                <p className="vl-pc-ep-vars-h">Tilgjengelige variabler</p>
                <code title="Navnet på mottakeren">{"{{mottakerNavn}}"}</code>
                <code title="Antall ubetalte lodd">{"{{antallLodd}}"}</code>
                <code title="Skyldig beløp i kroner">{"{{beløp}}"}</code>
                <code title="Ditt Vipps-nummer">{"{{vippsNummer}}"}</code>
                <code title="Navnet på lotteriet">{"{{lotteriNavn}}"}</code>
                <code title="Uke-/runde-etiketten">{"{{ukeNavn}}"}</code>
              </div>
            </div>
            <div className="vl-pc-ep-field">
              <label className="vl-pc-ep-label">Melding — purring om å hente premie</label>
              <textarea style={{ ...S.input,height:96 }}
                placeholder="Hei {{mottakerNavn}}! Du har vunnet {{premieNavn}}, men premien er ennå ikke hentet..."
                value={form.emailjsPremieHentingMelding||""}
                onChange={e=>setForm({ ...form,emailjsPremieHentingMelding:e.target.value })}/>
              <div className="vl-pc-ep-vars">
                <p className="vl-pc-ep-vars-h">Tilgjengelige variabler</p>
                <code title="Navnet på mottakeren">{"{{mottakerNavn}}"}</code>
                <code title="Navnet på premien">{"{{premieNavn}}"}</code>
                <code title="Navnet på lotteriet">{"{{lotteriNavn}}"}</code>
                <code title="Uke-/runde-etiketten">{"{{ukeNavn}}"}</code>
              </div>
            </div>
          </div>

          {/* KORT 4: Avlysnings-mal (v120) */}
          <div className="vl-pc-ep-card">
            <p className="vl-pc-ep-title"><span className="ico">📭</span> Mal for avlysnings-e-post</p>
            <p className="vl-pc-ep-desc">
              Sendes automatisk til hver spiller med tilgodehavende når arrangør avlyser en runde.
            </p>
            {(() => { const avlysPa = (form.emailjsAutoSendAvlys ?? form.emailjsAutoSend) !== false; return (<>
            <label style={{ display:"flex",alignItems:"center",gap:10,marginBottom:6,cursor:"pointer" }}
              onClick={async()=>{
                const newVal = !avlysPa;
                setForm(f=>({ ...f,emailjsAutoSendAvlys:newVal }));
                await setDoc(doc(db,"settings","config"),{ emailjsAutoSendAvlys:newVal },{ merge:true });
              }}>
              <span style={{ width:36,height:20,borderRadius:10,background:avlysPa?"#C9A84C":"#2a2a2a",position:"relative",display:"inline-block",flexShrink:0,transition:"background 0.15s" }}>
                <span style={{ position:"absolute",top:3,left:avlysPa?18:3,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left 0.15s" }}/>
              </span>
              <span style={{ fontSize:12,color:"#ccc" }}>Auto-send varsel ved avlysning</span>
            </label>
            <p style={{ margin:"0 0 14px",fontSize:11,color:"#888",lineHeight:1.5 }}>
              Sender e-post til deltakere med tilgodehavende automatisk ved avlysning.
            </p>
            </>); })()}
            <div className="vl-pc-ep-field">
              <label className="vl-pc-ep-label">Melding — avlysning</label>
              <textarea style={{ ...S.input,height:96 }}
                placeholder="Vinlotteriet for {{uke}} ble avlyst. Du har {{belop}} kr i tilgodehavende..."
                value={form.emailjsAvlysMelding||""}
                onChange={e=>setForm({ ...form,emailjsAvlysMelding:e.target.value })}/>
              <div className="vl-pc-ep-vars">
                <p className="vl-pc-ep-vars-h">Tilgjengelige variabler</p>
                <code title="Mottakerens navn">{"{{navn}}"}</code>
                <code title="Uke-/runde-etikett før avlysning">{"{{uke}}"}</code>
                <code title="Tilgodehavende-beløp i kroner">{"{{belop}}"}</code>
              </div>
            </div>
          </div>
        </>) : (<>
          <div style={S.settingsCard}>
            <p style={S.sectionLabel}>📧 Avsender</p>
            <p style={{ margin:"0 0 12px",fontSize:11,color:"#999",lineHeight:1.6 }}>
              Navnet som vises som avsender i alle e-poster. E-post sendes via Resend fra vinner@vinloddet.no.
            </p>
            <label style={S.inputLabel}>Avsendernavn (vises i e-posten)</label>
            <input style={S.input} placeholder="Fredagskaffen i DIO" value={form.emailjsAvsender||""} onChange={e=>setForm({ ...form,emailjsAvsender:e.target.value })}/>
          </div>
          <div style={S.settingsCard}>
            <p style={S.sectionLabel}>🏆 Mal for vinner-e-post</p>
            {(() => { const vinnerPa = (form.emailjsAutoSendVinner ?? form.emailjsAutoSend) !== false; return (<>
            <label style={{ display:"flex",alignItems:"center",gap:10,marginBottom:6,cursor:"pointer" }}
              onClick={async()=>{
                const newVal = !vinnerPa;
                setForm(f=>({ ...f,emailjsAutoSendVinner:newVal }));
                await setDoc(doc(db,"settings","config"),{ emailjsAutoSendVinner:newVal },{ merge:true });
              }}>
              <span style={{ width:36,height:20,borderRadius:10,background:vinnerPa?"#C9A84C":"#2a2a2a",position:"relative",display:"inline-block",flexShrink:0,transition:"background 0.15s" }}>
                <span style={{ position:"absolute",top:3,left:vinnerPa?18:3,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left 0.15s" }}/>
              </span>
              <span style={{ fontSize:12,color:"#ccc" }}>Auto-send varsel ved trekning</span>
            </label>
            <p style={{ margin:"0 0 12px",fontSize:10,color:"#888",lineHeight:1.5 }}>
              {vinnerPa
                ? "Sender e-post til vinnere automatisk når trekningen er fullført. (Endring påvirker kun fremtidige trekninger.)"
                : "⚠️ Av — manuell send-knapp vises etter trekning. (Endring påvirker kun fremtidige trekninger.)"}
            </p>
            </>); })()}
            <label style={S.inputLabel}>Melding til vinner</label>
            <textarea style={{ ...S.input,height:80,resize:"vertical" }}
              placeholder="Gratulerer! Du har vunnet {{premieNavn}} i {{lotteriNavn}}..."
              value={form.emailjsMelding||""}
              onChange={e=>setForm({ ...form,emailjsMelding:e.target.value })}/>
            <div style={{ background:"#0a0a0a",border:"1px solid #3a3a3a",borderRadius:4,padding:10,marginBottom:4 }}>
              <p style={{ margin:0,fontSize:10,color:"#777",lineHeight:1.6 }}>
                Variabler: <span style={{ color:"#C9A84C" }}>{"{{vinnerNavn}}"}</span> · <span style={{ color:"#C9A84C" }}>{"{{premieNavn}}"}</span> · <span style={{ color:"#C9A84C" }}>{"{{lotteriNavn}}"}</span> · <span style={{ color:"#C9A84C" }}>{"{{ukeNavn}}"}</span>
              </p>
              <p style={{ margin:"6px 0 0",fontSize:10,color:"#555",lineHeight:1.5 }}>
                {"💡 Vinner samme person flere premier får én samlet e-post: \"en flaske Château Margaux og en flaske Barolo 2019\". Skriv bare "}<span style={{ color:"#C9A84C" }}>{"{{premieNavn}}"}</span>{" — formatet settes automatisk."}
              </p>
            </div>
          </div>
          <div style={S.settingsCard}>
            <p style={S.sectionLabel}>📣 Mal for purre-e-post</p>

            <label style={{ ...S.inputLabel,marginTop:8 }}>Melding — betalingspurring</label>
            <textarea style={{ ...S.input,height:80,resize:"vertical" }}
              placeholder="Hei {{mottakerNavn}}! Vi minner om at du har ubetalte lodd..."
              value={form.emailjsPurreMelding||""}
              onChange={e=>setForm({ ...form,emailjsPurreMelding:e.target.value })}/>
            <div style={{ background:"#0a0a0a",border:"1px solid #3a3a3a",borderRadius:4,padding:10,marginBottom:12 }}>
              <p style={{ margin:0,fontSize:10,color:"#777",lineHeight:1.6 }}>
                Variabler: <span style={{ color:"#C9A84C" }}>{"{{mottakerNavn}}"}</span> · <span style={{ color:"#C9A84C" }}>{"{{antallLodd}}"}</span> · <span style={{ color:"#C9A84C" }}>{"{{beløp}}"}</span> · <span style={{ color:"#C9A84C" }}>{"{{vippsNummer}}"}</span> · <span style={{ color:"#C9A84C" }}>{"{{lotteriNavn}}"}</span> · <span style={{ color:"#C9A84C" }}>{"{{ukeNavn}}"}</span>
              </p>
            </div>

            <label style={S.inputLabel}>Melding — purring om å hente premie</label>
            <textarea style={{ ...S.input,height:80,resize:"vertical" }}
              placeholder="Hei {{mottakerNavn}}! Du har vunnet {{premieNavn}}, men premien er ennå ikke hentet..."
              value={form.emailjsPremieHentingMelding||""}
              onChange={e=>setForm({ ...form,emailjsPremieHentingMelding:e.target.value })}/>
            <div style={{ background:"#0a0a0a",border:"1px solid #3a3a3a",borderRadius:4,padding:10 }}>
              <p style={{ margin:0,fontSize:10,color:"#777",lineHeight:1.6 }}>
                Variabler: <span style={{ color:"#C9A84C" }}>{"{{mottakerNavn}}"}</span> · <span style={{ color:"#C9A84C" }}>{"{{premieNavn}}"}</span> · <span style={{ color:"#C9A84C" }}>{"{{lotteriNavn}}"}</span> · <span style={{ color:"#C9A84C" }}>{"{{ukeNavn}}"}</span>
              </p>
            </div>
          </div>
          <div style={S.settingsCard}>
            <p style={S.sectionLabel}>📭 Mal for avlysnings-e-post</p>
            <p style={{ margin:"0 0 10px",fontSize:11,color:"#999",lineHeight:1.5 }}>
              Sendes automatisk til hver spiller med tilgodehavende når en runde avlyses. Valgfritt — la stå tomt for å bruke vinner-malen (med avlys-subject).
            </p>
            {(() => { const avlysPa = (form.emailjsAutoSendAvlys ?? form.emailjsAutoSend) !== false; return (<>
            <label style={{ display:"flex",alignItems:"center",gap:10,marginBottom:6,cursor:"pointer" }}
              onClick={async()=>{
                const newVal = !avlysPa;
                setForm(f=>({ ...f,emailjsAutoSendAvlys:newVal }));
                await setDoc(doc(db,"settings","config"),{ emailjsAutoSendAvlys:newVal },{ merge:true });
              }}>
              <span style={{ width:36,height:20,borderRadius:10,background:avlysPa?"#C9A84C":"#2a2a2a",position:"relative",display:"inline-block",flexShrink:0,transition:"background 0.15s" }}>
                <span style={{ position:"absolute",top:3,left:avlysPa?18:3,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left 0.15s" }}/>
              </span>
              <span style={{ fontSize:12,color:"#ccc" }}>Auto-send varsel ved avlysning</span>
            </label>
            <p style={{ margin:"0 0 12px",fontSize:10,color:"#888",lineHeight:1.5 }}>
              Sender e-post til deltakere med tilgodehavende automatisk ved avlysning.
            </p>
            </>); })()}
            <label style={{ ...S.inputLabel,marginTop:8 }}>Melding — avlysning</label>
            <textarea style={{ ...S.input,height:80,resize:"vertical" }}
              placeholder="Vinlotteriet for {{uke}} ble avlyst. Du har {{belop}} kr i tilgodehavende..."
              value={form.emailjsAvlysMelding||""}
              onChange={e=>setForm({ ...form,emailjsAvlysMelding:e.target.value })}/>
            <div style={{ background:"#0a0a0a",border:"1px solid #3a3a3a",borderRadius:4,padding:10 }}>
              <p style={{ margin:0,fontSize:10,color:"#777",lineHeight:1.6 }}>
                Variabler: <span style={{ color:"#C9A84C" }}>{"{{navn}}"}</span> · <span style={{ color:"#C9A84C" }}>{"{{uke}}"}</span> · <span style={{ color:"#C9A84C" }}>{"{{belop}}"}</span>
              </p>
            </div>
          </div>
        </>))}

        {/* v170: "Trekning"-undersiden fjernet helt. Trekningsmetode (v167) og
            trekningsforløp + pause (v170) styres nå fra Trekning-dropdownen. */}

        {settingsTab==="system"&&(<>
          <div style={S.settingsCard}>
            <p style={S.sectionLabel}>⚠️ Nullstilling</p>
            <p style={{ margin:"0 0 12px",fontSize:12,color:"#999" }}>Disse handlingene kan ikke angres. Bruk med forsiktighet.</p>
            <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
              {pcLayout ? (
                <>
                  <button className="vl-pc-danger-btn" onClick={nullstillHistorikk}>
                    <span style={{ fontSize:18 }}>🗑</span>
                    <span style={{ flex:1 }}>
                      <div>Slett all historikk</div>
                      <div style={{ fontSize:10, color:"#888", marginTop:2, fontWeight:400 }}>Sletter deltakere, historikk og regnskap. Loddnummerering starter på 1. Krediter beholdes.</div>
                    </span>
                  </button>
                  <button className="vl-pc-danger-btn" onClick={nullstillRegnskap}>
                    <span style={{ fontSize:18 }}>🗑</span>
                    <span style={{ flex:1 }}>
                      <div>Nullstill regnskap</div>
                      <div style={{ fontSize:10, color:"#888", marginTop:2, fontWeight:400 }}>Bare regnskapstallene nullstilles. Historikk og deltakere beholdes.</div>
                    </span>
                  </button>
                </>
              ) : (
                <>
                  <button style={{ ...S.cancelBtn,borderColor:"#5a1a1a",color:"#e57373",textAlign:"left" }} onClick={nullstillHistorikk}>
                    🗑 Slett all historikk (deltakere + historikk + regnskap, krediter beholdes)
                  </button>
                  <button style={{ ...S.cancelBtn,borderColor:"#5a1a1a",color:"#e57373",textAlign:"left" }} onClick={nullstillRegnskap}>
                    🗑 Nullstill regnskap (kun regnskapstall)
                  </button>
                </>
              )}
            </div>
          </div>
        </>)}

      </div>
      ); })()}

      {/* v37 PC: stor save-knapp erstattet av auto-save + diskret backup. */}
      {pcLayout ? (
        <div style={{ padding:"0 16px", marginTop:12, display:"flex", justifyContent:"flex-end" }}>
          <button
            onClick={save} disabled={saving}
            title="Tving manuell lagring nå (auto-save fanger normalt opp endringer)"
            style={{
              background:"transparent", border:"1px solid #3a3a3a",
              color:"#888", padding:"6px 12px", borderRadius:4,
              fontSize:11, cursor:"pointer",
            }}>
            {saving ? "Lagrer…" : "🔄 Tving lagring nå"}
          </button>
        </div>
      ) : (
        <div style={{ padding:"0 16px",marginTop:8 }}>
          <button style={S.saveBtn} onClick={save} disabled={saving}>
            {saving?"Lagrer…":saveConfirm?"✅ Lagret!":"✓ Lagre innstillinger"}
          </button>
        </div>
      )}
      {/* v62: chroma-key-modal — åpnes når brukeren klikker "🎯 Velg
          bakgrunns-farge" på logo eller topbilde. Bruker resultat-data-URI
          erstatter form[key]; "✓ Klar"-blink etterpå. */}
      {chromaKeyImage && (
        <ChromaKeyModal
          imageUrl={form[chromaKeyImage]}
          onApply={(dataUri)=>{
            setForm(f=>({ ...f, [chromaKeyImage]: dataUri }));
            setBildeBgDone(s=>({ ...s, [chromaKeyImage]: true }));
            setTimeout(()=>setBildeBgDone(s=>({ ...s, [chromaKeyImage]: false })), 3000);
          }}
          onClose={()=>setChromaKeyImage(null)}/>
      )}
    </div>
  );
}

// ─── E-POST TIL VINNERE (EmailJS) ────────────────────────────────────────────
// v205: EmailSender (klient-EmailJS vinner-send) fjernet — vinner-e-post sendes
// nå server-side via Resend (sendWinnerEmails). Denne viser kun resultatet
// (winnerEmailServerStats) til arrangøren i "Trekning fullført"-blokken.
function WinnerEmailServerStatus({ lotteryState }) {
  const stats = lotteryState?.winnerEmailServerStats;
  if (stats) {
    // v287: serveren skriver nå disabled:true når sending er skrudd av
    // (emailjsAutoSendVinner=false / useServerEmail=false) i stedet for å
    // hoppe over i stillhet — meldingen reflekterer virkeligheten.
    if (stats.disabled) {
      return <span style={{ fontSize:12, color:"#888" }}>📧 Vinner-e-post er deaktivert — ingen e-post sendt</span>;
    }
    const sent = stats.sent || 0, failed = stats.failed || 0, skipped = stats.skipped || 0;
    let txt, color;
    if (failed > 0) { txt = `⚠️ Vinner-e-post: ${sent} sendt, ${failed} feilet`; color = "#ff9800"; }
    else if (sent > 0) { txt = `✅ Vinner-e-post sendt til ${sent} vinner${sent > 1 ? "e" : ""}${skipped > 0 ? ` (${skipped} hoppet over)` : ""}`; color = "#4caf50"; }
    else { txt = `📧 Ingen vinner-e-post sendt${skipped > 0 ? ` (${skipped} hoppet over)` : ""}`; color = "#888"; }
    return <span style={{ fontSize:12, color }}>{txt}</span>;
  }
  if (lotteryState?.status === "done" && lotteryState?.winnerEmailSent === false) {
    return <span style={{ fontSize:12, color:"#888" }}>📧 Venter på server-bekreftelse…</span>;
  }
  return null;
}

// ─── ARRANGØR-PANEL ───────────────────────────────────────────────────────────

// ─── TICKER PUBLISHER (arrangør) ─────────────────────────────────────────────
function TickerPublisher({ lotteryState, label, emojiOpenDirection = "up", showClearButton = true, stackVertical = false }) {
  const [msg, setMsg] = useState(lotteryState?.tickerMessage||"");
  const [saving, setSaving] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const emojiBtnRef = useRef(null);
  useEffect(()=>{ setMsg(lotteryState?.tickerMessage||""); },[lotteryState?.tickerMessage]);
  // v32: lukk popup ved klikk utenfor.
  useEffect(()=>{
    if (!emojiOpen) return;
    const onDoc = (e) => {
      if (emojiBtnRef.current && !emojiBtnRef.current.contains(e.target)) {
        const popup = document.getElementById("ticker-emoji-popup");
        if (!popup || !popup.contains(e.target)) setEmojiOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return ()=>document.removeEventListener("mousedown", onDoc);
  }, [emojiOpen]);
  const toggleEmoji = () => {
    if (emojiOpen) { setEmojiOpen(false); return; }
    const rect = emojiBtnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const POPUP_W = 248, POPUP_H = 160; // v163: 6×4 emoji-grid
    // Velg posisjon: ned hvis prop "down" eller default "up" men ikke nok plass opp.
    const wantDown = emojiOpenDirection === "down";
    const top = wantDown ? rect.bottom + 4 : rect.top - POPUP_H - 4;
    const left = Math.max(8, Math.min(rect.right - POPUP_W, window.innerWidth - POPUP_W - 8));
    setEmojiAnchor({ top, left });
    setEmojiOpen(true);
  };
  const publish = async () => {
    setSaving(true);
    try { await updateDoc(doc(db,"lottery","current"),{ tickerMessage:msg.trim()||null }); }
    catch(e) { showErrorToast("Kunne ikke publisere melding."); }
    setSaving(false);
  };
  // v31: label-prop legger en ledetekst foran input ("Melding til spiller").
  // v31: emojiOpenDirection-prop styrer om emoji-popup vises ned eller opp.
  // v32: emoji-popup rendres nå via React-portal til document.body, slik at
  // overflow:hidden eller stacking contexts på forfedre ikke kan klippe den.
  // v163: vinlotteri-emojier (vin/drikke/feiring/norsk flagg), 6×4-grid.
  const EMOJIS = ["🍷","🍾","🥂","🍻","🍺","🥃","🍸","🍹","🥤","🍇","🧀","🍫","🎉","🎊","🎁","🏆","🥇","🎯","🇳🇴","✨","🌟","⭐","❤️","🎵"];
  const textareaEl = (
    <textarea style={{ background:"#1a1a1a",border:"1px solid #444",borderRadius:3,padding:"5px 8px",color:"#f5efe0",fontSize:stackVertical?14:11,outline:"none",width:"100%",boxSizing:"border-box",resize:"vertical",minHeight:stackVertical?64:30,maxHeight:160,height:stackVertical?64:30,fontFamily:"inherit",lineHeight:"1.4" }}
      placeholder="📢 Melding til kjøpere (scrollende på salgssiden)"
      value={msg} onChange={e=>setMsg(e.target.value)}
      id="ticker-input"/>
  );
  const emojiBtn = (
    <button ref={emojiBtnRef} style={{ padding:"5px 7px",background:"#1a1a1a",border:"1px solid #444",borderRadius:3,cursor:"pointer",fontSize:13,height:30,lineHeight:1,flexShrink:0 }}
      onClick={toggleEmoji}>😊</button>
  );
  const publishBtn = (
    <button style={{ padding:"5px 10px",background:msg.trim()?"#C9A84C":"#1a1a1a",border:"1px solid #444",borderRadius:3,cursor:"pointer",fontSize:11,fontWeight:"bold",color:msg.trim()?"#000":"#555",flexShrink:0,whiteSpace:"nowrap",height:30,width:stackVertical?"100%":undefined }}
      onClick={publish} disabled={saving}>
      {saving?"…":"Publiser"}
    </button>
  );
  const clearBtn = (showClearButton && lotteryState?.tickerMessage) ? (
    <button style={{ padding:"5px 7px",background:"#2a0d0d",border:"1px solid #5a1a1a",borderRadius:3,cursor:"pointer",fontSize:10,color:"#e57373",flexShrink:0,height:30 }}
      onClick={async()=>{ setMsg(""); await updateDoc(doc(db,"lottery","current"),{tickerMessage:null}); }}>✕</button>
  ) : null;
  // v32/v163: emoji-popup via createPortal til document.body — forfedre med
  // overflow:hidden eller egne stacking contexts kan ikke klippe den. z-index
  // 99999 ligger over alt (inkl. ticker-sheet på z 1001).
  const emojiPopup = (emojiOpen && emojiAnchor) ? createPortal(
    <div id="ticker-emoji-popup" style={{
      position:"fixed", top:emojiAnchor.top, left:emojiAnchor.left,
      background:"#1a1a1a", border:"1px solid #444", borderRadius:6,
      padding:6, display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:4, width:248,
      zIndex:99999, boxShadow:"0 6px 20px rgba(0,0,0,0.65)",
    }}>
      {EMOJIS.map(em=>(
        <span key={em} style={{ fontSize:20,cursor:"pointer",padding:2,textAlign:"center" }}
          onClick={()=>{
            const inp = document.getElementById("ticker-input");
            if (inp) {
              const p = inp.selectionStart;
              setMsg(m => m.slice(0,p) + em + m.slice(p));
            }
            setEmojiOpen(false);
          }}>
          {em}
        </span>
      ))}
    </div>,
    document.body
  ) : null;

  // v163: vertikal stabling (mobil ticker-sheet) — tekstfelt på egen linje i
  // full bredde, knapper under. Gated på prop så PC/klassisk beholder grid.
  if (stackVertical) {
    return (
      <div style={{ background:"#0d0d0d", display:"flex", flexDirection:"column", gap:10 }}>
        {textareaEl}
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          {emojiBtn}
          <div style={{ flex:1 }}>{publishBtn}</div>
          {clearBtn}
        </div>
        {emojiPopup}
      </div>
    );
  }

  const gridCols = label ? "auto 1fr auto auto auto" : "1fr auto auto auto";
  return (
    <div style={{ margin:"0",padding:"6px 12px",background:"#0d0d0d",borderBottom:"1px solid #2a2a2a",display:"grid",gridTemplateColumns:gridCols,gap:8,alignItems:"center" }}>
      {label && (
        <span style={{
          fontSize:11, color:"#a89058", letterSpacing:"0.06em",
          textTransform:"uppercase", fontWeight:600, whiteSpace:"nowrap",
          paddingRight:2,
        }}>{label}</span>
      )}
      {textareaEl}
      <div style={{ position:"relative" }}>{emojiBtn}</div>
      {publishBtn}
      {clearBtn}
      {emojiPopup}
    </div>
  );
}

// ─── TESTDATA-KNAPP ───────────────────────────────────────────────────────────
const TESTPERSONER = [
  {name:"Anders Hansen",email:"anders.hansen@test.no",tickets:1},
  {name:"Berit Olsen",email:"berit.olsen@test.no",tickets:3},
  {name:"Christian Berg",email:"christian.berg@test.no",tickets:2},
  {name:"Diana Dahl",email:"diana.dahl@test.no",tickets:1},
  {name:"Erik Lund",email:"erik.lund@test.no",tickets:4},
  {name:"Frida Moen",email:"frida.moen@test.no",tickets:2},
  {name:"Geir Vik",email:"geir.vik@test.no",tickets:1},
  {name:"Hanne Sæther",email:"hanne.saether@test.no",tickets:3},
  {name:"Ivar Strand",email:"ivar.strand@test.no",tickets:1},
  {name:"Julie Knutsen",email:"julie.knutsen@test.no",tickets:2},
  {name:"Knut Bakke",email:"knut.bakke@test.no",tickets:1},
  {name:"Lise Rønning",email:"lise.ronning@test.no",tickets:2},
  {name:"Magnus Thorsen",email:"magnus.thorsen@test.no",tickets:3},
  {name:"Nina Aas",email:"nina.aas@test.no",tickets:1},
  {name:"Ole Petter Nygaard",email:"opnygaard@test.no",tickets:2},
  {name:"Pernille Vold",email:"pernille.vold@test.no",tickets:1},
  {name:"Rune Gundersen",email:"rune.gundersen@test.no",tickets:4},
  {name:"Silje Haugen",email:"silje.haugen@test.no",tickets:1},
  {name:"Tor Elias Brekke",email:"tor.brekke@test.no",tickets:2},
  {name:"Unni Mathiesen",email:"unni.mathiesen@test.no",tickets:1},
];
function TestDataKnapp({ settings, compact = false }) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const gen = async () => {
    setLoading(true);
    try {
      // v107: bruker samme atomiske allokering som handleBuy. Hver iterasjon
      // gjør sin egen transaksjon mot counters/tickets — testdata-knappen er
      // arrangør-only, så serielt og litt tregere er greit; viktig at telleren
      // er korrekt etterpå slik at neste reelle kjøp får riktig start.
      const price = settings?.ticketPrice || 10;
      for (const p of TESTPERSONER) {
        const tNums = await allocateTicketNumbers(p.tickets);
        await addDoc(collection(db,"participants"),{
          name:p.name, email:p.email, tickets:p.tickets,
          paid:true, amount:p.tickets*price, kredittBrukt:0,
          ticketNumbers:tNums, createdAt:serverTimestamp()
        });
      }
      setDone(true); setTimeout(()=>setDone(false),4000);
    } catch(e){ showErrorToast("Feil: "+e.message); }
    setLoading(false);
  };
  const slett = async () => {
    if (!window.confirm("Slette alle testpersoner (@test.no)?")) return;
    setLoading(true);
    try {
      const snap = await getDocs(collection(db,"participants"));
      await Promise.all(snap.docs.filter(d=>d.data().email?.includes("@test.no")).map(d=>deleteDoc(doc(db,"participants",d.id))));
    } catch(e){ showErrorToast("Feil: "+e.message); }
    setLoading(false);
  };
  // v52 FIX 6: compact-modus for topp-baren — to små diskrete knapper uten
  // den brede stiplede info-stripa. Brukes i PC-prototypens topp-bar rad 1.
  if (compact) {
    return (
      <div style={{ display:"flex",gap:6,alignItems:"center",flexShrink:0 }}>
        <button style={{ padding:"5px 9px",background:loading?"#1a1a1a":"#1a1a5a",border:"1px solid #3a3a9a",borderRadius:4,color:loading?"#555":"#9a9aff",fontSize:11,cursor:loading?"not-allowed":"pointer",whiteSpace:"nowrap" }}
          onClick={gen} disabled={loading} title="Generer 20 fiktive testspillere (42 lodd)">
          {done?"✅ Lagt til!":loading?"Legger inn…":"🧪 +20 testspillere"}
        </button>
        <button style={{ padding:"5px 9px",background:"#1a0a0a",border:"1px solid #6a1a1a",borderRadius:4,color:"#e57373",fontSize:11,cursor:loading?"not-allowed":"pointer",whiteSpace:"nowrap" }}
          onClick={slett} disabled={loading} title="Slett alle testspillere (@test.no)">
          🗑
        </button>
      </div>
    );
  }
  return (
    <div style={{ margin:"8px 0",padding:"10px 12px",background:"#0a0a1a",border:"1px dashed #3a3a8a",borderRadius:6,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap" }}>
      <span style={{ fontSize:11,color:"#5a5aaa",flex:1 }}>🧪 Testdata (20 fiktive spillere, 42 lodd)</span>
      <button style={{ padding:"5px 12px",background:loading?"#1a1a1a":"#1a1a5a",border:"1px solid #3a3a9a",borderRadius:3,color:loading?"#555":"#9a9aff",fontSize:11,cursor:loading?"not-allowed":"pointer" }}
        onClick={gen} disabled={loading}>
        {done?"✅ Lagt til!":loading?"Legger inn…":"➕ Generer 20 testspillere"}
      </button>
      <button style={{ padding:"5px 10px",background:"#1a0a0a",border:"1px solid #6a1a1a",borderRadius:3,color:"#e57373",fontSize:11,cursor:loading?"not-allowed":"pointer" }}
        onClick={slett} disabled={loading}>
        🗑 Slett testspillere
      </button>
    </div>
  );
}

// v27: PC-arrangør-dashboard. Tynt wrapper rundt OrganizerView med
// `pcDashboard=true`-flag. v57: permanent default på PC (≥ 900 px). Mobil
// bruker fortsatt vanlig OrganizerView uendret. All forretningslogikk
// (state, effekter, helpers) deles med OrganizerView — kun chrome/layout
// er forskjellig. Det lar oss iterere på PC-design uten å risikere mobil-
// regresjoner.
function OrganizerViewPC(props) {
  return <OrganizerView {...props} pcDashboard={true} />;
}

// v27: bred deltaker-tabell for PC-dashboard. Kolonner: avatar, navn,
// e-post, telefon, lodd, beløp, status. Klikk på header sorterer.
function OrganizerPCDeltakerTable({ participants }) {
  const [sortBy, setSortBy] = useState("tickets");
  const [sortDir, setSortDir] = useState("desc");
  const byEmail = {};
  const sorted = [...(participants||[])].sort((a,b)=>(a.createdAt?.seconds||0)-(b.createdAt?.seconds||0));
  sorted.forEach(p=>{
    const key = (p.email||p.name||"").toLowerCase()||p.id;
    if (!byEmail[key]) byEmail[key] = { ...p, tickets:0, amount:0, allPaid:true, ids:[], breakdown:[] };
    byEmail[key].tickets += (p.tickets||0);
    byEmail[key].amount += (p.amount||0);
    byEmail[key].allPaid = byEmail[key].allPaid && !!p.paid;
    byEmail[key].ids.push(p.id);
    byEmail[key].breakdown.push(p.tickets||0);
    byEmail[key].name = p.name;
    byEmail[key].email = p.email;
    byEmail[key].phone = p.phone || byEmail[key].phone;
  });
  const merged = Object.values(byEmail);
  const cmp = (a, b) => {
    let av, bv;
    if (sortBy === "name") { av = (a.name||"").toLowerCase(); bv = (b.name||"").toLowerCase(); }
    else if (sortBy === "email") { av = (a.email||"").toLowerCase(); bv = (b.email||"").toLowerCase(); }
    else if (sortBy === "phone") { av = (a.phone||"").toLowerCase(); bv = (b.phone||"").toLowerCase(); }
    else if (sortBy === "tickets") { av = a.tickets||0; bv = b.tickets||0; }
    else if (sortBy === "amount") { av = a.amount||0; bv = b.amount||0; }
    else if (sortBy === "status") { av = a.allPaid?1:0; bv = b.allPaid?1:0; }
    else { av = 0; bv = 0; }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  };
  const rows = merged.sort(cmp);
  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir(col === "name" || col === "email" || col === "phone" ? "asc" : "desc"); }
  };
  // v54 FIX 4: kolonne-headers — 11px fet lysere grå (#999), aktiv kolonne
  // i gull. Var 10px #666 — for dimt mot mørk bakgrunn.
  const headerStyle = (col) => ({
    fontSize:11, color:sortBy===col?"#C9A84C":"#999", textTransform:"uppercase", letterSpacing:"0.08em",
    cursor:"pointer", userSelect:"none", padding:"12px 14px", fontWeight:"bold",
  });
  const sortIcon = (col) => sortBy === col ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  if (rows.length === 0) {
    return <p style={{ padding:40, textAlign:"center", color:"#444" }}>Ingen deltakere ennå</p>;
  }

  return (
    <div style={{ background:"#0d0d0d", border:"1px solid #2a2a2a", borderRadius:8, overflow:"hidden" }}>
      <style>{`.vlPCRow:hover{background:#1a1408!important;}`}</style>
      <div style={{ display:"grid", gridTemplateColumns:"60px 2fr 2fr 1.2fr 100px 100px 130px", background:"#141414", borderBottom:"1px solid #2a2a2a" }}>
        <span style={{ ...headerStyle("name"), paddingLeft:14 }}>#</span>
        <span style={headerStyle("name")} onClick={()=>handleSort("name")}>Navn{sortIcon("name")}</span>
        <span style={headerStyle("email")} onClick={()=>handleSort("email")}>E-post{sortIcon("email")}</span>
        <span style={headerStyle("phone")} onClick={()=>handleSort("phone")}>Telefon{sortIcon("phone")}</span>
        <span style={{ ...headerStyle("tickets"), textAlign:"right" }} onClick={()=>handleSort("tickets")}>Lodd{sortIcon("tickets")}</span>
        <span style={{ ...headerStyle("amount"), textAlign:"right" }} onClick={()=>handleSort("amount")}>Beløp{sortIcon("amount")}</span>
        <span style={{ ...headerStyle("status"), textAlign:"center" }} onClick={()=>handleSort("status")}>Status{sortIcon("status")}</span>
      </div>
      {rows.map((p, i)=>(
        <div key={p.email||p.id} className="vlPCRow" style={{
          display:"grid", gridTemplateColumns:"60px 2fr 2fr 1.2fr 100px 100px 130px",
          alignItems:"center", padding:"10px 0", borderBottom:"1px solid #1a1a1a",
          transition:"background 0.12s",
        }}>
          <div style={{ paddingLeft:14, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{
              width:34, height:34, borderRadius:"50%", background:"#2a1f0a",
              border:"1px solid #C9A84C", color:"#C9A84C", fontSize:12, fontWeight:"bold",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontFamily:"'Inter',system-ui,sans-serif",
            }}>{p.name?.slice(0,2).toUpperCase() || "??"}</span>
          </div>
          <div style={{ padding:"0 14px", color:"#f5efe0", fontSize:13 }}>
            {p.name}
            {p.breakdown.length>1 && (
              <span style={{ marginLeft:8, fontSize:11, color:"#666" }}>
                ({p.breakdown.join("+")})
              </span>
            )}
          </div>
          <div style={{ padding:"0 14px", color:"#888", fontSize:12, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.email || "—"}</div>
          <div style={{ padding:"0 14px", color:"#888", fontSize:12 }}>{p.phone || "—"}</div>
          <div style={{ padding:"0 14px", color:"#f5efe0", fontSize:13, textAlign:"right", fontWeight:"bold" }}>{p.tickets}</div>
          <div style={{ padding:"0 14px", color:"#f5efe0", fontSize:13, textAlign:"right" }}>{p.amount},-</div>
          <div style={{ padding:"0 14px", textAlign:"center" }}>
            <span onClick={async()=>{
              const newPaid = !p.allPaid;
              await Promise.all(p.ids.map(id=>updateDoc(doc(db,"participants",id),{ paid:newPaid })));
            }} style={{
              display:"inline-block", padding:"4px 12px", borderRadius:12, fontSize:11, fontWeight:"bold",
              background:p.allPaid?"#0d2a0d":"#2a0d0d", color:p.allPaid?"#4caf50":"#e57373",
              cursor:"pointer", border:`1px solid ${p.allPaid?"#1a4a1a":"#5a1a1a"}`,
            }}>
              {p.allPaid?"✓ Betalt":"⏳ Ubetalt"}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// v27: bred ubetalt-tabell for PC-dashboard. Samme oppsett som deltaker-
// tabell men kun ubetalte. Klikk på status-pillen markerer betalt.
function OrganizerPCUbetalteTable({ participants }) {
  const byEmail = {};
  participants.forEach(p=>{
    const key=(p.email||p.name||"").toLowerCase()||p.id;
    if(!byEmail[key]) byEmail[key]={ ...p, tickets:0, amount:0, allPaid:true, ids:[] };
    byEmail[key].tickets+=(p.tickets||0);
    byEmail[key].amount+=(p.amount||0);
    byEmail[key].allPaid=byEmail[key].allPaid&&!!p.paid;
    byEmail[key].ids.push(p.id);
    byEmail[key].name=p.name; byEmail[key].email=p.email;
    byEmail[key].phone = p.phone || byEmail[key].phone;
  });
  const rows = Object.values(byEmail).filter(p=>!p.allPaid).sort((a,b)=>(b.tickets||0)-(a.tickets||0));
  const paid = Object.values(byEmail).filter(p=>p.allPaid).sort((a,b)=>b.tickets-a.tickets);
  return (
    <div style={{ background:"#0d0d0d", border:"1px solid #2a2a2a", borderRadius:8, overflow:"hidden", marginBottom:16 }}>
      <style>{`.vlPCRow:hover{background:#1a1408!important;}`}</style>
      <div style={{ display:"grid", gridTemplateColumns:"60px 2fr 2fr 1.2fr 100px 100px 150px", background:"#141414", borderBottom:"1px solid #2a2a2a", padding:"12px 0" }}>
        <span style={{ paddingLeft:14, fontSize:11, color:"#999", textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:"bold" }}>#</span>
        <span style={{ paddingLeft:14, fontSize:11, color:"#999", textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:"bold" }}>Navn</span>
        <span style={{ paddingLeft:14, fontSize:11, color:"#999", textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:"bold" }}>E-post</span>
        <span style={{ paddingLeft:14, fontSize:11, color:"#999", textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:"bold" }}>Telefon</span>
        <span style={{ paddingRight:14, fontSize:11, color:"#999", textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:"bold", textAlign:"right" }}>Lodd</span>
        <span style={{ paddingRight:14, fontSize:11, color:"#999", textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:"bold", textAlign:"right" }}>Beløp</span>
        <span style={{ paddingRight:14, fontSize:11, color:"#999", textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:"bold", textAlign:"center" }}>Handling</span>
      </div>
      {rows.map(p=>(
        <div key={p.email||p.id} className="vlPCRow" style={{
          display:"grid", gridTemplateColumns:"60px 2fr 2fr 1.2fr 100px 100px 150px",
          alignItems:"center", padding:"10px 0", borderBottom:"1px solid #1a1a1a",
          transition:"background 0.12s",
        }}>
          <div style={{ paddingLeft:14, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{
              width:34, height:34, borderRadius:"50%", background:"#2a1f0a",
              border:"1px solid #C9A84C", color:"#C9A84C", fontSize:12, fontWeight:"bold",
              display:"flex", alignItems:"center", justifyContent:"center",
            }}>{p.name?.slice(0,2).toUpperCase() || "??"}</span>
          </div>
          <div style={{ padding:"0 14px", color:"#f5efe0", fontSize:13 }}>{p.name}</div>
          <div style={{ padding:"0 14px", color:"#888", fontSize:12, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.email || "—"}</div>
          <div style={{ padding:"0 14px", color:"#888", fontSize:12 }}>{p.phone || "—"}</div>
          <div style={{ padding:"0 14px", color:"#f5efe0", fontSize:13, textAlign:"right", fontWeight:"bold" }}>{p.tickets}</div>
          <div style={{ padding:"0 14px", color:"#e57373", fontSize:13, textAlign:"right" }}>{p.amount},-</div>
          <div style={{ padding:"0 14px", textAlign:"center" }}>
            <span onClick={async()=>{ await Promise.all(p.ids.map(id=>updateDoc(doc(db,"participants",id),{paid:true}))); }}
              style={{
                display:"inline-block", padding:"5px 12px", borderRadius:12, fontSize:11, fontWeight:"bold",
                background:"#2a0d0d", color:"#e57373", cursor:"pointer", border:"1px solid #5a1a1a",
              }}>
              ⏳ Marker betalt
            </span>
          </div>
        </div>
      ))}
      {paid.length > 0 && (
        <>
          <div style={{ padding:"12px 14px", background:"#0a0a0a", borderTop:"1px solid #1a1a1a", fontSize:10, color:"#555", textTransform:"uppercase", letterSpacing:"0.1em" }}>
            ✅ Betalte – klikk for å angre
          </div>
          {paid.map(p=>(
            <div key={p.email||p.id} style={{
              display:"grid", gridTemplateColumns:"60px 2fr 2fr 1.2fr 100px 100px 150px",
              alignItems:"center", padding:"8px 0", borderBottom:"1px solid #1a1a1a", opacity:0.55,
            }}>
              <div style={{ paddingLeft:14 }}>—</div>
              <div style={{ padding:"0 14px", fontSize:12, color:"#666" }}>{p.name}</div>
              <div style={{ padding:"0 14px", fontSize:11, color:"#444", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.email || "—"}</div>
              <div style={{ padding:"0 14px", fontSize:11, color:"#444" }}>{p.phone || "—"}</div>
              <div style={{ padding:"0 14px", fontSize:12, color:"#555", textAlign:"right" }}>{p.tickets}</div>
              <div style={{ padding:"0 14px", fontSize:12, color:"#555", textAlign:"right" }}>{p.amount},-</div>
              <div style={{ padding:"0 14px", textAlign:"center" }}>
                <span onClick={async()=>{ await Promise.all(p.ids.map(id=>updateDoc(doc(db,"participants",id),{paid:false}))); }}
                  style={{
                    display:"inline-block", padding:"4px 10px", borderRadius:12, fontSize:10, fontWeight:"bold",
                    background:"#0d2a0d", color:"#4caf50", cursor:"pointer", border:"1px solid #1a4a1a",
                  }}>
                  ✓ Angre
                </span>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// v43-44: chroma-key-fjerning av hvit bakgrunn på Vinmonopolet-flaskebilder.
// Vinmonopolet (bilder.vinmonopolet.no) sender IKKE Access-Control-Allow-
// Origin på sine produktbilder, så direkte canvas-prosessering fra
// nettleseren blir "tainted" og getImageData() kaster SecurityError.
// v44: hybrid løsning. Først forsøker vi via en CORS-proxy
// (images.weserv.nl — gratis, populær, åpen CORS). Dette virker for
// Vinmonopolet og alle andre offentlige bilde-URLer. Hvis proxy-en er
// nede eller blokkerer en URL, faller vi tilbake til direkte forsøk
// (vil fungere hvis kilden faktisk har CORS satt selv).
// Data:-URLer prosesseres rett uten proxy (de er allerede lokale).
function vlChromaKey(img, threshold, softness) {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    let imageData;
    try {
      imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    } catch (e) {
      return { error: "tainted" };
    }
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const minBright = Math.min(r, g, b);
      if (minBright >= threshold) {
        data[i + 3] = 0;
      } else if (minBright > threshold - softness) {
        const t = (minBright - (threshold - softness)) / softness;
        data[i + 3] = Math.round(data[i + 3] * (1 - t));
      }
    }
    ctx.putImageData(imageData, 0, 0);
    return { dataUri: canvas.toDataURL("image/png") };
  } catch (e) {
    return { error: "process" };
  }
}
function tryLoadAndKey(srcUrl, threshold, softness) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(vlChromaKey(img, threshold, softness));
    img.onerror = () => resolve({ error: "load" });
    img.src = srcUrl;
  });
}
// Returnerer { dataUri } ved suksess, { error: "<kode>" } ved feil:
//   "load"     — bilde-URL kunne ikke lastes (404, ugyldig, nettverksfeil)
//   "tainted"  — CORS-blokk gjorde canvas utilgjengelig
//   "process"  — annen feil under canvas-prosessering
async function removeWhiteBackground(url, opts = {}) {
  // v56 FIX 2: terskel senket 240 → 222 og softness utvidet 15 → 45.
  // Tidligere ble kant-piksler i 222-240-området ikke fjernet, så flaskene
  // fikk et tynt hvitt halo mot mørk bakgrunn. Nå regnes minBright >= 222
  // som ren bakgrunn, og 177-222 fades gradvis ut → myk kant uten halo.
  const threshold = opts.threshold ?? 222;
  const softness = opts.softness ?? 45;
  if (!url) return { error: "load" };
  // Data:-URL — kan prosesseres direkte uten proxy.
  if (url.startsWith("data:")) {
    return await tryLoadAndKey(url, threshold, softness);
  }
  // Eksterne URLer går via CORS-proxy. images.weserv.nl tar URL uten
  // protokoll og legger til HTTPS automatisk.
  const stripped = url.replace(/^https?:\/\//i, "");
  const proxied = `https://images.weserv.nl/?url=${encodeURIComponent(stripped)}`;
  let r = await tryLoadAndKey(proxied, threshold, softness);
  if (r.dataUri) return r;
  // Fallback: prøv direkte (hvis kilden HAR CORS, eller hvis proxy er nede).
  r = await tryLoadAndKey(url, threshold, softness);
  return r;
}

// v62: generell chroma-key — fjerner piksler med valgt farge (Euclidean
// RGB-avstand). Brukes av ChromaKeyModal for å fjerne vilkårlig bakgrunns-
// farge fra logo/topbilde (svart, blå, gradient, …). Soft-edge gir gradvis
// transparens i båndet (tolerance .. tolerance+softness) så kantene ikke
// blir harde.
// target = { r, g, b } (0-255). tolerance og softness i RGB-avstand-enheter.
// Returnerer { dataUri } eller { error: "load" | "tainted" | "process" }.
function chromaKeyImageData(srcImageData, target, tolerance, softness) {
  const data = srcImageData.data;
  const out = new Uint8ClampedArray(data);
  const tR = target.r, tG = target.g, tB = target.b;
  const tol = Math.max(0, tolerance);
  const soft = Math.max(0.5, softness);
  const tolSq = tol * tol;
  const outerSq = (tol + soft) * (tol + soft);
  for (let i = 0; i < out.length; i += 4) {
    const dR = out[i] - tR;
    const dG = out[i + 1] - tG;
    const dB = out[i + 2] - tB;
    const distSq = dR*dR + dG*dG + dB*dB;
    if (distSq <= tolSq) {
      out[i + 3] = 0;
    } else if (distSq <= outerSq) {
      const dist = Math.sqrt(distSq);
      const t = (dist - tol) / soft;
      out[i + 3] = Math.round(out[i + 3] * t);
    }
  }
  return new ImageData(out, srcImageData.width, srcImageData.height);
}

function loadImageToCanvas(url) {
  return new Promise((resolve) => {
    if (!url) return resolve({ error: "load" });
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onerror = () => resolve({ error: "load" });
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        try {
          // CORS-test
          ctx.getImageData(0, 0, 1, 1);
          resolve({ canvas, ctx });
        } catch (e) {
          resolve({ error: "tainted" });
        }
      } catch (e) { resolve({ error: "process" }); }
    };
    img.src = url;
  });
}

// v62: Modal for klikk-for-farge chroma-key. Bruker klikker på piksel i
// preview → fanger RGB → live preview oppdateres når toleranse-slideren
// dras. Soft-edge er fast 15 enheter. Bruker "Bruk denne" → callback
// med data-URI for det prosesserte bildet. CORS-bilder som ikke er fra
// data-URI eller CORS-vennlig kilde → vises feilmelding.
function ChromaKeyModal({ imageUrl, onApply, onClose }) {
  const [picked, setPicked] = useState(null);          // { r, g, b }
  const [tolerance, setTolerance] = useState(30);      // 0-100
  const [previewDataUri, setPreviewDataUri] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [srcCanvas, setSrcCanvas] = useState(null);
  const SOFTNESS = 15;
  const CHECKER_BG = "repeating-conic-gradient(#2a2a2a 0 25%, #1a1a1a 0 50%) 50% / 16px 16px";

  // Last bildet inn i et off-screen canvas én gang (re-kjør hvis URL endres).
  useEffect(() => {
    let cancelled = false;
    setError(null); setPicked(null); setPreviewDataUri(null); setSrcCanvas(null);
    loadImageToCanvas(imageUrl).then(res => {
      if (cancelled) return;
      if (res.error) { setError(res.error); return; }
      setSrcCanvas(res.canvas);
    });
    return () => { cancelled = true; };
  }, [imageUrl]);

  // Live preview: regenerér når picked eller tolerance endres. Debounce 50 ms
  // for å unngå å kjøre for hver pixel-bevegelse på slideren.
  useEffect(() => {
    if (!picked || !srcCanvas) { setPreviewDataUri(null); return; }
    setProcessing(true);
    const handle = setTimeout(() => {
      try {
        const ctx = srcCanvas.getContext("2d");
        const src = ctx.getImageData(0, 0, srcCanvas.width, srcCanvas.height);
        const out = chromaKeyImageData(src, picked, tolerance, SOFTNESS);
        const outCanvas = document.createElement("canvas");
        outCanvas.width = srcCanvas.width;
        outCanvas.height = srcCanvas.height;
        outCanvas.getContext("2d").putImageData(out, 0, 0);
        setPreviewDataUri(outCanvas.toDataURL("image/png"));
      } catch (e) { /* ignore */ }
      setProcessing(false);
    }, 50);
    return () => clearTimeout(handle);
  }, [picked, tolerance, srcCanvas]);

  const handlePick = (e) => {
    if (!srcCanvas) return;
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const xFrac = (e.clientX - rect.left) / rect.width;
    const yFrac = (e.clientY - rect.top) / rect.height;
    const x = Math.max(0, Math.min(srcCanvas.width - 1, Math.floor(xFrac * srcCanvas.width)));
    const y = Math.max(0, Math.min(srcCanvas.height - 1, Math.floor(yFrac * srcCanvas.height)));
    const ctx = srcCanvas.getContext("2d");
    const px = ctx.getImageData(x, y, 1, 1).data;
    setPicked({ r: px[0], g: px[1], b: px[2] });
  };

  const hex = picked
    ? "#" + [picked.r, picked.g, picked.b].map(v => v.toString(16).padStart(2,"0")).join("").toUpperCase()
    : null;

  const handleApply = () => { if (previewDataUri) { onApply(previewDataUri); onClose(); } };

  return (
    <div onClick={onClose}
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.82)", zIndex:200,
        display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div onClick={e=>e.stopPropagation()}
        style={{ background:"#141414", border:"1px solid #3a3a3a", borderRadius:10,
          padding:"24px 26px", width:"100%", maxWidth:820, position:"relative",
          boxShadow:"0 8px 32px rgba(0,0,0,0.7)" }}>
        <button onClick={onClose} aria-label="Lukk"
          style={{ position:"absolute", top:12, right:12, width:30, height:30,
            background:"transparent", border:"1px solid #3a3a3a", color:"#aaa",
            borderRadius:15, cursor:"pointer", fontSize:14 }}>×</button>
        <p style={{ margin:"0 0 4px", fontSize:14, color:"#C9A84C", fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase" }}>🎯 Klikk-for-farge — bakgrunns-fjerning</p>
        <p style={{ margin:"0 0 16px", fontSize:12, color:"#999" }}>
          Klikk på bakgrunnen i bildet til venstre. Dra toleranse-slideren for å fange flere variasjoner. Resultatet vises live til høyre.
        </p>

        {error && (
          <p style={{ margin:"0 0 14px", fontSize:12, color:"#e57373", lineHeight:1.5 }}>
            {error === "tainted"
              ? "⚠️ Dette bildet kan ikke prosesseres (CORS). Last ned bildet og last det opp lokalt via fil-velgeren."
              : "⚠️ Kunne ikke laste bildet. Sjekk at URL-en er korrekt."}
          </p>
        )}

        {!error && (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:14 }}>
              <div>
                <p style={{ margin:"0 0 6px", fontSize:10, color:"#888", textTransform:"uppercase", letterSpacing:"0.08em" }}>Original — klikk på fargen</p>
                <div onClick={handlePick}
                  style={{ width:"100%", height:240, borderRadius:6,
                    border:"1px solid #2a2a2a", background:CHECKER_BG, overflow:"hidden",
                    cursor: srcCanvas ? "crosshair" : "wait",
                    display:"flex", alignItems:"center", justifyContent:"center" }}>
                  {srcCanvas
                    ? <img src={imageUrl} alt="Original"
                        style={{ maxWidth:"100%", maxHeight:"100%", objectFit:"contain", pointerEvents:"none", display:"block" }}/>
                    : <span style={{ fontSize:11, color:"#666" }}>Laster…</span>}
                </div>
              </div>
              <div>
                <p style={{ margin:"0 0 6px", fontSize:10, color:"#888", textTransform:"uppercase", letterSpacing:"0.08em" }}>Resultat (transparent bakgrunn)</p>
                <div style={{ width:"100%", height:240, borderRadius:6,
                  border:"1px solid #2a2a2a", background:CHECKER_BG, overflow:"hidden",
                  display:"flex", alignItems:"center", justifyContent:"center" }}>
                  {previewDataUri
                    ? <img src={previewDataUri} alt="Resultat"
                        style={{ maxWidth:"100%", maxHeight:"100%", objectFit:"contain", display:"block" }}/>
                    : <span style={{ fontSize:11, color:"#666" }}>{processing ? "Behandler…" : "Klikk på bakgrunnen først"}</span>}
                </div>
              </div>
            </div>

            <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:14, flexWrap:"wrap" }}>
              <span style={{ fontSize:11, color:"#999", textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600 }}>Valgt:</span>
              {picked ? (
                <span style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ width:24, height:24, borderRadius:4, background:hex, border:"1px solid #555", flexShrink:0 }}/>
                  <code style={{ fontSize:12, color:"#ddd", fontFamily:"'SF Mono',Menlo,Consolas,monospace" }}>{hex}</code>
                </span>
              ) : (
                <span style={{ fontSize:12, color:"#666" }}>Ingen farge valgt enda</span>
              )}
            </div>

            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:18 }}>
              <span style={{ fontSize:11, color:"#999", textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600, minWidth:80 }}>Toleranse: {tolerance}</span>
              <input type="range" min="0" max="100" step="1"
                value={tolerance}
                onChange={e=>setTolerance(parseInt(e.target.value)||0)}
                style={{ flex:1, accentColor:"#C9A84C" }}/>
            </div>
            <p style={{ margin:"-10px 0 16px", fontSize:10, color:"#666", lineHeight:1.5 }}>
              Lav verdi (0–10) = bare nøyaktig samme farge. Høyere verdi (50+) fanger skygger og gradient. Soft-edge ±{SOFTNESS} enheter gir myk overgang.
            </p>

            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={onClose}
                style={{ padding:"9px 16px", background:"transparent", border:"1px solid #3a3a3a", color:"#aaa", borderRadius:5, cursor:"pointer", fontSize:13 }}>
                Avbryt
              </button>
              <button onClick={handleApply}
                disabled={!previewDataUri || processing}
                style={{ padding:"9px 18px",
                  background: (!previewDataUri || processing) ? "#1a1a1a" : "#1a4a1a",
                  border: `1px solid ${(!previewDataUri || processing) ? "#3a3a3a" : "#2a6a2a"}`,
                  color: (!previewDataUri || processing) ? "#666" : "#a8e8a8",
                  borderRadius:5, cursor:(!previewDataUri || processing) ? "not-allowed" : "pointer",
                  fontSize:13, fontWeight:"bold" }}>
                🪄 Bruk denne
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function isVinmonopoletUrl(url) {
  if (!url || typeof url !== "string") return false;
  return /vinmonopolet\.no/i.test(url);
}

// v42: hook som konverterer ekstern bilde-URL til base64 data-URI.
// Bakgrunn: I v41 ble bilder embedd som <image href="..."> inni en SVG
// som ble brukt som CSS background-image via data:image/svg+xml. Når
// referansen er en ekstern URL (f.eks. cdn.pixabay.com), tolker browseren
// SVG-data-URI-en som en "ikke-samme-origin"-kontekst — eksterne bilder
// trenger da CORS-headers for å bli rendret inni SVG'en. Pixabay/de fleste
// CDN-er setter ikke disse for SVG-embedding → "ødelagt bilde"-ikon.
//
// Fix: forhåndshent bildet via fetch() (egen request, kan bruke CORS) og
// konverter til en data:-URI. Embed deretter data-URI'en i SVG'en — siden
// data:-URI'er ikke har en eksternt origin, gjelder ingen CORS-sjekk.
//
// Fallback: hvis fetch feiler (CORS blokkerer også selve fetch'en), settes
// `error: true`. Caller faller da tilbake til vanlig CSS-repeat (som ikke
// krever CORS for backgrounds).
//
// Data:-URI'er hopper rett gjennom og brukes som de er.
function useResolvedPatternUrl(url) {
  const [state, setState] = useState({ resolved:null, error:false, loading:false });
  useEffect(() => {
    if (!url) { setState({ resolved:null, error:false, loading:false }); return; }
    if (url.startsWith("data:")) { setState({ resolved:url, error:false, loading:false }); return; }
    let cancelled = false;
    setState({ resolved:null, error:false, loading:true });
    fetch(url, { mode:"cors" })
      .then(r => { if (!r.ok) throw new Error("HTTP "+r.status); return r.blob(); })
      .then(blob => new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(blob);
      }))
      .then(dataUri => { if (!cancelled) setState({ resolved:dataUri, error:false, loading:false }); })
      .catch(() => { if (!cancelled) setState({ resolved:null, error:true, loading:false }); });
    return () => { cancelled = true; };
  }, [url]);
  return state;
}

// v41: deterministisk PRNG (mulberry32) for stabil random plassering av
// bakgrunnsmønster. Samme seed gir samme sekvens på tvers av klienter slik
// at alle spillere ser identisk mønster.
function vlMulberry32(seed) {
  let s = seed >>> 0;
  return function() {
    s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// v41: bygger en SVG "super-tile" der `count` kopier av pattern-bildet
// plasseres med deterministisk jitter. Tile-en repeteres som CSS-background
// over hele spiller-vyen — innenfor hver tile er plasseringen tilfeldig,
// men tilen som helhet er deterministisk og gjentar seg ved repeat.
function generatePatternSVG({ url, imageSize, randomness, seed, tileSize = 600 }) {
  if (!url || !randomness) return null;
  const jitter = Math.min(100, Math.max(0, randomness)) / 100;
  // Antall kopier per tile: tilpasses pattern-størrelsen så tile-en ikke
  // blir for tynn eller overfylt. ~(tileSize/imageSize)^2 ganger ~1.2.
  const baseCount = Math.round(Math.pow(tileSize / Math.max(imageSize, 20), 2) * 1.2);
  const count = Math.max(4, Math.min(80, baseCount));
  const rand = vlMulberry32(seed);
  const cellsPerRow = Math.max(2, Math.ceil(Math.sqrt(count)));
  const cellSize = tileSize / cellsPerRow;
  let imgs = "";
  for (let i = 0; i < count; i++) {
    const col = i % cellsPerRow;
    const row = Math.floor(i / cellsPerRow);
    const baseX = col * cellSize + (cellSize - imageSize) / 2;
    const baseY = row * cellSize + (cellSize - imageSize) / 2;
    const jx = (rand() - 0.5) * cellSize * jitter * 1.4;
    const jy = (rand() - 0.5) * cellSize * jitter * 1.4;
    const rot = (rand() - 0.5) * 60 * jitter; // ±30° ved full random
    const cx = baseX + jx + imageSize / 2;
    const cy = baseY + jy + imageSize / 2;
    imgs += `<image href="${url}" x="${(baseX + jx).toFixed(1)}" y="${(baseY + jy).toFixed(1)}" width="${imageSize}" height="${imageSize}" transform="rotate(${rot.toFixed(1)} ${cx.toFixed(1)} ${cy.toFixed(1)})" preserveAspectRatio="xMidYMid meet"/>`;
  }
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${tileSize}" height="${tileSize}" viewBox="0 0 ${tileSize} ${tileSize}">${imgs}</svg>`;
  return { dataUri: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`, tileSize };
}

// v39: Custom font-velger for PC-prototypens Utseende-tab. Vises som knapp
// med valgt font-preview, popup rendret via createPortal (unngår overflow-
// klipping), hver option vist i sin egen font slik at brukeren ser hvordan
// skriften ser ut før de velger. Behold samme value-format som native select
// (font-family-string) så Firestore-data er uendret.
function FontPicker({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const btnRef = useRef(null);
  useEffect(()=>{
    if (!open) return;
    const onDoc = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      const popup = document.getElementById("vl-fontpicker-popup");
      if (popup && popup.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return ()=>document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const toggle = () => {
    if (open) { setOpen(false); return; }
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const POPUP_W = Math.max(rect.width, 320);
    const POPUP_H = 380;
    let top = rect.bottom + 4;
    if (top + POPUP_H > window.innerHeight - 8) top = Math.max(8, rect.top - POPUP_H - 4);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - POPUP_W - 8));
    setAnchor({ top, left, width: POPUP_W });
    setOpen(true);
  };
  const selected = options.find(o => o.value === value) || options[0];
  // Label uten "— BESKRIVELSE"-delen for kompakt visning på knappen.
  const cleanLabel = (lbl) => lbl.split("—")[0].trim();
  return (
    <>
      <button ref={btnRef} type="button" onClick={toggle}
        style={{
          ...S.input, marginBottom:0, padding:"6px 10px",
          background:"#1a1a1a", color:"#f5efe0", border:"1px solid #444",
          borderRadius:4, cursor:"pointer", textAlign:"left",
          display:"flex", alignItems:"center", justifyContent:"space-between",
          fontFamily: selected.value, fontSize:14,
        }}>
        <span>{cleanLabel(selected.label)}</span>
        <span style={{ color:"#888", fontSize:10, fontFamily:"sans-serif" }}>▼</span>
      </button>
      {open && anchor && createPortal(
        <div id="vl-fontpicker-popup" style={{
          position:"fixed", top:anchor.top, left:anchor.left, width:anchor.width,
          maxHeight:480, overflowY:"auto",
          background:"#1a1a1a", border:"1px solid #444", borderRadius:6,
          padding:6, zIndex:99999,
          boxShadow:"0 8px 24px rgba(0,0,0,0.65)",
        }}>
          {(()=>{
            // v40: grupperte per kategori. Render en header per gruppe.
            const out = [];
            let prevCat = null;
            options.forEach(opt => {
              if (opt.category && opt.category !== prevCat) {
                out.push(
                  <div key={"hdr-"+opt.category} style={{
                    padding:"8px 10px 4px", fontSize:9, color:"#666",
                    textTransform:"uppercase", letterSpacing:"0.12em",
                    fontFamily:"'Inter',sans-serif", fontWeight:600,
                    borderTop: prevCat ? "1px solid #2a2a2a" : "none",
                    marginTop: prevCat ? 4 : 0,
                  }}>{opt.category}</div>
                );
                prevCat = opt.category;
              }
              const active = opt.value === value;
              out.push(
                <div key={opt.value}
                  onClick={()=>{ onChange(opt.value); setOpen(false); }}
                  style={{
                    padding:"8px 12px", cursor:"pointer", borderRadius:4,
                    background: active ? "#2a1f0a" : "transparent",
                    border: active ? "1px solid #C9A84C" : "1px solid transparent",
                    marginBottom:2, color: active ? "#C9A84C" : "#f5efe0",
                    fontFamily: opt.value, fontSize:16, lineHeight:1.2,
                    display:"flex", alignItems:"center", justifyContent:"space-between",
                  }}
                  onMouseEnter={e=>{ if(!active) e.currentTarget.style.background = "#222"; }}
                  onMouseLeave={e=>{ if(!active) e.currentTarget.style.background = "transparent"; }}>
                  <span>{cleanLabel(opt.label)}</span>
                  {active && <span style={{ fontSize:12, fontFamily:"sans-serif" }}>✓</span>}
                </div>
              );
            });
            return out;
          })()}
        </div>,
        document.body
      )}
    </>
  );
}

// v35: Delt dato/tid-velger-kort for PC-prototypens dropdowns (Trekning +
// Loddsalg). Bruker react-datepicker via `portalId` slik at popupen rendres
// til document.body — utenfor overflow:hidden-grensen på drop-menyen som
// ellers klipper kalenderen. OK-knappen inni popupen lagrer til Firestore
// og lukker popupen; "✕ Fjern"-pillen nullstiller.
function PCDateTimePickerCard({
  label, value, onChange, storedValue, onActivate, onRemove,
  fmt, autoLabel,
}) {
  // v152: native datetime-local (fritt klokkeslett) i stedet for react-
  // datepicker med kvarter-slots. value/onChange er samme "YYYY-MM-DDTHH:mm"-
  // streng som før — ingen datatype-endring. pickerRef-prop ignoreres nå.
  return (
    <div style={{ padding:"10px 14px", borderBottom:"1px solid #2a2a2a" }} className="vl-datepicker-host">
      <label style={{ fontSize:9, color:"#ccc", textTransform:"uppercase", letterSpacing:"0.08em", display:"block", marginBottom:4 }}>{label}</label>
      <input
        type="datetime-local"
        value={value || ""}
        min={new Date(Date.now()+60000).toISOString().slice(0,16)}
        onChange={e => onChange(e.target.value)}
        style={{
          ...S.input, marginBottom:4, padding:"6px 8px", fontSize:11,
          colorScheme:"dark", accentColor:"#C9A84C",
          border:"1px solid #444", width:"100%", boxSizing:"border-box",
          background:"#1a1a1a", color:"#f5efe0", cursor:"pointer",
        }}/>
      {value && value !== (storedValue || "") && (()=>{
        const isPast = new Date(value) <= new Date();
        return isPast
          ? <p style={{ margin:"0 0 4px", fontSize:10, color:"#e57373" }}>⚠️ Tidspunktet er passert — velg fremtidig tid</p>
          : <button
              onClick={async()=>{
                if (new Date(value) <= new Date()) { showErrorToast("Velg et tidspunkt i fremtiden"); return; }
                try {
                  // v36: onActivate kan returnere false (validering feilet på
                  // callsite, egen toast vist) — da gjør vi ingenting mer.
                  const result = await onActivate(value);
                  if (result === false) return;
                } catch(err) {
                  showErrorToast("Kunne ikke lagre tidspunkt.");
                }
              }}
              style={{
                width:"100%", padding:"6px 10px", marginBottom:4,
                background:"#0d2a0d", border:"1px solid #1a4a1a", color:"#4caf50",
                borderRadius:3, cursor:"pointer", fontSize:11, fontWeight:"bold",
              }}>✓ Aktiver tidspunkt</button>;
      })()}
      {value && value === (storedValue || "") && new Date(value) <= new Date() && (
        <p style={{ margin:"4px 0 0", fontSize:10, color:"#e57373" }}>⚠️ Tidspunktet er passert</p>
      )}
      {storedValue && (
        <p style={{ margin:"4px 0 0", fontSize:10, color:"#4caf50" }}>
          ✓ Satt: {fmt(storedValue)}
          {autoLabel}
          <span className="vl-remove-drawtime"
            style={{
              marginLeft:10, color:"#e57373", cursor:"pointer", fontSize:11,
              fontWeight:"bold", padding:"2px 6px", borderRadius:3,
              border:"1px solid #5a1a1a", background:"rgba(90,26,26,0.15)",
            }}
            onClick={onRemove}>✕ Fjern</span>
        </p>
      )}
    </div>
  );
}

// v316: Vipps-sjekk-modal — vises rett etter avlysning. Lister ubetalte
// kjøpere (gruppert per e-post); arrangøren haker av hvem som har betalt på
// Vipps og trykker «Godkjenn valgte». Hver avhaket e-post sendes til den
// ferdige, idempotente callable godkjennBetaling (kreditt = amount −
// kredittBrukt). Ren snarvei oppå fase 3 — ingen ny pengelogikk.
function VippsSjekkModal({ weekId, kjopere, onClose }) {
  const [valgt,setValgt]=useState({});
  const [busy,setBusy]=useState(false);
  const toggle = (email) => setValgt(v=>({ ...v,[email]:!v[email] }));
  const valgteEmails = kjopere.filter(k=>valgt[k.email]).map(k=>k.email);
  const sumValgt = kjopere.filter(k=>valgt[k.email]).reduce((s,k)=>s+k.tilgode,0);
  const handleGodkjenn = async () => {
    setBusy(true);
    try {
      const fn = httpsCallable(getFunctions(app,"europe-west1"),"godkjennBetaling");
      let antall=0, totalKr=0, hoppet=0, feilet=0;
      for (const email of valgteEmails) {
        try {
          const res = await fn({ weekId, email });
          antall++; totalKr += res.data?.kreditert ?? 0;
        } catch(e) {
          if ((e?.message||"").includes("ingen-ubetalte-rader")) { hoppet++; }
          else { feilet++; console.error("godkjennBetaling (vipps-sjekk) feilet for", email, e); }
        }
      }
      const hoppTekst = hoppet>0 ? `\n${hoppet} var allerede godkjent.` : "";
      const feilTekst = feilet>0 ? `\n${feilet} feilet — se konsoll.` : "";
      await showAlert(`✅ Godkjent ${antall} person(er), totalt ${totalKr} kr i tilgode.${hoppTekst}${feilTekst}`);
      onClose();
    } finally { setBusy(false); }
  };
  return createPortal(
    <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:99999,display:"flex",alignItems:"center",justifyContent:"center",padding:20,boxSizing:"border-box",fontFamily:"Inter,system-ui,sans-serif" }}
      onClick={e=>{ if(e.target===e.currentTarget && !busy) onClose(); }}>
      <div style={{ background:"#1a1a1a",border:"1px solid #444",borderRadius:8,padding:24,maxWidth:460,width:"100%",maxHeight:"85vh",display:"flex",flexDirection:"column",boxShadow:"0 8px 40px rgba(0,0,0,0.8)" }}>
        <p style={{ margin:"0 0 6px",fontSize:16,fontWeight:700,color:"#f5efe0" }}>Sjekk Vipps-betalinger</p>
        <p style={{ margin:"0 0 16px",fontSize:13,color:"#aaa",lineHeight:1.5 }}>Hak av hvem som har betalt på Vipps. De får resten i tilgode (beløp − evt. brukt tilgode).</p>
        <div style={{ overflowY:"auto",flex:1,marginBottom:16 }}>
          {kjopere.map(k=>(
            <label key={k.email} style={{ display:"flex",alignItems:"center",gap:10,padding:"10px 8px",borderBottom:"1px solid #2a2a2a",cursor:busy?"default":"pointer" }}>
              <input type="checkbox" checked={!!valgt[k.email]} disabled={busy} onChange={()=>toggle(k.email)}
                style={{ width:18,height:18,accentColor:"#C9A84C",cursor:busy?"default":"pointer",flexShrink:0 }} />
              <span style={{ flex:1,minWidth:0 }}>
                <span style={{ display:"block",fontSize:13,color:"#f5efe0",fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{k.navn}</span>
                <span style={{ display:"block",fontSize:11,color:"#888",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{k.email} · {k.antallLodd} lodd</span>
              </span>
              <span style={{ fontSize:13,color:"#4caf50",fontWeight:700,whiteSpace:"nowrap" }}>+{k.tilgode},-</span>
            </label>
          ))}
        </div>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",gap:12 }}>
          <span style={{ fontSize:12,color:"#aaa" }}>{valgteEmails.length} valgt · {sumValgt},-</span>
          <div style={{ display:"flex",gap:8 }}>
            <button onClick={onClose} disabled={busy}
              style={{ padding:"8px 16px",borderRadius:5,border:"1px solid #444",background:"#2a2a2a",color:"#ccc",cursor:busy?"default":"pointer",fontSize:13 }}>Lukk</button>
            <button onClick={handleGodkjenn} disabled={busy||valgteEmails.length===0}
              style={{ padding:"8px 16px",borderRadius:5,border:"none",background:(busy||valgteEmails.length===0)?"#3a3a2a":"#C9A84C",color:(busy||valgteEmails.length===0)?"#777":"#000",cursor:(busy||valgteEmails.length===0)?"default":"pointer",fontSize:13,fontWeight:"bold" }}>
              {busy ? "Godkjenner…" : "Godkjenn valgte"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
function OrganizerView({ user, onLogout, onStartDraw, lotteryState, settings, onShowWinners, pcDashboard = false, onSwitchAdminView }) {
  const [participants,setParticipants]=useState([]);
  const [prizes,setPrizes]=useState([]);
  const [tab,setTab]=useState("deltakere");
  const [startDrawLoading,setStartDrawLoading]=useState(false); // v199: server-trekning-loader (Vei C feature-flag)
  // v50 FIX 3: Innstillinger-undertab løftet hit slik at PC-dashboardet kan
  // rendre undermenyene inline i hovedtab-raden. Sendes ned til SettingsPanel.
  const [settingsTab,setSettingsTab]=useState("lotteri");
  useEffect(()=>{
    const handler = e => setTab(e.detail);
    window.addEventListener("vl-open-tab", handler);
    return ()=>window.removeEventListener("vl-open-tab", handler);
  },[]);
  // v162: åpne ticker-bottom-sheet når 📢-knappen i App-root-topbaren trykkes.
  useEffect(()=>{
    const handler = () => setShowTickerSheet(true);
    window.addEventListener("vl-open-ticker", handler);
    return ()=>window.removeEventListener("vl-open-ticker", handler);
  },[]);
  // v106: sessionKicked/pendingSessionDecision-state fjernet — auto-claim
  // og kick-håndtering ligger nå i App-root (gated på isAllowed), så
  // mobilen kastes ut uavhengig av om OrganizerView er mounted.
  // Auto-kontroller (synkronisert fra lotteryState)
  const [drawTime,setDrawTime]=useState(lotteryState?.drawTime||"");
  const [saleOpenTime,setSaleOpenTime]=useState(lotteryState?.saleOpenTime||"");
  const [autoDrawEnabled,setAutoDrawEnabled]=useState(lotteryState?.autoDraw||false);
  const [showDraw,setShowDraw]=useState(false);
  const [showSale,setShowSale]=useState(false);
  const [showTickerSheet,setShowTickerSheet]=useState(false); // v162: bottom-sheet for ticker-redigering
  // v316: Vipps-sjekk-modal etter avlysning. null = lukket; ellers
  // { weekId, kjopere:[{ email, navn, antallLodd, tilgode }] }.
  const [vippsSjekk,setVippsSjekk]=useState(null);
  const dropdownRef = useRef(null);
  // v34/v35: refs for react-datepicker så OK-knappene kan lukke popupene
  // programmatisk via .setOpen(false). Brukes kun i PC-prototypen.
  const drawTimePickerRef = useRef(null);
  const saleTimePickerRef = useRef(null);
  useEffect(()=>{
    const handler = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        // v35: ikke lukk dropdown hvis klikket var inni en datepicker-popup
        // (rendret via portal til document.body, så ikke under dropdown-ref).
        if (typeof e.target.closest === "function" &&
            (e.target.closest('.react-datepicker') ||
             e.target.closest('[id^="vl-datepicker-portal"]'))) {
          return;
        }
        setShowDraw(false); setShowSale(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return ()=>document.removeEventListener("mousedown", handler);
  },[]);
  // Ukentlig åpning (fra settings)

  const status = lotteryState?.status;
  const drawDone = status==="done";
  const isDrawing = status==="drawing";

  // Synkroniser fra lotteryState og settings når de endres
  useEffect(()=>{
    if(lotteryState?.drawTime !== undefined) setDrawTime(lotteryState.drawTime||"");
    if(lotteryState?.saleOpenTime !== undefined) setSaleOpenTime(lotteryState.saleOpenTime||"");
    if(lotteryState?.autoDraw !== undefined) setAutoDrawEnabled(!!lotteryState.autoDraw);
  },[lotteryState?.drawTime, lotteryState?.saleOpenTime, lotteryState?.autoDraw]);

  // v319: prizes trengs ALLTID (ikke bare når Premier-fanen har vært åpnet)
  // for autotrekning-uten-premier-varslene under. Liten collection, billig lytter.
  useEffect(()=>{
    const unsub=onSnapshot(collection(db,"prizes"),snap=>{ setPrizes(snap.docs.map(d=>({ id:d.id,...d.data() })).sort((a,b)=>(a.createdAt?.seconds||0)-(b.createdAt?.seconds||0))); });
    return unsub;
  },[]);

  // v319: varsler for autotrekning uten premier. FØR: autoDraw+drawTime satt
  // men 0 premier valgt (medITrekning-filteret, samme som v301-sperren).
  // ETTER: server-autoDraw skrev autoDrawSkipped (utsatt/avlyst med grunn).
  // Rendres i BÅDE PC- og mobil-grenen via renderPremieVarsler.
  const fmtVarsel = d => { try { return new Date(d).toLocaleString("nb-NO",{ weekday:"long", day:"numeric", month:"long", hour:"2-digit", minute:"2-digit" }); } catch(_) { return String(d); } };
  const antallValgtePremier = prizes.filter(p=>p.medITrekning !== false).length;
  const visPremieForvarsel = status==="idle" && !!lotteryState?.autoDraw &&
    !!lotteryState?.drawTime && antallValgtePremier === 0;
  const autoDrawSkipped = lotteryState?.autoDrawSkipped;
  // v321: autotrekning kan ikke AKTIVERES uten valgte premier — hovedtilfellet
  // hindres ved kilden. Kanten "auto satt, så fjernes siste premie" fanges
  // fortsatt av v319/v320-sikkerhetsnettet. Fersk lesing (v301-mønsteret) så
  // sperren ikke feiler på nyss-mountet/stale prizes-state.
  // v322: samme sperre på AKTIVERING av trekningstidspunkt — drawTime alene
  // (uten autoDraw) starter nedtelling hos spillerne, og serveren rører aldri
  // runder med autoDraw av, så et passert tidspunkt blir liggende for alltid
  // og gir spiller "avsluttet" / arrangør "aktivt".
  const tellValgtePremierFersk = async () => {
    try {
      const snap = await getDocs(collection(db,"prizes"));
      return snap.docs.filter(d=>d.data().medITrekning !== false).length;
    } catch(_) { return null; /* nettverksfeil — ikke blokkér på usikker data */ }
  };
  const kanAktivereAutoDraw = async () => {
    if (await tellValgtePremierFersk() === 0) {
      showErrorToast("Du må velge minst én premie før du kan sette autotrekning.");
      return false;
    }
    return true;
  };
  const kanSetteDrawTime = async () => {
    if (await tellValgtePremierFersk() === 0) {
      showErrorToast("Du må velge minst én premie før du kan sette trekningstidspunkt.");
      return false;
    }
    return true;
  };
  const renderPremieVarsler = (pc) => {
    // v325 (minLodd steg c): forvarsel ved lavt salg — autotrekning er på,
    // tidspunkt er satt, premier finnes, men solgte LODD (ikke deltakere) er
    // under settings.minLodd. Serveren vil da avlyse med tilgode ved trekning.
    // Beregnes her inne (kalles ved render) — participants er live onSnapshot.
    const minLoddGrense = parseInt(settings?.minLodd, 10) || 0;
    const soldeLodd = participants.reduce((s,p)=>s+(p.tickets||0),0);
    const visLavtSalgForvarsel = status==="idle" && !!lotteryState?.autoDraw &&
      !!lotteryState?.drawTime && antallValgtePremier > 0 &&
      minLoddGrense > 0 && soldeLodd < minLoddGrense;
    if (!visPremieForvarsel && !autoDrawSkipped && !visLavtSalgForvarsel) return null;
    return (
      <div style={{ display:"flex", flexDirection:"column", gap:8, margin: pc ? "14px 32px 0" : "12px 20px 8px" }}>
        {visPremieForvarsel && (
          <div style={{ background:"#2a1f0a", border:"1px solid #C9A84C", borderRadius:6, padding:"10px 14px" }}>
            <p style={{ margin:0, fontSize:13, color:"#C9A84C", fontWeight:"bold" }}>
              ⚠️ Autotrekning {fmtVarsel(lotteryState.drawTime)} — ingen premier valgt!
            </p>
            <p style={{ margin:"4px 0 0", fontSize:12, color:"#d8c48a" }}>
              Trekningen blir utsatt hvis du ikke legger til premier før den tid.
            </p>
          </div>
        )}
        {visLavtSalgForvarsel && (
          <div style={{ background:"#2a1f0a", border:"1px solid #C9A84C", borderRadius:6, padding:"10px 14px" }}>
            <p style={{ margin:0, fontSize:13, color:"#C9A84C", fontWeight:"bold" }}>
              ⚠️ Autotrekning {fmtVarsel(lotteryState.drawTime)} — bare {soldeLodd} av minst {minLoddGrense} lodd solgt.
            </p>
            <p style={{ margin:"4px 0 0", fontSize:12, color:"#d8c48a" }}>
              Lotteriet avlyses automatisk med tilgode hvis det er solgt færre enn {minLoddGrense} lodd ved trekningstidspunktet.
            </p>
          </div>
        )}
        {autoDrawSkipped && (
          <div style={{ background:"#1a0d00", border:"1px solid #8B4500", borderRadius:6, padding:"10px 14px" }}>
            <p style={{ margin:0, fontSize:13, color:"#e5a44c", fontWeight:"bold" }}>
              {autoDrawSkipped.utsattTil
                ? `⏭ Autotrekningen ${autoDrawSkipped.at?fmtVarsel(autoDrawSkipped.at):""} ble utsatt til ${fmtVarsel(autoDrawSkipped.utsattTil)}`
                : `⛔ Autotrekningen ${autoDrawSkipped.at?fmtVarsel(autoDrawSkipped.at):""} ble avlyst`}
              {autoDrawSkipped.reason === "ingen-premier"
                ? " fordi ingen premier var valgt."
                : autoDrawSkipped.reason === "for-faa-lodd"
                  ? " fordi for få lodd var solgt. Deltakerne har fått tilgodehavende."
                  : " fordi ingen hadde kjøpt lodd."}
            </p>
          </div>
        )}
      </div>
    );
  };

  // v106: hele adminSession-mekanismen (claim, dialog, kick-lytter) er flyttet
  // til App-root så den fungerer uavhengig av om OrganizerView er mounted.
  // Se claimAdminSessionForEmail (modul-toppen) + kick-effekten i App.

  useEffect(()=>{
    const unsub=onSnapshot(collection(db,"participants"),snap=>{ setParticipants(snap.docs.map(d=>({ id:d.id,...d.data() }))); });
    return unsub;
  },[]);

  const totalTickets=participants.reduce((s,p)=>s+(p.tickets||0),0);
  const paidCount=participants.filter(p=>p.paid).length;
  const revenue=participants.filter(p=>p.paid).reduce((s,p)=>s+(p.amount||0),0);
  const cRevenue=useCountUp(revenue); const cTickets=useCountUp(totalTickets);

  const startDraw=async()=>{
    try {
      const freshSnap = await getDoc(doc(db,"lottery","current"));
      const freshStatus = freshSnap.exists() ? freshSnap.data().status : "idle";
      if(freshStatus==="done"){showAlert("⛔ Trekning er allerede gjennomført. Start nytt loddsalg for å kjøre ny trekning.");return;}
      if(freshStatus==="drawing"){showAlert("⛔ Trekning pågår allerede.");return;}
      if(participants.length===0){showAlert("⛔ Ingen deltakere! Kan ikke starte trekning uten deltakere.");return;}
      // v169: advar hvis færre lodd enn premier — trekningen stopper når loddene
      // er brukt opp (ett lodd kan ikke vinne flere ganger). Henter premie-antall
      // friskt siden OrganizerView.prizes kan være tom om Premier-fanen ikke er åpnet.
      try {
        const prizSnap = await getDocs(collection(db,"prizes"));
        // v301 (premie-bibliotek steg 3): 0-valgte-sperre. Tell premier som er
        // MED (medITrekning !== false, samme invariant som functions/PrizePanel/
        // ParticipantView). Serveren avviser også 0 valgte (steg 1), men her
        // hindrer vi at arrangøren i det hele tatt prøver — med tydelig melding.
        const antallPremier = prizSnap.docs.filter(d=>d.data().medITrekning !== false).length;
        if(antallPremier === 0){
          showAlert("⛔ Ingen premier valgt til trekning!\nVelg minst én premie i Premier-fanen før du starter trekningen.");
          return;
        }
        if(totalTickets > 0 && totalTickets < antallPremier){
          const z = antallPremier - totalTickets;
          const ok = await showConfirm(`Bare ${totalTickets} lodd solgt, men ${antallPremier} premier i potten.\n\nTrekningen vil stoppe etter ${totalTickets} ${totalTickets===1?"premie":"premier"} (${z} ${z===1?"premie":"premier"} blir ikke trukket).\n\nFortsette?`);
          if(!ok) return;
        }
      } catch(_) { /* nettverksfeil — la trekningen fortsette */ }
      const drawTime=freshSnap.exists()?freshSnap.data().drawTime:null;
      if(drawTime){
        const drawDate=new Date(drawTime);
        if(new Date()<drawDate){
          const formatted=drawDate.toLocaleString("nb-NO",{ weekday:"long",day:"numeric",month:"long",hour:"2-digit",minute:"2-digit" });
          const ok=await showConfirm(`⚠️ Trekningstidspunktet er ikke nådd ennå.\nPlanlagt: ${formatted}\n\nVil du starte trekning nå likevel?`);
          if(!ok) return;
        }
      } else {
        const uniqueCount = new Set(participants.map(p=>(p.email||p.name||p.id).toLowerCase())).size;
        const ok=await showConfirm(`Starte trekning nå?\n\n${uniqueCount} deltakere · ${totalTickets} lodd`);
        if(!ok) return;
      }
      // v204 (Vei C fase 2): manuell trekning kjører ALLTID via server
      // (startDrawNow → utforTrekning → drawnPrizes → LiveDrawView → server-Resend).
      // Klient-fallback-motoren (DrawScreen) er fjernet. Confirm-dialogene over gjelder.
      setStartDrawLoading(true);
      try {
        getAudioCtx(); // lås opp lyd mens vi venter på server (krever bruker-gest)
        const startDrawNow = httpsCallable(getFunctions(app, "europe-west1"), "startDrawNow");
        await startDrawNow();
        // Server skrev status:"drawing" + drawnPrizes → onSnapshot → App ruter til LiveDrawView.
      } catch (err) {
        console.error("startDrawNow feilet:", err);
        showErrorToast(`Trekning feilet: ${err?.message || "ukjent feil"}`);
      } finally {
        setStartDrawLoading(false);
      }
    } catch(e) {
      console.error("Feil ved start trekning:", e);
      showErrorToast("Kunne ikke starte trekning. Sjekk internett og prøv igjen.");
    }
  };

  // Avlys lotteri: refunder alle og loggfør i regnskap.
  // v120: auto-email til hver kreditt-mottaker + suksess-alert + lastClosedByAvlys-flagg.
  // PENGELOGIKK-DUPLIKAT (v285): speiler server-side utforAvlysning
  // (functions/index.js) — endres samlet i steg d (samle til én pengevei).
  // Endre ALDRI den ene uten den andre.
  const avlysLotteri = async () => {
    const harDeltakere = participants.length > 0;
    // v285: refusjons-berettigede = betalte (hele beløpet) ELLER ubetalte som
    // brukte tilgode ved kjøpet (kredittBrukt ble trukket i kjøps-transaksjonen
    // og må tilbake — ellers er det reelt pengetap).
    const refundParticipants = participants.filter(p=>p.paid || (p.kredittBrukt||0) > 0);
    const harRefund = refundParticipants.length > 0;
    const msg = harDeltakere
      ? `⛔ Avlys lotteriet?\n\nAlle deltakere slettes.${harRefund?`\n\n${refundParticipants.length} deltaker(e) vil få tilgodehavende registrert (betalte lodd refunderes fullt; for ubetalte refunderes kun brukt tilgode).`:""}\n\nDette kan ikke angres.`
      : "Steng loddsalget?\n\nIngen deltakere — salget stenges og kjøp-vinduet skjules for deltakere.";
    if (!await showConfirm(msg)) return;
    const partSnap = await getDocs(collection(db,"participants"));
    const partData = partSnap.docs.map(d=>d.data());
    // v285: refusjon per deltaker. paid=true → hele amount (inkluderer evt.
    // kredittBrukt — amount er full pris, så IKKE legg kredittBrukt oppå).
    // paid=false → kun kredittBrukt (Vipps-delen er muligens aldri betalt).
    const refundFor = (p) => p.paid ? (p.amount||0) : Math.max(0, p.kredittBrukt||0);
    const paidAmount = partData.filter(p=>p.paid).reduce((s,p)=>s+(p.amount||0),0);
    const kredittRefund = partData.filter(p=>!p.paid).reduce((s,p)=>s+Math.max(0,p.kredittBrukt||0),0);
    const totalRefund = paidAmount + kredittRefund;
    // Behold weekLabel før reset så avlysnings-email kan referere riktig runde
    const weekLabelBeforeReset = lotteryState?.weekLabel || "Ukjent uke";
    try {
      // v285: registrer tilgodehavende per e-post — betalte får hele beløpet,
      // ubetalte med brukt tilgode får kredittBrukt-delen tilbake. Samme
      // e-post med flere kjøp summeres (refundFor per kjøp, aldri dobbelt).
      const refundByEmail = {};
      partData.filter(p=>p.email && refundFor(p) > 0).forEach(p=>{
        const key=p.email.trim().toLowerCase();
        if(!refundByEmail[key]) refundByEmail[key]={ name:p.name, email:key, amount:0 };
        refundByEmail[key].amount += refundFor(p);
      });
      for(const [email,data] of Object.entries(refundByEmail)){
        const kredSnap = await getDoc(doc(db,"credits",email));
        const existing = kredSnap.exists() ? (kredSnap.data().amount||0) : 0;
        const nySaldo = existing+data.amount;
        await setDoc(doc(db,"credits",email),{
          name:data.name, email, amount:nySaldo,
          lastUpdated:serverTimestamp()
        });
        // v252: transaksjonslogg "avlysning" — best effort.
        try {
          await addDoc(collection(db,"credits",email,"transactions"),{
            type:"avlysning", beløp:data.amount, saldoEtter:nySaldo,
            dato:serverTimestamp(), ref:weekLabelBeforeReset
          });
        } catch(txErr) { /* logg-feil ignoreres */ }
      }
      let histRef = null; // v316: fang history-id for Vipps-sjekk-modalen
      if(harDeltakere && partData.length>0){
        histRef = await addDoc(collection(db,"history"),{
          weekLabel:weekLabelBeforeReset+" (AVLYST)",
          winners:[], participants:partData, avlyst:true, refundert:totalRefund, // v285: inkl. kreditt-refusjon til ubetalte
          createdAt:serverTimestamp()
        });
      }
      // v285: regnskaps-korreksjonen gjelder fortsatt KUN paidAmount.
      // kredittRefund skal IKKE trekkes: den kreditten ble korrigert (-)
      // da den opprinnelig ble utstedt, og forbruket i det avlyste kjøpet
      // bokførte aldri inntekt (paid=false). Re-utstedelsen gjenoppretter
      // bare den eksisterende forpliktelsen — ny korreksjon ville doblet den.
      if(paidAmount>0){
        const regSnap=await getDoc(doc(db,"settings","regnskap"));
        const ex=regSnap.exists()?regSnap.data():{ totalInntekt:0,totalUtgift:0,manuellKorreksjon:0 };
        await setDoc(doc(db,"settings","regnskap"),{ ...ex, manuellKorreksjon:(ex.manuellKorreksjon||0)-paidAmount },{ merge:true });
      }
      // v207: auto-email per kreditt-mottaker via server-Resend (sendReminderEmails,
      // type "cancellation"). Klient-EmailJS-løkka fjernet. Behold avlys-toggelen
      // (emailjsAutoSendAvlys) klient-sjekket, så arrangør-alertene under stemmer.
      let emailOk = 0, emailFail = 0, emailAttempted = false;
      const emailRecipients = Object.values(refundByEmail);
      if (emailRecipients.length > 0) {
        const cfgSnap = await getDoc(doc(db,"settings","config"));
        const cfg = cfgSnap.exists() ? cfgSnap.data() : {};
        const autoSend = (cfg.emailjsAutoSendAvlys ?? cfg.emailjsAutoSend) !== false;
        if (!autoSend) {
          console.info("[avlys-email] auto-send avlys er av — hopper over");
        } else {
          emailAttempted = true;
          try {
            const fn = httpsCallable(getFunctions(app, "europe-west1"), "sendReminderEmails");
            const res = await fn({
              type: "cancellation",
              recipients: emailRecipients.map(r => ({ email: r.email, name: capName(r.name), amount: r.amount })),
              weekLabel: weekLabelBeforeReset,
            });
            emailOk = res.data?.sent || 0;
            emailFail = res.data?.failed || 0;
          } catch (err) {
            console.error("sendReminderEmails (cancellation) feilet:", err);
            emailFail = emailRecipients.length;
          }
        }
      }
      const BATCH_SIZE = 20;
      for (let i = 0; i < partSnap.docs.length; i += BATCH_SIZE) {
        await Promise.all(partSnap.docs.slice(i, i + BATCH_SIZE).map(d=>deleteDoc(d.ref)));
      }
      const now=new Date();
      await updateDoc(doc(db,"lottery","current"),{
        status:"idle", salgStengt:true, lastClosedByAvlys:true,
        winner:null, winnerHistory:[], winnerEmailSent:false,
        winnerEmailServerStats:deleteField(), // v287: ikke vis forrige rundes e-post-status
        weekLabel:`Uke ${getWeekNumber(now)} ${now.getFullYear()}`,
        startedAt:null, drawTime:null, autoDraw:false, currentPrizeIdx:0, currentWinnerName:null,
        nextTicketNumber:deleteField(),
        // v181: rydd server-trekning-feltene ved avlys.
        drawnPrizes:deleteField(), drawStartedAt:deleteField(), premieVarighetMs:deleteField(), drawTotalMs:deleteField(), drawSource:deleteField(), // v192/v198
        autoDrawSkipped:deleteField(), // v319: rydd utsatt/avlyst-uten-premier-varselet
        avlysReason:deleteField(), // v325: manuell avlys skal vise generisk melding, ikke stale for-faa-lodd
      });
      await setDoc(doc(db,"counters","tickets"),{ next:1 });
      // v120 DEL B: arrangør-bekreftelse
      const recipientCount = emailRecipients.length;
      if (totalRefund === 0) {
        await showAlert("✅ Loddsalget er stengt.\nIngen tilgodehavende registrert.");
      } else if (emailAttempted) {
        const failText = emailFail > 0 ? ` (${emailFail} feilet)` : "";
        await showAlert(`✅ Avlysning fullført.\n${recipientCount} deltaker(e) fikk totalt ${totalRefund} kr i tilgodehavende.<br/>${emailOk} avlysnings-email(s) sendt${failText}.`);
      } else {
        await showAlert(`✅ Avlysning fullført.\n${recipientCount} deltaker(e) fikk totalt ${totalRefund} kr i tilgodehavende.\n(Auto-email er deaktivert — husk å varsle deltakerne manuelt.)`);
      }
      // v316: tilby Vipps-sjekk med en gang. Gruppér ubetalte kjøpere per
      // e-post; tilgode = amount − kredittBrukt (det godkjennBetaling vil gi).
      if (histRef) {
        const ubetaltByEmail = {};
        partData.filter(p=>!p.paid && p.email).forEach(p=>{
          const key=p.email.trim().toLowerCase();
          if(!ubetaltByEmail[key]) ubetaltByEmail[key]={ email:key, navn:p.name||key, antallLodd:0, tilgode:0 };
          ubetaltByEmail[key].antallLodd += (p.tickets||0);
          ubetaltByEmail[key].tilgode += Math.max(0,(p.amount||0)-(p.kredittBrukt||0));
        });
        const kjopere = Object.values(ubetaltByEmail);
        if (kjopere.length > 0) {
          const vil = await showConfirm("Sjekke Vipps-betalinger nå?\nHak av hvem som har betalt på Vipps, så får de resten i tilgode. (Kan også gjøres senere i Statistikk.)");
          if (vil) setVippsSjekk({ weekId: histRef.id, kjopere });
        }
      }
    } catch(e) {
      console.error("Feil ved avlys lotteri:", e);
      showErrorToast("Noe gikk galt ved avlysning. Prøv igjen.");
    }
  };

  // Sikker "start nytt": kun tillatt når status=done
  const startNewSale=async()=>{
    if(status==="drawing"){
      showAlert("⛔ Trekning pågår! Du kan ikke starte nytt loddsalg under en trekning.");
      return;
    }
    // v173: sjekk FAKTISKE Firestore-premier (frisk) i stedet for stale lokal
    // state. Den lokale prizes-staten fylles kun når Premier-fanen har vært
    // åpnet i økten, så den ga falsk "Ingen premier" selv når forrige rundes
    // premier lå i databasen. Samme mønster som startDraw (v169).
    const prizSnap = await getDocs(collection(db,"prizes"));
    const harPremier = prizSnap.size > 0;
    if(!harPremier){
      const ok=await showConfirm("⚠️ Ingen premier er lagt til ennå.\n\nGå til Premier-fanen og legg inn premier, eller start loddsalget uten (kjøpere vil se at premier ikke er publisert ennå).\n\nStart loddsalget uten premier?");
      if(!ok){
        // Åpne premier-fanen via en custom event
        window.dispatchEvent(new CustomEvent("vl-open-tab",{ detail:"premier" }));
        return;
      }
    }
    // Bekreftelsestekst basert på tilstand. v173: når premier finnes, opplys at
    // forrige rundes premier beholdes (slettes ikke ved nytt loddsalg).
    const premierNote = harPremier ? "\n\nForrige rundes premier beholdes — endre i Premier-fanen ved behov." : "";
    let confirmMsg;
    if(status==="done"){
      confirmMsg = "Trekning er fullført. Starte nytt loddsalg?\nVinnere lagres i historikk." + premierNote;
    } else if(participants.length>0){
      confirmMsg = `⚠️ Det er ${participants.length} deltakere som ikke har vært med i trekning.\n\nEr du sikker på at du vil slette disse og starte på nytt?` + premierNote;
    } else {
      confirmMsg = "Starte nytt loddsalg?" + premierNote;
    }
    const ok = await showConfirm(confirmMsg);
    if(!ok) return;

    try {
      const partSnap=await getDocs(collection(db,"participants"));
      const partData=partSnap.docs.map(d=>d.data());

      if(lotteryState?.winnerHistory?.length>0){
        await addDoc(collection(db,"history"),{
          weekLabel:lotteryState.weekLabel||"Ukjent uke",
          winners:lotteryState.winnerHistory,
          participants:partData,
          createdAt:serverTimestamp()
        });
      }

      const paidAmount=partData.filter(p=>p.paid).reduce((s,p)=>s+(p.amount||0),0);
      const prizesSnap=await getDocs(collection(db,"prizes"));
      const prizeAmount=prizesSnap.docs.reduce((s,d)=>s+(parseInt(d.data().price)||0),0);
      if(status==="done"){
        const regSnap=await getDoc(doc(db,"settings","regnskap"));
        const existing=regSnap.exists()?regSnap.data():{ totalInntekt:0,totalUtgift:0,manuellKorreksjon:0 };
        await setDoc(doc(db,"settings","regnskap"),{
          totalInntekt:(existing.totalInntekt||0)+paidAmount,
          totalUtgift:(existing.totalUtgift||0)+prizeAmount,
          manuellKorreksjon:existing.manuellKorreksjon||0,
        });
      }

      // Slett deltakere i batches for å håndtere 100+ deltakere
      const BATCH_SIZE = 20;
      for (let i = 0; i < partSnap.docs.length; i += BATCH_SIZE) {
        await Promise.all(partSnap.docs.slice(i, i + BATCH_SIZE).map(d=>deleteDoc(d.ref)));
      }

      const now=new Date();
      await updateDoc(doc(db,"lottery","current"),{
        status:"idle", salgStengt:false, lastClosedByAvlys:false,
        winner:null, winnerHistory:[], winnerEmailSent:false,
        winnerEmailServerStats:deleteField(), // v287: ikke vis forrige rundes e-post-status
        weekLabel:`Uke ${getWeekNumber(now)} ${now.getFullYear()}`,
        startedAt:null, drawTime:null, saleOpenTime:null, repeatWeekly:false,
        currentPrizeIdx:0, currentWinnerName:null, currentWinnerTicketNum:null,
        nextTicketNumber:deleteField(),
        // v181: rydd server-trekning-feltene så stale data ikke henger igjen
        // (forhindrer loop-bugen fra v179 + at autoDraw blokkeres av idempotens-guard).
        drawnPrizes:deleteField(), drawStartedAt:deleteField(), premieVarighetMs:deleteField(), drawTotalMs:deleteField(), drawSource:deleteField(), // v192/v198
        autoDrawSkipped:deleteField(), // v319: rydd utsatt/avlyst-uten-premier-varselet
        avlysReason:deleteField(), // v325: ny runde starter uten stale avlysningsgrunn
      });
      await setDoc(doc(db,"counters","tickets"),{ next:1 });
    } catch(e) {
      console.error("Feil ved start nytt loddsalg:", e);
      showErrorToast("Noe gikk galt. Prøv igjen — noen deltakere kan ha blitt slettet.");
    }
  };

  // v106: dialog ("Erstatt aktiv økt"), loading- og sessionKicked-JSX fjernet.
  // Claim skjer automatisk i App-root, og en kicket enhet får hard reload.

  // v167/v171: hurtig-velger for trekningsmetode i Trekning-dropdownen (PC + mobil).
  // Skriver direkte til settings/config. aktivMetode leses fra settings (live via
  // App-root onSnapshot). v171: miks-utvalg fjernet — miks veksler alltid mellom alle 4.
  const aktivMetode = settings?.trekningsMetode || "hjul";
  const settMetode = async (id) => {
    try { await setDoc(doc(db,"settings","config"),{ trekningsMetode:id },{ merge:true }); }
    catch(e){ showErrorToast("Kunne ikke lagre trekningsmetode."); }
  };
  const renderMetodeVelger = (compact) => {
    const sz = compact ? 34 : 38, fs = compact ? 16 : 18;
    const knapp = (sel, onClick, title, content, accent="#C9A84C") => (
      <button key={title} onClick={onClick} title={title} aria-label={title}
        style={{ width:sz, height:sz, fontSize:fs, borderRadius:6, cursor:"pointer", flexShrink:0,
          display:"flex", alignItems:"center", justifyContent:"center",
          background: sel?"#1a1200":"#0d0d0d", border:`1px solid ${sel?accent:"#3a3a3a"}`,
          boxShadow: sel?`0 0 8px ${accent}80`:"none" }}>{content}</button>
    );
    return (
      <div style={{ padding:"10px 14px", borderBottom:"1px solid #2a2a2a" }}>
        <label style={{ fontSize:9,color:"#777",textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:6 }}>Trekningsmetode</label>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {TREKNING_METODER.map(m => knapp(aktivMetode===m.id, ()=>settMetode(m.id), m.navn, m.icon))}
          {knapp(aktivMetode==="mix", ()=>settMetode("mix"), "Tilfeldig miks", "🎲")}
        </div>
        {aktivMetode==="mix" && (
          <p style={{ margin:"8px 0 0",fontSize:10,color:"#aaa",lineHeight:1.4 }}>Spillene veksler tilfeldig per premie.</p>
        )}
      </div>
    );
  };

  // v170: trekningsforløp (auto/manuell) + pause mellom premier — flyttet hit
  // fra Innstillinger > Trekning. Skriver direkte til settings/config.
  const settForlop = async (auto) => {
    try { await setDoc(doc(db,"settings","config"),{ trekningsForlop: auto?"auto":"manuell" },{ merge:true }); }
    catch(e){ showErrorToast("Kunne ikke lagre trekningsforløp."); }
  };
  const settPause = async (v) => {
    const n = Math.max(1, Math.min(60, parseInt(v) || 10));
    try { await setDoc(doc(db,"settings","config"),{ trekningsPause: n },{ merge:true }); }
    catch(e){ showErrorToast("Kunne ikke lagre pause."); }
  };
  // v301 (premie-bibliotek steg 3): behold premievalg etter trekning.
  // Default false ⇒ alle premier nullstilles til ikke-valgt etter hver
  // fullførte trekning (server-side trigger resetPrizeSelection). PÅ ⇒ utvalget
  // består til neste runde.
  const settBeholdPremievalg = async (behold) => {
    try { await setDoc(doc(db,"settings","config"),{ beholdPremievalg: behold },{ merge:true }); }
    catch(e){ showErrorToast("Kunne ikke lagre premievalg-innstilling."); }
  };
  // v323 (minLodd steg a): minste antall lodd for at autotrekning gjennomføres.
  // KUN lagring i denne omgangen — ingen funksjonell effekt. Terskel-sjekken
  // kommer server-side i steg b (autoDraw avlyser med tilgode under terskelen).
  // Bor i settings/config (policy, overlever runde-resets) — samme skrive-
  // mønster som settPause. 0/tomt = ingen terskel.
  const settMinLodd = async (v) => {
    const n = Math.max(0, parseInt(v) || 0);
    try { await setDoc(doc(db,"settings","config"),{ minLodd: n },{ merge:true }); }
    catch(e){ showErrorToast("Kunne ikke lagre minste antall lodd."); }
  };
  const renderMinLoddFelt = (compact) => (
    <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid #222" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
        <span style={{ fontSize:compact?12:13, color:"#ccc" }}>🎟 Minste antall lodd for trekning</span>
        <input type="number" min={0} value={settings?.minLodd || 0}
          onChange={e=>settMinLodd(e.target.value)}
          style={{ width:64, padding:"5px 8px", fontSize:13, textAlign:"center", borderRadius:4, flexShrink:0,
            background:"#0d0d0d", border:"1px solid #3a3a3a", color:"#f5efe0", outline:"none", colorScheme:"dark" }}/>
      </div>
      <p style={{ margin:"6px 0 0", fontSize:10, color:"#aaa", lineHeight:1.4 }}>
        0 = ingen terskel. Ved færre solgte lodd avlyses trekningen automatisk når nedtellingen er ute, og kjøperne får tilgode til neste trekning.
      </p>
    </div>
  );
  const renderForlopVelger = (compact) => {
    const auto = settings?.trekningsForlop !== "manuell"; // default auto
    const pause = settings?.trekningsPause || 10;
    const behold = settings?.beholdPremievalg === true; // default false (nullstill)
    return (
      <div style={{ padding:"10px 14px", borderBottom:"1px solid #2a2a2a" }}>
        <label style={{ fontSize:9,color:"#777",textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:6 }}>Trekningsforløp</label>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span style={{ fontSize:compact?12:13, color:"#ccc" }}>⚡ Automatisk neste trekning</span>
          <div style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer" }} onClick={()=>settForlop(!auto)}>
            <div style={{ width:30, height:17, borderRadius:9, background:auto?"#C9A84C":"#333", position:"relative" }}>
              <div style={{ position:"absolute", top:1.5, left:auto?14:1.5, width:14, height:14, borderRadius:"50%", background:"#fff", transition:"left 0.15s" }}/>
            </div>
            <span style={{ fontSize:11, color:auto?"#C9A84C":"#999" }}>{auto?"PÅ":"AV"}</span>
          </div>
        </div>
        {auto ? (
          <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:8 }}>
            <span style={{ fontSize:compact?12:13, color:"#aaa" }}>Pause:</span>
            <input type="number" min={1} max={60} value={pause}
              onChange={e=>settPause(e.target.value)}
              style={{ width:56, padding:"5px 8px", fontSize:13, textAlign:"center", borderRadius:4,
                background:"#0d0d0d", border:"1px solid #3a3a3a", color:"#f5efe0", outline:"none", colorScheme:"dark" }}/>
            <span style={{ fontSize:compact?12:13, color:"#aaa" }}>sek mellom premier</span>
          </div>
        ) : (
          <p style={{ margin:"8px 0 0", fontSize:10, color:"#aaa", lineHeight:1.4 }}>Manuell: arrangør går videre med space (PC) / dobbelttrykk (mobil).</p>
        )}
        {/* v301: behold premievalg etter trekning */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:12, paddingTop:10, borderTop:"1px solid #222" }}>
          <span style={{ fontSize:compact?12:13, color:"#ccc" }}>📌 Behold premievalg til neste runde</span>
          <div style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer" }} onClick={()=>settBeholdPremievalg(!behold)}>
            <div style={{ width:30, height:17, borderRadius:9, background:behold?"#C9A84C":"#333", position:"relative" }}>
              <div style={{ position:"absolute", top:1.5, left:behold?14:1.5, width:14, height:14, borderRadius:"50%", background:"#fff", transition:"left 0.15s" }}/>
            </div>
            <span style={{ fontSize:11, color:behold?"#C9A84C":"#999" }}>{behold?"PÅ":"AV"}</span>
          </div>
        </div>
        <p style={{ margin:"6px 0 0", fontSize:10, color:"#aaa", lineHeight:1.4 }}>Av = alle premier nullstilles til ikke-valgt etter hver trekning.</p>
      </div>
    );
  };

  // v27: PC-dashboard-prototype. Bygges som en bred layout med 4-kolonners
  // stats-grid, horisontale tabs og deltaker-tabell med flere kolonner. All
  // forretningslogikk (state, helpers, session-mgmt) gjenbrukes fra
  // OrganizerView ovenfor — kun chrome/layout er nytt.
  if (pcDashboard) {
    const uniquePersons = new Set(participants.map(p=>(p.email||p.name||p.id).toLowerCase())).size;
    // v29: aksentfarger per tile (lilla/gull/grønn/rød-eller-grønn).
    // v31: mørk fargenyanse i bakgrunnen (tint) + sterkere glow rundt
    // kortene gir mer karakter uten å konkurrere med tall-innholdet.
    const allPaid = paidCount === participants.length && participants.length > 0;
    // v50 FIX 5: rekkefølge endret til Deltakere, Lodd, Betalt, Inntekt
    // (Betalt og Inntekt byttet plass). Betalt-tilen er grønn når alle
    // lodd er betalt, ellers rød — gjelder aksent-stripe, ikon og tint.
    const statsCards = [
      { label:"Deltakere", value:uniquePersons, accent:"#a78bfa", tint:"#1a1230", icon:"👥" },
      { label:"Lodd",      value:cTickets,      accent:"#fbbf24", tint:"#1f1808", icon:"🎟" },
      { label:"Betalt",    value:`${paidCount}/${participants.length}`,
        accent: allPaid ? "#34d399" : "#f87171",
        tint:   allPaid ? "#0e1f18" : "#1f1010",
        icon:   allPaid ? "✓" : "⏳" },
      { label:"Inntekt",   value:`${cRevenue},-`, accent:"#34d399", tint:"#0e1f18", icon:"💰" },
    ];
    const fmt = d => new Date(d).toLocaleString("nb-NO",{weekday:"short",day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"});
    return (
      <div style={{ minHeight:"100vh", background:"#0a0a0a", color:"#f5efe0", fontFamily:"'Inter',system-ui,sans-serif" }}>
        {/* v34: CSS-overrides for react-datepicker dark theme + hover-stiler.
            Default react-datepicker.css er hvit; vi overstyrer alle relevante
            klasser til mørkt tema som matcher resten av PC-dashboardet. */}
        <style>{`
          .vl-datepicker-host .react-datepicker-wrapper{display:block;width:100%;}
          /* v151: lysere placeholder ("Velg dato og tid") for kontrast */
          .vl-datepicker-host input::placeholder{color:#999 !important;opacity:1;}
          .react-datepicker-popper{z-index:1000;}
          .react-datepicker{background:#1a1a1a !important;border:1px solid #444 !important;font-family:'Inter',system-ui,sans-serif !important;color:#f5efe0 !important;border-radius:6px;}
          .react-datepicker__header{background:#0d0d0d !important;border-bottom:1px solid #2a2a2a !important;}
          .react-datepicker__current-month,
          .react-datepicker-time__header,
          .react-datepicker__day-name{color:#C9A84C !important;}
          .react-datepicker__day,.react-datepicker__time-name{color:#f5efe0 !important;}
          .react-datepicker__day:hover,.react-datepicker__time-list-item:hover{background:#2a1f0a !important;color:#f5efe0 !important;}
          .react-datepicker__day--selected,.react-datepicker__day--keyboard-selected,
          .react-datepicker__time-list-item--selected{background:#C9A84C !important;color:#0a0a0a !important;font-weight:bold;}
          .react-datepicker__day--disabled,.react-datepicker__day--outside-month{color:#444 !important;}
          .react-datepicker__navigation-icon::before{border-color:#C9A84C !important;}
          .react-datepicker__time-container{background:#1a1a1a !important;border-left:1px solid #2a2a2a !important;}
          .react-datepicker__time{background:#1a1a1a !important;}
          .react-datepicker__time-list{background:#1a1a1a !important;}
          .react-datepicker__time-list-item{color:#f5efe0 !important;}
          .react-datepicker__triangle{display:none !important;}
          .vl-remove-drawtime:hover{background:rgba(208,64,64,0.25) !important;color:#ff7070 !important;text-decoration:underline;}
        `}</style>
        {/* TOPP-BAR — v30: kompakt 2-rads layout.
            RAD 1: branding + Ticker (begrenset bredde) + bruker-info.
            RAD 2: Trekning/Loddsalg-knapper + kompakt status-stripe. */}
        <header style={{
          background:"#0d0d0d", borderBottom:"1px solid #3a3a3a",
          position:"sticky", top:0, zIndex:50,
        }}>
        {/* v149: ÉN banner-rad. Branding (uten 🍾) + skille-strek + knapper
            (Loddsalg→Trekning via CSS order) + Melding-felt + bruker-info.
            RAD 2 fjernet. flexWrap som fail-safe på smale skjermer. */}
        <div style={{
          padding:"10px 32px", display:"flex", alignItems:"center", gap:20, flexWrap:"wrap",
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", flexShrink:0 }}>
            <div>
              <div style={{ fontSize:14, color:"#C9A84C", fontWeight:"bold", letterSpacing:"0.08em", textTransform:"uppercase", whiteSpace:"nowrap" }}>{settings?.lotteryName||"Vinloddet"}</div>
              <div style={{ fontSize:9, color:"#aaa", letterSpacing:"0.1em", textTransform:"uppercase", whiteSpace:"nowrap" }}>
                {lotteryState?.weekLabel||""}<span style={{ marginLeft:8, color:"#888" }}>v{APP_VERSION}</span>
              </div>
            </div>
          </div>

          {/* v149: vertikal skille-strek mellom branding og knapper */}
          <div style={{ width:1, alignSelf:"stretch", minHeight:32, background:"#3a3a3a", margin:"0 8px" }}/>

          {/* PRIMÆR-AKSJONER (Loddsalg + Trekning). v149: flyttet opp i banner-
              raden. Rekkefølge styres via CSS order (Loddsalg=1, Trekning=2).
              v29: gull = inaktiv, grønn = aktiv modus. */}
          <div ref={dropdownRef} style={{ display:"flex", gap:12, position:"relative", flexShrink:0 }}>
            {(()=>{
              const drawActive = isDrawing || drawDone || !!lotteryState?.drawTime;
              const saleActive = status === "idle" && !lotteryState?.salgStengt;
              const goldBg = "#C9A84C", goldEdge = "#a87000";
              const greenBg = "#4caf50", greenEdge = "#2a6a2a";
              const drawBg = drawActive ? greenBg : goldBg;
              const drawEdge = drawActive ? greenEdge : goldEdge;
              const saleBg = saleActive ? greenBg : goldBg;
              const saleEdge = saleActive ? greenEdge : goldEdge;
              return (
            <>
            {/* TREKNING dropdown — v149: order:2 (vises etter Loddsalg) */}
            <div style={{ position:"relative", order:2 }}>
              <div style={{ display:"flex", borderRadius:6, overflow:"hidden", boxShadow:`0 2px 8px ${drawActive?"rgba(76,175,80,0.35)":"rgba(201,168,76,0.25)"}` }}>
                <button style={{ padding:"10px 20px", fontWeight:"bold", fontSize:13, cursor:(drawDone||isDrawing)?"not-allowed":"pointer",
                  border:"none", background:drawBg,
                  color:"#0a0a0a", letterSpacing:"0.08em", textTransform:"uppercase" }}
                  onClick={()=>{ if(!drawDone&&!isDrawing) startDraw(); }}>
                  🎰 Trekning
                </button>
                <button style={{ padding:"10px 10px", fontWeight:"bold", fontSize:11, cursor:"pointer",
                  border:"none", borderLeft:`1px solid ${drawEdge}`,
                  background:drawBg, color:"#0a0a0a" }}
                  onClick={()=>{ setShowDraw(v=>!v); setShowSale(false); }}>▼</button>
              </div>
              {showDraw&&(
                <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, minWidth:280, background:"#1a1a1a", border:"1px solid #444", borderRadius:6, zIndex:100, overflow:"hidden" }}>
                  <button style={{ width:"100%", padding:"10px 14px", background:"transparent", border:"none", color:(drawDone||isDrawing)?"#555":"#f5efe0", cursor:(drawDone||isDrawing)?"not-allowed":"pointer", textAlign:"left", fontSize:12, fontWeight:"bold", borderBottom:"1px solid #2a2a2a" }}
                    onClick={()=>{ setShowDraw(false); startDraw(); }} disabled={drawDone||isDrawing||startDrawLoading}>
                    {startDrawLoading ? "⏳ Starter trekning…" : "🎰 Start trekning nå"}
                  </button>
                  {/* v167: hurtig-velger for trekningsmetode */}
                  {renderMetodeVelger(false)}
                  {/* v35: bruker delt PCDateTimePickerCard-komponent.
                      Popup-rendring via portalId unngår overflow-klipping.
                      v36: validerer mot saleOpenTime — trekning kan ikke
                      starte før loddsalget er åpent. */}
                  <PCDateTimePickerCard
                    label="Trekningstidspunkt"
                    value={drawTime}
                    onChange={setDrawTime}
                    storedValue={lotteryState?.drawTime}
                    pickerRef={drawTimePickerRef}
                    fmt={fmt}
                    onActivate={async v => {
                      // v322: premie-sperre (samme som autoDraw-togglen).
                      if (!(await kanSetteDrawTime())) return false;
                      // v36: les fersk Firestore-state for å unngå race
                      // condition med en annen arrangør-enhet.
                      try {
                        const snap = await getDoc(doc(db,"lottery","current"));
                        const sot = snap.exists() ? snap.data().saleOpenTime : null;
                        if (sot && new Date(sot) >= new Date(v)) {
                          showErrorToast(`Trekningstidspunkt må være ETTER at loddsalget åpner (${fmt(sot)}).`);
                          return false;
                        }
                      } catch(_) { /* nettverksfeil — la skrivingen forsøke */ }
                      await updateDoc(doc(db,"lottery","current"),{ drawTime:v });
                    }}
                    onRemove={async () => {
                      setDrawTime("");
                      await updateDoc(doc(db,"lottery","current"),{ drawTime:null, autoDraw:false });
                    }}
                  />
                  {/* AUTO-TREKNING-TOGGLE. v33: hjelpetekst lagt til under
                      toggelen. Logikken (verifisert i App() linje ~10076):
                        autoDraw=true + drawTime satt → systemet starter
                          trekningen automatisk når drawTime nås.
                        autoDraw=false + drawTime satt → tidspunktet vises
                          kun som info; arrangør må starte manuelt.
                        Toggle er deaktivert hvis drawTime ikke er satt
                          (auto-trekning uten tidspunkt har ingen effekt). */}
                  <div style={{ padding:"10px 14px" }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <span style={{ fontSize:12, color:drawTime?"#ccc":"#888" }}>Auto-trekning</span>
                      <div style={{ display:"flex", alignItems:"center", gap:6, cursor:drawTime?"pointer":"not-allowed", opacity:drawTime?1:0.5 }}
                        onClick={async()=>{ if(!drawTime) return; const next=!autoDrawEnabled; if(next && !(await kanAktivereAutoDraw())) return; setAutoDrawEnabled(next); await updateDoc(doc(db,"lottery","current"),{ autoDraw:next&&!!drawTime }); }}>
                        <div style={{ width:30, height:17, borderRadius:9, background:autoDrawEnabled&&drawTime?"#C9A84C":"#333", position:"relative" }}>
                          <div style={{ position:"absolute", top:1.5, left:autoDrawEnabled&&drawTime?14:1.5, width:14, height:14, borderRadius:"50%", background:"#fff", transition:"left 0.15s" }}/>
                        </div>
                        <span style={{ fontSize:11, color:autoDrawEnabled&&drawTime?"#C9A84C":"#999" }}>{autoDrawEnabled&&drawTime?"PÅ":"AV"}</span>
                      </div>
                    </div>
                    <p style={{ margin:"6px 0 0", fontSize:10, color:"#aaa", lineHeight:1.4 }}>
                      {!drawTime
                        ? "Sett først et trekningstidspunkt over."
                        : autoDrawEnabled
                          ? "Trekningen starter automatisk på valgt tidspunkt."
                          : "Tidspunktet vises kun som info — du må starte trekningen manuelt."}
                    </p>
                    {renderMinLoddFelt(false)}
                  </div>
                  {/* v170: trekningsforløp (auto/manuell) + pause */}
                  {renderForlopVelger(false)}
                </div>
              )}
            </div>

            {/* LODDSALG dropdown — v149: order:1 (vises FØR Trekning),
                ekstra glow-ring når salget er åpent (saleActive). */}
            <div style={{ position:"relative", order:1 }}>
              <div style={{ display:"flex", borderRadius:6, overflow:"hidden",
                boxShadow: saleActive
                  ? "0 0 0 2px rgba(76,175,80,0.6), 0 2px 12px rgba(76,175,80,0.5)"
                  : `0 2px 8px rgba(201,168,76,0.25)` }}>
                <button style={{ padding:"10px 20px", fontWeight:"bold", fontSize:13, cursor:isDrawing?"not-allowed":"pointer",
                  background:saleBg, color:"#0a0a0a", border:"none",
                  letterSpacing:"0.08em", textTransform:"uppercase" }}
                  onClick={async()=>{
                    if(isDrawing) return;
                    if(saleActive){ showErrorToast("Loddsalget er allerede åpent"); return; }
                    await startNewSale();
                  }}>
                  🔄 Loddsalg
                </button>
                <button style={{ padding:"10px 10px", background:saleBg, color:"#0a0a0a",
                  border:"none", borderLeft:`1px solid ${saleEdge}`, cursor:"pointer", fontSize:11 }}
                  onClick={()=>{ setShowSale(v=>!v); setShowDraw(false); }}>▼</button>
              </div>
              {showSale&&(
                <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, minWidth:300, background:"#1a1a1a", border:"1px solid #444", borderRadius:6, zIndex:100, overflow:"hidden" }}>
                  <button style={{ width:"100%", padding:"10px 14px", background:"transparent", border:"none", color:isDrawing?"#555":"#f5efe0", cursor:isDrawing?"not-allowed":"pointer", textAlign:"left", fontSize:12, fontWeight:"bold", borderBottom:"1px solid #2a2a2a" }}
                    onClick={async()=>{ await startNewSale(); setShowSale(false); }} disabled={isDrawing}>
                    🔄 Start nytt loddsalg
                  </button>
                  {/* v35: Auto-åpning av loddsalg — samme kalender-komponent
                      som Trekning bruker. Lagrer til saleOpenTime (samme felt
                      som klassisk mobil-OrganizerView), så endringer her
                      reflekteres på spiller-vyen umiddelbart. */}
                  <PCDateTimePickerCard
                    label="Auto-åpning av loddsalg"
                    value={saleOpenTime}
                    onChange={setSaleOpenTime}
                    storedValue={lotteryState?.saleOpenTime}
                    pickerRef={saleTimePickerRef}
                    fmt={fmt}
                    onActivate={async v => {
                      // v36: hvis ny åpningstid ligger ETTER allerede satt
                      // trekningstid, nullstilles drawTime samtidig — ellers
                      // havner vi i et logisk paradoks (trekning før salget
                      // åpner). Varsler arrangøren via toast.
                      try {
                        const snap = await getDoc(doc(db,"lottery","current"));
                        const dt = snap.exists() ? snap.data().drawTime : null;
                        if (dt && new Date(v) >= new Date(dt)) {
                          showErrorToast(`Trekningstidspunkt (${fmt(dt)}) lå før åpningstid — det er nullstilt.`);
                          setDrawTime("");
                          await updateDoc(doc(db,"lottery","current"),{
                            saleOpenTime:v, repeatWeekly:false,
                            drawTime:null, autoDraw:false,
                          });
                          return;
                        }
                      } catch(_) { /* nettverksfeil — fortsett */ }
                      await updateDoc(doc(db,"lottery","current"),{ saleOpenTime:v, repeatWeekly:false });
                    }}
                    onRemove={async () => {
                      setSaleOpenTime("");
                      await updateDoc(doc(db,"lottery","current"),{ saleOpenTime:null, repeatWeekly:false });
                    }}
                  />
                  {/* repeatWeekly-toggle (kun synlig når saleOpenTime er satt).
                      Mobil-OrganizerView har samme toggle for å indikere at
                      auto-åpning skal gjenta ukentlig. Ikke å forveksle med
                      Auto-trekning-flagget (autoDraw). */}
                  {lotteryState?.saleOpenTime && (
                    <div style={{ padding:"10px 14px", borderBottom:"1px solid #2a2a2a" }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                        <span style={{ fontSize:12, color:"#ccc" }}>🔁 Gjenta ukentlig</span>
                        <div style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer" }}
                          onClick={async()=>{ const next=!lotteryState?.repeatWeekly; await updateDoc(doc(db,"lottery","current"),{ repeatWeekly:next }); }}>
                          <div style={{ width:30, height:17, borderRadius:9, background:lotteryState?.repeatWeekly?"#C9A84C":"#333", position:"relative" }}>
                            <div style={{ position:"absolute", top:1.5, left:lotteryState?.repeatWeekly?14:1.5, width:14, height:14, borderRadius:"50%", background:"#fff", transition:"left 0.15s" }}/>
                          </div>
                          <span style={{ fontSize:11, color:lotteryState?.repeatWeekly?"#C9A84C":"#999" }}>{lotteryState?.repeatWeekly?"PÅ":"AV"}</span>
                        </div>
                      </div>
                      <p style={{ margin:"6px 0 0", fontSize:10, color:"#aaa", lineHeight:1.4 }}>
                        {lotteryState?.repeatWeekly
                          ? "Loddsalget åpnes automatisk samme tidspunkt hver uke."
                          : "Loddsalget åpnes kun ÉN gang på valgt tidspunkt."}
                      </p>
                    </div>
                  )}
                  {(()=>{
                    const kanAvlyse = status === "idle";
                    return (
                      <button style={{ width:"100%", padding:"10px 14px", background:"transparent", border:"none",
                        color:kanAvlyse?"#e57373":"#444", cursor:kanAvlyse?"pointer":"not-allowed",
                        textAlign:"left", fontSize:12, borderBottom:"1px solid #2a2a2a" }}
                        onClick={async()=>{ if(!kanAvlyse) return; await avlysLotteri(); setShowSale(false); }}
                        title={!kanAvlyse?"Ikke tilgjengelig under trekning":""}>
                        ⛔ Avlys lotteri
                      </button>
                    );
                  })()}
                </div>
              )}
            </div>
            </>
              );
            })()}
          </div>

          {/* v150: ticker bredere (vokser opp til 680px), skjøvet mot høyre
              (marginLeft:auto + marginRight:0) så den ligger nær bruker-info.
              Label forenklet til "📣 Ticker". */}
          <div style={{
            flex:"1 1 400px", maxWidth:680, minWidth:300,
            marginLeft:"auto", marginRight:0,
            borderRadius:6, overflow:"hidden", border:"1px solid #2a2a2a",
          }}>
            <TickerPublisher
              lotteryState={lotteryState}
              label="📣 Ticker"
              emojiOpenDirection="down"
            />
          </div>

          {/* Testdata-knapper */}
          <TestDataKnapp settings={settings} compact={true}/>

          {/* Bruker-info + view-toggle + logout */}
          <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
            {user?.email && <span style={{ fontSize:11, color:"#777", whiteSpace:"nowrap" }}>{user.email}</span>}
            {user?.photoURL && <img src={user.photoURL} alt="" style={{ width:28, height:28, borderRadius:"50%", border:"1px solid #3a3a3a" }} referrerPolicy="no-referrer"/>}
            {onSwitchAdminView && (
              <button onClick={()=>onSwitchAdminView("participant")} style={{ background:"transparent", border:"1px solid #3a3a3a", color:"#888", padding:"5px 10px", borderRadius:4, fontSize:13, cursor:"pointer" }} title="Vis spiller-vy">👁</button>
            )}
            <button onClick={onLogout} style={{ background:"transparent", border:"1px solid #5a1a1a", color:"#e57373", padding:"5px 10px", borderRadius:4, fontSize:11, cursor:"pointer", whiteSpace:"nowrap" }} title="Logg ut">🚪 Logg ut</button>
          </div>
        </div>
        </header>

        {/* STATUS-STRIPE — full-bredde rad mellom topp-bar og stats-tiles.
            v32: gjenopprettet som egen rad (var inline i topbar i v30-v31
            der den ofte ble klemt eller usynlig). Viser åpent/stengt-
            status, trekningstidspunkt, auto-flagg og auto-åpning. Bakgrunn
            tar dempet farge fra status: grønn-tonet hvis salg åpent, rød-
            tonet hvis stengt. Skjules under pågående trekning / fullført —
            de har egne fullbredde-alerter. */}
        {isDrawing && (
          <div style={{ background:"#1a0800", borderBottom:"1px solid #C9A84C", padding:"10px 32px", color:"#C9A84C", fontSize:13 }}>
            🎰 Trekning pågår — loddsalg er stengt
          </div>
        )}
        {drawDone && (
          <div style={{ background:"#0d2a0d", borderBottom:"1px solid #2a5a2a", padding:"10px 32px" }}>
            <p style={{ margin:"0 0 8px", fontSize:13, color:"#4caf50", fontWeight:"bold" }}>✅ Trekning fullført! Start nytt loddsalg for neste runde.</p>
            <WinnerEmailServerStatus lotteryState={lotteryState}/>
          </div>
        )}
        {!isDrawing && !drawDone && (()=>{
          const hasTrekn = !!lotteryState?.drawTime;
          const hasSaleOpen = !!lotteryState?.saleOpenTime;
          const salgsAktivt = status==="idle" && !lotteryState?.salgStengt;
          if (!hasTrekn && !hasSaleOpen && !salgsAktivt) return null;
          // Bakgrunns-tone basert på loddsalg-status (dempet grønn / rød).
          const bg = salgsAktivt ? "#0d1a0d" : "#1a0d0d";
          const borderColor = salgsAktivt ? "#2a5a2a" : "#5a2a2a";
          return (
            <div style={{
              background:bg, borderBottom:`1px solid ${borderColor}`,
              padding:"14px 32px", display:"flex", alignItems:"center",
              gap:18, flexWrap:"wrap", fontSize:16, color:"#aaa",
            }}>
              {salgsAktivt
                ? <span style={{ color:"#4caf50", fontWeight:"bold", whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:8, fontSize:17 }}>
                    <span style={{ width:10, height:10, borderRadius:"50%", background:"#4caf50", boxShadow:"0 0 8px #4caf50" }}/> Loddsalget er åpent
                  </span>
                : <span style={{ color:"#e57373", fontWeight:"bold", whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:8, fontSize:17 }}>
                    <span style={{ width:10, height:10, borderRadius:"50%", background:"#e57373" }}/> Loddsalget er stengt
                  </span>}
              {/* v36: Auto-åpning kommer FØR Trekning i kronologisk rekkefølge
                  (salget åpner først, så trekkes vinneren). */}
              {hasSaleOpen && (
                <span style={{ color:"#64b5f6", whiteSpace:"nowrap" }}>
                  🔓 Auto-åpning: <strong>{fmt(lotteryState.saleOpenTime)}</strong>
                  {lotteryState?.repeatWeekly&&" · 🔁 ukentlig"}
                </span>
              )}
              {hasTrekn && (
                <span style={{ whiteSpace:"nowrap" }}>⏱ Trekning: <strong style={{ color:"#4caf50" }}>{fmt(lotteryState.drawTime)}</strong>
                  {lotteryState?.autoDraw && <span style={{ marginLeft:6, color:"#66bb6a", fontSize:11 }}>⚡ Auto</span>}
                </span>
              )}
            </div>
          );
        })()}

        {/* v319: autotrekning-uten-premier-varsler (før/etter) */}
        {renderPremieVarsler(true)}

        {/* STATS-GRID — v29: livligere tiles med aksentfarger per kort.
            Hver tile får: venstre-stripe i aksent-farge, lett gradient,
            ikon-pille i aksent-farge med glow, og hover-løft. Bakgrunn
            for stats-rad har en subtil radial-glow for å skille fra
            tabs-raden under. */}
        <style>{`
          .vlPCStatCard{transition:transform 0.15s ease, box-shadow 0.15s ease;}
          .vlPCStatCard:hover{transform:translateY(-2px);}
        `}</style>
        <div style={{
          display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:18,
          padding:"28px 32px",
          background:"radial-gradient(ellipse at top, #15110a 0%, #0a0a0a 65%)",
          borderBottom:"1px solid #1a1a1a",
        }}>
          {statsCards.map(s => {
            const accent = s.accent;
            const tint = s.tint;
            return (
            <div key={s.label} className="vlPCStatCard" style={{
              // v31: gradient starter med tonet aksent-tint og glir til
              // tint-fargen (mørk versjon av aksent) — gir hver tile en
              // distinkt fargenyanse i bakgrunnen.
              background:`linear-gradient(135deg, ${accent}26 0%, ${tint} 55%, ${tint} 100%)`,
              border:`1px solid ${accent}55`,
              borderLeft:`3px solid ${accent}`,
              borderRadius:10, padding:"22px 24px",
              display:"flex", alignItems:"center", gap:18,
              // Sterkere glow: drop-shadow + ambient accent-bloom utenfor kortet.
              boxShadow:`0 6px 22px ${accent}40, 0 0 36px ${accent}1c, inset 0 1px 0 ${accent}22`,
              position:"relative", overflow:"hidden",
            }}>
              {/* Diagonal corner-glow — sterkere enn v29 */}
              <div style={{
                position:"absolute", top:-40, right:-40, width:160, height:160,
                background:`radial-gradient(circle, ${accent}33 0%, ${accent}10 40%, transparent 75%)`,
                pointerEvents:"none",
              }}/>
              {/* Ikon-pille med kraftigere glow */}
              <div style={{
                fontSize:28, width:54, height:54, flexShrink:0,
                display:"flex", alignItems:"center", justifyContent:"center",
                background:`linear-gradient(145deg, ${accent}33 0%, ${accent}14 100%)`,
                border:`1px solid ${accent}55`,
                borderRadius:12,
                boxShadow:`0 0 18px ${accent}55, inset 0 1px 0 ${accent}33`,
                position:"relative", zIndex:1,
              }}>{s.icon}</div>
              <div style={{ position:"relative", zIndex:1 }}>
                <p style={{
                  margin:"0 0 4px", fontSize:32, fontWeight:800, color:accent,
                  lineHeight:1, letterSpacing:"-0.02em",
                  textShadow:`0 0 22px ${accent}55`,
                  fontFamily:"'Inter',system-ui,sans-serif",
                }}>{s.value}</p>
                <p style={{
                  margin:0, fontSize:10, color:"#a89a8a",
                  textTransform:"uppercase", letterSpacing:"0.14em", fontWeight:600,
                }}>{s.label}</p>
              </div>
            </div>
            );
          })}
        </div>

        {/* TABS-RAD — hovedtabbene. v56 FIX 1: Innstillinger-undermenyene
            ligger IKKE lenger inline her — de har fått en egen midstilt rad
            rett under (se under denne div-en).
            v54 FIX 1: container-padding redusert fra 32px → 10px. Tab-
            knappene har selv 22px venstre-padding, så første tab-tekst
            lander på 10+22 = 32px — nøyaktig samme x som stats-tiles og
            hovedinnholdet (begge har 32px horisontal padding). */}
        {/* v147: FLAT PC-nav — Innstillinger-undermenyene er nå direkte hoved-
            tabs (ingen egen sub-rad). 4 hoved + 6 settings = 10 tabs. flexWrap
            som sikkerhet på smale skjermer. Større/tydeligere tekst (15px, #ccc
            inaktiv). Mobil-nav (lenger ned) beholder to-trinns hierarki. */}
        <div style={{ background:"#0d0d0d", borderTop:"1px solid #1a1a1a", borderBottom:"1px solid #2a2a2a", padding:"0 10px", display:"flex", flexWrap:"wrap", alignItems:"center", justifyContent:"center" }}>
          {(() => {
            const ubetaltLabel = (()=>{
              const byEmail = {};
              participants.forEach(p=>{const k=(p.email||p.name||"").toLowerCase()||p.id;if(!byEmail[k])byEmail[k]={allPaid:true};byEmail[k].allPaid=byEmail[k].allPaid&&!!p.paid;});
              const n = Object.values(byEmail).filter(p=>!p.allPaid).length;
              return `Ubetalte${n>0?" ("+n+")":""}`;
            })();
            const flatTabs = [
              {id:"deltakere", label:"Deltakere"},
              {id:"ubetalte", label:ubetaltLabel},
              {id:"regnskap", label:"Regnskap"},
              {id:"premier", label:"Premier"},
              ...SETTINGS_SUBTABS,
            ];
            return flatTabs.map(t => {
              const isActive = tab === t.id;
              return (
                <button key={t.id} onClick={()=>setTab(t.id)} style={{
                  background:"transparent", border:"none", color:isActive?"#C9A84C":"#ccc",
                  padding:"14px 16px", fontSize:15, fontWeight:isActive?"bold":"normal",
                  cursor:"pointer", letterSpacing:"0.04em", whiteSpace:"nowrap",
                  borderBottom: isActive ? "2px solid #C9A84C" : "2px solid transparent",
                  fontFamily:"'Inter',system-ui,sans-serif",
                }}>{t.label}</button>
              );
            });
          })()}
        </div>

        {/* HOVED-INNHOLD — v29: subtilt avvikende bakgrunn for å skille
            innholds-seksjonen fra stats-raden og tabs-raden over. */}
        <div style={{ background:"#070707", minHeight:400 }}>
          <main style={{ padding:"28px 32px", maxWidth:1600, margin:"0 auto" }}>
          {/* v54 FIX 4: enhetlig hoved-seksjonsoverskrift for tabber uten
              kort-baserte titler (Deltakere, Ubetalte). 14px fet dempet
              gull uppercase + ikon — samme stil som kort-titlene i de
              øvrige tabbene. */}
          {tab === "deltakere" && (
            <div>
              <p style={{ margin:"0 0 16px",fontSize:14,color:"#C9A84C",fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase" }}>👥 Deltakere</p>
              <OrganizerPCDeltakerTable participants={participants}/>
            </div>
          )}
          {tab === "ubetalte" && (
            <div>
              <p style={{ margin:"0 0 16px",fontSize:14,color:"#C9A84C",fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase" }}>📋 Ubetalte lodd — inneværende runde</p>
              {participants.filter(p=>!p.paid).length===0?(
                <p style={{ color:"#4caf50", fontSize:13 }}>✅ Alle lodd er registrert som betalt</p>
              ):(
                <>
                  <div style={{ ...S.regnskapRow, background:"#2a0d0d", border:"1px solid #5a1a1a", marginBottom:16 }}>
                    <span style={{ ...S.regnskapLabel, color:"#e57373" }}>💸 Totalt ubetalte</span>
                    <span style={{ ...S.regnskapVal, color:"#e57373" }}>{(()=>{
                      const byEmail={};
                      participants.forEach(p=>{const k=(p.email||p.name||"").toLowerCase()||p.id;if(!byEmail[k])byEmail[k]={amount:0,allPaid:true};byEmail[k].amount+=(p.amount||0);byEmail[k].allPaid=byEmail[k].allPaid&&!!p.paid;});
                      return Object.values(byEmail).filter(p=>!p.allPaid).reduce((s,p)=>s+p.amount,0);
                    })()},-</span>
                  </div>
                  <OrganizerPCUbetalteTable participants={participants}/>
                </>
              )}
              <UbetalteHistorikk settings={settings}/>
            </div>
          )}
          {tab === "premier" && <PrizeManager onPrizeChange={setPrizes} pcLayout={true}/>}
          {tab === "regnskap" && (
            <div>
              <Regnskap participants={participants} prizes={prizes} settings={settings} pcLayout={true}/>
              <KredittOversikt pcLayout={true}/>
              <Statistikk pcLayout={true}/>
            </div>
          )}
          {/* v147: settings-id som flat hoved-tab → SettingsPanel med den
              sub-fanen. (Beholder også "innstillinger" for bakoverkompat.) */}
          {(tab === "innstillinger" || SETTINGS_TAB_IDS.includes(tab)) && (
            <SettingsPanel lotteryState={lotteryState} onDataChanged={()=>{}} pcLayout={true}
              settingsTab={SETTINGS_TAB_IDS.includes(tab) ? tab : settingsTab}
              setSettingsTab={SETTINGS_TAB_IDS.includes(tab) ? setTab : setSettingsTab}/>
          )}
          </main>
        </div>
        {/* v52 FIX 6: fot-stripa med testdata-knappene er fjernet — knappene
            ligger nå i topp-baren (rad 1). */}
      </div>
    );
  }

  return (
    <div style={S.dashView}>
      {/* v316: Vipps-sjekk-modal etter avlysning (rendres via portal). */}
      {vippsSjekk && (
        <VippsSjekkModal weekId={vippsSjekk.weekId} kjopere={vippsSjekk.kjopere} onClose={()=>setVippsSjekk(null)} />
      )}
      <header style={{ background:"#0d0d0d",borderBottom:"1px solid #3a3a3a",padding:"12px 20px" }}>
        {/* Trekning og Loddsalg som dropdown-menyer */}
        <div ref={dropdownRef} style={{ display:"flex",gap:8,position:"relative" }}>
              {/* TREKNING dropdown — v162: order:2 (vises etter Loddsalg, matcher PC v149) */}
              <div style={{ flex:1,position:"relative",order:2 }}>
                <div style={{ display:"flex",borderRadius:4,overflow:"hidden" }}>
                  <button style={{ flex:1,padding:"10px 12px",fontWeight:"bold",fontSize:13,cursor:"pointer",
                    border:"none",background:(drawDone||isDrawing)?"#1a1a1a":"#C9A84C",
                    color:(drawDone||isDrawing)?"#555":"#0a0a0a",display:"flex",alignItems:"center",justifyContent:"center" }}
                    onClick={()=>{ if(!drawDone&&!isDrawing) startDraw(); }}>
                    🎰 Trekning
                  </button>
                  <button style={{ padding:"10px 10px",fontWeight:"bold",fontSize:13,cursor:"pointer",
                    border:"none",borderLeft:`1px solid ${(drawDone||isDrawing)?"#333":"#a87000"}`,
                    background:(drawDone||isDrawing)?"#1a1a1a":"#C9A84C",
                    color:(drawDone||isDrawing)?"#555":"#0a0a0a" }}
                    onClick={()=>{ setShowDraw(v=>!v); setShowSale(false); }}>▼</button>
                </div>
                {showDraw&&(
                  <div style={{ position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:"#1a1a1a",border:"1px solid #444",borderRadius:4,zIndex:100,overflow:"hidden" }}>
                    <button style={{ width:"100%",padding:"10px 14px",background:"transparent",border:"none",color:(drawDone||isDrawing)?"#555":"#f5efe0",cursor:(drawDone||isDrawing)?"not-allowed":"pointer",textAlign:"left",fontSize:12,fontWeight:"bold",borderBottom:"1px solid #2a2a2a" }}
                      onClick={()=>{ setShowDraw(false); startDraw(); }} disabled={drawDone||isDrawing||startDrawLoading}>
                      {startDrawLoading ? "⏳ Starter trekning…" : "🎰 Start trekning nå"}
                    </button>
                    {/* v167: hurtig-velger for trekningsmetode */}
                    {renderMetodeVelger(true)}
                    <div style={{ padding:"10px 14px",borderBottom:"1px solid #2a2a2a" }}>
                      <label style={{ fontSize:9,color:"#777",textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:4 }}>Trekningstidspunkt</label>
                      <input style={{ ...S.input,marginBottom:4,padding:"6px 8px",fontSize:11,colorScheme:"dark",accentColor:"#C9A84C",border:"1px solid #444" }}
                        type="datetime-local" value={drawTime}
                        min={new Date(Date.now()+60000).toISOString().slice(0,16)}
                        onChange={e=>{ setDrawTime(e.target.value); }}/>
                      {drawTime&&drawTime!==(lotteryState?.drawTime||"")&&(()=>{
                        const isPast = drawTime && new Date(drawTime) <= new Date();
                        return isPast
                          ? <p style={{ margin:0,fontSize:10,color:"#e57373" }}>⚠️ Tidspunktet er passert — velg fremtidig tid</p>
                          : <button style={{ width:"100%",padding:"5px 10px",background:"#0d2a0d",border:"1px solid #1a4a1a",color:"#4caf50",borderRadius:3,cursor:"pointer",fontSize:11,fontWeight:"bold" }}
                              onClick={async()=>{
                                if(new Date(drawTime)<=new Date()){showErrorToast("Velg et tidspunkt i fremtiden");return;}
                                // v322: premie-sperre (samme som autoDraw-togglen).
                                if(!(await kanSetteDrawTime())) return;
                                // v166: kryssvalidering (paritet med PC) — trekning kan
                                // ikke starte før loddsalget åpner. Leser fersk state for
                                // å unngå race med annen arrangør-enhet.
                                try {
                                  const snap = await getDoc(doc(db,"lottery","current"));
                                  const sot = snap.exists() ? snap.data().saleOpenTime : null;
                                  if (sot && new Date(sot) >= new Date(drawTime)) {
                                    showErrorToast(`Trekningstidspunkt må være ETTER at loddsalget åpner (${new Date(sot).toLocaleString("nb-NO",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}).`);
                                    return;
                                  }
                                } catch(_) { /* nettverksfeil — la skrivingen forsøke */ }
                                await updateDoc(doc(db,"lottery","current"),{drawTime:drawTime||null});
                              }}>✓ Aktiver tidspunkt</button>;
                      })()}
                      {lotteryState?.drawTime&&(
                        <p style={{ margin:"4px 0 0",fontSize:10,color:"#4caf50" }}>
                          ✓ Satt: {new Date(lotteryState.drawTime).toLocaleString("nb-NO",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}
                          <span style={{ marginLeft:8,color:"#555",cursor:"pointer",fontSize:10 }}
                            onClick={async()=>{ setDrawTime(""); await updateDoc(doc(db,"lottery","current"),{drawTime:null,autoDraw:false}); }}>✕ Fjern</span>
                        </p>
                      )}
                    </div>
                    <div style={{ padding:"10px 14px" }}>
                      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between" }}>
                        <span style={{ fontSize:12,color:drawTime?"#ccc":"#888" }}>Auto-trekning</span>
                        <div style={{ display:"flex",alignItems:"center",gap:6,cursor:drawTime?"pointer":"not-allowed",opacity:drawTime?1:0.5 }}
                          onClick={async()=>{ if(!drawTime) return; const next=!autoDrawEnabled; if(next && !(await kanAktivereAutoDraw())) return; setAutoDrawEnabled(next); await updateDoc(doc(db,"lottery","current"),{ autoDraw:next&&!!drawTime }); }}>
                          <div style={{ width:30,height:17,borderRadius:9,background:autoDrawEnabled&&drawTime?"#C9A84C":"#333",position:"relative" }}>
                            <div style={{ position:"absolute",top:1.5,left:autoDrawEnabled&&drawTime?14:1.5,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left 0.15s" }}/>
                          </div>
                          <span style={{ fontSize:11,color:autoDrawEnabled&&drawTime?"#C9A84C":"#999" }}>{autoDrawEnabled&&drawTime?"PÅ":"AV"}</span>
                        </div>
                      </div>
                      {/* v166: hjelpetekst (paritet med PC) */}
                      <p style={{ margin:"6px 0 0",fontSize:10,color:"#aaa",lineHeight:1.4 }}>
                        {!drawTime
                          ? "Sett først et trekningstidspunkt over."
                          : autoDrawEnabled
                            ? "Trekningen starter automatisk på valgt tidspunkt."
                            : "Tidspunktet vises kun som info — du må starte trekningen manuelt."}
                      </p>
                      {renderMinLoddFelt(true)}
                    </div>
                    {/* v170: trekningsforløp (auto/manuell) + pause */}
                    {renderForlopVelger(true)}
                  </div>
                )}
              </div>

              {/* LODDSALG dropdown — v162: order:1 (vises før Trekning, matcher PC v149).
                  v287: grønn (#4caf50, samme som PC saleBg) når salget er åpent —
                  samme betingelse som PC saleActive (status idle + ikke salgStengt). */}
              <div style={{ flex:1,position:"relative",order:1 }}>
                {(()=>{
                  const saleActive = status==="idle" && !lotteryState?.salgStengt;
                  return (
                <div style={{ display:"flex",borderRadius:4,overflow:"hidden",border:`1px solid ${isDrawing?"#2a0d0d":saleActive?"#2a6a2a":drawDone?"#1a4a1a":"#2a2a2a"}` }}>
                  <button style={{ flex:1,padding:"10px 12px",fontWeight:"bold",fontSize:13,cursor:isDrawing?"not-allowed":"pointer",
                    background:isDrawing?"#0a0a0a":saleActive?"#4caf50":drawDone?"#0d2a0d":"#1a1a1a",
                    color:isDrawing?"#555":saleActive?"#0a0a0a":drawDone?"#4caf50":"#aaa",border:"none",
                    display:"flex",alignItems:"center",justifyContent:"center",gap:6 }}
                    onClick={async()=>{
                      if(isDrawing) return;
                      if(saleActive){ showErrorToast("Loddsalget er allerede åpent"); return; } // v163: paritet med PC v148
                      await startNewSale();
                    }}>
                    🔄 Loddsalg
                  </button>
                  <button style={{ padding:"10px 10px",background:isDrawing?"#0a0a0a":saleActive?"#4caf50":drawDone?"#0d2a0d":"#1a1a1a",
                    color:isDrawing?"#555":saleActive?"#0a0a0a":drawDone?"#4caf50":"#777",border:"none",borderLeft:`1px solid ${saleActive?"#2a6a2a":"#333"}`,cursor:"pointer",fontSize:9 }}
                    onClick={()=>{ setShowSale(v=>!v); setShowDraw(false); }}>▼</button>
                </div>
                  );
                })()}
                {showSale&&(
                  <div style={{ position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:"#1a1a1a",border:"1px solid #444",borderRadius:4,zIndex:100,overflow:"hidden" }}>
                    <button style={{ width:"100%",padding:"10px 14px",background:"transparent",border:"none",color:isDrawing?"#555":"#f5efe0",cursor:isDrawing?"not-allowed":"pointer",textAlign:"left",fontSize:12,fontWeight:"bold",borderBottom:"1px solid #2a2a2a" }}
                      onClick={async ()=>{ await startNewSale(); setShowSale(false); }} disabled={isDrawing}>
                      🔄 Start nytt loddsalg
                    </button>
                    {(()=>{
                      const kanAvlyse = status === "idle"; // Alltid tilgjengelig i idle, selv uten deltakere
                      return (
                        <button style={{ width:"100%",padding:"10px 14px",background:"transparent",border:"none",
                          color:kanAvlyse?"#e57373":"#444",cursor:kanAvlyse?"pointer":"not-allowed",
                          textAlign:"left",fontSize:12,borderBottom:"1px solid #2a2a2a" }}
                          onClick={async ()=>{ if(!kanAvlyse) return; await avlysLotteri(); setShowSale(false); }}
                          title={!kanAvlyse?"Ikke tilgjengelig under trekning":""}>
                          ⛔ {participants.length>0?"Avlys lotteriet":"Steng loddsalget"}
                        </button>
                      );
                    })()}
                    <div style={{ padding:"10px 14px",borderBottom:"1px solid #2a2a2a" }}>
                      <label style={{ fontSize:9,color:"#777",textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:4 }}>Auto-åpning (dato og tid)</label>
                      <input style={{ ...S.input,marginBottom:4,padding:"6px 8px",fontSize:11,colorScheme:"dark",accentColor:"#C9A84C",border:"1px solid #444" }}
                        type="datetime-local" value={saleOpenTime}
                        min={new Date(Date.now()+60000).toISOString().slice(0,16)}
                        onChange={e=>{ setSaleOpenTime(e.target.value); }}/>
                      {saleOpenTime && saleOpenTime !== (lotteryState?.saleOpenTime||"") && (()=>{
                        const isPast = saleOpenTime && new Date(saleOpenTime) <= new Date();
                        return isPast
                          ? <p style={{ margin:"0 0 4px",fontSize:10,color:"#e57373" }}>⚠️ Tidspunktet er passert — velg fremtidig tid</p>
                          : <button style={{ width:"100%",padding:"5px 10px",background:"#0d2a0d",border:"1px solid #1a4a1a",color:"#4caf50",borderRadius:3,cursor:"pointer",fontSize:11,fontWeight:"bold",marginBottom:4 }}
                              onClick={async()=>{
                                if(new Date(saleOpenTime)<=new Date()){showErrorToast("Velg et tidspunkt i fremtiden");return;}
                                // v166: kryssvalidering (paritet med PC) — hvis åpningstid
                                // ligger ETTER trekningstid nullstilles drawTime samtidig
                                // (ellers logisk paradoks: trekning før salget åpner).
                                try {
                                  const snap = await getDoc(doc(db,"lottery","current"));
                                  const dt = snap.exists() ? snap.data().drawTime : null;
                                  if (dt && new Date(saleOpenTime) >= new Date(dt)) {
                                    showErrorToast(`Trekningstidspunkt (${new Date(dt).toLocaleString("nb-NO",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}) lå før åpningstid — det er nullstilt.`);
                                    setDrawTime("");
                                    await updateDoc(doc(db,"lottery","current"),{ saleOpenTime:saleOpenTime, repeatWeekly:false, drawTime:null, autoDraw:false });
                                    return;
                                  }
                                } catch(_) { /* nettverksfeil — fortsett */ }
                                await updateDoc(doc(db,"lottery","current"),{ saleOpenTime:saleOpenTime, repeatWeekly:false });
                              }}>✓ Aktiver auto-åpning</button>;
                      })()}
                      {lotteryState?.saleOpenTime&&(
                        <>
                          <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6 }}>
                            <span style={{ fontSize:11,color:"#aaa" }}>🔁 Gjenta ukentlig</span>
                            <div style={{ display:"flex",alignItems:"center",gap:6,cursor:"pointer" }}
                              onClick={async()=>{ const next=!lotteryState?.repeatWeekly; await updateDoc(doc(db,"lottery","current"),{ repeatWeekly:next }); }}>
                              <div style={{ width:28,height:16,borderRadius:8,background:lotteryState?.repeatWeekly?"#C9A84C":"#333",position:"relative" }}>
                                <div style={{ position:"absolute",top:1,left:lotteryState?.repeatWeekly?13:1,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left 0.15s" }}/>
                              </div>
                              <span style={{ fontSize:10,color:lotteryState?.repeatWeekly?"#C9A84C":"#666" }}>{lotteryState?.repeatWeekly?"PÅ":"AV"}</span>
                            </div>
                          </div>
                          {/* v166: hjelpetekst (paritet med PC) */}
                          <p style={{ margin:"0 0 6px",fontSize:10,color:"#aaa",lineHeight:1.4 }}>
                            {lotteryState?.repeatWeekly
                              ? "Loddsalget åpnes automatisk samme tidspunkt hver uke."
                              : "Loddsalget åpnes kun ÉN gang på valgt tidspunkt."}
                          </p>
                          <button style={{ width:"100%",padding:"6px 10px",background:"#1a0a0a",border:"1px solid #5a1a1a",color:"#e57373",borderRadius:4,cursor:"pointer",fontSize:11,fontWeight:"bold" }}
                            onClick={async()=>{ setSaleOpenTime(""); await updateDoc(doc(db,"lottery","current"),{ saleOpenTime:null, repeatWeekly:false }); }}>
                            ✕ Fjern auto-åpning
                          </button>
                        </>
                      )}
                      {!lotteryState?.saleOpenTime&&!saleOpenTime&&<p style={{ margin:0,fontSize:10,color:"#555" }}>Velg dato/tid for å aktivere</p>}
                    </div>
                  </div>
                )}
              </div>
            </div>
      </header>



      {/* Statusbanner */}
      {isDrawing&&(
        <div style={{ margin:"12px 20px 8px",background:"#1a0800",border:"1px solid #C9A84C",borderRadius:4,padding:"12px 16px" }}>
          <p style={{ margin:0,fontSize:13,color:"#C9A84C" }}>🎰 Trekning pågår — loddsalg er stengt</p>
        </div>
      )}
      {!isDrawing&&!drawDone&&(()=>{
        const hasTrekn = !!lotteryState?.drawTime;
        const hasSaleOpen = !!lotteryState?.saleOpenTime;
        const salgsAktivt = status==="idle" && !lotteryState?.salgStengt;
        if (!hasTrekn && !hasSaleOpen && !salgsAktivt) return null;
        const fmt = d => new Date(d).toLocaleString("nb-NO",{weekday:"short",day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"});
        return (
          <div style={{ margin:"12px 20px 8px",background:"#0d1a0d",border:"1px solid #2a5a2a",borderRadius:6,padding:"10px 14px",display:"flex",flexDirection:"column",gap:6 }}>
            <div style={{ display:"flex",alignItems:"center",gap:8,flexWrap:"wrap" }}>
              {/* v166: visuell paritet med PC (v149) — større font + CSS-rund
                  glødende prikk i stedet for emoji-prikk. */}
              {salgsAktivt
                ? <span style={{ fontSize:15,color:"#4caf50",fontWeight:"bold",display:"flex",alignItems:"center",gap:8,whiteSpace:"nowrap" }}><span style={{ width:9,height:9,borderRadius:"50%",background:"#4caf50",boxShadow:"0 0 8px #4caf50",flexShrink:0 }}/>Loddsalget er åpent</span>
                : <span style={{ fontSize:15,color:"#e57373",fontWeight:"bold",display:"flex",alignItems:"center",gap:8,whiteSpace:"nowrap" }}><span style={{ width:9,height:9,borderRadius:"50%",background:"#e57373",flexShrink:0 }}/>Loddsalget er stengt</span>}
              {hasTrekn&&<span style={{ fontSize:12,color:"#aaa" }}>⏱ Trekning: <strong style={{ color:"#4caf50" }}>{fmt(lotteryState.drawTime)}</strong>
                {lotteryState?.autoDraw&&<span style={{ marginLeft:6,color:"#66bb6a",fontSize:11 }}>⚡ Auto</span>}
              </span>}
            </div>
            {hasSaleOpen&&(
              <span style={{ fontSize:12,color:"#64b5f6" }}>
                🔓 Auto-åpning: <strong>{fmt(lotteryState.saleOpenTime)}</strong>
                {lotteryState?.repeatWeekly&&" · 🔁 ukentlig"}
              </span>
            )}
          </div>
        );
      })()}
      {/* v319: autotrekning-uten-premier-varsler (før/etter) */}
      {renderPremieVarsler(false)}
      {drawDone&&(
        <div style={{ margin:"12px 20px 8px",background:"#0d2a0d",border:"1px solid #2a5a2a",borderRadius:4,padding:"10px 14px" }}>
          <p style={{ margin:"0 0 8px",fontSize:12,color:"#4caf50",fontWeight:"bold" }}>✅ Trekning fullført! Start nytt loddsalg for neste runde.</p>
          <WinnerEmailServerStatus lotteryState={lotteryState}/>
        </div>
      )}
      {/* v162: inline ticker-felt fjernet — redigering flyttet til bottom-sheet
          som åpnes via 📢-knappen i App-root-topbaren (vl-open-ticker-event). */}
      <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",background:"#0a0a0a",border:"1px solid #3a3a3a" }}>
        {(()=>{
          const uniquePersons = new Set(participants.map(p=>(p.email||p.name||p.id).toLowerCase())).size;
          return [
            {label:"Deltakere",value:uniquePersons,color:"#C9A84C"},
            {label:"Lodd",value:cTickets,color:"#C9A84C"},
            {label:"Inntekt",value:`${cRevenue},-`,color:"#4caf50"},
            {label:"Betalt",value:`${paidCount}/${participants.length}`,color:paidCount===participants.length&&participants.length>0?"#4caf50":"#e57373"},
          ];
        })().map(s=>(
          <div key={s.label} style={{ padding:"14px 8px",textAlign:"center",borderRight:"1px solid #3a3a3a",boxSizing:"border-box" }}>
            <p style={{ margin:"0 0 3px",fontSize:20,fontWeight:"bold",color:s.color }}>{s.value}</p>
            <p style={{ margin:0,fontSize:9,color:"#777",textTransform:"uppercase",letterSpacing:"0.08em" }}>{s.label}</p>
          </div>
        ))}
      </div>

      <div style={S.tabRow}>
        {["deltakere","ubetalte","regnskap","premier","innstillinger"].map(t=>(
          <button key={t} style={{ ...S.tabBtn,...(tab===t?S.tabBtnActive:{}) }} onClick={()=>setTab(t)}>
            {t==="ubetalte"
              ? <span>{(()=>{
                  const byEmail={};
                  participants.forEach(p=>{const k=(p.email||p.name||"").toLowerCase()||p.id;if(!byEmail[k])byEmail[k]={allPaid:true};byEmail[k].allPaid=byEmail[k].allPaid&&!!p.paid;});
                  const n=Object.values(byEmail).filter(p=>!p.allPaid).length;
                  return `Ubetalte${n>0?" ("+n+")":""}`;
                })()}</span>
              : t.charAt(0).toUpperCase()+t.slice(1)}
          </button>
        ))}
      </div>

      {tab==="ubetalte"&&(
        <div style={{ padding:16 }}>
          <p style={S.sectionLabel}>Ubetalte lodd – inneværende runde</p>
          {participants.filter(p=>!p.paid).length===0?(
            <p style={{ color:"#4caf50",fontSize:13 }}>✅ Alle lodd er registrert som betalt</p>
          ):(
            <>
              <div style={{ ...S.regnskapRow,background:"#2a0d0d",border:"1px solid #5a1a1a",marginBottom:16 }}>
                <span style={{ ...S.regnskapLabel,color:"#e57373" }}>💸 Totalt ubetalte</span>
                <span style={{ ...S.regnskapVal,color:"#e57373" }}>{(()=>{
                  const byEmail={};
                  participants.forEach(p=>{const k=(p.email||p.name||"").toLowerCase()||p.id;if(!byEmail[k])byEmail[k]={amount:0,allPaid:true};byEmail[k].amount+=(p.amount||0);byEmail[k].allPaid=byEmail[k].allPaid&&!!p.paid;});
                  return Object.values(byEmail).filter(p=>!p.allPaid).reduce((s,p)=>s+p.amount,0);
                })()},-</span>
              </div>
              <div style={S.table}>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 50px 60px 60px",padding:"10px 14px",borderBottom:"1px solid #333",fontSize:9,color:"#444",textTransform:"uppercase",letterSpacing:"0.1em" }}>
                  <span>Navn</span><span>Lodd</span><span>Beløp</span><span>Status</span>
                </div>
                {(()=>{
                  // Slå sammen per e-post, vis kun ubetalte
                  const byEmail = {};
                  participants.forEach(p=>{
                    const key=(p.email||p.name||"").toLowerCase()||p.id;
                    if(!byEmail[key]) byEmail[key]={ ...p,tickets:0,amount:0,allPaid:true,ids:[] };
                    byEmail[key].tickets+=(p.tickets||0);
                    byEmail[key].amount+=(p.amount||0);
                    byEmail[key].allPaid=byEmail[key].allPaid&&!!p.paid;
                    byEmail[key].ids.push(p.id);
                    byEmail[key].name=p.name; byEmail[key].email=p.email;
                  });
                  return Object.values(byEmail).filter(p=>!p.allPaid).sort((a,b)=>(b.tickets||0)-(a.tickets||0)).map(p=>(
                    <div key={p.email||p.id} style={{ display:"grid",gridTemplateColumns:"1fr 50px 60px 80px",padding:"10px 14px",borderBottom:"1px solid #2a2a2a",alignItems:"center" }}>
                      <span style={S.tableName}>
                        <span style={S.tableAvatar}>{p.name?.slice(0,2).toUpperCase()}</span>
                        <span>
                          <span style={{ display:"block",fontSize:13,color:"#f5efe0" }}>{p.name}</span>
                          {p.email&&<span style={{ fontSize:10,color:"#444" }}>{p.email}</span>}
                        </span>
                      </span>
                      <span style={S.tableCell}>{p.tickets}</span>
                      <span style={{ ...S.tableCell,color:"#e57373" }}>{p.amount},-</span>
                      <span onClick={async()=>{ await Promise.all(p.ids.map(id=>updateDoc(doc(db,"participants",id),{paid:true}))); }}
                        style={{ ...S.tableStatus,background:"#2a0d0d",color:"#e57373",cursor:"pointer",fontSize:11,padding:"4px 6px" }}>
                        ⏳ Marker betalt
                      </span>
                    </div>
                  ));
                })()}
              </div>
              {/* Betalte deltakere — kan angres */}
              {(()=>{
                const byEmail = {};
                participants.forEach(p=>{
                  const key=(p.email||p.name||"").toLowerCase()||p.id;
                  if(!byEmail[key]) byEmail[key]={ ...p,tickets:0,amount:0,allPaid:true,ids:[] };
                  byEmail[key].tickets+=(p.tickets||0); byEmail[key].amount+=(p.amount||0);
                  byEmail[key].allPaid=byEmail[key].allPaid&&!!p.paid;
                  byEmail[key].ids.push(p.id);
                  byEmail[key].name=p.name; byEmail[key].email=p.email;
                });
                const paidList = Object.values(byEmail).filter(p=>p.allPaid);
                if (!paidList.length) return null;
                return (<>
                  <p style={{ ...S.sectionLabel,marginTop:16 }}>✅ Betalte – klikk for å angre</p>
                  {paidList.sort((a,b)=>b.tickets-a.tickets).map(p=>(
                    <div key={p.email||p.id} style={{ display:"grid",gridTemplateColumns:"1fr 50px 60px 80px",padding:"8px 14px",borderBottom:"1px solid #1a1a1a",alignItems:"center",opacity:0.65 }}>
                      <span style={{ fontSize:12,color:"#888" }}>{p.name}</span>
                      <span style={{ fontSize:12,color:"#555",textAlign:"right" }}>{p.tickets}</span>
                      <span style={{ fontSize:12,color:"#555",textAlign:"right" }}>{p.amount},-</span>
                      <span onClick={async()=>{ await Promise.all(p.ids.map(id=>updateDoc(doc(db,"participants",id),{paid:false}))); }}
                        style={{ fontSize:10,padding:"3px 6px",borderRadius:3,background:"#0d2a0d",color:"#4caf50",cursor:"pointer",textAlign:"center",border:"1px solid #1a4a1a" }}>
                        ✓ Angre
                      </span>
                    </div>
                  ))}
                </>);
              })()}
            </>
          )}
          <UbetalteHistorikk settings={settings}/>
        </div>
      )}
      {tab==="deltakere"&&(
        <div style={{ paddingBottom:20 }}>
          {/* v165: TestDataKnapp-blokken fjernet på mobil — T-knappen i
              topbaren har overtatt funksjonen. Komponenten beholdes for PC
              (compact-variant i PC-dashboardets topp-bar). */}
          <div style={S.table}>
            <div style={S.tableHead}><span>Navn</span><span>Lodd</span><span>Beløp</span><span>Status</span></div>
            {participants.length===0&&<p style={{ padding:"20px",textAlign:"center",color:"#333" }}>Ingen deltakere ennå</p>}
            {(()=>{
              // Slå sammen alle rader per e-post til én linje
              const byEmail = {};
              // Sorter deltakere etter kjøpstidspunkt for riktig rekkefølge i breakdown
              const sorted = [...participants].sort((a,b)=>(a.createdAt?.seconds||0)-(b.createdAt?.seconds||0));
              sorted.forEach(p=>{
                const key = (p.email||p.name||"").toLowerCase()||p.id;
                if (!byEmail[key]) byEmail[key] = { ...p, tickets:0, amount:0, allPaid:true, ids:[], breakdown:[] };
                byEmail[key].tickets += (p.tickets||0);
                byEmail[key].amount += (p.amount||0);
                byEmail[key].allPaid = byEmail[key].allPaid && !!p.paid;
                byEmail[key].ids.push(p.id);
                byEmail[key].breakdown.push(p.tickets||0); // logg hvert enkelt kjøp
                byEmail[key].name = p.name;
                byEmail[key].email = p.email;
              });
              const merged = Object.values(byEmail).sort((a,b)=>(b.tickets||0)-(a.tickets||0));
              return merged.map(p=>(
                <div key={p.email||p.id} style={S.tableRow}>
                  <span style={S.tableName}>
                    <span style={S.tableAvatar}>{p.name?.slice(0,2).toUpperCase()}</span>
                    <span>
                      <span style={{ display:"block",fontSize:13,color:"#f5efe0" }}>{p.name}</span>
                      {p.email&&<span style={{ fontSize:10,color:"#444" }}>{p.email}</span>}
                      {p.breakdown.length>1&&(
                        <span style={{ fontSize:12,color:"#666",display:"inline",marginLeft:6 }}>
                          ({p.breakdown.join("+")} )
                        </span>
                      )}
                    </span>
                  </span>
                  <span style={S.tableCell}>{p.tickets}</span>
                  <span style={S.tableCell}>{p.amount},-</span>
                  <span onClick={async()=>{
                    const newPaid = !p.allPaid;
                    await Promise.all(p.ids.map(id=>updateDoc(doc(db,"participants",id),{ paid:newPaid })));
                  }} style={{ ...S.tableStatus,background:p.allPaid?"#0d2a0d":"#2a0d0d",color:p.allPaid?"#4caf50":"#e57373",cursor:"pointer" }}>
                    {p.allPaid?"✓":"⏳"}
                  </span>
                </div>
              ));
            })()}
          </div>
        </div>
      )}
      {tab==="premier"&&<PrizeManager onPrizeChange={setPrizes}/>}
      {tab==="regnskap"&&(
        <div>
          <Regnskap participants={participants} prizes={prizes} settings={settings}/>
          <KredittOversikt pcLayout={false}/>
          <div style={{ borderTop:"1px solid #3a3a3a",margin:"0 16px" }}/>
          <Statistikk/>
        </div>
      )}
      {/* v147: mobil beholder to-trinns "innstillinger"-hierarki. Fallback:
          hvis tab er en settings-id (f.eks. satt på PC før resize til mobil),
          vis SettingsPanel med den sub-fanen så innholdet ikke blir blankt. */}
      {tab==="innstillinger"&&<SettingsPanel lotteryState={lotteryState} onDataChanged={()=>{}}/>}
      {SETTINGS_TAB_IDS.includes(tab)&&<SettingsPanel lotteryState={lotteryState} onDataChanged={()=>{}} settingsTab={tab} setSettingsTab={setTab}/>}

      {/* v162: ticker-bottom-sheet — åpnes via 📢-knappen i App-root-topbaren
          (vl-open-ticker). Backdrop z 1000, sheet z 1001 (emoji-popupen i
          TickerPublisher ligger på z 99999, altså over). Alltid rendret for
          glide-animasjon; translateY(100%) skjuler den under viewportet.
          Samme visuelle mønster som kjøps-sheeten i ParticipantView. */}
      <div
        onClick={()=>setShowTickerSheet(false)}
        style={{
          position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:1000,
          opacity: showTickerSheet ? 1 : 0,
          pointerEvents: showTickerSheet ? "auto" : "none",
          transition:"opacity 0.25s ease",
        }}/>
      <div style={{
        position:"fixed", bottom:0, left:0, right:0, zIndex:1001,
        background:"#0d0d0d", borderTop:"1px solid #3a3a3a",
        borderRadius:"20px 20px 0 0", maxHeight:"90svh", overflowY:"auto",
        padding:`12px 16px calc(16px + env(safe-area-inset-bottom, 0px))`,
        boxShadow:"0 -8px 30px rgba(0,0,0,0.6)",
        transform: showTickerSheet ? "translateY(0)" : "translateY(100%)",
        transition:"transform 0.3s cubic-bezier(0.32,0.72,0,1)",
      }}>
        <div style={{ width:40, height:4, borderRadius:2, background:"#444", margin:"0 auto 14px" }}/>
        {/* v163: Lukk-knapp fjernet — backdrop-klikk lukker. */}
        <div style={{ marginBottom:12 }}>
          <span style={{ fontSize:15, fontWeight:"bold", color:"#C9A84C" }}>📢 Ticker-melding</span>
        </div>
        {/* v163: stackVertical (felt i full bredde over knapper) + ingen ✕-slett. */}
        <TickerPublisher lotteryState={lotteryState} stackVertical={true} showClearButton={false}/>
      </div>
    </div>
  );
}

// ─── TREKNING-SKJERM (arrangør) ───────────────────────────────────────────────


// ─── LODDTREKNING ─────────────────────────────────────────────────────────────
// ─── NY LOTTERY DRAW (HATT-TREKNING v8) ───────────────────────────────────
// Lodd ramler ned i magiker-hatt, hatten rister, vinnerlodd flyr ut i spiral
function LotteryDraw({ winnerName, winnerTicketNum, onSpinDone, heightOverride, totalTickets, availableTickets, countdown, viewMode = "player-mobile" }) {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    phase: 'idle', phaseStart: 0,
    glitter: [], rainTix: [], winTix: null,
    hatShake: 0, raf: null, audioCtx: null,
    hatImg: null, hatLoaded: false,
    W: 0, H: 0, hatCX: 0, hatCY: 0, hatR: 0,
    hatCrownH: 0, hatCrownR: 0, hatOpeningY: 0,
    pendingStart: false,
    doneCalled: false,
    winnerNum: null,
    ticketCount: 47,
  });
  const startedRef = useRef(false);

  useEffect(() => {
    const s = stateRef.current;

    // Last hat-bildet
    const img = new Image();
    img.onload = () => {
      s.hatLoaded = true;
      s.needsResize = true;  // Sett flagg slik at loop kaller resize neste frame
      // I tillegg: trigge en direkte resize via rAF for å være sikker.
      // Dette dekker tilfellet hvor bildet lastes før loop starter.
      requestAnimationFrame(() => {
        if (typeof resize === "function") resize();
      });
    };
    img.src = '/magic_hat.png';
    s.hatImg = img;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    function getAC() {
      // Bruk global AudioContext så én aktivering dekker alle komponenter
      const ac = getAudioCtx();
      if (ac) s.audioCtx = ac;
      return ac;
    }

    function playMagicSparkle() {
      try {
        const ac = getAC();
        [1568, 2093, 2794, 3520].forEach((f, i) => {
          const o = ac.createOscillator(), g = ac.createGain();
          o.type='sine'; o.frequency.value=f;
          const t = ac.currentTime + i * 0.08;
          g.gain.setValueAtTime(0, t);
          g.gain.linearRampToValueAtTime(0.08, t+0.02);
          g.gain.exponentialRampToValueAtTime(0.001, t+0.4);
          o.connect(g); g.connect(ac.destination);
          o.start(t); o.stop(t+0.4);
        });
      } catch(e) {}
    }
    function playRainSound() {
      try {
        const ac = getAC(), sr = ac.sampleRate;
        for (let i = 0; i < 20; i++) {
          const t = ac.currentTime + Math.random() * 2.0;
          const blen = Math.floor((0.02 + Math.random() * 0.04) * sr);
          const buf = ac.createBuffer(1, blen, sr);
          const d = buf.getChannelData(0);
          for (let j = 0; j < blen; j++) {
            const env = Math.sin(Math.PI * j / blen);
            d[j] = (Math.random() * 2 - 1) * env * 0.5;
          }
          const src = ac.createBufferSource(); src.buffer = buf;
          const bp = ac.createBiquadFilter(); bp.type = 'bandpass';
          bp.frequency.value = 3000 + Math.random() * 3000; bp.Q.value = 0.6;
          const g = ac.createGain(); g.gain.value = 0.07 + Math.random() * 0.05;
          src.connect(bp); bp.connect(g); g.connect(ac.destination);
          src.start(t);
        }
      } catch(e) {}
    }
    function playShake() {
      try {
        const ac = getAC(), sr = ac.sampleRate;
        for (let i = 0; i < 12; i++) {
          const t = ac.currentTime + 0.05 + i * 0.08 + Math.random() * 0.04;
          const blen = Math.floor(0.05 * sr);
          const buf = ac.createBuffer(1, blen, sr);
          const d = buf.getChannelData(0);
          for (let j = 0; j < blen; j++) {
            const env = Math.exp(-j / blen * 3);
            d[j] = (Math.random() * 2 - 1) * env * 0.7;
          }
          const src = ac.createBufferSource(); src.buffer = buf;
          const lp = ac.createBiquadFilter(); lp.type = 'lowpass';
          lp.frequency.value = 800 + Math.random() * 600;
          const g = ac.createGain(); g.gain.value = 0.09 + Math.random() * 0.05;
          src.connect(lp); lp.connect(g); g.connect(ac.destination);
          src.start(t);
        }
      } catch(e) {}
    }
    function playWhoosh() {
      try {
        const ac = getAC(), now = ac.currentTime;
        const o = ac.createOscillator(), g = ac.createGain();
        o.type='sine';
        o.frequency.setValueAtTime(150, now);
        o.frequency.exponentialRampToValueAtTime(1500, now + 1.2);
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(0.12, now + 0.2);
        g.gain.linearRampToValueAtTime(0.1, now + 1.0);
        g.gain.exponentialRampToValueAtTime(0.001, now + 1.4);
        o.connect(g); g.connect(ac.destination);
        o.start(now); o.stop(now + 1.4);
        [2400, 3200, 4000].forEach((f, i) => {
          const o2 = ac.createOscillator(), g2 = ac.createGain();
          o2.type='sine'; o2.frequency.value=f;
          const t = now + i * 0.25;
          g2.gain.setValueAtTime(0, t);
          g2.gain.linearRampToValueAtTime(0.04, t + 0.05);
          g2.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
          o2.connect(g2); g2.connect(ac.destination);
          o2.start(t); o2.stop(t + 0.5);
        });
      } catch(e) {}
    }
    function playDing() {
      try {
        const ac = getAC(), t = ac.currentTime;
        [1568, 2093].forEach((f, i) => {
          const o = ac.createOscillator(), g = ac.createGain();
          o.type='sine'; o.frequency.value=f;
          const st = t + i * 0.1;
          g.gain.setValueAtTime(0, st);
          g.gain.linearRampToValueAtTime(0.15, st+0.01);
          g.gain.exponentialRampToValueAtTime(0.001, st+0.6);
          o.connect(g); g.connect(ac.destination);
          o.start(st); o.stop(st+0.6);
        });
      } catch(e) {}
    }

    function resize() {
      const rect = canvas.getBoundingClientRect();
      s.W = rect.width; s.H = rect.height;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = s.W * dpr;
      canvas.height = s.H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Gullramme tegnes inne i canvas for player-mobile (full-bleed)
      s.goldBorder = (viewMode === "player-mobile" || viewMode === "organizer-mobile") ? 6 : 0;
      const isPCView = !(viewMode === "player-mobile" || viewMode === "organizer-mobile");
      // PC: hatten skal være tydelig stor og helt klistret nederst.
      // Beregnes som min av: 16% av høyde, 16% av bredde, eller 240 px hard cap.
      // Mobil: original — 32% bredde, 14% høyde.
      s.hatR = isPCView
        ? Math.min(s.W * 0.16, s.H * 0.16, 240)
        : Math.min(s.W * 0.32, s.H * 0.14);
      s.hatCrownR = s.hatR * 0.68;
      s.hatCrownH = s.hatR * 1.35;
      s.hatCX = s.W / 2;
      // Hatt-bilde: hatHeight = hatR*2.6 * imgAspect
      // hatBottom = imgY + hatHeight, og imgY = hatOpeningY - hatHeight*0.29
      // Så hatBottom = hatOpeningY + hatHeight * 0.71
      //              = (hatCY - hatR*0.675) + (hatR*2.6 * imgAspect * 0.71)
      //              = hatCY + hatR*(1.846*imgAspect - 0.675)
      // drawHat() flytter bildet 12% ned (transparentBottomRatio) for å matche
      // den visuelle bunnen i magic_hat.png. Vi kompenserer med transparentShift
      // slik at den VISUELLE bunnen lander på s.H - 10 (ikke bilde-bunnen).
      // Hvis bildet ikke er lastet ennå, bruk standard plassering (rekonstrueres ved onload).
      const imgAspect = (s.hatImg && s.hatImg.naturalHeight && s.hatImg.naturalWidth)
        ? s.hatImg.naturalHeight / s.hatImg.naturalWidth
        : 1.0;
      const hatBottomFactor = 1.846 * imgAspect - 0.675;
      const transparentBottomRatio = 0.12;
      const hatHeightLocal = s.hatR * 2.6 * imgAspect;
      const transparentShift = hatHeightLocal * transparentBottomRatio;
      s.hatCY = isPCView
        ? s.H - 10 - s.hatR * hatBottomFactor + transparentShift
        : (viewMode === "player-mobile" || viewMode === "organizer-mobile") ? s.H * 0.88 : s.H * 0.78;
      s.hatOpeningY = s.hatCY - s.hatCrownH * 0.5;
    }

    function spawnGlitter(x, y, count, speed) {
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const sp = speed * (0.4 + Math.random() * 0.8);
        s.glitter.push({
          x, y,
          vx: Math.cos(angle) * sp, vy: Math.sin(angle) * sp - 0.5,
          life: 1, rot: Math.random() * Math.PI * 2,
          rotSpd: (Math.random() - 0.5) * 0.3,
          size: 2 + Math.random() * 2,
          color: ['#FFD700','#FFE566','#FFA500','#fff5a0'][Math.floor(Math.random()*4)],
        });
      }
    }
    function updateGlitter() {
      s.glitter.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        p.vy += 0.08; p.vx *= 0.98;
        p.rot += p.rotSpd; p.life -= 0.015;
      });
      s.glitter = s.glitter.filter(p => p.life > 0);
    }
    function drawGlitter() {
      s.glitter.forEach(p => {
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.beginPath();
        for (let i = 0; i < 4; i++) {
          const a = (i / 4) * Math.PI * 2;
          ctx.lineTo(Math.cos(a) * p.size, Math.sin(a) * p.size);
          ctx.lineTo(Math.cos(a + Math.PI/4) * p.size * 0.35, Math.sin(a + Math.PI/4) * p.size * 0.35);
        }
        ctx.closePath(); ctx.fill();
        ctx.restore();
      });
    }

    function drawHat() {
      if (!s.hatLoaded) return;
      ctx.save();
      if (s.hatShake > 0) {
        ctx.translate((Math.random() - 0.5) * s.hatShake * 2, (Math.random() - 0.5) * s.hatShake * 0.8);
      }
      const imgAspect = s.hatImg.naturalHeight / s.hatImg.naturalWidth;
      const hatWidth = s.hatR * 2.6;
      const hatHeight = hatWidth * imgAspect;
      const openingRatioInImage = 0.290;
      const imgX = s.hatCX - hatWidth / 2;
      // Bildet (magic_hat.png) er kvadratisk og har transparent padding rundt
      // selve hatten. For at hattens FAKTISKE BUNN skal være ved s.H - 10 på PC,
      // må vi kompensere ved å la imgY (toppen av bildet) være litt LAVERE.
      // Empirisk anslag: ~12% av bildet er transparent under hatten.
      const transparentBottomRatio = 0.12;
      const isPCViewLodd = !(viewMode === "player-mobile" || viewMode === "organizer-mobile");
      const imgY = isPCViewLodd
        ? s.H - 10 - hatHeight + (hatHeight * transparentBottomRatio)
        : s.hatOpeningY - hatHeight * openingRatioInImage;

      ctx.save();
      ctx.globalAlpha = 0.65;
      const shadowY = imgY + hatHeight - 4;
      const shG = ctx.createRadialGradient(s.hatCX, shadowY, s.hatR * 0.2, s.hatCX, shadowY, s.hatR * 1.9);
      shG.addColorStop(0, 'rgba(0,0,0,0.9)');
      shG.addColorStop(0.5, 'rgba(0,0,0,0.4)');
      shG.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = shG;
      ctx.beginPath();
      ctx.ellipse(s.hatCX, shadowY, s.hatR * 1.6, s.hatR * 0.22, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      ctx.drawImage(s.hatImg, imgX, imgY, hatWidth, hatHeight);
      ctx.restore();
    }

    function spawnRain(count) {
      s.rainTix = [];
      const variants = [
        { fill:'#ede0be', stroke:'#C9A84C' },
        { fill:'#f5e8c0', stroke:'#a09050' },
        { fill:'#e8d5a0', stroke:'#8B6B1A' },
        { fill:'#fff5d0', stroke:'#C9A84C' },
      ];
      // v263: sampler fra GJENVÆRENDE loddnumre når lista finnes (vunne lodd
      // ekskludert), ellers fallback til 1..count. Vinneren legges alltid til.
      const avail = (Array.isArray(s.availableNums) && s.availableNums.length > 0)
        ? s.availableNums
        : null;
      const source = avail || Array.from({ length: count }, (_, i) => i + 1);
      const visibleCount = Math.min(source.length, 60);
      let numbers = [];
      if (source.length <= visibleCount) {
        numbers = source.slice();
      } else {
        const set = new Set();
        const step = source.length / (visibleCount - 1);
        for (let i = 0; i < visibleCount - 1; i++) {
          set.add(source[Math.max(0, Math.min(source.length - 1, Math.round(i * step)))]);
        }
        if (s.winnerNum) set.add(s.winnerNum);
        numbers = Array.from(set);
      }
      numbers.sort((a, b) => a - b);
      for (let i = 0; i < numbers.length; i++) {
        const delay = (i / numbers.length) * 1.8;
        const startX = s.hatCX + (Math.random() - 0.5) * s.hatR * 2.2;
        const startY = -s.hatR * 0.5 - Math.random() * s.H * 0.3;
        const endAngle = Math.random() * Math.PI * 2;
        const endR = Math.random() * s.hatCrownR * 0.55;
        const endX = s.hatCX + Math.cos(endAngle) * endR;
        const endY = s.hatOpeningY + Math.sin(endAngle) * s.hatR * 0.05;
        const variant = variants[Math.floor(Math.random() * variants.length)];
        s.rainTix.push({
          startX, startY, endX, endY, x: startX, y: startY,
          rot: Math.random() * Math.PI * 2,
          rotSpd: (Math.random() - 0.5) * 0.15,
          len: s.hatR * (0.28 + Math.random() * 0.18),
          thick: s.hatR * (0.10 + Math.random() * 0.04),
          fill: variant.fill, stroke: variant.stroke,
          num: numbers[i],
          wavePhase: Math.random() * Math.PI * 2,
          waveSpd: 0.08 + Math.random() * 0.06,
          delay, startTime: 0,
          fallDur: 1.0 + Math.random() * 0.5,
          state: 'waiting',
        });
      }
    }
    function updateRain(phaseTime) {
      s.rainTix.forEach(t => {
        if (t.state === 'waiting' && phaseTime >= t.delay) {
          t.state = 'falling'; t.startTime = phaseTime;
        }
        if (t.state === 'falling') {
          const elapsed = phaseTime - t.startTime;
          const u = Math.min(elapsed / t.fallDur, 1);
          const ease = u * u;
          t.x = t.startX + (t.endX - t.startX) * ease;
          t.y = t.startY + (t.endY - t.startY) * ease;
          t.x += Math.sin(t.wavePhase) * 1.5;
          t.wavePhase += t.waveSpd;
          t.rot += t.rotSpd;
          if (u >= 1) t.state = 'landed';
        }
      });
    }
    function drawRainTicket(t) {
      if (t.state === 'waiting' || t.state === 'landed') return;
      ctx.save();
      ctx.translate(t.x, t.y);
      ctx.rotate(t.rot);
      const wave = Math.sin(t.wavePhase) * t.len * 0.05;
      const len = t.len, thick = t.thick;
      ctx.save();
      ctx.globalAlpha = 0.2;
      ctx.fillStyle = '#000';
      ctx.fillRect(-len/2 + 1, -thick/2 + 1, len, thick);
      ctx.restore();
      ctx.fillStyle = t.fill;
      ctx.beginPath();
      ctx.moveTo(-len/2, -thick/2);
      ctx.quadraticCurveTo(0, -thick/2 + wave, len/2, -thick/2);
      ctx.lineTo(len/2, thick/2);
      ctx.quadraticCurveTo(0, thick/2 + wave, -len/2, thick/2);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = t.stroke;
      ctx.lineWidth = 0.5;
      ctx.stroke();
      if (thick > 5) {
        ctx.font = `bold ${thick * 0.55}px Georgia,serif`;
        ctx.fillStyle = 'rgba(90,50,0,0.7)';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(t.num, 0, 0);
      }
      ctx.restore();
    }

    function drawWinnerTicket() {
      const wt = s.winTix;
      if (!wt) return;
      ctx.save();
      ctx.translate(wt.x, wt.y);
      ctx.rotate(wt.rot);
      ctx.scale(wt.scale, wt.scale);
      const len = s.hatR * 1.2, thick = s.hatR * 0.45;

      if (wt.glow > 0) {
        const gr = len * 0.9;
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, gr);
        g.addColorStop(0, `rgba(255,215,80,${0.55 * wt.glow})`);
        g.addColorStop(0.5, `rgba(255,180,60,${0.25 * wt.glow})`);
        g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.ellipse(0, 0, gr, gr * 0.7, 0, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.save();
      ctx.globalAlpha = 0.4;
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.ellipse(0, thick/2 + 3, len/2 * 0.85, thick * 0.15, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      const paperG = ctx.createLinearGradient(0, -thick/2, 0, thick/2);
      paperG.addColorStop(0, '#fff5d0');
      paperG.addColorStop(0.5, '#f8e8b5');
      paperG.addColorStop(1, '#e8d090');
      ctx.fillStyle = paperG;
      const bend = 2;
      ctx.beginPath();
      ctx.moveTo(-len/2, -thick/2 + bend);
      ctx.quadraticCurveTo(-len/2 - 3, -thick/2, -len/2 + 3, -thick/2);
      ctx.lineTo(len/2 - 3, -thick/2);
      ctx.quadraticCurveTo(len/2 + 3, -thick/2, len/2, -thick/2 + bend);
      ctx.lineTo(len/2, thick/2 - bend);
      ctx.quadraticCurveTo(len/2 + 3, thick/2, len/2 - 3, thick/2);
      ctx.lineTo(-len/2 + 3, thick/2);
      ctx.quadraticCurveTo(-len/2 - 3, thick/2, -len/2, thick/2 - bend);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#8B6000'; ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.strokeStyle = '#C9A84C'; ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.rect(-len/2 + 5, -thick/2 + 5, len - 10, thick - 10);
      ctx.stroke();

      if (wt.showContent > 0) {
        ctx.save();
        ctx.globalAlpha = wt.showContent;
        ctx.fillStyle = '#8B6000';
        const cornerOff = thick * 0.18;
        [[-1,-1],[1,-1],[-1,1],[1,1]].forEach(([sx, sy]) => {
          ctx.save();
          ctx.translate(sx * (len/2 - cornerOff * 2), sy * (thick/2 - cornerOff));
          const sR = thick * 0.09;
          ctx.beginPath();
          for (let i = 0; i < 5; i++) {
            const a = -Math.PI/2 + (i / 5) * Math.PI * 2;
            const a2 = a + Math.PI/5;
            ctx.lineTo(Math.cos(a) * sR, Math.sin(a) * sR);
            ctx.lineTo(Math.cos(a2) * sR * 0.4, Math.sin(a2) * sR * 0.4);
          }
          ctx.closePath(); ctx.fill();
          ctx.restore();
        });
        ctx.textAlign='center'; ctx.textBaseline='middle';
        ctx.font = `italic ${thick * 0.14}px 'Montserrat','Playfair Display',Georgia,serif`;
        ctx.fillStyle = 'rgba(100,60,10,0.9)';
        // LODDNR-tekst plasseres i øvre del av loddet, men ikke helt øverst
        ctx.fillText('— LODDNR —', 0, -thick * 0.20);
        // Nummer-fonten: bruker Bebas Neue / Oswald (kondensert sans-serif) som
        // gir et mer klassisk loddtypografi-look enn Montserrat 900 italic.
        ctx.font = `700 ${thick * 0.55}px 'Bebas Neue', 'Oswald', 'Arial Narrow', Impact, sans-serif`;
        const numG = ctx.createLinearGradient(0, thick*0.05, 0, thick*0.35);
        numG.addColorStop(0, '#FFD700');
        numG.addColorStop(0.5, '#C9A84C');
        numG.addColorStop(1, '#8B6000');
        ctx.fillStyle = numG;
        // Nummeret plasseres lengre ned — godt under LODDNR-teksten.
        // v248: opp ~2 lokale px (≈4px på skjerm ved scale 2.1) for bedre sentrering.
        ctx.fillText(s.winnerNum || '?', 0, thick * 0.20 - 2);
        ctx.strokeStyle = '#5a3800'; ctx.lineWidth = 1.2;
        ctx.strokeText(s.winnerNum || '?', 0, thick * 0.20 - 2);
        ctx.restore();
      }
      ctx.restore();
    }

    function drawGoldBorder() {
      const bw = s.goldBorder;
      if (bw <= 0) return;
      const g = ctx.createLinearGradient(0, 0, s.W, s.H);
      g.addColorStop(0, '#fff5a0');
      g.addColorStop(0.12, '#c89010');
      g.addColorStop(0.28, '#ffe566');
      g.addColorStop(0.48, '#a87000');
      g.addColorStop(0.68, '#ffe566');
      g.addColorStop(0.84, '#c89010');
      g.addColorStop(1, '#fff5a0');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, s.W, bw);
      ctx.fillRect(0, s.H - bw, s.W, bw);
      ctx.fillRect(0, 0, bw, s.H);
      ctx.fillRect(s.W - bw, 0, bw, s.H);
      ctx.strokeStyle = '#5a3800';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(bw, bw, s.W - bw*2, s.H - bw*2);
      ctx.strokeStyle = '#5a3800';
      ctx.lineWidth = 1;
      ctx.strokeRect(0.5, 0.5, s.W - 1, s.H - 1);
    }

    function drawScene() {
      ctx.clearRect(0, 0, s.W, s.H);
      drawHat();
      s.rainTix.forEach(drawRainTicket);
      drawGlitter();
      drawWinnerTicket();
      drawGoldBorder();
    }

    function loop(ts) {
      // Re-kjør resize hvis hat-bildet nettopp ble lastet (for korrekt imgAspect)
      if (s.needsResize) {
        s.needsResize = false;
        resize();
      }
      // Sjekk om vi skal starte
      if (s.pendingStart && s.hatLoaded) {
        s.pendingStart = false;
        spawnRain(s.ticketCount);
        s.phase = 'rain';
        s.phaseStart = ts;
        playRainSound();
      }

      const phaseTime = (ts - s.phaseStart) / 1000;

      if (s.phase === 'idle') {
        updateGlitter();
      }
      else if (s.phase === 'rain') {
        updateRain(phaseTime);
        updateGlitter();
        s.rainTix.forEach(t => {
          if (t.state === 'landed' && !t._puffed) {
            t._puffed = true;
            spawnGlitter(t.endX, t.endY, 3, 1.5);
          }
        });
        const allLanded = s.rainTix.every(t => t.state === 'landed');
        if (allLanded || phaseTime > 3.5) {
          s.phase = 'settle'; s.phaseStart = ts;
        }
      }
      else if (s.phase === 'settle') {
        updateGlitter();
        if (phaseTime > 0.35) {
          s.phase = 'shake'; s.phaseStart = ts;
          playShake();
        }
      }
      else if (s.phase === 'shake') {
        const t = Math.min(phaseTime / 1.0, 1);
        s.hatShake = Math.sin(t * Math.PI) * 5;
        updateGlitter();
        if (Math.random() < 0.25) {
          spawnGlitter(s.hatCX + (Math.random() - 0.5) * s.hatR, s.hatOpeningY + (Math.random() - 0.5) * s.hatR * 0.2, 1, 1.5);
        }
        if (t >= 1) {
          s.hatShake = 0;
          s.phase = 'preFly'; s.phaseStart = ts;
          playMagicSparkle();
        }
      }
      else if (s.phase === 'preFly') {
        const t = Math.min(phaseTime / 0.4, 1);
        if (Math.random() < 0.6) {
          spawnGlitter(s.hatCX + (Math.random() - 0.5) * s.hatCrownR, s.hatOpeningY, 2, 2.5);
        }
        updateGlitter();
        if (t >= 1) {
          s.phase = 'flyUp'; s.phaseStart = ts;
          s.winTix = { x: s.hatCX, y: s.hatOpeningY, rot: 0, scale: 0.3, glow: 0.3, showContent: 0 };
          playWhoosh();
        }
      }
      else if (s.phase === 'flyUp') {
        const wt = s.winTix;
        const circleR = Math.min(s.W, s.H) * 0.22;
        const circleVR = circleR * 0.55;
        const bottomOfCircleY = circleVR * 2 + s.H * 0.04;
        const circleCX = s.hatCX;
        const circleCY = bottomOfCircleY - circleVR;
        const rise1End = 1.3, spiral2End = 3.3, approach3End = 4.3;

        if (phaseTime < rise1End) {
          const t = phaseTime / rise1End;
          const ease = 1 - Math.pow(1 - t, 2);
          wt.x = s.hatCX + (circleCX - s.hatCX) * ease;
          wt.y = s.hatOpeningY + (bottomOfCircleY - s.hatOpeningY) * ease;
          wt.scale = 0.3 + ease * 0.15;
          wt.rot = Math.sin(t * Math.PI) * 0.12;
          wt.glow = 0.2 + ease * 0.2;
          wt.showContent = 0;
        } else if (phaseTime < spiral2End) {
          const t = (phaseTime - rise1End) / (spiral2End - rise1End);
          const theta = Math.PI/2 + t * Math.PI * 4;
          wt.x = circleCX + Math.cos(theta) * circleR;
          wt.y = circleCY + Math.sin(theta) * circleVR;
          wt.scale = 0.45 + t * 0.1;
          const tangentAngle = Math.atan2(circleVR * Math.cos(theta), -circleR * Math.sin(theta));
          wt.rot = tangentAngle * 0.4;
          wt.glow = 0.4 + t * 0.2;
          wt.showContent = 0;
        } else if (phaseTime < approach3End) {
          const t = (phaseTime - spiral2End) / (approach3End - spiral2End);
          const ease = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
          const startX = circleCX, startY = circleCY + circleVR;
          const endX = s.W / 2, endY = s.H * 0.40 - 4; // v248: hele billetten 4px opp
          wt.x = startX + (endX - startX) * ease;
          wt.y = startY + (endY - startY) * ease;
          wt.scale = 0.55 + ease * 1.55;
          wt.rot = wt.rot * (1 - ease);
          wt.glow = 0.6 + ease * 0.4;
          wt.showContent = Math.min(1, t * 2.5);
        }

        updateGlitter();
        const trailProb = 0.3 + Math.min(1, phaseTime / approach3End) * 0.4;
        if (Math.random() < trailProb) {
          spawnGlitter(wt.x + (Math.random() - 0.5) * 8, wt.y + (Math.random() - 0.5) * 8, 1, 1);
        }

        if (phaseTime >= approach3End) {
          wt.x = s.W / 2; wt.y = s.H * 0.40 - 4; // v248: hele billetten 4px opp
          wt.rot = 0; wt.scale = 2.1; wt.glow = 1; wt.showContent = 1;
          s.phase = 'done'; s.phaseStart = ts;
          playDing();
          spawnGlitter(wt.x, wt.y, 30, 3);
          if (!s.doneCalled) {
            s.doneCalled = true;
            setTimeout(() => { if (onSpinDone) onSpinDone(); }, 100);
          }
        }
      }
      else if (s.phase === 'done') {
        updateGlitter();
        const wt = s.winTix;
        wt.y = s.H * 0.40 - 4 + Math.sin(phaseTime * 1.2) * 2; // v248: hele billetten 4px opp
        wt.rot = Math.sin(phaseTime * 0.9) * 0.02;
      }

      drawScene();
      s.raf = requestAnimationFrame(loop);
    }

    resize();
    window.addEventListener('resize', resize);
    s.raf = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('resize', resize);
      if (s.raf) cancelAnimationFrame(s.raf);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Start trekningen når winnerName kommer inn (eller når den endrer seg til en NY vinner).
  // Når winnerName blir null mellom trekninger, nullstilles også canvas til tom scene.
  const lastWinnerRef = useRef(null);
  useEffect(() => {
    const s = stateRef.current;

    // Hvis winnerName blir null (mellom trekninger), tøm canvas helt
    if (!winnerName) {
      s.phase = 'idle';
      s.phaseStart = 0;
      s.glitter = [];
      s.rainTix = [];
      s.winTix = null;
      s.hatShake = 0;
      s.doneCalled = false;
      s.pendingStart = false;
      startedRef.current = false;
      lastWinnerRef.current = null;
      return;
    }

    // Lag en unik signatur basert på navn + loddnummer slik at også samme navn to ganger på rad trigger reset
    const sig = String(winnerName) + '|' + String(winnerTicketNum);
    if (lastWinnerRef.current === sig) return;
    lastWinnerRef.current = sig;

    // Nullstill ALL intern state slik at trekning 2, 3, 4... fungerer like bra som trekning 1
    s.phase = 'idle';
    s.phaseStart = 0;
    s.glitter = [];
    s.rainTix = [];
    s.winTix = null;
    s.hatShake = 0;
    s.doneCalled = false;
    s.pendingStart = true;
    s.winnerNum = winnerTicketNum || 42;
    s.ticketCount = totalTickets && totalTickets > 0 ? totalTickets : 47;
    // v263: gjenværende loddnumre (vunne lodd filtrert bort i LiveDrawView).
    // spawnRain sampler fra denne i stedet for 1..ticketCount når den finnes.
    s.availableNums = Array.isArray(availableTickets)
      ? availableTickets.filter((n) => Number.isFinite(n) && n > 0)
      : null;
    startedRef.current = true;

    // Aktiver audio context — bruk global så én aktivering dekker alle
    try {
      const ac = getAudioCtx();
      if (ac) s.audioCtx = ac;
    } catch(e) {}
  }, [winnerName, winnerTicketNum, totalTickets, availableTickets]);

  // Full-bleed canvas (gullramme tegnes inne i canvas) for player-mobile
  if (viewMode === "player-mobile" || viewMode === "organizer-mobile") {
    return (
      <div style={{
        position:'relative', width:'100%',
        height: heightOverride ? `${heightOverride}px` : '100vh',
        overflow:'hidden'
      }}>
        <canvas ref={canvasRef} style={{position:'absolute',inset:0,width:'100%',height:'100%',display:'block'}}/>
      </div>
    );
  }

  // Fallback: PC-visning — svart bakgrunn, ingen ramme, hatten klistret nederst
  return (
    <div style={{
      position:'relative',
      width:'100%',
      height: heightOverride ? `${heightOverride}px` : '100vh',
      background: '#000',
      overflow:'hidden'
    }}>
      {/* INGEN gullramme på PC — kun svart bakgrunn slik som Banditt og Lotto.
          Canvas dekker hele området. Hatten/lodd-objektet plasseres nederst i canvas.
          Nedtelling og gratulerer håndteres av parent (LiveDrawView/DrawScreen). */}
      <canvas ref={canvasRef} style={{position:'absolute',inset:0,width:'100%',height:'100%',display:'block'}}/>
    </div>
  );
}


// ─── LOTTO-KULE ───────────────────────────────────────────────────────────────
// Basert på lotto_prototype_v8.html.
// Samme prop-signatur som LotteryDraw slik at den er "drop-in replacement".
// ─── LottoDraw3D (hybrid: bilde + 3D canvas) ─────────────────────────────
// Lottokule med pseudo-3D fysikk, frostet glass-look, og roterende kuler.
// Bruker public/lotto_machine.png som bakgrunn.
// Kun for mobile viewModes (player-mobile, organizer-mobile).
function LottoDraw3D({ winnerName, winnerTicketNum, onSpinDone, heightOverride, totalTickets, availableTickets, countdown, viewMode = "player-mobile" }) {
  const isMobileLottoView = viewMode === "player-mobile" || viewMode === "organizer-mobile";
  const canvasRef = useRef(null);
  const stateRef = useRef({
    W: 0, H: 0,
    balls: [],
    phase: 'idle',          // idle | spinning | rising | tube | top
    phaseStart: 0,
    winner: null,
    raf: null,
    doneCalled: false,
    rotationDir: 1,
    rotationSwitchAt: 0,
    pendingStart: false,
  });
  const startedRef = useRef(false);
  const lastWinnerKey = useRef(null);
  const resetForKeyRef = useRef(null);
  const noWinnerResetRef = useRef(true); // v202b: reset-once-vakt for !winnerName (hindrer hopp per nedtellings-tick)

  // Hardkodede kalibreringer (justert for cropped 929×1023 bilde)
  const CFG = {
    sphere: { x: 50, y: 38, r: 33 },
    ballSizePct: 6.6,  // 10% større enn forrige (6.0 × 1.10)
    gravity: 0.15,
    turbulence: 0.5,   // v201: var 0.3 — mer kaos/agitasjon under spinning
    rotation: 0.6,     // v201: var 0.4 — mer rotasjons-energi
    rotationMode: 'alternating',
    spinDuration: 6,   // v201: var 4 — spinn varer 2 sek lenger
    tube: { topY: 2, insideY: 62 },
  };
  const MAX_BALLS = 50;             // maks antall fysiske kuler i maskinen
  const NUM_BALLS_LIMIT = 1000;     // maks antall lodd vi støtter

  const bgImgRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.onload = () => { bgImgRef.current = img; setImgLoaded(true); };
    img.onerror = () => { console.warn('lotto_machine.png kunne ikke lastes'); setImgLoaded(true); };
    img.src = '/lotto_machine.png?v=3';
  }, []);

  useEffect(() => {
    const s = stateRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let resizeSettleTimer = null;
    function resize() {
      const oldW = s.W, oldH = s.H;
      const rect = canvas.getBoundingClientRect();
      s.W = rect.width; s.H = rect.height;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = s.W * dpr;
      canvas.height = s.H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // v200: kule-posisjoner er absolutte canvas-koordinater. Når dimensjonene
      // endrer seg (mobil-adresselinje kollapser / rotasjon) flyttes glasskula,
      // men kulene blir stående → "henger i løse luften" (Bug 3). Re-settle dem
      // til bunnen av den nye kula. Kun i 'idle' (statisk fase der fysikken er av);
      // spinning/done re-settler seg selv via fysikk-loopen. Debounced.
      // v202: IKKE re-settle under nedtelling — fasen er 'idle' da også, og en
      // synkron simulateSettle ville få kulene til å HOPPE. s.countdown speiles
      // fra prop (resize har stale closure pga deps []).
      const countdownAktiv = s.countdown != null && s.countdown > 0;
      if (oldW > 0 && s.phase === 'idle' && !countdownAktiv && s.balls.length &&
          (Math.abs(s.W - oldW) > 10 || Math.abs(s.H - oldH) > 10)) {
        if (resizeSettleTimer) clearTimeout(resizeSettleTimer);
        resizeSettleTimer = setTimeout(() => {
          if (s.simulateSettle) s.simulateSettle(s.balls, 200);
        }, 150);
      }
    }

    // Maskinens dimensjoner og posisjon i canvas:
    // - Mobil: bredde = hele canvas-bredden (s.W), høyde = bredde × bilde-aspect.
    //   Klistret nederst — bunn = s.H, topp = s.H - maskinHøyde.
    // - PC: høyde = 80% av canvas-høyden, bredde = høyde × 929/1023 (smalere).
    //   Klistret nederst og sentrert horisontalt — matcher DOM-plasseringen.
    const isPCViewLotto = !isMobileLottoView;
    function getMachineHeight() {
      if (isPCViewLotto) return s.H * 0.70;
      return s.W * 1023 / 929;
    }
    function getMachineWidth() {
      if (isPCViewLotto) return getMachineHeight() * 929 / 1023;
      return s.W;
    }
    function getMachineLeftX() {
      if (isPCViewLotto) return (s.W - getMachineWidth()) / 2;
      return 0;
    }
    function getMachineTopY() { return s.H - getMachineHeight(); }

    function getSphere() {
      const mLeft = getMachineLeftX();
      const mTop = getMachineTopY();
      const mW = getMachineWidth();
      const mH = getMachineHeight();
      return {
        cx: mLeft + mW * CFG.sphere.x / 100,
        // sphere.y er nå PROSENT av MASKINEN (ikke canvas)
        cy: mTop + mH * CFG.sphere.y / 100,
        r: mW * CFG.sphere.r / 100,
      };
    }
    function getBallRadius() { return getMachineWidth() * CFG.ballSizePct / 100 / 2; }
    function getTubeInsideY() {
      const mTop = getMachineTopY();
      const mH = getMachineHeight();
      return mTop + mH * CFG.tube.insideY / 100;
    }
    function getTubeTopY() {
      const mTop = getMachineTopY();
      const mH = getMachineHeight();
      return mTop + mH * CFG.tube.topY / 100;
    }

    function createBalls(numbers) {
      const balls = [];
      const sphere = getSphere();
      const r = getBallRadius();
      const innerR = sphere.r - r - 2;
      // Plasser kulene tilfeldig i nedre halvdel — settling-simulering nedenfor
      // får dem til å falle realistisk til bunnen
      for (let i = 0; i < numbers.length; i++) {
        let px, py, pz, tries = 0;
        do {
          const u = 0.3 + Math.random() * 0.7;  // nedre 70% av sfæren
          const phi = Math.random() * Math.PI * 2;
          const rr = Math.cbrt(Math.random()) * innerR * 0.9;
          const sinTheta = Math.sqrt(1 - u*u);
          px = sphere.cx + rr * sinTheta * Math.cos(phi);
          py = sphere.cy + rr * u;
          pz = rr * sinTheta * Math.sin(phi);
          tries++;
        // eslint-disable-next-line no-loop-func -- .some()-callbacken er synkron og lever ikke past iterasjonen; px/py/pz leses umiddelbart
        } while (tries < 30 && balls.some(b => {
          const dx = b.x - px, dy = b.y - py, dz = b.z - pz;
          return Math.sqrt(dx*dx + dy*dy + dz*dz) < r * 2.0;
        }));
        balls.push({
          id: i, n: numbers[i],
          x: px, y: py, z: pz,
          vx: 0, vy: 0, vz: 0,
          r: r,
          pitch: 0, yaw: 0,
          state: 'sphere',
        });
      }
      return balls;
    }

    // Simulerer fysikk i et antall frames slik at kulene faller naturlig til
    // bunnen og stabiliserer som en ekte haug. Brukes ved oppstart og reset.
    // Lagres på s slik at den er tilgjengelig fra winner-effect også.
    s.simulateSettle = function simulateSettle(ballArray, frames = 200) {
      const sphere = getSphere();
      const g = CFG.gravity;
      for (let f = 0; f < frames; f++) {
        // Apply gravity + damping for hver kule
        for (const b of ballArray) {
          b.vy += g;
          b.x += b.vx; b.y += b.vy; b.z += b.vz;
          // Damping (mister fart)
          b.vx *= 0.9; b.vy *= 0.9; b.vz *= 0.9;
          // Sfære-vegg kollisjon
          const dx = b.x - sphere.cx;
          const dy = b.y - sphere.cy;
          const dz = b.z;
          const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
          const maxDist = sphere.r - b.r - 1;
          if (dist > maxDist) {
            const nx = dx / dist, ny = dy / dist, nz = dz / dist;
            b.x = sphere.cx + nx * maxDist;
            b.y = sphere.cy + ny * maxDist;
            b.z = nz * maxDist;
            const dot = b.vx * nx + b.vy * ny + b.vz * nz;
            // Lav elasticitet (kuler "lander" og blir liggende)
            b.vx -= 2 * dot * nx * 0.3;
            b.vy -= 2 * dot * ny * 0.3;
            b.vz -= 2 * dot * nz * 0.3;
          }
        }
        // Kule-mot-kule kollisjon
        for (let i = 0; i < ballArray.length; i++) {
          for (let j = i + 1; j < ballArray.length; j++) {
            const a = ballArray[i], c = ballArray[j];
            const dx = c.x - a.x, dy = c.y - a.y, dz = c.z - a.z;
            const d = Math.sqrt(dx*dx + dy*dy + dz*dz);
            const minD = a.r + c.r;
            if (d < minD && d > 0) {
              const overlap = (minD - d) / 2;
              const nx = dx / d, ny = dy / d, nz = dz / d;
              a.x -= nx * overlap; a.y -= ny * overlap; a.z -= nz * overlap;
              c.x += nx * overlap; c.y += ny * overlap; c.z += nz * overlap;
              const dvx = c.vx - a.vx, dvy = c.vy - a.vy, dvz = c.vz - a.vz;
              const vn = dvx * nx + dvy * ny + dvz * nz;
              if (vn < 0) {
                const impulse = vn * 0.5;  // lav elasticitet
                a.vx += impulse * nx; a.vy += impulse * ny; a.vz += impulse * nz;
                c.vx -= impulse * nx; c.vy -= impulse * ny; c.vz -= impulse * nz;
              }
            }
          }
        }
      }
      // Etter settling, nullstill alle hastigheter
      for (const b of ballArray) {
        b.vx = 0; b.vy = 0; b.vz = 0;
      }
    };

    function updateBallPhysics(now) {
      const sphere = getSphere();
      const g = CFG.gravity;
      const turb = CFG.turbulence;
      const dt = 1;
      // v202b: kulene skal være I BEVEGELSE (full agitasjon: turbulens + rotasjon)
      // helt til vinneren er NESTEN på toppen av røret — ikke fryse og ikke kollapse
      // tidlig. Full agitasjon under spinning OG mens vinneren stiger (rising +
      // tube til ~85%). Først når vinneren er nesten på topp slår kollaps inn
      // (kun gravitasjon + damping), så kulene faller ned. Vinner-kulen har
      // state≠'sphere' under rising/tube/top → skippes av sjekken i løkken.
      const winnerLeftSphere = !!s.winner && s.winner.state !== 'sphere';
      const winnerNearTop = !!s.winner && (s.winner.state === 'top' ||
        (s.winner.state === 'tube' && (s.winner.tubeProgress || 0) >= 0.85));
      const isSpinning = s.phase === 'spinning' || (winnerLeftSphere && !winnerNearTop);
      const isCollapsing = winnerNearTop;
      if (!isSpinning && !isCollapsing) return;
      if (isSpinning && CFG.rotationMode === 'alternating') {
        if (now >= s.rotationSwitchAt) {
          s.rotationDir *= -1;
          s.rotationSwitchAt = now + 1500 + Math.random() * 500;
        }
      }
      for (const b of s.balls) {
        if (b.state !== 'sphere') continue;
        b.vy += g;
        // I hvile: ingen turbulens (kuler ligger stille). Under spin/rising/tube: kraftig.
        const turbFreq = isSpinning ? 0.18 : 0;
        if (Math.random() < turbFreq) {
          const intensity = isSpinning ? turb * 6 : turb;
          b.vx += (Math.random() - 0.5) * intensity;
          b.vy += (Math.random() - 0.5) * intensity;
          b.vz += (Math.random() - 0.5) * intensity;
          if (isSpinning) b.vy -= Math.random() * 0.7;
        }
        if (isSpinning && CFG.rotation > 0) {
          const dx = b.x - sphere.cx;
          const dz = b.z;
          const distXZ = Math.sqrt(dx*dx + dz*dz);
          if (distXZ > 1) {
            const tx = -dz / distXZ * s.rotationDir;
            const tz = dx / distXZ * s.rotationDir;
            const forceScale = Math.min(1, distXZ / sphere.r);
            b.vx += tx * CFG.rotation * forceScale * 0.5;
            b.vz += tz * CFG.rotation * forceScale * 0.5;
          }
        }
        b.x += b.vx * dt;
        b.y += b.vy * dt;
        b.z += b.vz * dt;
        const damp = isSpinning ? 0.995 : 0.92;
        b.vx *= damp; b.vy *= damp; b.vz *= damp;
        // Roter kulen basert på hastighet
        const rollFactor = 0.05;
        b.yaw += b.vx * rollFactor;
        b.pitch += b.vy * rollFactor;
        b.yaw += b.vz * rollFactor * 0.3;
        // Sfære-vegg kollisjon
        const dx2 = b.x - sphere.cx;
        const dy2 = b.y - sphere.cy;
        const dz2 = b.z;
        const dist2 = Math.sqrt(dx2*dx2 + dy2*dy2 + dz2*dz2);
        const maxDist = sphere.r - b.r - 1;
        if (dist2 > maxDist) {
          const nx = dx2 / dist2;
          const ny = dy2 / dist2;
          const nz = dz2 / dist2;
          b.x = sphere.cx + nx * maxDist;
          b.y = sphere.cy + ny * maxDist;
          b.z = nz * maxDist;
          const dot = b.vx * nx + b.vy * ny + b.vz * nz;
          const wallElasticity = isSpinning ? 0.95 : 0.85;
          b.vx -= 2 * dot * nx * wallElasticity;
          b.vy -= 2 * dot * ny * wallElasticity;
          b.vz -= 2 * dot * nz * wallElasticity;
        }
      }
      // Kule-mot-kule kollisjoner
      const balls = s.balls.filter(b => b.state === 'sphere');
      for (let i = 0; i < balls.length; i++) {
        for (let j = i + 1; j < balls.length; j++) {
          const a = balls[i], c = balls[j];
          const dx = c.x - a.x;
          const dy = c.y - a.y;
          const dz = c.z - a.z;
          const d = Math.sqrt(dx*dx + dy*dy + dz*dz);
          const minD = a.r + c.r;
          if (d < minD && d > 0) {
            const overlap = (minD - d) / 2;
            const nx = dx / d, ny = dy / d, nz = dz / d;
            a.x -= nx * overlap; a.y -= ny * overlap; a.z -= nz * overlap;
            c.x += nx * overlap; c.y += ny * overlap; c.z += nz * overlap;
            const dvx = c.vx - a.vx;
            const dvy = c.vy - a.vy;
            const dvz = c.vz - a.vz;
            const vn = dvx * nx + dvy * ny + dvz * nz;
            if (vn < 0) {
              const elasticity = s.phase === 'spinning' ? 1.0 : 0.9;
              const impulse = vn * elasticity;
              a.vx += impulse * nx; a.vy += impulse * ny; a.vz += impulse * nz;
              c.vx -= impulse * nx; c.vy -= impulse * ny; c.vz -= impulse * nz;
            }
          }
        }
      }
    }

    function updateWinner(now) {
      const winner = s.winner;
      if (!winner) return;
      if (winner.state === 'rising') {
        const tubeInsideY = getTubeInsideY();
        const sphere = getSphere();
        const targetX = sphere.cx;
        const targetY = tubeInsideY;
        const targetZ = 0;
        const speed = 0.06;
        winner.x += (targetX - winner.x) * speed;
        winner.y += (targetY - winner.y) * speed;
        winner.z += (targetZ - winner.z) * speed;
        winner.vx *= 0.85; winner.vy *= 0.85; winner.vz *= 0.85;
        winner.pitch *= 0.9; winner.yaw *= 0.9;
        if (Math.abs(winner.x - targetX) < 2 && Math.abs(winner.y - targetY) < 2 && Math.abs(winner.z) < 2) {
          winner.state = 'tube';
          s.phase = 'tube';
          s.phaseStart = now;
          winner.tubeProgress = 0;
          winner.z = 0;
        }
      } else if (winner.state === 'tube') {
        winner.tubeProgress = Math.min(1, (now - s.phaseStart) / 1500);
        const tubeBottomY = getTubeInsideY();
        const tubeTopY = getTubeTopY();
        const sphere = getSphere();
        winner.x = sphere.cx;
        winner.z = 0;
        winner.pitch = 0; winner.yaw = 0;
        const easedT = 1 - Math.pow(1 - winner.tubeProgress, 2);
        // Sluttposisjon: bunn av kule sitter på rør-toppen → senter ved (tubeTopY - r)
        const r = winner.r;
        const targetY = tubeTopY - r;
        winner.y = tubeBottomY + (targetY - tubeBottomY) * easedT;
        if (winner.tubeProgress >= 1) {
          winner.state = 'top';
          s.phase = 'done';
          s.phaseStart = now;
          // Lyden stoppes automatisk av drawFrame ved phase-endring
          if (!s.doneCalled) {
            s.doneCalled = true;
            if (onSpinDone) setTimeout(onSpinDone, 800);
          }
        }
      }
    }

    function drawNumberAt(b, r, nx, ny, nz) {
      const tx = b.x + nx * r * 0.85;
      const ty = b.y + ny * r * 0.85;
      const numScale = nz;
      const fontSize = Math.round(r * 1.05 * numScale);
      if (fontSize < 4) return;
      ctx.save();
      const tilt = Math.atan2(ny * 0.3, nx * 0.3 + 0.5) * 0.2;
      ctx.translate(tx, ty);
      ctx.rotate(tilt);
      ctx.font = `700 ${fontSize}px 'Oswald', 'Arial Black', sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'rgba(0,0,0,0.25)';
      ctx.fillText(String(b.n), 0.5, 0.5);
      ctx.fillStyle = '#1a1a1a';
      ctx.fillText(String(b.n), 0, 0);
      ctx.restore();
    }

    function drawRotatingNumber(b, r) {
      const cp = Math.cos(b.pitch), sp = Math.sin(b.pitch);
      const cy = Math.cos(b.yaw), sy = Math.sin(b.yaw);
      function rot(x, y, z) {
        const y1 = y * cp - z * sp;
        const z1 = y * sp + z * cp;
        const x2 = x * cy + z1 * sy;
        const z2 = -x * sy + z1 * cy;
        return { nx: x2, ny: y1, nz: z2 };
      }
      const sides = [
        rot(0, 0, 1), rot(1, 0, 0), rot(0, 0, -1), rot(-1, 0, 0),
      ];
      for (const sd of sides) {
        if (sd.nz > 0.05) drawNumberAt(b, r, sd.nx, sd.ny, sd.nz);
      }
    }

    function drawBall(b, isBehind) {
      const sphere = getSphere();
      const zNorm = b.z / sphere.r;
      let scale = 1 + 0.3 * zNorm;
      if (b.topScale && b.state === 'top') scale = b.topScale;
      const r = b.r * scale;
      const dimAlpha = isBehind ? 0.65 : 1.0;
      const isWinnerOnTop = (b.state === 'top');
      const winnerPulse = isWinnerOnTop ? (0.9 + 0.1 * Math.sin(performance.now() / 250)) : 1;
      const corePulse = isWinnerOnTop ? (0.75 + 0.25 * Math.sin(performance.now() / 200)) : 1;

      ctx.save();
      ctx.globalAlpha = dimAlpha;

      // Sunburst-stråler (kun vinner på topp)
      if (isWinnerOnTop) {
        ctx.save();
        ctx.translate(b.x, b.y);
        ctx.rotate(performance.now() / 3000);
        const numRays = 8;
        const rayLen = r * 4;
        for (let i = 0; i < numRays; i++) {
          ctx.rotate(Math.PI * 2 / numRays);
          const rayGrad = ctx.createLinearGradient(0, 0, rayLen, 0);
          rayGrad.addColorStop(0, `rgba(255, 245, 180, ${0.6 * corePulse})`);
          rayGrad.addColorStop(0.3, `rgba(255, 230, 150, ${0.25 * corePulse})`);
          rayGrad.addColorStop(1, 'rgba(255, 200, 100, 0)');
          ctx.fillStyle = rayGrad;
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(rayLen, -r * 0.3);
          ctx.lineTo(rayLen, r * 0.3);
          ctx.closePath();
          ctx.fill();
        }
        ctx.restore();
      }

      // Stor ytre glow
      if (isWinnerOnTop) {
        const glowR = r * 3.5;
        const glow = ctx.createRadialGradient(b.x, b.y, r * 0.5, b.x, b.y, glowR);
        glow.addColorStop(0, `rgba(255, 250, 220, ${0.85 * winnerPulse})`);
        glow.addColorStop(0.2, `rgba(255, 240, 180, ${0.55 * winnerPulse})`);
        glow.addColorStop(0.5, `rgba(255, 220, 130, ${0.25 * winnerPulse})`);
        glow.addColorStop(1, 'rgba(255, 200, 100, 0)');
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(b.x, b.y, glowR, 0, Math.PI * 2);
        ctx.fill();
      }

      // Skygge under
      ctx.fillStyle = 'rgba(0,0,0,0.25)';
      ctx.beginPath();
      ctx.ellipse(b.x, b.y + r * 0.85, r * 0.85, r * 0.18, 0, 0, Math.PI * 2);
      ctx.fill();

      // Base
      if (isWinnerOnTop) {
        ctx.fillStyle = 'rgba(255, 255, 250, 1)';
      } else {
        ctx.fillStyle = 'rgba(220, 225, 232, 0.92)';
      }
      ctx.beginPath();
      ctx.arc(b.x, b.y, r, 0, Math.PI * 2);
      ctx.fill();

      // Indre lys
      if (isWinnerOnTop) {
        const innerGlow = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, r);
        innerGlow.addColorStop(0, `rgba(255, 255, 255, ${winnerPulse})`);
        innerGlow.addColorStop(0.3, `rgba(255, 250, 220, ${winnerPulse})`);
        innerGlow.addColorStop(0.6, `rgba(255, 235, 170, ${0.95 * winnerPulse})`);
        innerGlow.addColorStop(0.85, `rgba(255, 215, 130, ${0.7 * winnerPulse})`);
        innerGlow.addColorStop(1, `rgba(255, 200, 110, ${0.5 * winnerPulse})`);
        ctx.fillStyle = innerGlow;
        ctx.beginPath();
        ctx.arc(b.x, b.y, r, 0, Math.PI * 2);
        ctx.fill();

        const coreGrad = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, r * 0.55);
        coreGrad.addColorStop(0, `rgba(255, 255, 255, ${corePulse})`);
        coreGrad.addColorStop(0.4, `rgba(255, 255, 240, ${0.7 * corePulse})`);
        coreGrad.addColorStop(1, `rgba(255, 250, 220, 0)`);
        ctx.fillStyle = coreGrad;
        ctx.beginPath();
        ctx.arc(b.x, b.y, r * 0.55, 0, Math.PI * 2);
        ctx.fill();
      } else {
        const innerGlow = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, r);
        innerGlow.addColorStop(0, 'rgba(255,255,255,0.45)');
        innerGlow.addColorStop(0.5, 'rgba(245,248,252,0.25)');
        innerGlow.addColorStop(1, 'rgba(200,210,220,0)');
        ctx.fillStyle = innerGlow;
        ctx.beginPath();
        ctx.arc(b.x, b.y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Hovedlys
      const lightX = b.x - r * 0.4;
      const lightY = b.y - r * 0.5;
      const mainGrad = ctx.createRadialGradient(lightX, lightY, 0, lightX, lightY, r * 1.4);
      mainGrad.addColorStop(0, 'rgba(255,255,255,0.7)');
      mainGrad.addColorStop(0.3, 'rgba(250,252,255,0.35)');
      mainGrad.addColorStop(0.7, 'rgba(220,225,235,0)');
      mainGrad.addColorStop(1, 'rgba(180,190,200,0)');
      ctx.fillStyle = mainGrad;
      ctx.beginPath();
      ctx.arc(b.x, b.y, r, 0, Math.PI * 2);
      ctx.fill();

      // Skygge-side
      const shadowGrad = ctx.createRadialGradient(b.x + r * 0.4, b.y + r * 0.5, 0, b.x + r * 0.4, b.y + r * 0.5, r * 1.2);
      shadowGrad.addColorStop(0, 'rgba(50,55,70,0.4)');
      shadowGrad.addColorStop(0.3, 'rgba(70,75,90,0.2)');
      shadowGrad.addColorStop(0.7, 'rgba(100,105,120,0)');
      shadowGrad.addColorStop(1, 'rgba(120,125,140,0)');
      ctx.fillStyle = shadowGrad;
      ctx.beginPath();
      ctx.arc(b.x, b.y, r, 0, Math.PI * 2);
      ctx.fill();

      // Tall (4 sider)
      drawRotatingNumber(b, r);

      // Glanspunkt
      const specGrad = ctx.createRadialGradient(lightX + r * 0.05, lightY + r * 0.05, 0, lightX, lightY, r * 0.4);
      specGrad.addColorStop(0, 'rgba(255,255,255,1)');
      specGrad.addColorStop(0.3, 'rgba(255,255,255,0.5)');
      specGrad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = specGrad;
      ctx.beginPath();
      ctx.arc(b.x, b.y, r, 0, Math.PI * 2);
      ctx.fill();

      // Sekundær glanspunkt
      const spec2X = b.x + r * 0.3;
      const spec2Y = b.y + r * 0.35;
      const spec2 = ctx.createRadialGradient(spec2X, spec2Y, 0, spec2X, spec2Y, r * 0.25);
      spec2.addColorStop(0, 'rgba(255,255,255,0.4)');
      spec2.addColorStop(0.5, 'rgba(255,255,255,0.15)');
      spec2.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = spec2;
      ctx.beginPath();
      ctx.arc(b.x, b.y, r, 0, Math.PI * 2);
      ctx.fill();

      // Glass-kant
      ctx.strokeStyle = 'rgba(40,50,65,0.5)';
      ctx.lineWidth = 1.0 * scale;
      ctx.beginPath();
      ctx.arc(b.x, b.y, r, 0, Math.PI * 2);
      ctx.stroke();

      ctx.restore();
    }

    function drawGlassOverlay() {
      const sphere = getSphere();
      ctx.save();
      ctx.beginPath();
      ctx.arc(sphere.cx, sphere.cy, sphere.r, 0, Math.PI * 2);
      ctx.clip();

      const refl = ctx.createRadialGradient(
        sphere.cx - sphere.r * 0.4, sphere.cy - sphere.r * 0.45, 0,
        sphere.cx - sphere.r * 0.4, sphere.cy - sphere.r * 0.45, sphere.r * 0.7
      );
      refl.addColorStop(0, 'rgba(255,255,255,0.45)');
      refl.addColorStop(0.3, 'rgba(255,255,255,0.22)');
      refl.addColorStop(0.7, 'rgba(255,255,255,0.05)');
      refl.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = refl;
      ctx.fillRect(sphere.cx - sphere.r, sphere.cy - sphere.r, sphere.r * 2, sphere.r * 2);

      ctx.save();
      ctx.translate(sphere.cx - sphere.r * 0.25, sphere.cy - sphere.r * 0.55);
      ctx.rotate(-0.5);
      const streak = ctx.createLinearGradient(0, 0, sphere.r * 0.6, 0);
      streak.addColorStop(0, 'rgba(255,255,255,0)');
      streak.addColorStop(0.5, 'rgba(255,255,255,0.6)');
      streak.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = streak;
      ctx.beginPath();
      ctx.ellipse(sphere.r * 0.3, 0, sphere.r * 0.3, sphere.r * 0.06, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      const refl2 = ctx.createRadialGradient(
        sphere.cx + sphere.r * 0.35, sphere.cy + sphere.r * 0.4, 0,
        sphere.cx + sphere.r * 0.35, sphere.cy + sphere.r * 0.4, sphere.r * 0.35
      );
      refl2.addColorStop(0, 'rgba(255,255,255,0.18)');
      refl2.addColorStop(0.5, 'rgba(255,255,255,0.05)');
      refl2.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = refl2;
      ctx.fillRect(sphere.cx - sphere.r, sphere.cy - sphere.r, sphere.r * 2, sphere.r * 2);

      ctx.restore();
    }

    function drawFrame(now) {
      ctx.clearRect(0, 0, s.W, s.H);
      // Synkroniser lyd med phase: rumling AV unntatt under spinning/rising/tube
      const shouldRumble = s.phase === 'spinning' || s.phase === 'rising' || s.phase === 'tube';
      if (shouldRumble && !s.rumbleActive) {
        startLottoRumble();
        s.rumbleActive = true;
      } else if (!shouldRumble && s.rumbleActive) {
        stopLottoRumble();
        s.rumbleActive = false;
      }
      updateBallPhysics(now);
      if (s.winner && s.winner.state !== 'sphere' && s.winner.state !== 'top') {
        updateWinner(now);
      } else if (s.winner && s.winner.state === 'top') {
        const sphere = getSphere();
        s.winner.x = sphere.cx;
        s.winner.z = 0;
        s.winner.pitch = 0; s.winner.yaw = 0;
        const elapsed = now - s.phaseStart;
        const t = Math.min(1, elapsed / 500);
        const eased = 1 - Math.pow(1 - t, 3);
        s.winner.topScale = 1 + 1 * eased;
        // Plasser bunnen av kulen rett på toppen av røret.
        // Kulens senter (y) skal være ved (tubeTopY - r), der r = baseR * topScale
        const baseR = s.winner.r;
        const fullR = baseR * s.winner.topScale;
        s.winner.y = getTubeTopY() - fullR;
      }

      const sphere = getSphere();
      ctx.save();
      ctx.beginPath();
      ctx.arc(sphere.cx, sphere.cy, sphere.r, 0, Math.PI * 2);
      ctx.clip();

      const sphereBalls = s.balls
        .filter(b => b.state === 'sphere')
        .sort((a, b) => a.z - b.z);

      for (const b of sphereBalls) {
        if (b.z < 0) drawBall(b, true);
      }
      if (s.winner && s.winner.state === 'rising' && s.winner.z < 0) {
        drawBall(s.winner, false);
      }

      drawGlassOverlay();

      for (const b of sphereBalls) {
        if (b.z >= 0) drawBall(b, false);
      }
      if (s.winner && s.winner.state === 'rising' && s.winner.z >= 0) {
        drawBall(s.winner, false);
      }

      // Vignett
      const vignette = ctx.createRadialGradient(
        sphere.cx, sphere.cy, sphere.r * 0.55,
        sphere.cx, sphere.cy, sphere.r
      );
      vignette.addColorStop(0, 'rgba(0,0,0,0)');
      vignette.addColorStop(0.7, 'rgba(0,0,0,0.18)');
      vignette.addColorStop(1, 'rgba(0,0,0,0.55)');
      ctx.fillStyle = vignette;
      ctx.fillRect(sphere.cx - sphere.r, sphere.cy - sphere.r, sphere.r * 2, sphere.r * 2);

      ctx.restore();

      // Vinner i tube/top utenfor klipping
      if (s.winner && (s.winner.state === 'tube' || s.winner.state === 'top')) {
        drawBall(s.winner, false);
      }

      s.raf = requestAnimationFrame(drawFrame);
    }

    resize();
    window.addEventListener('resize', resize);
    // Bygg kuler. Logikk:
    // - solgte ≤ 50: alle nummer 1..solgte
    // - solgte > 50: 50 tilfeldige nummer fra 1..solgte (vinneren legges til via winner-effect)
    function buildBallNumbers() {
      // v263: bruk den faktiske GJENVÆRENDE lodd-lista når den finnes (alt vunne
      // lodd er alt filtrert bort i LiveDrawView via usedTicketNums). Vinneren er
      // garantert i lista; om >50 plasseres han uansett via winner-effekten.
      // Fallback til 1..total når lista mangler (legacy/fallback-sti).
      const avail = Array.isArray(availableTickets)
        ? availableTickets.filter((n) => Number.isFinite(n) && n > 0)
        : null;
      const source = (avail && avail.length > 0)
        ? avail
        : Array.from({length: Math.max(1, Math.min(NUM_BALLS_LIMIT, totalTickets || NUM_BALLS_LIMIT))}, (_, i) => i + 1);
      if (source.length <= MAX_BALLS) {
        return source.slice();
      }
      // Velg MAX_BALLS-1 tilfeldige nummer fra kilden (vinneren plasseres senere)
      const pool = source.slice();
      // Fisher-Yates shuffle, ta de første MAX_BALLS-1
      for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
      }
      return pool.slice(0, MAX_BALLS - 1);
    }

    s.balls = createBalls(buildBallNumbers());
    s.simulateSettle(s.balls, 200);  // la kuler "falle" til bunnen før vi tegner
    s.raf = requestAnimationFrame(drawFrame);

    return () => {
      cancelAnimationFrame(s.raf);
      window.removeEventListener('resize', resize);
      if (resizeSettleTimer) clearTimeout(resizeSettleTimer); // v200
      // Stopp rumling hvis komponenten unmounteres mens den er aktiv
      stopLottoRumble();
      s.rumbleActive = false;
    };
    // eslint-disable-next-line
  }, []);

  // Trekning-effekt
  useEffect(() => {
    const s = stateRef.current;
    const winnerKey = `${winnerName||''}_${winnerTicketNum||''}`;

    // Hjelpefunksjon: tilbakestill alle kuler til sfæren
    function resetBallsToSphere() {
      // Plasser kulene tilfeldig i nedre halvdel, og la simulateSettle
      // få dem til å falle realistisk til bunnen
      const mH = s.W * 1023 / 929;
      const mTop = s.H - mH;
      const sphere = {
        cx: s.W * CFG.sphere.x / 100,
        cy: mTop + mH * CFG.sphere.y / 100,
        r: s.W * CFG.sphere.r / 100,
      };
      const r = s.W * CFG.ballSizePct / 100 / 2;
      const innerR = sphere.r - r - 2;
      for (const b of s.balls) {
        const u = 0.3 + Math.random() * 0.7;  // nedre 70%
        const phi = Math.random() * Math.PI * 2;
        const rr = Math.cbrt(Math.random()) * innerR * 0.9;
        const sinTheta = Math.sqrt(1 - u*u);
        b.x = sphere.cx + rr * sinTheta * Math.cos(phi);
        b.y = sphere.cy + rr * u;
        b.z = rr * sinTheta * Math.sin(phi);
        b.vx = 0; b.vy = 0; b.vz = 0;
        b.pitch = 0; b.yaw = 0;
        b.state = 'sphere';
        b.topScale = undefined;
        b.tubeProgress = undefined;
      }
      // La fysikk simulere noen frames slik at kuler faller til bunnen
      if (s.simulateSettle) s.simulateSettle(s.balls, 200);
    }

    // Rydd eventuell pågående setTimeout fra forrige trekning
    if (s.risingTimeoutId) {
      clearTimeout(s.risingTimeoutId);
      s.risingTimeoutId = null;
    }

    // Reset hvis ingen winner. v25-fix: sjekk KUN winnerName — tidligere
    // sjekket vi `!winnerName && !winnerTicketNum`, men DrawScreen sender
    // `winnerTicketNum = currentWinner?.ticketNum || "?"` som er en sannhetsverdi
    // også under nedtelling (step=0, currentWinner=null). Det fikk ballene til
    // å begynne å spinne FØR nedtellingen var ferdig på arrangør-PC. Nå
    // gater vi kun på winnerName, som er null til step >= 1.
    if (!winnerName) {
      s.doneCalled = false;
      s.phase = 'idle';
      s.winner = null;
      lastWinnerKey.current = null;
      startedRef.current = false;
      // v202b: resetBallsToSphere re-randomiserer posisjoner. Effekten kjører på
      // HVER nedtellings-tick (countdown i deps, winnerName=null under nedtelling),
      // så uten vakt hoppet kulene for hvert tall. Reset KUN én gang per idle-
      // periode; flagget nullstilles når en trekning faktisk starter (vinner satt).
      if (!noWinnerResetRef.current) {
        resetBallsToSphere();
        noWinnerResetRef.current = true;
      }
      return;
    }

    // VENT med å starte spinning hvis countdown er > 0
    // (kuler skal ligge stille til nedtellingen er ferdig)
    if (countdown != null && countdown > 0) {
      s.phase = 'idle';
      // Reset KUN én gang per ny winnerKey — ellers ville hver countdown-tick
      // re-randomisere kuleposisjonene og vise "hopp" på skjermen.
      // resetForKeyRef sporer hva vi har resatt for, separat fra lastWinnerKey
      // (som styrer trigger for spinning når countdown=0).
      if (resetForKeyRef.current !== winnerKey) {
        s.winner = null;
        s.doneCalled = false;
        startedRef.current = false;
        resetBallsToSphere();
        resetForKeyRef.current = winnerKey;
      }
      return;
    }

    if (lastWinnerKey.current === winnerKey && startedRef.current) return;
    lastWinnerKey.current = winnerKey;
    startedRef.current = true;
    s.doneCalled = false;

    // Tilbakestill ALLE kuler (i tilfelle forrige vinner henger igjen)
    resetBallsToSphere();

    const winnerNum = parseInt(winnerTicketNum) || 1;
    if (winnerNum < 1) {
      console.warn(`Ugyldig vinner-nummer: ${winnerNum}`);
      return;
    }

    // Sjekk om vinneren allerede finnes blant kulene
    let winner = s.balls.find(b => b.n === winnerNum);
    if (!winner) {
      // Vinneren er ikke i utvalget — erstatt en tilfeldig kule med vinnernummeret
      // (skjer når totalTickets > MAX_BALLS, dvs. > 50)
      if (s.balls.length === 0) {
        console.warn('Ingen kuler i maskinen');
        return;
      }
      const replaceIdx = Math.floor(Math.random() * s.balls.length);
      s.balls[replaceIdx].n = winnerNum;
      winner = s.balls[replaceIdx];
    }

    s.winner = winner;
    s.phase = 'spinning';
    s.phaseStart = performance.now();
    s.rotationDir = 1;
    s.rotationSwitchAt = performance.now() + 1500 + Math.random() * 500;
    noWinnerResetRef.current = false; // v202b: tillat ÉN reset når vi senere går tilbake til idle (rydd opp etter premien)
    // Lyden styres automatisk av drawFrame basert på phase

    s.risingTimeoutId = setTimeout(() => {
      s.risingTimeoutId = null;
      if (winner.state !== 'sphere') return;
      winner.state = 'rising';
      s.phase = 'rising';
      s.phaseStart = performance.now();
    }, CFG.spinDuration * 1000);
  }, [winnerName, winnerTicketNum, countdown]);  // eslint-disable-line

  // v201: vibrasjon under nedtelling FJERNET (v200 Bug 2 revertert) — kulene skal
  // stå HELT i ro under nedtelling. I 'idle' returnerer updateBallPhysics som før.
  // v202: speil live countdown til scene-state så resize-handleren (som har stale
  // closure pga deps []) kan hoppe over re-settle under nedtelling (ellers hopper
  // kulene ved mobil-resize, siden fasen er 'idle' under nedtelling).
  useEffect(() => {
    if (stateRef.current) stateRef.current.countdown = countdown;
  }, [countdown]);

  return (
    <div style={{
      position:'relative',
      width:'100%',
      height: heightOverride ? `${heightOverride}px` : '100vh',
      background: '#000',
      overflow:'hidden',
    }}>
      {/* Gullramme rundt hele området — KUN på mobil */}
      {isMobileLottoView && (
        <div style={{
          position:'absolute', inset:0,
          border: '6px solid',
          borderImage: 'linear-gradient(145deg,#fff5a0 0%,#C9A84C 20%,#8B6B1A 50%,#C9A84C 80%,#fff5a0 100%) 1',
          boxShadow: 'inset 0 0 0 2px rgba(0,0,0,0.4), 0 0 20px rgba(201,168,76,0.3)',
          pointerEvents: 'none',
          zIndex: 4,
        }}/>
      )}

      {/* Maskinen-bildet plassert NEDERST i container med korrekt aspect ratio.
          Mobil: full bredde, klistret til bunn med 6px luft.
          PC: høyde 80% av container, sentrert horisontalt, HELT klistret til bunn (bottom:0). */}
      <div style={{
        position:'absolute',
        zIndex: 1,
        ...(isMobileLottoView
          ? { left: 6, right: 6, bottom: 6, aspectRatio: '929/1023' }
          : {
              left: '50%', bottom: 0,
              transform: 'translateX(-50%)',
              height: '70%',
              aspectRatio: '929/1023',
            }
        ),
      }}>
        {imgLoaded && (
          <img
            src="/lotto_machine.png?v=3"
            alt="Lotto Machine"
            style={{
              position:'absolute', inset:0,
              width:'100%', height:'100%',
              objectFit:'cover',
              objectPosition:'bottom',
              pointerEvents:'none',
              userSelect:'none'
            }}
          />
        )}
      </div>

      {/* Canvas dekker HELE container — slik at vinnerkulen kan stige opp i det
          svarte området OVER maskinen uten å bli klippet.
          Mobil: 6px luft mot ramme. PC: full container (ingen luft). */}
      <canvas
        ref={canvasRef}
        style={{
          position:'absolute',
          ...(isMobileLottoView
            ? { left: 6, right: 6, top: 6, bottom: 6, width: 'calc(100% - 12px)', height: 'calc(100% - 12px)' }
            : { left: 0, right: 0, top: 0, bottom: 0, width: '100%', height: '100%' }
          ),
          display:'block',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />

      {/* MERK: Countdown håndteres av LiveDrawView i parent — ikke duplisert her */}
    </div>
  );
}

function LottoDraw({ winnerName, winnerTicketNum, onSpinDone, heightOverride, totalTickets, availableTickets, countdown, viewMode = "player-mobile" }) {
  // Ren dispatcher (ingen hooks): bruker LottoDraw3D (3D-design med lotto_machine.png)
  // for ALLE viewModes — både mobil og PC.
  return <LottoDraw3D winnerName={winnerName} winnerTicketNum={winnerTicketNum} onSpinDone={onSpinDone} heightOverride={heightOverride} totalTickets={totalTickets} availableTickets={availableTickets} countdown={countdown} viewMode={viewMode}/>;
}


// ─── ENEARMET BANDITT v2 ──────────────────────────────────────────────────────
// Canvas-basert slot machine med alle v2-forbedringer fra prototypen:
//   • Utsmykket maskin-ramme med dobbel gullkant og dekorativ topp "VINLODDET"
//   • 48 lyspærer som blinker i chase-mønster rundt skjermen
//   • Motion blur på ruller (via filter)
//   • Anticipation på siste rull
//   • Maskin-ramme lyser opp i vinnermodus
//   • Lever-lyd ved start, tick per celle, clunk per stopp
// Canvas-tilnærmingen unngår CSS-kollisjoner som v2-prototypen tidligere led av.
// ─── NY SLOTMACHINE (hybrid: bilde + canvas) ───────────────────────────────
// Erstatter gammel DOM/canvas-variant med hybrid tilnærming:
// - public/slot_machine.png som bakgrunn
// - Canvas tegner sylindere oppå (maskert med svart bak for å dekke bildets
//   innebygde sylindere), pluss lyspærer med løpelys + blink-effekter.
// Animasjonslogikk (anticipation, sekvensiell stopp, lyd) er beholdt fra gammel.
function SlotMachine({ winnerName, winnerTicketNum, onSpinDone, heightOverride, countdown, viewMode = "player-mobile" }) {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    W: 0, H: 0,
    // Sylindere: 19 stk — matcher prototypens kalibrering mot slot_machine.png
    reels: [],
    raf: null,
    phase: 'idle',   // idle | spinning | done
    phaseStart: 0,
    // Lys-blink etter spin
    lightsBlinkUntil: 0,
    // Lyd
    audioCtx: null,
    lastTickTs: 0,
    // Kontroll-flagg
    doneCalled: false,
    pendingSpinTimeoutId: null,
  });
  const startedRef = useRef(false);
  const lastWinnerRef = useRef(null);
  // Refs som holder NÅVÆRENDE verdi av props (oppdateres hver render)
  // Dette løser React closure-bug i draw()-loopen
  const winnerNameRef = useRef(winnerName);
  const winnerTicketNumRef = useRef(winnerTicketNum); // v273
  const countdownRef = useRef(countdown);
  const onSpinDoneRef = useRef(onSpinDone);
  winnerNameRef.current = winnerName;
  winnerTicketNumRef.current = winnerTicketNum;
  countdownRef.current = countdown;
  onSpinDoneRef.current = onSpinDone;

  const POOL_CHARS = 'ABCDEFGHIJKLMNOPRSTUVWYÆØÅ';
  const TOTAL_REELS = 19;
  const POOL_SIZE = 32;

  // Hardkodede kalibreringer (hentet fra prototypen)
  const CYL_LEFT = 8.6, CYL_RIGHT = 90.8;
  const CYL_TOP = 43, CYL_BOTTOM = 50.6;
  const CYL_OFFSET_Y = -1.6;
  const FONT_FAMILY = "'Oswald', 'Bebas Neue', 'Arial Narrow', Impact, sans-serif";
  const FONT_SIZE_PCT = 64;
  const FONT_SCALE_X = 0.74;
  const FONT_OFFSET_Y_PCT = 4;
  const FONT_OUTLINE = 2;
  const MASK_PAD_PCT = 1.4;
  const LIGHTS_TOP_Y = 38.3, LIGHTS_BOTTOM_Y = 51.5;
  const LIGHTS_SPEED_IDLE = 1.2, LIGHTS_SPEED_SPIN = 20;
  // v274: vinner-lodd (papir-billett-stil, replikert fra LotteryDraw sin
  // drawWinnerTicket) i prosent av maskin-rect. Senter-Y er litt over det
  // øvre lysrekken (LIGHTS_TOP_Y=38.3) — i JACKPOT-tabell-området, godt unna
  // sylinder-båndet (CYL_TOP=43). JUSTERBARE — flytt eller skaler loddet ved
  // å endre disse to konstantene. Tegnes kun når s.phase==='done'.
  // Aspect (len:thick) er låst til 2.667:1 (samme som originalen: 1.2/0.45).
  // v275: bredden økt 28 → 36 (~+29%), senter-Y løftet 32 → 30 for å beholde
  // klaring til lysrekka når loddet vippes.
  const LODD_CENTER_Y_PCT = 30;
  const LODD_WIDTH_PCT = 36;
  // v275: lett gynging — som om loddet henger fra opphengspunktet på toppen.
  // Tidsbasert (sin) så den er jevn uavhengig av FPS. JUSTERBARE.
  const SWAY_AMPLITUDE_DEG = 3.5;     // grader, ±
  const SWAY_PERIOD_MS = 3000;        // én full frem-tilbake-syklus
  const BOB_AMPLITUDE_PX = 2;         // piksler, ±
  const BOB_PERIOD_MS = 4500;         // litt ute av fase med sway → naturlig

  // Bildet som bakgrunn — lastes som <img> DOM-element, ikke canvas
  const bgImgRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.onload = () => { bgImgRef.current = img; setImgLoaded(true); };
    img.onerror = () => { console.warn('slot_machine.png kunne ikke lastes'); setImgLoaded(true); };
    img.src = '/slot_machine.png';
  }, []);

  useEffect(() => {
    const s = stateRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    function getAC() {
      const ac = getAudioCtx();
      if (ac) s.audioCtx = ac;
      return ac;
    }

    function playTick() {
      const now = performance.now();
      if (now - s.lastTickTs < 50) return;
      s.lastTickTs = now;
      try {
        const ac = getAC();
        if (!ac) return;
        const acNow = ac.currentTime;
        const noiseDur = 0.025;
        const bufferSize = Math.max(1, Math.floor(ac.sampleRate * noiseDur));
        const noiseBuffer = ac.createBuffer(1, bufferSize, ac.sampleRate);
        const data = noiseBuffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
          data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.4));
        }
        const noise = ac.createBufferSource();
        noise.buffer = noiseBuffer;
        const hp = ac.createBiquadFilter();
        hp.type = 'highpass';
        hp.frequency.value = 1500;
        const ng = ac.createGain();
        ng.gain.setValueAtTime(0.10, acNow);
        ng.gain.exponentialRampToValueAtTime(0.001, acNow + noiseDur);
        noise.connect(hp); hp.connect(ng); ng.connect(ac.destination);
        noise.start(acNow);
      } catch(e) {}
    }
    function playClunk() {
      try {
        const ac = getAC();
        if (!ac) return;
        const now = ac.currentTime;
        const o = ac.createOscillator(), g = ac.createGain();
        o.type = 'sine';
        o.frequency.setValueAtTime(120, now);
        o.frequency.exponentialRampToValueAtTime(80, now + 0.12);
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(0.22, now + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
        o.connect(g); g.connect(ac.destination);
        o.start(now); o.stop(now + 0.2);
        const o2 = ac.createOscillator(), g2 = ac.createGain();
        o2.type = 'triangle';
        o2.frequency.value = 420;
        g2.gain.setValueAtTime(0, now);
        g2.gain.linearRampToValueAtTime(0.08, now + 0.01);
        g2.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
        o2.connect(g2); g2.connect(ac.destination);
        o2.start(now); o2.stop(now + 0.16);
      } catch(e) {}
    }
    function playFanfare() {
      try {
        const ac = getAC();
        if (!ac) return;
        const t0 = ac.currentTime;
        [523.25, 659.25, 783.99, 1046.50].forEach((f, i) => {
          const o = ac.createOscillator(), g = ac.createGain();
          o.type = 'triangle'; o.frequency.value = f;
          const t = t0 + i * 0.08;
          g.gain.setValueAtTime(0, t);
          g.gain.linearRampToValueAtTime(0.18, t + 0.02);
          g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
          o.connect(g); g.connect(ac.destination);
          o.start(t); o.stop(t + 0.5);
        });
      } catch(e) {}
    }

    // ─── RESIZE + POSISJON ───
    function resize() {
      const rect = canvas.getBoundingClientRect();
      s.W = rect.width; s.H = rect.height;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = s.W * dpr;
      canvas.height = s.H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    // Beregner maskinens posisjon innenfor canvas. Både mobil og PC bruker
    // nå croppet aspect (941×1358) der svarte felt på topp/bunn er fjernet.
    // Mobil: maskinen fyller bredden, klistret nederst.
    // PC: maskinen klistret nederst med høyde = 85% av canvas-høyden.
    function getMachineRect() {
      // Effektiv maskin-aspect: bildet er 941×1672, men har 9.4% svart felt på topp/bunn.
      // Effektiv maskin = 941 × 1358 ≈ aspect 0.693
      const aspect = 941 / 1358;
      if (isMobile) {
        // Mobil: maskinen fyller bredden, klistret nederst.
        // +5: maskinen flyttes 5px ned (bottom:1 i container vs bottom:6 før)
        const mW = s.W;
        const mH = mW / aspect;
        return { x: 0, y: s.H - mH + 5, w: mW, h: mH };
      } else {
        // PC: maskinen klistret til bunn, høyde 80% av canvas-høyden.
        // (Var 85%, krympet 5% for mer luft over til gratulerer/nedtelling.)
        // Hvis bredden ikke får plass (smal/høy canvas), begrens av bredden i stedet.
        const targetH = s.H * 0.80;
        const targetW = targetH * aspect;
        let mW, mH;
        if (targetW <= s.W) {
          mH = targetH;
          mW = targetW;
        } else {
          // Sjelden: canvas er så smal at 80% høyde gir for bred maskin → begrens av bredde
          mW = s.W;
          mH = mW / aspect;
        }
        return { x: (s.W - mW) / 2, y: s.H - mH, w: mW, h: mH };
      }
    }

    function getCylinderRect() {
      const m = getMachineRect();
      const topPct = CYL_TOP + CYL_OFFSET_Y;
      const bottomPct = CYL_BOTTOM + CYL_OFFSET_Y;
      return {
        x: m.x + m.w * CYL_LEFT / 100,
        y: m.y + m.h * topPct / 100,
        w: m.w * (CYL_RIGHT - CYL_LEFT) / 100,
        h: m.h * (bottomPct - topPct) / 100,
      };
    }

    // ─── TEGNING AV SYLINDER (én) ───
    function drawCylinder(r, cellW, cellH, cx, cy) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(cx - cellW/2, cy - cellH/2, cellW, cellH);
      ctx.clip();

      // Beige/cream bakgrunn med gradient
      const grad = ctx.createLinearGradient(0, cy - cellH/2, 0, cy + cellH/2);
      grad.addColorStop(0, '#d9c9a8');
      grad.addColorStop(0.15, '#efdfc0');
      grad.addColorStop(0.5, '#f8ecd2');
      grad.addColorStop(0.85, '#efdfc0');
      grad.addColorStop(1, '#d9c9a8');
      ctx.fillStyle = grad;
      ctx.fillRect(cx - cellW/2, cy - cellH/2, cellW, cellH);

      // Bokstaver (kun etter at spinning har startet)
      // Før spinning: helt blank (bare cream-bakgrunn)
      if (r.hasSpun && !(r.stopped && !r.ch)) {
        const fontSize = Math.round(cellH * FONT_SIZE_PCT / 100);
        ctx.font = `900 ${fontSize}px ${FONT_FAMILY}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.save();
        ctx.translate(cx, 0);
        ctx.scale(FONT_SCALE_X, 1);

        const pool = r.pool;
        const poolLen = pool.length;
        const yInt = Math.floor(r.y);
        const yFrac = r.y - yInt;

        for (let offset = -1; offset <= 1; offset++) {
          const poolIdx = ((yInt + offset) % poolLen + poolLen) % poolLen;
          const ch = pool[poolIdx];
          if (!ch) continue;

          const fontOffsetPx = cellH * FONT_OFFSET_Y_PCT / 100;
          const drawY = cy + (offset - yFrac) * cellH + fontOffsetPx;

          // Motion blur mens spinning
          if (!r.stopped && r.spd > 0.5) {
            ctx.globalAlpha = Math.max(0.25, 1 - r.spd * 0.04);
          } else {
            ctx.globalAlpha = 1;
          }

          if (FONT_OUTLINE > 0) {
            ctx.strokeStyle = '#000';
            ctx.lineWidth = FONT_OUTLINE;
            ctx.lineJoin = 'round';
            ctx.strokeText(ch, 0, drawY);
          }
          ctx.fillStyle = '#c41e1e';
          ctx.fillText(ch, 0, drawY);
        }
        ctx.globalAlpha = 1;
        ctx.restore();
      }

      // Subtil skygging på kantene for runding
      const shade = ctx.createLinearGradient(cx - cellW/2, 0, cx + cellW/2, 0);
      shade.addColorStop(0, 'rgba(0,0,0,0.18)');
      shade.addColorStop(0.15, 'rgba(0,0,0,0)');
      shade.addColorStop(0.85, 'rgba(0,0,0,0)');
      shade.addColorStop(1, 'rgba(0,0,0,0.18)');
      ctx.fillStyle = shade;
      ctx.fillRect(cx - cellW/2, cy - cellH/2, cellW, cellH);

      ctx.restore();
    }

    // ─── TEGNING AV MASKE OG LYS ───
    function drawMask() {
      const rect = getCylinderRect();
      const m = getMachineRect();
      const padX = m.w * MASK_PAD_PCT / 100;
      ctx.fillStyle = '#000';
      // Posisjon av lysrekkene (Y-senter for hver pære)
      const lightsTopY = m.y + m.h * LIGHTS_TOP_Y / 100;
      const lightsBottomY = m.y + m.h * LIGHTS_BOTTOM_Y / 100;
      // Bruk SAMME radius-formel som drawLights() — der er bulbR = bulbSpacing * 0.28
      // og bulbSpacing = rect.w / numBulbs. Dette gir korrekt størrelse.
      const bulbSpacing = rect.w / TOTAL_REELS;
      const bulbR = bulbSpacing * 0.28;
      // Maskerings-området DEKKER BEGGE LYSREKKENE FULLT UT, slik at maskinens
      // egen grafikk (BAR-symboler, JACKPOT-tabeller osv.) ikke skinner gjennom
      // mellom lyspærene. Lysene tegnes OPPÅ masken etterpå.
      // Utvider med ekstra margin (bulbR * 2) på topp og bunn for å sikre full dekning
      // selv om lyspæren har glow eller blir tegnet med litt større halo.
      const margin = bulbR * 2;
      const maskTop = lightsTopY - bulbR - margin;
      const maskBottom = lightsBottomY + bulbR + margin;
      ctx.fillRect(rect.x - padX, maskTop, rect.w + padX * 2, maskBottom - maskTop);
    }

    function drawLights(now) {
      const rect = getCylinderRect();
      const numBulbs = TOTAL_REELS;
      const bulbSpacing = rect.w / numBulbs;
      const bulbR = bulbSpacing * 0.28;
      const t = now / 1000;

      // Intensitets-funksjon avhengig av tilstand
      let getIntensity;
      if (s.phase === 'spinning') {
        // Løpelys mot midten
        const speed = LIGHTS_SPEED_SPIN;
        const halfLen = numBulbs / 2;
        const cyclePos = (t * speed) % halfLen;
        const leftHead = cyclePos;
        const rightHead = numBulbs - 1 - cyclePos;
        const trailLength = 4;

        getIntensity = (i) => {
          const distLeft = leftHead - i;
          const distRight = i - rightHead;
          let intensity = 0.15;
          if (distLeft >= 0 && distLeft < trailLength) {
            intensity = Math.max(intensity, 1 - distLeft / trailLength);
          }
          if (distRight >= 0 && distRight < trailLength) {
            intensity = Math.max(intensity, 1 - distRight / trailLength);
          }
          if (Math.abs(distLeft) < 0.5) intensity = 1;
          if (Math.abs(distRight) < 0.5) intensity = 1;
          return intensity;
        };
      } else if (now < s.lightsBlinkUntil) {
        // Blinking av/på etter spin
        const blinkFreq = 6;
        const phase = Math.floor(t * blinkFreq * 2) % 2;
        const allOn = phase === 0;
        getIntensity = () => allOn ? 1.0 : 0.1;
      } else {
        // Hvile: sakte chase rundt
        const speed = LIGHTS_SPEED_IDLE;
        const chaseOffset = t * speed;
        getIntensity = (i) => {
          const phase = (i - chaseOffset) % numBulbs;
          const normPhase = ((phase % numBulbs) + numBulbs) % numBulbs;
          return 0.3 + 0.7 * Math.pow(
            Math.max(0, Math.cos(normPhase / numBulbs * Math.PI * 4)),
            2
          );
        };
      }

      const m = getMachineRect();
      const rows = [
        { y: m.y + m.h * LIGHTS_TOP_Y / 100 },
        { y: m.y + m.h * LIGHTS_BOTTOM_Y / 100 }
      ];

      for (const row of rows) {
        for (let i = 0; i < numBulbs; i++) {
          const cx = rect.x + (i + 0.5) * bulbSpacing;
          const cy = row.y;
          const intensity = getIntensity(i);

          // Farge: under spin = alle gule, ellers = alternerende gull/hvit
          let baseColor;
          if (s.phase === 'spinning') {
            baseColor = [255, 220, 120];
          } else {
            const isGold = (i % 2 === 0);
            baseColor = isGold ? [255, 220, 120] : [255, 255, 240];
          }
          const [r, g, b] = baseColor;

          ctx.save();
          ctx.shadowColor = `rgba(${r},${g},${b},${intensity})`;
          ctx.shadowBlur = bulbR * 3 * intensity;

          // Ytre halo
          const haloGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, bulbR * 1.8);
          haloGrad.addColorStop(0, `rgba(${r},${g},${b},${0.3 * intensity})`);
          haloGrad.addColorStop(1, `rgba(${r},${g},${b},0)`);
          ctx.fillStyle = haloGrad;
          ctx.beginPath();
          ctx.arc(cx, cy, bulbR * 1.8, 0, Math.PI * 2);
          ctx.fill();

          // Pæren selv
          const core = ctx.createRadialGradient(cx - bulbR * 0.2, cy - bulbR * 0.2, 0, cx, cy, bulbR);
          const alpha = 0.4 + 0.6 * intensity;
          core.addColorStop(0, `rgba(255,255,255,${alpha})`);
          core.addColorStop(0.3, `rgba(${r},${g},${b},${alpha})`);
          core.addColorStop(1, `rgba(${Math.floor(r*0.7)},${Math.floor(g*0.6)},${Math.floor(b*0.5)},${alpha * 0.8})`);
          ctx.fillStyle = core;
          ctx.beginPath();
          ctx.arc(cx, cy, bulbR, 0, Math.PI * 2);
          ctx.fill();

          // Mørk ring rundt
          ctx.strokeStyle = 'rgba(40,30,10,0.6)';
          ctx.lineWidth = Math.max(1, bulbR * 0.12);
          ctx.stroke();

          ctx.restore();
        }
      }
    }

    // ─── VINNER-LODD (v274) ───
    // Replikert fra LotteryDraw sin drawWinnerTicket (App.js ~11294): papir-
    // billett med gradient-paper (fff5d0→f8e8b5→e8d090), bøyde kortender (curl),
    // mørk gull-ramme (#8B6000) + indre lys-gull-ramme (#C9A84C), 4 hjørne-
    // stjerner, "— LODDNR —" italic-tekst over, og det store loddnummeret i
    // Bebas Neue/Oswald med gradient-gull-fyll (#FFD700→#C9A84C→#8B6000) og
    // mørk-gull outline. Ikke gjenbrukt — originalen er kapslet i LotteryDraw
    // sin canvas-closure (sitt eget ctx/s.winTix/s.hatR), så stilen er kopiert.
    // Plassert prosent-basert av maskin-rect, sentrert horisontalt — treffer
    // PC og mobil likt.
    function drawWinnerLodd(ts) {
      const num = winnerTicketNumRef.current;
      if (num == null || num === '' || num === '?') return;
      const m = getMachineRect();
      const cx = m.x + m.w / 2;
      const cy = m.y + m.h * LODD_CENTER_Y_PCT / 100;
      const len = m.w * LODD_WIDTH_PCT / 100;
      const thick = len * (0.45 / 1.2); // bevar originalens aspect 2.667:1
      // Fade-in 400ms + content-fade 600ms fra det øyeblikket fasen ble 'done'
      const elapsed = ts - s.phaseStart;
      const alpha = Math.max(0, Math.min(1, elapsed / 400));
      const showContent = Math.max(0, Math.min(1, (elapsed - 200) / 600));
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(cx, cy);

      // v275: gynging rundt opphengspunktet (toppen av loddet) + liten bob.
      // Tidsbasert sin → jevn uansett FPS. Hele loddet (inkl. halo/skygge)
      // beveger seg som én enhet, så det føles som det henger fra noe.
      const swayRad = Math.sin((ts / SWAY_PERIOD_MS) * Math.PI * 2) * (SWAY_AMPLITUDE_DEG * Math.PI / 180);
      const bobPx = Math.sin((ts / BOB_PERIOD_MS) * Math.PI * 2) * BOB_AMPLITUDE_PX;
      ctx.translate(0, bobPx);
      ctx.translate(0, -thick / 2);
      ctx.rotate(swayRad);
      ctx.translate(0, thick / 2);

      // Gull-glow halo bak (pulser litt)
      const pulse = 0.7 + 0.3 * (0.5 + 0.5 * Math.sin(ts / 320));
      const gr = len * 0.9;
      const haloG = ctx.createRadialGradient(0, 0, 0, 0, 0, gr);
      haloG.addColorStop(0, `rgba(255,215,80,${0.55 * pulse})`);
      haloG.addColorStop(0.5, `rgba(255,180,60,${0.25 * pulse})`);
      haloG.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = haloG;
      ctx.beginPath();
      ctx.ellipse(0, 0, gr, gr * 0.7, 0, 0, Math.PI * 2);
      ctx.fill();

      // Skygge under loddet
      ctx.save();
      ctx.globalAlpha = 0.4;
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.ellipse(0, thick / 2 + 3, len / 2 * 0.85, thick * 0.15, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Papir med curl på endene
      const paperG = ctx.createLinearGradient(0, -thick / 2, 0, thick / 2);
      paperG.addColorStop(0, '#fff5d0');
      paperG.addColorStop(0.5, '#f8e8b5');
      paperG.addColorStop(1, '#e8d090');
      ctx.fillStyle = paperG;
      const bend = 2;
      ctx.beginPath();
      ctx.moveTo(-len / 2, -thick / 2 + bend);
      ctx.quadraticCurveTo(-len / 2 - 3, -thick / 2, -len / 2 + 3, -thick / 2);
      ctx.lineTo(len / 2 - 3, -thick / 2);
      ctx.quadraticCurveTo(len / 2 + 3, -thick / 2, len / 2, -thick / 2 + bend);
      ctx.lineTo(len / 2, thick / 2 - bend);
      ctx.quadraticCurveTo(len / 2 + 3, thick / 2, len / 2 - 3, thick / 2);
      ctx.lineTo(-len / 2 + 3, thick / 2);
      ctx.quadraticCurveTo(-len / 2 - 3, thick / 2, -len / 2, thick / 2 - bend);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#8B6000'; ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.strokeStyle = '#C9A84C'; ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.rect(-len / 2 + 5, -thick / 2 + 5, len - 10, thick - 10);
      ctx.stroke();

      // Innhold: stjerner, LODDNR-tekst, stort nummer
      if (showContent > 0) {
        ctx.save();
        ctx.globalAlpha = showContent;
        ctx.fillStyle = '#8B6000';
        const cornerOff = thick * 0.18;
        [[-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(([sx, sy]) => {
          ctx.save();
          ctx.translate(sx * (len / 2 - cornerOff * 2), sy * (thick / 2 - cornerOff));
          const sR = thick * 0.09;
          ctx.beginPath();
          for (let i = 0; i < 5; i++) {
            const a = -Math.PI / 2 + (i / 5) * Math.PI * 2;
            const a2 = a + Math.PI / 5;
            ctx.lineTo(Math.cos(a) * sR, Math.sin(a) * sR);
            ctx.lineTo(Math.cos(a2) * sR * 0.4, Math.sin(a2) * sR * 0.4);
          }
          ctx.closePath(); ctx.fill();
          ctx.restore();
        });
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.font = `italic ${thick * 0.14}px 'Montserrat','Playfair Display',Georgia,serif`;
        ctx.fillStyle = 'rgba(100,60,10,0.9)';
        ctx.fillText('— LODDNR —', 0, -thick * 0.20);
        ctx.font = `700 ${thick * 0.55}px 'Bebas Neue', 'Oswald', 'Arial Narrow', Impact, sans-serif`;
        const numG = ctx.createLinearGradient(0, thick * 0.05, 0, thick * 0.35);
        numG.addColorStop(0, '#FFD700');
        numG.addColorStop(0.5, '#C9A84C');
        numG.addColorStop(1, '#8B6000');
        ctx.fillStyle = numG;
        const numStr = String(num);
        ctx.fillText(numStr, 0, thick * 0.20 - 2);
        ctx.strokeStyle = '#5a3800'; ctx.lineWidth = 1.2;
        ctx.strokeText(numStr, 0, thick * 0.20 - 2);
        ctx.restore();
      }
      ctx.restore();
    }

    // ─── HOVED-ANIMASJONS-LØKKE ───
    function updateSpin(ts) {
      // Anti-duplicate stop-logikk fra gammel SlotMachine
      const cellH = s.cellH || 60;
      let anyActive = false;
      s.reels.forEach((r) => {
        if (r.stopped) return;
        anyActive = true;
        const elapsed = ts - r.startTime;
        if (!r.braking && elapsed >= r.stopTime) r.braking = true;
        if (r.braking) {
          const brakeRate = r.isLast ? 0.93 : 0.875;
          r.spd = Math.max(r.spd * brakeRate, 0.8);
        }
        r.y += r.spd / cellH;  // r.y er i celler
        if (r.y >= r.pool.length) r.y -= r.pool.length;

        // Tick-lyd når vi krysser en celle (throttled)
        const yInt = Math.floor(r.y);
        if (yInt !== r.lastTickY) {
          r.lastTickY = yInt;
          playTick();
        }

        // Stopp
        if (r.braking && r.spd <= 0.9) {
          r.y = r.mid;
          r.spd = 0;
          r.spinning = false;
          r.stopped = true;
          playClunk();
          if (r.isLast) {
            s.phase = 'done';
            s.phaseStart = ts;
            s.lightsBlinkUntil = ts + 3000;
          }
        }
      });
      return anyActive;
    }

    function draw(ts) {
      ctx.clearRect(0, 0, s.W, s.H);
      // Les LIVE props via refs (closure-fix)
      const liveWinnerName = winnerNameRef.current;
      const liveCountdown = countdownRef.current;

      // SAFETY NET: hvis ingen winner men gamle reels finnes — rens dem
      if (!liveWinnerName && s.reels.length > 0) {
        s.reels = [];
        s.phase = 'idle';
        s.lightsBlinkUntil = 0;
        s.doneCalled = false;
        lastWinnerRef.current = null;
        startedRef.current = false;
      }
      // SAFETY NET 2: hvis countdown er aktiv (>0) MEN vi har gamle reels og phase ikke er idle
      // → rydd opp så ny trekning kan starte
      if (liveCountdown != null && liveCountdown > 0 && s.phase !== 'idle' && s.phase !== 'spinning') {
        s.reels = [];
        s.phase = 'idle';
        s.lightsBlinkUntil = 0;
        s.doneCalled = false;
        lastWinnerRef.current = null;
        startedRef.current = false;
      }

      // 1) Oppdater spinn (kun hvis spinning eller nylig done — sjekk om vi må kalle onSpinDone)
      if (s.phase === 'spinning' || s.phase === 'done') {
        const anyActive = s.phase === 'spinning' ? updateSpin(ts) : false;
        if (!anyActive) {
          // Alle ruller har stoppet — sett fanfare og kall onSpinDone
          if (!s.doneCalled) {
            s.doneCalled = true;
            setTimeout(() => playFanfare(), 400);
            const liveOnSpinDone = onSpinDoneRef.current;
            if (liveOnSpinDone) setTimeout(liveOnSpinDone, 800);
          }
        }
      }

      // 2) Tegn svart maske bak sylinderne
      drawMask();

      // 3) Tegn lyspærene
      drawLights(ts);

      // 4) Tegn sylinderne
      const rect = getCylinderRect();
      const cellW = rect.w / TOTAL_REELS;
      const cellH = rect.h;
      s.cellH = cellH;  // lagres for updateSpin

      for (let i = 0; i < TOTAL_REELS; i++) {
        const cx = rect.x + (i + 0.5) * cellW;
        const cy = rect.y + cellH / 2;
        const r = s.reels[i] || { pool: [''], y: 0, stopped: true, ch: '', mid: 0, spd: 0 };
        drawCylinder(r, cellW, cellH, cx, cy);
      }

      // 5) Vinner-lodd (papir-billett-stil) — kun når alle reels er stoppet (samme øyeblikk som fanfaren)
      if (s.phase === 'done') {
        drawWinnerLodd(ts);
      }

      s.raf = requestAnimationFrame(draw);
    }

    resize();
    window.addEventListener('resize', resize);
    s.raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(s.raf);
      window.removeEventListener('resize', resize);
      if (s.pendingSpinTimeoutId) clearTimeout(s.pendingSpinTimeoutId);
    };
  }, []); // eslint-disable-line

  // ─── WINNER-EFFEKT: Sett i gang spinn når winnerName endres ───
  useEffect(() => {
    const s = stateRef.current;

    // STEG 1: Reset ved ingen winner
    if (!winnerName) {
      s.doneCalled = false;
      s.phase = 'idle';
      s.reels = [];
      s.lightsBlinkUntil = 0;
      lastWinnerRef.current = null;
      startedRef.current = false;
      if (s.pendingSpinTimeoutId) {
        clearTimeout(s.pendingSpinTimeoutId);
        s.pendingSpinTimeoutId = null;
      }
      return;
    }

    // STEG 2: VENT med å starte spinn hvis countdown > 0
    if (countdown != null && countdown > 0) {
      s.phase = 'idle';
      // Hvis ny vinner under nedtelling — rens reels
      if (lastWinnerRef.current !== winnerName) {
        s.reels = [];
        s.lightsBlinkUntil = 0;
        s.doneCalled = false;
        startedRef.current = false;
        if (s.pendingSpinTimeoutId) {
          clearTimeout(s.pendingSpinTimeoutId);
          s.pendingSpinTimeoutId = null;
        }
      }
      return;
    }

    // STEG 3: Hvis denne winner allerede HAR vært igang og er ferdig — ikke gjør noe nytt
    // (men hvis den er en NY winner, fortsett til reset+spin nedenfor)
    if (lastWinnerRef.current === winnerName && startedRef.current) {
      return;
    }

    // STEG 4: NY WINNER (eller første gang) — full reset og start spinn
    if (s.pendingSpinTimeoutId) {
      clearTimeout(s.pendingSpinTimeoutId);
      s.pendingSpinTimeoutId = null;
    }
    s.phase = 'idle';
    s.reels = [];
    s.lightsBlinkUntil = 0;
    s.doneCalled = false;

    lastWinnerRef.current = winnerName;
    startedRef.current = true;

    // Rebuild reels med ønsket sluttposisjon
    const displayName = (() => {
      const parts = winnerName.trim().split(/\s+/).filter(Boolean);
      const joined = parts.length <= 3 ? parts.join(' ') : parts[0] + ' ' + parts[parts.length-1];
      const up = joined.toUpperCase();
      if (up.length <= TOTAL_REELS) return up;
      const p2 = up.split(' ');
      if (p2.length > 1) {
        const ss = p2[0] + ' ' + p2[p2.length-1];
        return ss.length <= TOTAL_REELS ? ss : ss.slice(0, TOTAL_REELS);
      }
      return up.slice(0, TOTAL_REELS);
    })();

    const charMap = (() => {
      const chars = displayName.split('');
      const n = chars.length;
      const result = new Array(TOTAL_REELS).fill('');
      const startIdx = Math.floor((TOTAL_REELS - n) / 2);
      chars.forEach((c, i) => { result[startIdx + i] = c; });
      return result;
    })();

    s.reels = [];
    charMap.forEach((ch) => {
      const mid = Math.floor(POOL_SIZE / 2);
      const pool = [];
      for (let i = 0; i < POOL_SIZE; i++) {
        pool.push(POOL_CHARS[Math.floor(Math.random() * POOL_CHARS.length)]);
      }
      pool[mid] = ch || '';
      // Initial y: helt UNNA mid (motsatt side av poolen)
      // mid=16, så vi velger y i området 0-7 eller 24-31 (langt fra mid=16)
      const farFromMid = Math.random() < 0.5
        ? Math.floor(Math.random() * 8)         // 0-7
        : 24 + Math.floor(Math.random() * 8);   // 24-31
      s.reels.push({
        pool, mid, ch,
        y: farFromMid,
        spd: 0,
        spinning: false, braking: false, stopTime: 0, startTime: 0,
        isLast: false, stopped: true,
        lastTickY: Math.floor(farFromMid),
      });
    });

    // Lever-lyd
    try {
      const ac = getAudioCtx();
      if (ac) {
        s.audioCtx = ac;
        const now = ac.currentTime;
        const o = ac.createOscillator(), g = ac.createGain();
        o.connect(g); g.connect(ac.destination);
        o.type = 'sawtooth';
        o.frequency.setValueAtTime(400, now);
        o.frequency.exponentialRampToValueAtTime(80, now + 0.4);
        g.gain.setValueAtTime(0.13, now);
        g.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
        o.start(now); o.stop(now + 0.4);
      }
    } catch(e) {}

    // Etter 400ms: sett alle i gang med randomisert stop-tid (sekvensiell)
    s.pendingSpinTimeoutId = setTimeout(() => {
      s.pendingSpinTimeoutId = null;
      const activeIndices = s.reels.map((r, i) => i);
      const a = [...activeIndices];
      for (let i = a.length-1; i > 0; i--) {
        const j = Math.floor(Math.random()*(i+1));
        [a[i],a[j]] = [a[j],a[i]];
      }
      const minStop = 1400, maxStop = 4800;
      a.forEach((ri, step) => {
        const isLast = step === a.length - 1;
        let t;
        if (isLast) {
          t = maxStop + 600 + (Math.random() - 0.5) * 200;
        } else {
          t = minStop + (step / Math.max(a.length-1, 1)) * (maxStop - minStop) + (Math.random() - 0.5) * 350;
        }
        const reel = s.reels[ri];
        const cellH = s.cellH || 60;
        reel.spd = cellH * 0.28;
        reel.spinning = true;
        reel.braking = false;
        reel.stopped = false;
        reel.hasSpun = true;  // nå skal pool-bokstaver vises
        reel.startTime = performance.now();
        reel.stopTime = t;
        reel.isLast = isLast;
        reel.lastTickY = Math.floor(reel.y);
      });
      s.phase = 'spinning';
    }, 400);
  }, [winnerName, countdown]); // eslint-disable-line

  // ─── RENDERING ───
  // For alle viewModes: bildet sentreres proporsjonalt, canvas oppå på eksakt samme sted.
  // Mobil = fyller bredden. PC/bred skjerm = sentrert med svart bakgrunn.
  const isMobile = viewMode === "player-mobile" || viewMode === "organizer-mobile";

  return (
    <div style={{
      position:'relative',
      width:'100%',
      height: heightOverride ? `${heightOverride}px` : '100vh',
      background: '#000',
      overflow:'hidden',
    }}>
      {/* Gullramme rundt hele området — KUN på mobil */}
      {isMobile && (
        <div style={{
          position:'absolute', inset:0,
          border: '6px solid',
          borderImage: 'linear-gradient(145deg,#fff5a0 0%,#C9A84C 20%,#8B6B1A 50%,#C9A84C 80%,#fff5a0 100%) 1',
          boxShadow: 'inset 0 0 0 2px rgba(0,0,0,0.4), 0 0 20px rgba(201,168,76,0.3)',
          pointerEvents: 'none',
          zIndex: 4,
        }}/>
      )}

      {/* Maskinen-bildet plassert NEDERST i container (både mobil og PC).
          PC: maskinen klistres til bunnen og tar 85% av høyden, slik at det
          frigjøres plass på toppen til nedtelling og gratulerer-kortet. */}
      <div style={{
        position:'absolute',
        // Mobil: full bredde. PC: sentrert, med høyde 85% og auto bredde via aspect.
        ...(isMobile
          ? {
              left: 6, right: 6,
              bottom: 1,
              // På mobil: container med EFFEKTIV maskin-aspect (kutter svart felt på topp/bunn)
              // Bildet er 941×1672 med ~9.4% svart på topp og bunn.
              // Effektiv maskin: 941 × (1672 × 0.812) = 941 × 1358 ≈ aspect 0.693
              aspectRatio: '941 / 1358'
            }
          : {
              // PC: klistret til bunn, høyde 80% av container, sentrert horisontalt.
              // (Var 85%, krympet 5% for mer luft over til gratulerer/nedtelling.)
              left: '50%', bottom: 6,
              transform: 'translateX(-50%)',
              height: '80%',
              aspectRatio: '941 / 1358',
            }
        ),
        zIndex: 1,
      }}>
        {imgLoaded && (
          // Samme background-image-trikset på både mobil og PC: skyver de svarte
          // feltene på topp/bunn av bildet utenfor synlig område.
          <div style={{
            position:'absolute', inset:0,
            backgroundImage: 'url(/slot_machine.png)',
            backgroundSize: '100% 123%',  // 100/0.812 ≈ 123%, svart felt skyves utenfor
            backgroundPosition: 'center 50%',
            backgroundRepeat: 'no-repeat',
            pointerEvents:'none',
          }} />
        )}
      </div>

      {/* Canvas dekker HELE container — slik at vinneranimasjoner kan synes
          i hele området (ikke begrenset til bilde-container) */}
      <canvas
        ref={canvasRef}
        style={{
          position:'absolute',
          left: 6, right: 6, top: 6, bottom: 6,
          width: 'calc(100% - 12px)', height: 'calc(100% - 12px)',
          display:'block',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />

      {/* MERK: Countdown håndteres av LiveDrawView i parent — ikke duplisert her */}
    </div>
  );
}


// ARKITEKTUR: status i Firestore er sannheten. Alt routing bestemmes av status.
// Ingen sessionStorage, ingen requestedView-state som kan komme ut av sync.

export default function App() {
  const [user, setUser] = useState(null);
  const [scrollToWinners, setScrollToWinners] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [lotteryState, setLotteryState] = useState(null); // null = ikke lastet ennå
  const [settings, setSettings] = useState({});
  const [prizes, setPrizes] = useState([]);
  const [participants, setParticipants] = useState([]);

  // Navigasjon for ikke-trekning-tilstander
  const [adminView, setAdminView] = useState("participant"); // participant | organizer
  const [logoClicks, setLogoClicks] = useState(0);
  const logoClickTimer = useRef(null);
  // v163: T-knapp i topbaren genererer 20 testspillere (samme atomiske
  // allokering som TestDataKnapp/handleBuy). Ingen slett — det gjøres via
  // eksisterende UI. testBusy hindrer dobbeltklikk.
  const [testBusy, setTestBusy] = useState(false);
  const addTestUsers = async () => {
    if (testBusy) return;
    setTestBusy(true);
    try {
      const price = settings?.ticketPrice || 10;
      for (const p of TESTPERSONER) {
        const tNums = await allocateTicketNumbers(p.tickets);
        await addDoc(collection(db,"participants"),{
          name:p.name, email:p.email, tickets:p.tickets,
          paid:true, amount:p.tickets*price, kredittBrukt:0,
          ticketNumbers:tNums, createdAt:serverTimestamp()
        });
      }
    } catch(e){ showErrorToast("Feil: "+e.message); }
    setTestBusy(false);
  };

  // v57: bredde-basert valg av arrangør-vy. PC (>= 900 px) → dashboard;
  // mobil (< 900 px) → klassisk kolonne-layout. Reaktiv på vindus-resize.
  const pcDashboardActive = usePcDashboard();
  // v58 Fase 1: PC-spillside (Alt A) bruker samme bredde-terskel som
  // arrangør-dashboardet. Ingen classic-override for spillere.
  const isPcWidth = useIsPcWidth();

  const isAllowed = user && ALLOWED_EMAILS.includes(user.email);
  const status = lotteryState?.status; // idle | drawing | done
  // v183/v184: når en fersk server-trekning har spilt gjennom alle premier,
  // skriver HVILKEN SOM HELST klient (også anonym tilskuer) status:"done" +
  // slår av autoDraw/drawTime + rydder drawn-feltene. Idempotent (flere klienter
  // kan skrive samme verdi). Server-funksjonen autoCompleteDraw er sikkerhetsnett
  // når INGEN klient er til stede. Stale-guarden i useDrawProgress hindrer at
  // gammel data trigger dette (v179-lærdom).
  const appDrawProgress = useDrawProgress(lotteryState);
  useEffect(() => {
    if (!appDrawProgress.ready || !appDrawProgress.ferdig) return;
    if (lotteryState?.status !== "drawing") return;
    let cancelled = false;
    let attempts = 0;
    // v185: transaksjonell — KUN den første klienten som vinner transaksjonen
    // flipper drawing→done. Andre klienter (og server-nettet) leser status=done
    // → no-op. Hindrer flere samtidige skrivinger → ingen render-flicker.
    // v194: A) godartet "permission-denied" (en annen klient vant kappløpet og
    // dokumentet er allerede "done" → done-overgangsregelen nekter taperen) logges
    // IKKE som feil. B) hvis dokumentet fortsatt er "drawing" etter en nektelse
    // (sjeldent — kan skje ved samtidig kollisjon), prøv igjen innen 1s i stedet
    // for å vente på serverens autoCompleteDraw (~30s). Regelen tillater en ren
    // drawing→done (verifisert via Rules test-API), så retryen lykkes raskt.
    const writeDone = async () => {
      if (cancelled) return;
      attempts++;
      try {
        await runTransaction(db, async (txn) => {
          const ref = doc(db, "lottery", "current");
          const snap = await txn.get(ref);
          if (!snap.exists()) return;
          if (snap.data().status !== "drawing") return; // allerede done → ikke skriv
          txn.update(ref, {
            status: "done", autoDraw: false, drawTime: null,
            drawnPrizes: deleteField(), drawStartedAt: deleteField(), premieVarighetMs: deleteField(),
            currentDrawMethod: deleteField(), mixOrder: deleteField(), // v189: rydd stale miks-felter ved done
            drawTotalMs: deleteField(), // v190: rydd total-varighet ved done
          });
        });
      } catch (err) {
        if (err?.code === "permission-denied") {
          // Sjekk om dokumentet faktisk ble "done". Hvis ja: godartet (en annen
          // vant) → ingen logg. Hvis fortsatt "drawing": forbigående kollisjon →
          // prøv igjen (maks 4 forsøk; serveren er endelig sikkerhetsnett).
          if (cancelled || attempts >= 4) return;
          try {
            const s = await getDoc(doc(db, "lottery", "current"));
            if (!cancelled && s.exists() && s.data().status === "drawing") {
              setTimeout(writeDone, 1000);
            }
          } catch (_) { /* stille */ }
        } else {
          console.error("done-handler transaksjon feilet:", err); // eslint-disable-line no-console
        }
      }
    };
    const t = setTimeout(writeDone, 3000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [appDrawProgress.ready, appDrawProgress.ferdig, lotteryState?.status]);
  // v42: pre-konverter ekstern bgPattern-URL til base64 for å unngå CORS-
  // problem ved SVG-embedding i random-modus. Data:-URI'er passerer rett
  // gjennom. Brukes nedenfor i normal-render.
  const bgPatternResolved = useResolvedPatternUrl(settings?.bgPatternUrl);

  useEffect(()=>{ if(window.location.search.includes("admin")) setAdminView("organizer"); },[]);
  // v64 FIX 1: kritisk bug-fix — "Noe gikk galt" ved Registrer-klikk skyldtes
  // at firestore.rules krever request.auth != null for writes. Spillere som
  // ikke er logget inn med Google fikk derfor permission-denied når
  // addDoc(participants, …) ble kalt. Fix: auto-anonym-innlogging for alle
  // besøkende — gir en stabil UID som tilfredsstiller rules-en, uten å gi
  // arrangør-rettigheter (isAllowed sjekker fortsatt mot ALLOWED_EMAILS).
  useEffect(()=>{
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        try { await signInAnonymously(auth); }
        catch (e) {
          console.warn("Anonym innlogging feilet — Firestore-skriving vil mislykkes til Anonymous Auth er aktivert i Firebase-konsollet (Authentication → Sign-in method → Anonymous):", e);
          setAuthChecked(true);
        }
        // Vent på neste onAuthStateChanged-fyring med den nye anonyme brukeren.
      } else {
        setUser(u);
        setAuthChecked(true);
      }
    });
    return unsub;
  },[]);
  // Initialiser kun hvis dokumentet IKKE finnes fra før.
  // Skriv ALDRI over eksisterende status – det er Firestore-status som er sannheten.
  // v109: gates på isAllowed. Anonyme deltakere skal ikke initiere DB-en —
  // den nye rules-strammingen (v110) vil avvise slik skriving uansett.
  // Hvis DB er tom og kun deltakere åpner appen, venter init til en arrangør
  // er innlogget. Logger én gang per session når init hoppes over.
  const initGateLoggedRef = useRef(false);
  useEffect(()=>{
    if (!isAllowed) {
      if (!initGateLoggedRef.current) {
        initGateLoggedRef.current = true;
        console.log("[init-gate] App-root init hoppet over — ikke arrangør");
      }
      return;
    }
    const init = async () => {
      const snap = await getDoc(doc(db,"lottery","current"));
      if (!snap.exists()) {
        const now = new Date();
        await setDoc(doc(db,"lottery","current"),{
          status:"idle",
          weekLabel:`Uke ${getWeekNumber(now)} ${now.getFullYear()}`,
          winnerHistory:[], winnerEmailSent:false, currentPrizeIdx:0, currentWinnerName:null,
        });
        await setDoc(doc(db,"counters","tickets"),{ next:1 });
      }
    };
    init();
  },[isAllowed]);
  useEffect(()=>{ const unsub=onSnapshot(doc(db,"lottery","current"),snap=>{ if(snap.exists()) setLotteryState(snap.data()); }); return unsub; },[]);

  // Auto-trekning og auto-åpning — leser ALLTID fersk status fra Firestore.
  // v109: gates på isAllowed. Intervallet kjører kun for arrangører, så
  // deltakere ikke lenger forsøker å skrive lottery/current og
  // counters/tickets fra denne tick-funksjonen. Når en arrangør logger ut
  // flippes isAllowed til false → effekten re-mountes og clearInterval
  // kjører. Logger én gang per session når tick er gated av.
  const autoTickGateLoggedRef = useRef(false);
  useEffect(()=>{
    if (!isAllowed) {
      if (!autoTickGateLoggedRef.current) {
        autoTickGateLoggedRef.current = true;
        console.log("[auto-tick-gate] Auto-tick ikke startet — ikke arrangør");
      }
      return;
    }
    const tick = setInterval(async()=>{
      // Les alltid fra Firestore — unngår stale lotteryState i closure
      const snap = await getDoc(doc(db,"lottery","current"));
      if (!snap.exists()) return;
      const fresh = snap.data();

      // v193: klient-side auto-trekning FJERNET. Server (functions/index.js autoDraw,
      // every 1 minutes) eier auto-trekning. Klient-triggeren vant kappløpet mot
      // serveren (10s vs 60s) og startet en klient-fallback-trekning uten drawnPrizes
      // → DrawScreen + fallback-motor → kuttet siste premie. Server skriver nå
      // drawnPrizes/startOffsetMs/drawTotalMs, og arrangør ser LiveDrawView som tilskuer.
      // v193: klient-side auto-åpning FJERNET. Server (functions/index.js autoOpenSale) eier den.
      // Beholder kun idle-reset av scroll-til-vinnere-flagget.
      if(fresh.status==="idle") { setScrollToWinners(false); }
    }, 10000); // sjekk hvert 10 sekund
    return ()=>clearInterval(tick);
  },[isAllowed]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{ const unsub=onSnapshot(doc(db,"settings","config"),snap=>{ if(snap.exists()) setSettings(snap.data()); }); return unsub; },[]);
  useEffect(()=>{ const unsub=onSnapshot(collection(db,"prizes"),snap=>{ setPrizes(snap.docs.map(d=>({ id:d.id,...d.data() })).sort((a,b)=>(a.createdAt?.seconds||0)-(b.createdAt?.seconds||0))); }); return unsub; },[]);
  useEffect(()=>{ const unsub=onSnapshot(collection(db,"participants"),snap=>{ setParticipants(snap.docs.map(d=>({ id:d.id,...d.data() }))); }); return unsub; },[]);

  // v106: Single-device-arrangør, løftet fra OrganizerView til App-root så
  // mobilen kastes ut uavhengig av om OrganizerView er mounted (f.eks. når
  // mobil-arrangør står på deltaker-vy mens PC tar over).
  //
  // Flyt: når isAllowed flipper til true → (1) claim settings/adminSession +
  // lottery/current.activeSession med denne fanens SESSION_ID; (2) start
  // onSnapshot + 5s poll + visibility/focus-handler på settings/adminSession;
  // hvis lagret sessionId ≠ SESSION_ID, gjør signOut og hard reload.
  //
  // Claim kjøres FØR listeneren startes for å unngå selv-kick race der
  // listenerens første snapshot ville lese den forrige enhetens sessionId.
  useEffect(()=>{
    if (!isAllowed || !user?.email) return;
    let unsubscribed = false;
    const cleanups = [];
    (async () => {
      try {
        await claimAdminSessionForEmail(user.email);
      } catch (e) {
        console.error("[adminSession] claim feilet:", e);
      }
      if (unsubscribed) return;

      let kicked = false;
      const handleKick = async () => {
        if (kicked) return;
        kicked = true;
        console.log("[adminSession] KICKED — annen enhet har tatt over");
        try {
          setAdminView("participant");
          setUser(null);
          await signOut(auth);
        } catch (e) {
          console.error("[adminSession] signOut feilet under kick:", e);
        }
        setTimeout(() => { try { window.location.reload(); } catch (e) {} }, 200);
      };
      const checkForKick = (data) => {
        if (data?.sessionId && data.sessionId !== SESSION_ID) handleKick();
      };

      const unsub = onSnapshot(doc(db,"settings","adminSession"), snap => {
        if (snap.exists()) checkForKick(snap.data());
      });
      cleanups.push(unsub);

      const pollInterval = setInterval(async () => {
        if (unsubscribed) return;
        try {
          const snap = await getDoc(doc(db,"settings","adminSession"));
          if (snap.exists()) checkForKick(snap.data());
        } catch (e) { /* ignorer */ }
      }, 5000);
      cleanups.push(() => clearInterval(pollInterval));

      const visibilityHandler = async () => {
        if (document.visibilityState !== "visible" || unsubscribed) return;
        try {
          const snap = await getDoc(doc(db,"settings","adminSession"));
          if (snap.exists()) checkForKick(snap.data());
        } catch (e) { /* ignorer */ }
      };
      document.addEventListener("visibilitychange", visibilityHandler);
      window.addEventListener("focus", visibilityHandler);
      cleanups.push(() => {
        document.removeEventListener("visibilitychange", visibilityHandler);
        window.removeEventListener("focus", visibilityHandler);
      });
    })();
    return () => {
      unsubscribed = true;
      cleanups.forEach(fn => { try { fn(); } catch (e) {} });
    };
  },[isAllowed, user?.email]);



  const handleLogoClick = () => {
    if (status==="drawing") return;
    const n = logoClicks + 1; setLogoClicks(n); clearTimeout(logoClickTimer.current);
    if (n >= 3) { setAdminView("organizer"); setLogoClicks(0); }
    else logoClickTimer.current = setTimeout(()=>setLogoClicks(0), 1000);
  };
  // v91: TOPBANNER styres av `isAllowed && !(...)` (App.js:13840). isAllowed
  // = user && ALLOWED_EMAILS.includes(user.email). signOut() fyrer
  // onAuthStateChanged(null), men handleren der setter IKKE setUser(null) —
  // den prøver bare signInAnonymously og venter. Mens vi venter, beholder
  // React det gamle admin-user-objektet i state → isAllowed forblir true →
  // BANNER FORBLIR SYNLIG. v90 satte bare adminView="participant" som
  // ikke er nok. Fix v91: setUser(null) eksplisitt så isAllowed flipper
  // til false umiddelbart, banneret forsvinner, og UI lander på
  // ParticipantView. Console-logs hjelper diagnose hvis dette feiler igjen.
  const handleLogout = async () => {
    setAdminView("participant");
    setUser(null);
    try {
      await signOut(auth);
    } catch (e) {
      console.error("[logout] signOut feilet:", e);
    }
  };

  // Laster: vent til Firestore-status er kjent
  if (!authChecked || lotteryState === null) {
    return (
      <div style={{ background:"#0a0a0a",minHeight:"100vh",width:"100%",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden" }}>
        <p style={{ color:"#333",fontFamily:"'Inter',system-ui,sans-serif",fontSize:16 }}>Laster…</p>
      </div>
    );
  }

  // ── TREKNING PÅGÅR ──────────────────────────────────────────────────────────
  // Firestore sier status=drawing → VIS ALLTID TREKNINGSSKJERM, uansett refresh
  if (status === "drawing") {
    return (
      <div style={{ fontFamily:"'Inter',system-ui,-apple-system,sans-serif",background:"#0a0a0a",minHeight:"100vh",width:"100%",maxWidth:"100vw",color:"#f5efe0",display:"flex",flexDirection:"column",overflowX:"hidden" }}>
        <div style={{ background:"#0d0d0d",borderBottom:"1px solid #333",padding:"12px 16px",display:"flex",alignItems:"center",gap:10,boxSizing:"border-box" }}>
          <span style={{ fontSize:20 }}>🍾</span>
          <span style={{ fontSize:16,color:"#C9A84C",letterSpacing:"0.12em",textTransform:"uppercase",fontFamily:settings?.tittelFont||"'Inter',system-ui,sans-serif" }}>{settings?.lotteryName||"Vinloddet"}</span>
        </div>
        {/* v204 (Vei C fase 2): ALLE trekninger er server-styrt (utforTrekning skriver
            drawnPrizes). Både arrangør og tilskuere ser LiveDrawView (tidsbasert).
            DrawScreen/fallback-motoren er fjernet. */}
        <LiveDrawView lotteryState={lotteryState} prizes={prizes} participants={participants} settings={settings}/>
      </div>
    );
  }

  // ── NORMAL VISNING (idle / done) ─────────────────────────────────────────────
  // v40-42: bakgrunns-mønster. Hvis bgPatternRandomness > 0 brukes en
  // deterministisk SVG-super-tile (via generatePatternSVG) der kopiene er
  // jittered. v42: for ekstern URL må vi pre-konvertere til base64 (CORS).
  // Hvis fetch feiler, faller vi automatisk tilbake til vanlig CSS-repeat
  // (som ikke krever CORS for backgrounds).
  const bgPattern = settings?.bgPatternEnabled && settings?.bgPatternUrl ? (()=>{
    const size = settings.bgPatternSize || 80;
    const opacity = (settings.bgPatternOpacity || 25) / 100;
    const randomness = settings.bgPatternRandomness || 0;
    const seed = settings.bgPatternSeed || 42;
    if (randomness > 0 && bgPatternResolved.resolved) {
      const svg = generatePatternSVG({ url:bgPatternResolved.resolved, imageSize:size, randomness, seed });
      if (svg) {
        return { image:svg.dataUri, repeatSize:svg.tileSize, opacity };
      }
    }
    return { image:settings.bgPatternUrl, repeatSize:size, opacity };
  })() : null;
  return (
    <div style={{ fontFamily:"'Inter',system-ui,-apple-system,sans-serif",background:settings?.bakgrunnsFarge||"#0a0a0a",minHeight:"100vh",width:"100%",maxWidth:"100vw",color:"#f5efe0",boxSizing:"border-box",overflowX:"clip",position:"relative" }}>
      {bgPattern && (
        <div style={{
          position:"absolute", inset:0, pointerEvents:"none",
          backgroundImage:`url("${bgPattern.image}")`,
          backgroundRepeat:"repeat",
          backgroundSize:`${bgPattern.repeatSize}px`,
          opacity:bgPattern.opacity,
          zIndex:0,
        }}/>
      )}
      {/* v40: alt innhold ligger på relative-laget med zIndex:1 slik at
          bakgrunns-mønster fra ::before-overlay forblir bak. */}
      <div style={{ position:"relative", zIndex:1 }}>
      {/* Globalt CSS: tving "lining numerals" så tall vises normalt
          (ellers stikker 0,3,4,5,7,9 under linjen i mange serif-fonter). */}
      <style>{`
        * { font-variant-numeric: lining-nums tabular-nums; font-feature-settings: 'lnum' 1, 'tnum' 1; }
      `}</style>
      {/* Topbar kun for innlogget arrangør. v28: skjules når PC-dashboard
          er aktiv (PC-dashboardet har sin egen topbar med branding + bytte-
          knapper). Branding ville ellers dukket opp to ganger.
          v57: pcDashboardActive er nå rent bredde-basert (≥ 900 px). */}
      {isAllowed && !(adminView==="organizer" && pcDashboardActive) && (
        <div style={{
          background:"#0d0d0d", borderBottom:"1px solid #333",
          padding: isPcWidth ? "10px 16px" : "calc(env(safe-area-inset-top, 0px) + 10px) 16px 10px",
          display:"flex", alignItems:"center", gap:6,
          boxSizing:"border-box",
          // v91: sticky på mobil så banneret følger med ved scroll. z-index
          // 100 ligger OVER ParticipantView's sticky-top (z-index 95).
          ...(isPcWidth ? {} : { position:"sticky", top:0, zIndex:100 }),
        }}>
          {/* v165: alle 6 ikon-knapper (📢, T, profil, 👁, ⚙️, 🚪) i ÉN rad med
              lik størrelse (S.topbarBtn, 34×34). Versjon (vNN-NNN) stablet over
              raden, høyre-justert. Venstre spacer er tappbar (beholder
              handleLogoClick-gesten). 📢/T vises kun i arrangør-modus. */}
          <div style={{ flex:1, minWidth:0, alignSelf:"stretch", cursor:"pointer" }} onClick={handleLogoClick}/>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:3, flexShrink:0 }}>
            <div style={{ fontSize:9,color:"#C9A84C",letterSpacing:"0.06em",fontFamily:"'Courier New', monospace",opacity:0.7,whiteSpace:"nowrap" }}>v{APP_VERSION.split(".")[2]}</div>
            <div style={{ display:"flex",gap:6,alignItems:"center" }}>
              {adminView==="organizer" && (
                <button onClick={()=>window.dispatchEvent(new CustomEvent("vl-open-ticker"))} title="Ticker-melding"
                  style={{ ...S.topbarBtn, color:"#C9A84C", borderColor:"#C9A84C" }}>📢</button>
              )}
              {adminView==="organizer" && (
                <button onClick={addTestUsers} disabled={testBusy} title="Generer 20 testspillere"
                  style={{ ...S.topbarBtn, color:"#9a9aff", borderColor:"#3a3a9a", fontWeight:"bold", fontSize:14, opacity:testBusy?0.5:1, cursor:testBusy?"not-allowed":"pointer" }}>{testBusy?"…":"T"}</button>
              )}
              {user?.photoURL&&<img src={user.photoURL} alt="" style={{ width:34,height:34,borderRadius:"50%",border:"1px solid #3a3a3a",flexShrink:0,objectFit:"cover" }} referrerPolicy="no-referrer"/>}
              <button style={{ ...S.topbarBtn,...(adminView==="participant"?S.adminTabActive:{}) }} onClick={()=>setAdminView("participant")} title="Forhåndsvisning">👁</button>
              <button style={{ ...S.topbarBtn,...(adminView==="organizer"?S.adminTabActive:{}) }} onClick={()=>setAdminView("organizer")} title="Innstillinger">⚙️</button>
              <button style={{ ...S.topbarBtn,color:"#e57373",borderColor:"#5a1a1a" }} onClick={handleLogout} title="Logg ut">🚪</button>
            </div>
          </div>
        </div>
      )}

      {/* v57: PC-arrangør-dashboard er permanent default på PC (≥ 900 px).
          v58 Fase 1: PC-spillside (Alt A) får eget bredt 2-kolonners layout
          via pcLayout-prop. 540-px wrapper lukkes for PC-spillere — mobil
          og klassisk arrangør (?admin=classic) beholder den smale kolonnen.
          ?admin=classic på PC tvinger fortsatt klassisk vy (URL-only). */}
      {(()=>{
        const showPCDashboard = adminView==="organizer" && isAllowed && pcDashboardActive;
        if (showPCDashboard) {
          return <OrganizerViewPC user={user} onLogout={handleLogout} onStartDraw={()=>{}} lotteryState={lotteryState} settings={settings} onShowWinners={()=>setScrollToWinners(true)} onSwitchAdminView={setAdminView}/>;
        }
        // PC-spillside (Fase 1): ingen 540-cap, ParticipantView eier eget
        // 1280-wrapper. Mobil og klassisk arrangør: behold 540-kolonne.
        const isPcPlayerView = isPcWidth && adminView==="participant";
        const wrapperStyle = isPcPlayerView
          ? { width:"100%", paddingBottom:60, boxSizing:"border-box" }
          : { maxWidth:540, margin:"0 auto", paddingBottom:60, width:"100%", boxSizing:"border-box" };
        return (
          <div style={wrapperStyle}>
            {/* Deltaker-visning */}
            {adminView==="participant"&&(
              <>
                <ParticipantView lotteryState={lotteryState} settings={settings} prizes={prizes} participants={participants} onLogoClick={handleLogoClick} pcLayout={isPcWidth}
                  stickyTopOffset={!isPcWidth && isAllowed && !(adminView==="organizer" && pcDashboardActive) ? 56 : 0}
                  scrollToWinners={scrollToWinners}/>
                {/* v309: WinnerViewWithScroll (done-vinnere) er flyttet INN i
                    ParticipantView (innenfor gullrammen) på PC. Tidligere lå den
                    her som søsken → utenfor rammen. Mobil viser vinnerlista inne
                    i ParticipantView's midtre flex-seksjon som før. */}
              </>
            )}
            {/* Arrangør-visning */}
            {adminView==="organizer"&&!isAllowed&&<LoginScreen/>}
            {adminView==="organizer"&&isAllowed&&(
              <OrganizerView user={user} onLogout={handleLogout} onStartDraw={()=>{}} lotteryState={lotteryState} settings={settings} onShowWinners={()=>setScrollToWinners(true)}/>
            )}
          </div>
        );
      })()}
      </div>{/* v40: lukker wrapper-div for innhold-over-bakgrunnsmønster */}
    </div>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const gold="#C9A84C", dark="#0a0a0a", card="#141414", border="#444", cream="#f5efe0";
const S = {
  // Shop
  shopView:{ padding:"0 16px calc(120px + env(safe-area-inset-bottom, 0px))",width:"100%",maxWidth:600,margin:"0 auto",boxSizing:"border-box" },
  shopHeader:{ padding:"20px 20px 14px",borderBottom:`1px solid ${border}` },
  shopEyebrow:{ margin:"0 0 4px",fontSize:10,color:"#555",letterSpacing:"0.2em",textTransform:"uppercase" },
  shopTitle:{ margin:0,fontSize:24,fontWeight:"700",color:"#C9A84C",letterSpacing:"0.06em",textTransform:"uppercase",fontFamily:"'Inter',system-ui,sans-serif",lineHeight:1.15 },
  logoWrap:{ padding:"14px 20px 0",textAlign:"center" },
  logoImg:{ width:"100%",objectFit:"contain",display:"block" },
  headerImg:{ width:"100%",objectFit:"cover",display:"block" },
  countdownBanner:{ margin:"14px 20px 0",background:"#1a1200",border:`1px solid ${gold}`,borderRadius:4,padding:"12px 16px",textAlign:"center" },
  countdownLabel:{ margin:"0 0 8px",fontSize:10,color:"#888",letterSpacing:"0.12em",textTransform:"uppercase",fontFamily:"'Lato',Arial,sans-serif" },
  countdownTimer:{ display:"flex",justifyContent:"center",gap:14 },
  countdownUnit:{ fontSize:28,fontWeight:"bold",color:gold,minWidth:48,textAlign:"center",fontFamily:"'Lato',Arial,sans-serif",lineHeight:1.1 },
  prizesSection:{ padding:"18px 0 0" },
  sectionLabel:{ margin:"0 0 12px",fontSize:10,color:"#888",letterSpacing:"0.2em",textTransform:"uppercase" },
  prizeList:{ display:"flex",flexDirection:"column",gap:1 },
  prizeRow:{ padding:"14px 12px",border:"1px solid #e8e8e8",display:"flex",alignItems:"center",gap:12 },
  bottleWrap:{ width:72,display:"flex",justifyContent:"center",alignItems:"center",flexShrink:0,background:"#fff" },
  bottleImg:{ width:"100%",objectFit:"contain" },
  prizeRank:{ margin:"0 0 4px",fontSize:11,color:"#8a6a00",textTransform:"uppercase",letterSpacing:"0.12em",fontWeight:"bold" },
  prizeName:{ margin:"0 0 3px",fontSize:14,fontWeight:"bold",color:"#222" },
  prizeMeta:{ margin:"0 0 3px",fontSize:11,color:"#888" },
  prizeDesc:{ margin:"0 0 5px",fontSize:12,color:"#666" },
  moreInfoLink:{ fontSize:12,color:"#8a6a00",textDecoration:"none",fontWeight:"bold" },
  buyPanel:{ background:"#0f0f0f",margin:"16px 20px 0",borderRadius:6,padding:20,border:"2px solid #3a2800",boxShadow:"0 0 20px rgba(201,168,76,0.12)" },
  buyPanelTitle:{ margin:"0 0 12px",fontSize:10,color:"#999",letterSpacing:"0.2em",textTransform:"uppercase" },
  input:{ width:"100%",padding:"10px 12px",borderRadius:2,background:"#0d0d0d",border:"1px solid #333",color:cream,fontSize:14,marginBottom:10,boxSizing:"border-box",fontFamily:"'Inter',system-ui,sans-serif",outline:"none" },
  inputLabel:{ display:"block",fontSize:10,color:"#aaa",letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:4 },
  buyLabel:{ margin:"4px 0 10px",fontSize:10,color:"#999",letterSpacing:"0.12em",textTransform:"uppercase" },
  qtyRow:{ display:"flex",alignItems:"center",gap:8,marginBottom:12,flexWrap:"wrap" },
  qtyBtn:{ width:38,height:38,borderRadius:2,background:"#0d0d0d",border:"1px solid #333",color:cream,fontSize:22,cursor:"pointer",flexShrink:0 },
  qtyNum:{ fontSize:24,fontWeight:"bold",color:gold,minWidth:30,textAlign:"center" },
  qtyPreset:{ padding:"6px 12px",borderRadius:2,background:"#0d0d0d",border:"1px solid #333",color:"#555",fontSize:12,cursor:"pointer" },
  qtyActive:{ border:`1px solid ${gold}`,color:gold,background:"#1a1200" },
  vippsBtn:{ width:"100%",padding:"14px",borderRadius:2,background:"#FF5B24",border:"none",color:"#fff",fontSize:15,fontWeight:"bold",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8 },
  vippsHint:{ margin:"8px 0 0",fontSize:10,color:"#999",textAlign:"center" },
  salesLockedBanner:{ margin:"16px 0 0",background:"#1a0d00",border:"1px solid #8B4500",borderRadius:4,padding:"14px",textAlign:"center",color:"#C9A84C",fontSize:14,marginBottom:0 },
  leaderboard:{ padding:"18px 20px 0" },
  leaderRow:{ display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:2,marginBottom:2,border:"1px solid #3a3a3a" },
  leaderRank:{ fontSize:18,minWidth:28 },
  leaderName:{ flex:1,fontSize:13,color:cream,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" },
  leaderTickets:{ fontSize:12,color:"#888",flexShrink:0 },
  leaderAmount:{ fontSize:12,color:gold,flexShrink:0 },
  participantStrip:{ padding:"18px 20px 0" },
  avatarRow:{ display:"flex",flexWrap:"wrap",gap:6 },
  avatar:{ width:34,height:34,borderRadius:"50%",background:"#1a1200",color:gold,fontSize:9,fontWeight:"bold",display:"flex",alignItems:"center",justifyContent:"center",border:"1px solid #2a2000",flexShrink:0 },
  waitingScreen:{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"100svh",paddingTop:24,paddingLeft:24,paddingRight:24,paddingBottom:"calc(24px + env(safe-area-inset-bottom, 0px))",gap:14 },
  waitingTitle:{ margin:0,fontSize:22,fontWeight:"normal",color:gold,letterSpacing:"0.1em",textAlign:"center" },
  myTicketsBadge:{ padding:"8px 20px",background:"#1a1200",border:`1px solid ${gold}`,borderRadius:20,fontSize:13,color:gold },
  ghostBtn:{ padding:"10px 24px",borderRadius:2,background:"transparent",border:"1px solid #333",color:"#555",cursor:"pointer",fontSize:11,letterSpacing:"0.1em",textTransform:"uppercase" },
  // v137: delt gul tilbake-knapp (kvitteringsside) — brukt på både PC og mobil.
  backBtnGold:{ background:"#C9A84C",color:"#1a1a1a",border:"none",borderRadius:6,padding:"8px 24px",fontSize:14,fontWeight:"bold",cursor:"pointer",letterSpacing:"0.05em" },

  // Login
  loginScreen:{ display:"flex",alignItems:"center",justifyContent:"center",minHeight:"80vh",padding:24 },
  loginCard:{ background:card,borderRadius:4,padding:40,width:"100%",maxWidth:300,textAlign:"center",border:"1px solid #333" },
  loginTitle:{ margin:"0 0 4px",fontSize:26,fontWeight:"normal",color:gold,letterSpacing:"0.2em",textTransform:"uppercase" },
  loginRule:{ margin:"0 0 24px",fontSize:10,color:"#444",letterSpacing:"0.2em",textTransform:"uppercase" },
  googleBtn:{ width:"100%",padding:"12px",background:"#fff",border:"none",color:"#222",fontSize:14,fontWeight:"bold",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:10,borderRadius:2 },
  loginError:{ color:"#e57373",fontSize:12,marginTop:14 },

  // Vinnerskjerm
  winnerScreen:{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"60vh",padding:24,gap:12 },
  winnerTitle:{ margin:0,fontSize:26,fontWeight:"normal",color:gold,letterSpacing:"0.15em",textTransform:"uppercase" },
  winnerCard:{ border:"1px solid #e0e0e0",borderRadius:6,padding:"10px 14px",display:"flex",alignItems:"center",gap:14,marginBottom:6,width:"100%",boxSizing:"border-box" },
  winnerMedal:{ fontSize:26,minWidth:32 },
  winnerName:{ margin:0,fontSize:15,fontWeight:"bold",color:"#111" },
  winnerPrizeName:{ margin:0,fontSize:12,color:"#8B6914" },

  // Live draw
  liveDrawScreen:{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flex:1,padding:20,gap:16,background:"radial-gradient(ellipse at center, #0d0800 0%, #0a0a0a 80%)",minHeight:"calc(100vh - 50px)" },
  drawAlertBanner:{ background:gold,color:dark,textAlign:"center",padding:"12px 20px",fontWeight:"bold",fontSize:14,letterSpacing:"0.1em",width:"100%",borderRadius:4,maxWidth:480,boxSizing:"border-box" },

  // Admin tabs
  adminTab:{ padding:"6px 10px",background:"none",border:"1px solid #333",color:"#555",cursor:"pointer",fontSize:14,borderRadius:2 },
  adminTabActive:{ color:gold,borderColor:gold,background:"#1a1200" },
  // v165: uniform kvadratisk topbar-knapp — alle 6 mobil-arrangør-ikoner
  // (📢, T, profil, 👁, ⚙️, 🚪) får samme størrelse og ligger på samme linje.
  topbarBtn:{ width:34,height:34,padding:0,display:"flex",alignItems:"center",justifyContent:"center",background:"none",border:"1px solid #333",borderRadius:6,cursor:"pointer",fontSize:15,lineHeight:1,flexShrink:0,boxSizing:"border-box" },

  // Dashboard
  dashView:{ paddingBottom:40,width:"100%" },
  dashHeader:{ display:"flex",alignItems:"flex-start",justifyContent:"space-between",padding:"18px 20px 14px",borderBottom:`1px solid ${border}`,gap:12 },
  dashTitle:{ margin:0,fontSize:17,fontWeight:"700",color:"#C9A84C",letterSpacing:"0.06em",textTransform:"uppercase",fontFamily:"'Inter',system-ui,sans-serif" },
  dashSub:{ margin:"2px 0 0",fontSize:10,color:"#888",letterSpacing:"0.12em",textTransform:"uppercase" },
  drawBtn:{ padding:"8px 14px",background:gold,border:"none",color:dark,borderRadius:2,cursor:"pointer",fontSize:12,fontWeight:"bold",letterSpacing:"0.06em",textTransform:"uppercase",whiteSpace:"nowrap",display:"block",width:"100%",textAlign:"center",marginBottom:4 },
  userBar:{ display:"flex",alignItems:"center",gap:10,padding:"10px 20px",background:"#0d0d0d",borderBottom:`1px solid ${border}` },
  userPhoto:{ width:26,height:26,borderRadius:"50%",border:"1px solid #333",flexShrink:0 },
  userName:{ fontSize:12,color:"#ccc",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" },
  logoutBtn:{ padding:"4px 10px",background:"transparent",border:`1px solid #444`,color:"#aaa",borderRadius:2,cursor:"pointer",fontSize:11,whiteSpace:"nowrap" },
  statsGrid:{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",borderBottom:`1px solid ${border}` },
  statCard:{ padding:"12px 4px",borderRight:`1px solid ${border}`,textAlign:"center" },
  statVal:{ margin:"0 0 2px",fontSize:16,fontWeight:"bold",color:gold },
  statLbl:{ margin:0,fontSize:8,color:"#888",textTransform:"uppercase",letterSpacing:"0.06em" },
  tabRow:{ display:"flex",borderBottom:`1px solid ${border}`,overflowX:"auto",justifyContent:"center" },
  tabBtn:{ flex:"0 0 auto",padding:"11px 6px",background:"none",border:"none",color:"#bbb",cursor:"pointer",fontSize:9,letterSpacing:"0.04em",textTransform:"uppercase",whiteSpace:"nowrap" },
  tabBtnActive:{ color:gold,borderBottom:`2px solid ${gold}` },
  table:{ background:card,border:"1px solid #333" },
  tableHead:{ display:"grid",gridTemplateColumns:"1fr 50px 60px 50px",padding:"10px 14px",borderBottom:`1px solid ${border}`,fontSize:9,color:"#444",textTransform:"uppercase",letterSpacing:"0.1em" },
  tableRow:{ display:"grid",gridTemplateColumns:"1fr 50px 60px 50px",padding:"10px 14px",borderBottom:"1px solid #2a2a2a",alignItems:"center" },
  tableName:{ display:"flex",alignItems:"center",gap:8,minWidth:0,overflow:"hidden" },
  tableAvatar:{ width:26,height:26,borderRadius:"50%",background:"#1a1200",color:gold,fontSize:8,fontWeight:"bold",display:"inline-flex",alignItems:"center",justifyContent:"center",flexShrink:0 },
  tableCell:{ fontSize:12,color:"#bbb" },
  tableStatus:{ fontSize:11,padding:"3px 6px",borderRadius:2,textAlign:"center" },
  settingsCard:{ background:"#0d0d0d",borderRadius:6,padding:18,border:"1px solid #3a3a3a",marginBottom:12 },
  prizeEditorCard:{ background:"#0d0d0d",padding:12,marginBottom:2,border:"1px solid #333" },
  rankBadge:{ width:22,height:22,borderRadius:"50%",background:"#1a1200",color:gold,fontSize:11,fontWeight:"bold",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 },
  editBtn:{ padding:"4px 8px",background:"transparent",border:"1px solid #333",color:"#555",borderRadius:2,cursor:"pointer",fontSize:11 },
  deleteBtn:{ padding:"4px 8px",background:"transparent",border:"1px solid #2a0d0d",color:"#8B0000",borderRadius:2,cursor:"pointer",fontSize:11 },
  saveBtn:{ padding:"10px 16px",background:"#0d2a0d",border:"1px solid #1a4a1a",color:"#4caf50",borderRadius:2,cursor:"pointer",fontSize:12,fontWeight:"bold",width:"100%" },
  cancelBtn:{ padding:"9px 16px",background:"transparent",border:"1px solid #333",color:"#555",borderRadius:2,cursor:"pointer",fontSize:12 },
  addBtn:{ width:"100%",padding:"12px",borderRadius:2,background:"#0d2a0d",border:"1px solid #1a4a1a",color:"#4caf50",fontSize:13,cursor:"pointer",fontWeight:"bold" },
  regnskapRow:{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",background:"#0d0d0d",borderRadius:4,marginBottom:6,border:"1px solid #3a3a3a",gap:8 },
  regnskapLabel:{ fontSize:13,color:"#ccc",flex:1 },
  regnskapVal:{ fontSize:16,fontWeight:"bold",color:cream,flexShrink:0 },

  // Trekning
  drawScreen:{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"100vh",padding:20,background:"radial-gradient(ellipse at center, #0d0800 0%, #0a0a0a 80%)",width:"100%",boxSizing:"border-box" },
  drawPrizeLabel:{ margin:"0 0 8px",fontSize:13,color:gold,textAlign:"center",letterSpacing:"0.08em" },
  drawSmallLabel:{ margin:"0 0 8px",fontSize:11,color:"#666",letterSpacing:"0.2em",textTransform:"uppercase",textAlign:"center" },
  bigCountdown:{ fontSize:120,fontWeight:"bold",color:gold,lineHeight:1,textAlign:"center" },
  wheelResult:{ background:card,border:`1px solid ${gold}`,borderRadius:4,padding:"20px 24px",textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",gap:10,width:"100%",maxWidth:400,boxSizing:"border-box" },
  wheelWinnerName:{ margin:0,fontSize:26,fontWeight:"bold",color:cream },
  summaryScreen:{ textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",gap:14,width:"100%",maxWidth:420,padding:"0 20px",boxSizing:"border-box" },
  summaryTitle:{ margin:0,fontSize:22,fontWeight:"normal",color:gold,letterSpacing:"0.15em",textTransform:"uppercase" },
  summaryRow:{ display:"flex",alignItems:"center",gap:14,padding:"14px 16px",background:card,border:"1px solid #333",width:"100%",boxSizing:"border-box",marginBottom:2 },
  summaryMedal:{ fontSize:24,minWidth:30 },
  summaryName:{ margin:0,fontSize:16,fontWeight:"bold",color:cream,textAlign:"left" },
  summaryPrize:{ margin:0,fontSize:12,color:gold,textAlign:"left" },
};
